import React, { useState, useRef, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { OPS } from 'pdfjs-dist';
import {
  Square, Circle, Type, Pen, MousePointer, Hand, Save, Download,
  Trash2, Undo, Redo, Minus, ChevronLeft, ChevronRight,
  ZoomIn, ZoomOut, Crosshair, RotateCw, Camera, Send, Edit3,
  ArrowRight, Cloud, MessageSquare, StickyNote,
  MessageCircle, Stamp as StampIcon, Ruler, FileText, Zap, ArrowLeft, Calculator, Sparkles, Key, Layers, HardHat, Rocket, FolderOpen, Hash, X, Cable, Loader2, Check, Lightbulb, Grid3X3, Plus, Scan,
  Sun, Moon, Eye, EyeOff
} from 'lucide-react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { supabase } from '../lib/supabase';
import { embedMetadataInPDF, buildMetadataPayload } from '../lib/bidflowMetadata';
import { useBlueprintContext } from '../contexts/BlueprintContext';
import { useAnnotationSaveContext } from '../contexts/AnnotationSaveContext';
import { HandDrawnAnnotations } from './HandDrawnAnnotations';
import { CostEstimationTable } from './CostEstimationTable';
import { SimplifiedEstimateTable } from './SimplifiedEstimateTable';
import PageIndexSidebar from './PageIndexSidebar';
import RFIManager from './RFIManager';
import { WireTypeModal, WireType } from './WireTypeModal';
import { supabase as supabaseClient } from '../lib/supabase';
import { deleteAnnotationsByIdV2, loadGroupedAnnotationsFromV2, saveAnnotationSnapshotV2 } from '../lib/annotationV2Persistence';
import { autoEstimateComponent } from '../services/autoEstimation';
import { componentLibraryService } from '../services/componentLibraryService';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getUniqueComponentName, deduplicateComponentNames } from '../utils/componentNameUtils';
import { STANDARD_RECEPTACLE_CLASSES, LEGACY_STANDARD_RECEPTACLE_NAMES } from '../utils/receptacleUtils';
import JSZip from 'jszip';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '../styles/PDFAnnotator.css';

// Set up the PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFAnnotatorProps {
  file: {
    url: string;
    name: string;
    id?: string;
  } | undefined;
  estimationId: string;
  specsFiles?: any[];  // Array of all specs files
  currentSpecsFile?: any;  // Currently selected specs file
  onSpecsFileChange?: (fileId: string) => void;  // Callback to switch specs file
  drawingFiles?: any[];
  currentDrawingFile?: any;
  onDrawingFileChange?: (fileId: string) => void;
  currentDrawingFileId?: string;  // ID of the current drawing file for annotation tracking
  onScreenshotCaptured?: (base64Image: string) => void;
  electricalKey?: {
    components: Array<{
      name: string;
      symbol?: string;
      description: string;
      color: string;
      symbolType?: string;
    }>;
  };
  onAutoAnnotationTrigger?: (config: {
    pageNumber: number;
    electricalKey: any;
    estimationId: string;
    onDismiss?: () => void;
  }) => void;
  onKeyExtracted?: (extractedKey: any) => void;
  onKeyExtractionProgress?: (progress: string) => void;
  autoExtractKeyOnSnapshot?: boolean;
  activeMode?: 'draw' | 'specs' | 'rfi' | 'neca' | 'estimate';
  estimation?: any;
  isFilesPanelCollapsed?: boolean;
  onFilesToggle?: () => void;
  onHomeClick?: () => void;
  onModeChange?: (mode: 'draw' | 'specs' | 'rfi' | 'neca' | 'estimate') => void;
  isAgentChatCollapsed?: boolean;
  onChatToggle?: () => void;
  annotationCounts?: Map<string, Map<number, number>>;
  countingModeActive?: boolean;
  activePlacementComponent?: { name: string; symbol: string; color: string; symbolType?: string } | null;
  onPlacementComplete?: () => void;
  onStopCounting?: () => void;
  onAnnotationSaved?: (changedComponentTypes?: string[]) => Promise<void> | void;
  onSwitchToCountingTab?: () => void;
  onSwitchToWiresTab?: () => void;
  onAddComponentToKey?: (component: { name: string; color: string; symbol: string; referenceImage?: string }) => Promise<void>;
  agentChatRef?: React.RefObject<any>;
  pageIndex?: Array<{ page: number; label: string; sheetName?: string }> | null;
  onReindex?: () => Promise<void>;
}

// Annotation types
type AnnotationTool = 'select' | 'drag' | 'rectangle' | 'line' | 'pen' | 'text' | 'circle' | 'snapshot' | 'piping' | 'wire-run' |
  'hand-pen' | 'hand-line' | 'hand-arrow' | 'hand-rectangle' | 'hand-ellipse' | 'hand-cloud' |
  'hand-text' | 'hand-note' | 'hand-callout' | 'hand-stamp' | 'hand-polyline' | 'hand-polygon' | 'hand-wire-run' |
  'hand-square-footage';

interface Annotation {
  id: string;
  type: AnnotationTool;
  component_type?: string;
  page: number;
  startX: number;
  startY: number;
  endX?: number;
  endY?: number;
  width?: number;
  height?: number;
  text?: string;
  color: string;
  thickness: number;
  points?: { x: number; y: number }[];
  timestamp: string;
  number?: number;
  // For AI-generated circle annotations
  x?: number;
  y?: number;
  radius?: number;
  // For piping tool
  startAnnotationId?: string;
  endAnnotationId?: string;
  measurement?: number;  // Calculated real-world distance
  measurementUnit?: string;  // ft, in, m, etc.
  // For takeoff reports
  symbolType?: string;
  componentName?: string;
  // For wire runs
  wireTypeName?: string;
  wireTypeColor?: string;
  totalLength?: number;
  // For square footage
  totalArea?: number;
  areaUnit?: string;
  // For AI-generated detections
  auto_detected?: boolean;
  confidence?: number;
  // Relative positions for DPI independence
  relativeX?: number;
  relativeY?: number;
  relativeStartX?: number;
  relativeStartY?: number;
  relativeWidth?: number;
  relativeHeight?: number;
  savedCanvasWidth?: number;
  savedCanvasHeight?: number;
}

interface PDFVectorElement {
  type: 'line' | 'rect' | 'curve' | 'path' | 'xobject' | 'text';
  bounds: { x: number; y: number; width: number; height: number };
  path?: { x: number; y: number }[];
  color?: string;
  lineWidth?: number;
  text?: string;
  objectName?: string;
}

// Electrical color presets - solid colors for better visibility
const ELECTRICAL_COLORS = {
  power: 'rgb(255, 0, 0)',         // Red for power
  neutral: 'rgb(128, 128, 128)',   // Gray for neutral
  ground: 'rgb(0, 255, 0)',        // Green for ground
  data: 'rgb(0, 0, 255)',          // Blue for data/low voltage
  highlight: 'rgb(255, 255, 0)',   // Yellow for highlights
};

// Tooltip Button Component
const TooltipButton: React.FC<{
  className?: string;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  tooltip: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ className, onClick, disabled, tooltip, children, style }) => {
  return (
    <div className="toolbar-btn-wrapper">
      <button
        className={className}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick?.(e);
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        disabled={disabled}
        style={style}
      >
        {children}
      </button>
      <div className="custom-tooltip">{tooltip}</div>
    </div>
  );
};

// Calibration Modal Component
const CalibrationModal: React.FC<{
  isOpen: boolean;
  percentage: number;
  onClose: () => void;
  onSave: (realValue: number, unit: string) => void;
  onRedo: () => void;
}> = ({ isOpen, percentage, onClose, onSave, onRedo }) => {
  const [realValue, setRealValue] = useState('');
  const [unit, setUnit] = useState<'ft' | 'in' | 'm' | 'cm' | 'mm'>('ft');
  
  if (!isOpen) return null;
  
  return (
    <div className="calibration-modal-overlay" onClick={onClose}>
      <div className="calibration-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Set Real-World Measurement</h3>
        
        <div className="calibration-measurement-info">
          <p>Line measures <strong>{percentage}%</strong> of canvas</p>
        </div>
        
        <div className="calibration-measurement-input">
          <label>What is the real-world measurement?</label>
          <div className="calibration-input-group">
            <input
              type="number"
              step="0.1"
              value={realValue}
              onChange={(e) => setRealValue(e.target.value)}
              placeholder="Enter value"
              autoFocus
            />
            <select value={unit} onChange={(e) => setUnit(e.target.value as any)}>
              <option value="ft">Feet (ft)</option>
              <option value="in">Inches (in)</option>
              <option value="m">Meters (m)</option>
              <option value="cm">Centimeters (cm)</option>
              <option value="mm">Millimeters (mm)</option>
            </select>
          </div>
        </div>
        
        <div className="calibration-modal-actions">
          <button onClick={onRedo} className="btn-secondary">
            ↻ Redo
          </button>
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button 
            onClick={() => {
              const val = parseFloat(realValue);
              if (!isNaN(val) && val > 0) {
                onSave(val, unit);
              }
            }}
            disabled={!realValue || parseFloat(realValue) <= 0}
            className="btn-primary"
          >
            ✓ Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Calibration Dropdown Component
const CalibrationDropdown: React.FC<{
  isOpen: boolean;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  currentPageNumber: number;
  currentPageCalibration?: any;
  allCalibrations: any;
  onStartCalibration: () => void;
  onCopyFromPage: (pageNum: number) => void;
  onToggleReferenceLine: () => void;
}> = ({ isOpen, dropdownRef, currentPageNumber, currentPageCalibration, allCalibrations, onStartCalibration, onCopyFromPage, onToggleReferenceLine }) => {
  const [showPageDropdown, setShowPageDropdown] = useState(false);
  
  if (!isOpen) return null;
  
  const otherPages = Object.keys(allCalibrations).filter(p => parseInt(p) !== currentPageNumber);
  
  return (
    <div ref={dropdownRef} className="calibration-dropdown">
      <div className="calibration-dropdown-header">
        <h4>Calibrate Scale</h4>
      </div>
      
      {currentPageCalibration ? (
        <div className="calibration-current">
          <div className="calibration-current-display">
            <strong>Current Calibration:</strong>
            <span className="calibration-ratio">
              {currentPageCalibration.canvasPercentage}% : {currentPageCalibration.realWorldValue}{currentPageCalibration.unit}
            </span>
          </div>
          {currentPageCalibration.referenceLine && (
            <div className="calibration-reference-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={currentPageCalibration.showReferenceLine !== false}
                  onChange={onToggleReferenceLine}
                />
                <span>Show reference line</span>
              </label>
            </div>
          )}
        </div>
      ) : (
        <div className="calibration-no-calibration">
          <p>No calibration set for this page</p>
        </div>
      )}
      
      <div className="calibration-dropdown-actions">
        <button onClick={onStartCalibration} className="calibration-btn-primary">
          Set New Calibration
        </button>
        
        {otherPages.length > 0 && (
          <div className="calibration-copy">
            <label>Or copy from another page:</label>
            <div className="calibration-page-selector">
              <button 
                className="calibration-page-selector-button"
                onClick={() => setShowPageDropdown(!showPageDropdown)}
              >
                <span>Select page...</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showPageDropdown && (
                <div className="calibration-page-selector-menu">
                  {otherPages.map(p => {
                    const cal = allCalibrations[p];
                    return (
                      <button
                        key={p}
                        className="calibration-page-selector-item"
                        onClick={() => {
                          onCopyFromPage(parseInt(p));
                          setShowPageDropdown(false);
                        }}
                      >
                        <span className="page-number">Page {p}</span>
                        <span className="page-calibration">
                          {cal.canvasPercentage}% : {cal.realWorldValue}{cal.unit}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to parse PDF and extract elements using simpler approach
const parsePDFAsSVG = async (page: any): Promise<PDFVectorElement[]> => {
  const elements: PDFVectorElement[] = [];
  
  try {
    console.log('📊 Extracting PDF elements...');
    
    // Get canvas for scaling
    const canvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    if (!canvas) {
      console.warn('⚠️ Canvas not available');
      return elements;
    }
    
    const viewport = page.getViewport({ scale: 1 });
    const scaleX = canvas.width / viewport.width;
    const scaleY = canvas.height / viewport.height;
    
    // Get text content (always works)
    const textContent = await page.getTextContent();
    
    // Add text elements
    textContent.items.forEach((item: any) => {
      if (item.str && item.transform) {
        const x = item.transform[4] * scaleX;
        const y = item.transform[5] * scaleY;
        const fontSize = Math.sqrt(item.transform[0] * item.transform[0] + item.transform[1] * item.transform[1]);
        const textWidth = (item.width || item.str.length * fontSize * 0.6) * scaleX;
        const textHeight = fontSize * scaleY;
        
        elements.push({
          type: 'text',
          bounds: { x, y: y - textHeight, width: textWidth, height: textHeight },
          text: item.str,
          color: 'rgba(0, 255, 255, 0.7)'
        });
      }
    });
    
    // Get operator list for shape and symbol detection
    const operatorList = await page.getOperatorList();
    const ops = operatorList.fnArray;
    const args = operatorList.argsArray;
    
    // Parse operators to detect shapes and symbols
    let currentTransform = [1, 0, 0, 1, 0, 0];
    const transformStack: number[][] = [];
    
    // Helper to transform coords
    const transformCoord = (x: number, y: number) => ({
      x: x * scaleX,
      y: y * scaleY
    });
    
    // Track paths and detect filled shapes (electrical symbols!)
    let currentPath: {x: number, y: number}[] = [];
    const filledPaths: {x: number, y: number}[][] = [];
    
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];
      const arg = args[i];
      
      // Track transforms
      if (op === OPS.transform && arg && arg.length >= 6) {
        currentTransform = arg;
      } else if (op === OPS.save) {
        transformStack.push([...currentTransform]);
      } else if (op === OPS.restore && transformStack.length > 0) {
        currentTransform = transformStack.pop()!;
      }
      
      // Build paths from constructPath
      if (op === OPS.constructPath && arg && arg.length >= 3 && arg[1] && arg[1][0]) {
        const data = arg[1][0];
        let idx = 0;
        currentPath = [];
        
        while (idx < data.length) {
          const pathOp = data[idx++];
          if (pathOp === 1 && idx + 1 < data.length) { // moveTo
            currentPath = [transformCoord(data[idx++], data[idx++])];
          } else if (pathOp === 2 && idx + 1 < data.length) { // lineTo
            currentPath.push(transformCoord(data[idx++], data[idx++]));
          } else if (pathOp === 3 && idx + 5 < data.length) { // curveTo
            // Just add the endpoint for simplicity
            idx += 4; // Skip control points
            currentPath.push(transformCoord(data[idx++], data[idx++]));
          }
        }
      }
      
      // When a path is filled, it's likely a symbol!
      if ((op === OPS.fill || op === OPS.eoFill || op === OPS.fillStroke) && currentPath.length > 2) {
        filledPaths.push([...currentPath]);
        
        // Calculate bounds
        const xs = currentPath.map(p => p.x);
        const ys = currentPath.map(p => p.y);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const width = maxX - minX;
        const height = maxY - minY;
        
        // Skip very large fills (backgrounds) and tiny artifacts
        if (width > 5 && width < 200 && height > 5 && height < 200) {
          elements.push({
            type: 'xobject', // Treat filled shapes as symbols
            bounds: { x: minX, y: minY, width, height },
            objectName: 'filled-shape',
            color: 'rgba(255, 0, 255, 0.7)'
          });
        }
        
        currentPath = [];
      }
      
      // Also detect XObject rendering operations
      if (op === OPS.paintFormXObjectBegin || op === OPS.paintImageXObject) {
        const tx = currentTransform[4];
        const ty = currentTransform[5];
        const sx = Math.abs(currentTransform[0]);
        const sy = Math.abs(currentTransform[3]);
        
        const coord = transformCoord(tx, ty);
        const size = Math.max(sx * scaleX, sy * scaleY, 40);
        
        elements.push({
          type: 'xobject',
          bounds: {
            x: coord.x - size / 2,
            y: coord.y - size / 2,
            width: size,
            height: size
          },
          objectName: 'xobject',
          color: 'rgba(255, 0, 255, 0.7)'
        });
      }
    }
    
    console.log(`📊 Detected ${filledPaths.length} filled paths (likely symbols)`);
    
    const typeCounts = {
      xobject: elements.filter(e => e.type === 'xobject').length,
      text: elements.filter(e => e.type === 'text').length,
      curve: elements.filter(e => e.type === 'curve').length,
      path: elements.filter(e => e.type === 'path').length,
      line: elements.filter(e => e.type === 'line').length,
      rect: elements.filter(e => e.type === 'rect').length
    };
    console.log(`✅ Parsed ${elements.length} SVG elements:`, typeCounts);
    
  } catch (error) {
    console.error('Error parsing PDF as SVG:', error);
  }
  
  return elements;
};

/**
 * PDFAnnotator Component
 * 
 * Coordinate Systems:
 * - Canvas Coordinates: The internal resolution (e.g., 16000x11428)
 *   This is determined by react-pdf based on device pixel ratio and target resolution
 * - Visual Coordinates: How it appears on screen (varies with DPR)
 *   Calculated as canvas coordinates * (display size / canvas size)
 * - Zoom-adjusted: Visual coordinates * zoom scale
 *   Final position accounting for user zoom level
 * 
 * All annotations are stored in canvas coordinates for consistency.
 * All UI positioning uses zoom-adjusted visual coordinates.
 */

function computeLightingOverallPct(
  pageProgress: Map<number, { phase: string; pct: number; completedTiles: number; totalTiles: number; verifiedSoFar?: number; totalToVerify?: number }>,
  totalPages: number
): number {
  if (totalPages === 0) return 0;
  let sum = 0;
  Array.from(pageProgress.values()).forEach(pp => {
    if (pp.phase === 'done') {
      sum += 100;
    } else if (pp.phase === 'verifying') {
      // Smoothly crawl from 88% to 100% based on verification progress
      const verifyRatio = (pp.totalToVerify && pp.totalToVerify > 0) 
        ? (pp.verifiedSoFar || 0) / pp.totalToVerify 
        : 0;
      sum += 88 + (verifyRatio * 12);
    } else if (pp.totalTiles > 0 && pp.completedTiles > 0) {
      const tileRatio = pp.completedTiles / pp.totalTiles;
      sum += 15 + tileRatio * 73;
    } else {
      sum += Math.min(pp.pct || 0, 100);
    }
  });
  return Math.min(Math.round(sum / totalPages), 100);
}

const PDFAnnotator = forwardRef<any, PDFAnnotatorProps>(({ file, estimationId, specsFiles, currentSpecsFile, onSpecsFileChange, drawingFiles, currentDrawingFile, onDrawingFileChange, currentDrawingFileId, onScreenshotCaptured, electricalKey, onAutoAnnotationTrigger, onKeyExtracted, onKeyExtractionProgress, autoExtractKeyOnSnapshot = false, activeMode = 'draw', estimation, isFilesPanelCollapsed, onFilesToggle, onHomeClick, onModeChange, isAgentChatCollapsed, onChatToggle, annotationCounts, countingModeActive, activePlacementComponent, onPlacementComplete, onStopCounting, onAnnotationSaved, onSwitchToCountingTab, onSwitchToWiresTab, onAddComponentToKey, agentChatRef, pageIndex, onReindex }, ref) => {
  // Increase hit box for better annotation interaction
  const HIT_BOX_PADDING = 15; // pixels of padding around annotations for easier selection
  
  // Get current user for library sync
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [specsNumPages, setSpecsNumPages] = useState<number | null>(null);
  const [specsPageNumber, setSpecsPageNumber] = useState(1);
  const [specsScale, setSpecsScale] = useState(1.2);
  const [specsPageRotations, setSpecsPageRotations] = useState<Record<number, number>>({});
  const [necaNumPages, setNecaNumPages] = useState<number | null>(null);
  const [necaPageNumber, setNecaPageNumber] = useState(1);
  const [necaScale, setNecaScale] = useState(1.2); // 120% default zoom
  
  // Page number input state for editable input
  const [pageInputValue, setPageInputValue] = useState<string>('1');
  
  // Sync input value with actual page number
  useEffect(() => {
    setPageInputValue(pageNumber.toString());
  }, [pageNumber]);
  
  // Debug: Log NECA page changes
  useEffect(() => {
    if (activeMode === 'neca') {
      console.log('📄 NECA page changed to:', necaPageNumber);
    }
  }, [necaPageNumber, activeMode]);
  
  // Debug: Log NECA scale changes
  useEffect(() => {
    if (activeMode === 'neca') {
      console.log('🔍 NECA scale changed to:', necaScale);
    }
  }, [necaScale, activeMode]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [highlightBoundingBox, setHighlightBoundingBox] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const MIN_VIEW_SCALE = 0.0;
  const DEFAULT_VIEW_SCALE = 0.15;
  const [scale, setScale] = useState(DEFAULT_VIEW_SCALE);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [pdfRenderScale, setPdfRenderScale] = useState(1);
  const [isHighResReady, setIsHighResReady] = useState(false);
  const [isUpgradingRes, setIsUpgradingRes] = useState(false);
  const highResRenderRef = useRef<{ cancel: boolean }>({ cancel: false });
  const pdfPageRef = useRef<any>(null);

  const FAST_RES = 4000;
  const FULL_RES = 8000;
  // Keep render density consistent across displays (Retina and non-Retina)
  const DPR_CAP = 1;
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [annotationsLoaded, setAnnotationsLoaded] = useState(false);
  const [isCanvasSynced, setIsCanvasSynced] = useState(false);
  const [isPDFRendered, setIsPDFRendered] = useState(false);
  const [isPageFullyLoaded, setIsPageFullyLoaded] = useState(false);
  const [currentTool, setCurrentTool] = useState<AnnotationTool>('select');
  const [currentColor, setCurrentColor] = useState(ELECTRICAL_COLORS.power);
  const [thickness, setThickness] = useState(4);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentAnnotation, setCurrentAnnotation] = useState<Partial<Annotation> | null>(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState<string | null>(null);
  const [history, setHistory] = useState<Annotation[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [pdfDocument, setPdfDocument] = useState<any>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [clickCoords, setClickCoords] = useState<{ x: number; y: number } | null>(null);
  // Resize state for rectangle annotations
  const [resizeHandle, setResizeHandle] = useState<{ annotationId: string; handle: 'nw' | 'ne' | 'sw' | 'se'; startX: number; startY: number; origRect: { startX: number; startY: number; width: number; height: number } } | null>(null);
  const [hoverCursor, setHoverCursor] = useState<string>('default');
  const lastMousePosRef = useRef<{ x: number; y: number } | null>(null);
  const lastWheelTimeRef = useRef<number>(0);
  const shouldCenterAfterRenderRef = useRef<boolean>(true);
  const activeRenderTokenRef = useRef<number>(0);
  const [highlightedComponentType, setHighlightedComponentType] = useState<string | null>(null);
  const [viewAllMode, setViewAllMode] = useState(false);
  const [animatingAnnotations, setAnimatingAnnotations] = useState<Set<string>>(new Set());
  const [annotationTimestamps, setAnnotationTimestamps] = useState<Map<string, number>>(new Map());
  const [isPanning, setIsPanning] = useState(false);
  const [isRightClickPanning, setIsRightClickPanning] = useState(false); // Temporary pan via right-click hold
  
  // UNIFIED SELECT: Track selected hand-drawn strokes alongside counting annotations
  const [selectedHandDrawnStrokes, setSelectedHandDrawnStrokes] = useState<Set<string>>(new Set());
  const [panStart, setPanStart] = useState<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const [hoveredAnnotation, setHoveredAnnotation] = useState<string | null>(null);
  const [hoveredAnnotationPoint, setHoveredAnnotationPoint] = useState<{ x: number; y: number } | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionRect, setSelectionRect] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
  const [selectedAnnotations, setSelectedAnnotations] = useState<Set<string>>(new Set());
  const [pageRotations, setPageRotations] = useState<{ [pageNum: number]: number }>({});
  const [snapshotSelection, setSnapshotSelection] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
  const [handDrawnEnabled, setHandDrawnEnabled] = useState(false);
  const [handDrawnColor, setHandDrawnColor] = useState('#FF0000');
  const [handDrawnLineWidth, setHandDrawnLineWidth] = useState(0.5); // Percentage of canvas width (0.1% to 10%)
  const [handDrawnFontSize, setHandDrawnFontSize] = useState(1.0); // Percentage of canvas width (0.3% to 3%)
  const fontDropdownRef = useRef<HTMLDivElement>(null);
  const [canUndoHandDrawn, setCanUndoHandDrawn] = useState(false);
  const [showNewComponentModal, setShowNewComponentModal] = useState(false);
  const [countingModalMode, setCountingModalMode] = useState<'manual' | 'auto'>('manual');
  const [newComponentName, setNewComponentName] = useState('');
  const [newComponentColor, setNewComponentColor] = useState('#3b82f6');
  const [isAddingComponent, setIsAddingComponent] = useState(false);
  const [isAddingNewOnly, setIsAddingNewOnly] = useState(false);
  const [isAddingNewOnlyScanMode, setIsAddingNewOnlyScanMode] = useState(false);
  const [selectedComponentsForBatch, setSelectedComponentsForBatch] = useState<Set<string>>(new Set());
  
  // Autocount area selection states
  const [autocountMode, setAutocountMode] = useState<'area-selection' | null>(null);
  const [autocountSelection, setAutocountSelection] = useState<{startX: number, startY: number, endX: number, endY: number} | null>(null);
  const [showAutocountConfirmModal, setShowAutocountConfirmModal] = useState(false);
  const [pendingAutocountComponent, setPendingAutocountComponent] = useState<{name: string, color: string; isLighting?: boolean} | null>(null);
  const [referenceAreaImage, setReferenceAreaImage] = useState<string | null>(null);
  const [queuedComponents, setQueuedComponents] = useState<Array<{ name: string; color: string; referenceImage: string }>>([]);
  const [editingQueuedName, setEditingQueuedName] = useState('');
  const [isAutocounting, setIsAutocounting] = useState(false);
  const [autocountProgress, setAutocountProgress] = useState('');
  const [showAutocountReviewModal, setShowAutocountReviewModal] = useState(false);
  const [showReceptacleRefPopup, setShowReceptacleRefPopup] = useState(false);
  const [quickCountSearch, setQuickCountSearch] = useState('');
  const [autocountDetections, setAutocountDetections] = useState<Array<{
    prediction: any;
    thumbnail: string;
    id: string;
  }>>([]);
  const [selectedDetectionsToKeep, setSelectedDetectionsToKeep] = useState<Set<string>>(new Set());
  const [canRedoHandDrawn, setCanRedoHandDrawn] = useState(false);
  
  // Streaming autocount progress tracking
  const [autocountTotalBatches, setAutocountTotalBatches] = useState(0);
  const [autocountCompletedBatches, setAutocountCompletedBatches] = useState(0);
  
  // Multi-page autocount progress tracking
  const [pageProgressMap, setPageProgressMap] = useState<Map<number, {
    current: number;
    total: number;
    detections: number;
    status: 'processing' | 'complete' | 'error';
  }>>(new Map());
  
  // Multi-component "Count All" state
  const [isCountingAll, setIsCountingAll] = useState(false);
  const [countAllProgress, setCountAllProgress] = useState('');

  // Lighting-only counting modal (no review, progress-only)
  const [showLightingCountingModal, setShowLightingCountingModal] = useState(false);
  const [lightingCountingFinished, setLightingCountingFinished] = useState(false);
  const [lightingProgress, setLightingProgress] = useState<{
    totalPages: number;
    pageProgress: Map<number, { phase: string; pct: number; completedTiles: number; totalTiles: number; verifiedSoFar?: number; totalToVerify?: number }>;
    overallPct: number;
    detectionsFound: number;
    verifiedCount: number;
    totalCandidates: number;
    message: string;
  }>({ totalPages: 0, pageProgress: new Map(), overallPct: 0, detectionsFound: 0, verifiedCount: 0, totalCandidates: 0, message: '' });
  
  // Key extraction from legend states
  const [keyExtractionMode, setKeyExtractionMode] = useState(false);
  const [keyExtractionSelection, setKeyExtractionSelection] = useState<{startX: number, startY: number, endX: number, endY: number} | null>(null);
  const [isExtractingKeyFromLegend, setIsExtractingKeyFromLegend] = useState(false);
  const [keyExtractionSnapshot, setKeyExtractionSnapshot] = useState<string | null>(null);
  const [showKeyExtractionConfirmModal, setShowKeyExtractionConfirmModal] = useState(false);
  const [showKeyExtractionPreview, setShowKeyExtractionPreview] = useState(false);
  const [extractedKeyPreview, setExtractedKeyPreview] = useState<Array<{
    name: string;
    abbreviation: string;
    description: string;
    componentType: string;
    subscript: string;
    box_2d: number[] | null;
    croppedImage: string | null;
    rowIndex: number;
  }>>([]);

  // Multi-area key extraction jobs (concurrent)
  type KeyExtractionJob = {
    id: string;
    snapshot: string;
    status: 'queued' | 'processing' | 'done' | 'error';
    results?: Array<{ name: string; abbreviation: string; description: string; componentType: string; subscript: string; box_2d: number[] | null; croppedImage: string | null; rowIndex: number }>;
    error?: string;
    abortController?: AbortController;
  };
  const [keyExtractionJobs, setKeyExtractionJobs] = useState<KeyExtractionJob[]>([]);

  // Lighting schedule extraction states
  const [lightingScheduleMode, setLightingScheduleMode] = useState(false);
  const [lightingScheduleSelection, setLightingScheduleSelection] = useState<{startX: number, startY: number, endX: number, endY: number} | null>(null);
  const [isExtractingLightingSchedule, setIsExtractingLightingSchedule] = useState(false);
  const [lightingScheduleSnapshot, setLightingScheduleSnapshot] = useState<string | null>(null);
  const [showLightingScheduleConfirmModal, setShowLightingScheduleConfirmModal] = useState(false);
  const [showLightingSchedulePreview, setShowLightingSchedulePreview] = useState(false);
  const [extractedLightingFixtures, setExtractedLightingFixtures] = useState<Array<{
    code: string;
    name: string;
    description: string;
    rowIndex: number;
  }>>([]);
  const [selectedLightingFixtureIndices, setSelectedLightingFixtureIndices] = useState<Set<number>>(new Set());
  const [selectedKeyPreviewIndices, setSelectedKeyPreviewIndices] = useState<Set<number>>(new Set());
  const [autocountModalTab, setAutocountModalTab] = useState<'components' | 'lighting'>('components');

  const [countAllComponentProgress, setCountAllComponentProgress] = useState<Array<{
    name: string;
    color: string;
    referenceImage: string;
    status: 'pending' | 'processing' | 'complete';
    count?: number;
    completedBatches?: number; // Total batches completed across all pages
    totalBatches?: number; // Total batches across all pages (pages × 5)
    currentPage?: number;
    currentBatch?: number;
  }>>([]);
  const [multiComponentDetections, setMultiComponentDetections] = useState<Array<{
    componentName: string;
    componentColor: string;
    detections: Array<{ prediction: any; thumbnail: string; id: string }>;
    referenceImage: string;
    isLighting?: boolean;
  }>>([]);
  const [showMultiComponentReviewModal, setShowMultiComponentReviewModal] = useState(false);
  const [currentReviewComponentIndex, setCurrentReviewComponentIndex] = useState(0);
  const [allComponentSelections, setAllComponentSelections] = useState<Map<string, Set<string>>>(new Map());
  
  // Original receptacle components when grouped into a single RECEPTACLES pass
  const [receptacleGroupComponents, setReceptacleGroupComponents] = useState<Array<{
    color: string; name: string; symbol: string; referenceImage: string;
    symbolType?: string; componentType?: string; subscript?: string; description?: string;
  }>>([]);
  // Original tele/data components when grouped into a single TELE/DATA pass
  const [teleDataGroupComponents, setTeleDataGroupComponents] = useState<Array<{
    color: string; name: string; symbol: string; referenceImage: string;
    symbolType?: string; componentType?: string; subscript?: string; description?: string;
  }>>([]);
  // Original lighting components when grouped into a single LIGHTING pass
  const [lightingGroupComponents, setLightingGroupComponents] = useState<Array<{
    color: string; name: string; symbol: string; referenceImage: string;
    symbolType?: string; componentType?: string; subscript?: string; description?: string;
  }>>([]);
  // Per-detection classification overrides (detection id -> component name)
  const [detectionClassifications, setDetectionClassifications] = useState<Map<string, string>>(new Map());
  const [receptacleClassFilter, setReceptacleClassFilter] = useState<string>('all');
  const [teleDataClassFilter, setTeleDataClassFilter] = useState<string>('all');
  const [lightingClassFilter, setLightingClassFilter] = useState<string>('all');
  const [bulkSelectedDetections, setBulkSelectedDetections] = useState<Set<string>>(new Set());
  const [bulkReclassTarget, setBulkReclassTarget] = useState<string>('');
  const [isExportingDataset, setIsExportingDataset] = useState(false);
  const [isParallelCountSession, setIsParallelCountSession] = useState(false);
  
  // State for sequential key extraction counting
  const [pendingKeyComponents, setPendingKeyComponents] = useState<Array<{
    color: string;
    name: string;
    symbol: string;
    referenceImage: string;
    symbolType?: string;
    componentType?: string;
    subscript?: string;
    description?: string;
  }>>([]);

  // Page selection modal (before counting a component)
  const [showPageSelectionModal, setShowPageSelectionModal] = useState(false);
  const [pageSelectionContext, setPageSelectionContext] = useState<{
    component: { color: string; name: string; symbol: string; referenceImage: string; symbolType?: string; componentType?: string; subscript?: string; description?: string };
    componentIndex: number;
    newComponents?: Array<{ color: string; name: string; symbol: string; referenceImage: string; symbolType?: string; componentType?: string; subscript?: string; description?: string }>;
    parallelMode?: boolean;
  } | null>(null);
  const [selectedPagesForCount, setSelectedPagesForCount] = useState<Set<number>>(new Set());
  const [pageThumbnailsForSelection, setPageThumbnailsForSelection] = useState<Array<{ pageNumber: number; base64: string }> | null>(null);
  
  const [showLaborEstimationModal, setShowLaborEstimationModal] = useState(false);
  const [laborEstimationProgress, setLaborEstimationProgress] = useState<Array<{
    name: string;
    count: number;
    status: 'pending' | 'processing' | 'complete' | 'error';
  }>>([]);
  const [showDrawingDropdown, setShowDrawingDropdown] = useState(false);
  const drawingDropdownRef = useRef<HTMLDivElement | null>(null);
  const [showFilesDropdown, setShowFilesDropdown] = useState(false);
  const filesDropdownRef = useRef<HTMLDivElement | null>(null);
  
  // Wire run states
  const [isWireRunMode, setIsWireRunMode] = useState(false);
  const [showWireTypeModal, setShowWireTypeModal] = useState(false);
  const [pendingWireRunPoints, setPendingWireRunPoints] = useState<{x: number; y: number}[]>([]);
  const [pendingWireRunLength, setPendingWireRunLength] = useState(0);
  const [wireRunContextMenu, setWireRunContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [pendingWireRunPage, setPendingWireRunPage] = useState(1);
  const [wireRunRefreshTrigger, setWireRunRefreshTrigger] = useState(0);
  const [selectedWireType, setSelectedWireType] = useState<WireType | null>(null); // Store selected type for drawing
  // Context menu for active wire run drawing - REMOVED: Right-click now auto-finishes
  
  // Square footage states
  const [isSquareFootageMode, setIsSquareFootageMode] = useState(false);
  
  // Exit wire run mode when switching to a different tool
  useEffect(() => {
    if (currentTool !== 'hand-wire-run' && isWireRunMode) {
      console.log('🔌 Exiting wire run mode - tool changed to:', currentTool);
      setIsWireRunMode(false);
      setSelectedWireType(null);
    }
  }, [currentTool, isWireRunMode]);
  
  // Exit square footage mode when switching to a different tool
  useEffect(() => {
    if (currentTool !== 'hand-square-footage' && isSquareFootageMode) {
      setIsSquareFootageMode(false);
    }
  }, [currentTool, isSquareFootageMode]);
  
  // Per-tool settings
  const [toolSettings, setToolSettings] = useState<{[key: string]: { color: string; thickness: number; opacity?: number }}>({
    'hand-pen': { color: 'var(--text-primary)', thickness: 0.05 },
    'hand-highlight': { color: '#FFFF00', thickness: 0.05, opacity: 0.3 },
    'hand-line': { color: '#1E40AF', thickness: 0.05 },
    'hand-arrow': { color: '#DC2626', thickness: 0.05 },
    'hand-dimension': { color: '#0891B2', thickness: 0.05 },
    'hand-eraser': { color: '#ffffff', thickness: 0.05 },
  });
  const [showToolDropdown, setShowToolDropdown] = useState<{[key: string]: boolean}>({});
  const toolDropdownRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  const [dropdownPositions, setDropdownPositions] = useState<{[key: string]: {top: number; left: number}}>({});
  const [isCapturingSnapshot, setIsCapturingSnapshot] = useState(false);
  const [showSnapshotActions, setShowSnapshotActions] = useState(false);
  // Initialize with 0 - will be synced with actual PDF dimensions once rendered
  const [currentCanvasWidth, setCurrentCanvasWidth] = useState(0);
  const [currentCanvasHeight, setCurrentCanvasHeight] = useState(0);
  
  // Calibration states
  const [showCalibrationDropdown, setShowCalibrationDropdown] = useState(false);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [calibrationLine, setCalibrationLine] = useState<{
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    percentage: number;
  } | null>(null);
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [calibrationData, setCalibrationData] = useState<{
    [pageNumber: string]: {
      canvasPercentage: number;
      realWorldValue: number;
      unit: 'ft' | 'in' | 'm' | 'cm' | 'mm';
      referencePageNumber?: number;
      createdAt?: string;
      referenceLine?: {
        startX: number;
        startY: number;
        endX: number;
        endY: number;
      };
      showReferenceLine?: boolean;
    }
  }>({});
  const [isGridSnapActive, setIsGridSnapActive] = useState(false);
  const [showDebugTileGrid, setShowDebugTileGrid] = useState(false);
  const [pipingStart, setPipingStart] = useState<{ annotationId: string; x: number; y: number } | null>(null);
  const [pipingPreview, setPipingPreview] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
  const [pipingSnapTarget, setPipingSnapTarget] = useState<{ annotationId: string; x: number; y: number } | null>(null);
  const [isElementDetectionMode, setIsElementDetectionMode] = useState(false);
  const [pdfVectorElements, setPdfVectorElements] = useState<PDFVectorElement[]>([]);
  const [hoveredPDFElement, setHoveredPDFElement] = useState<PDFVectorElement | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debugTileGridRef = useRef<{ gridSize: number; tileWidth: number; tileHeight: number; overlap: number; hiResWidth: number; hiResHeight: number } | null>(null);
  const calibrationDropdownRef = useRef<HTMLDivElement>(null);
  const lastPlacedAnnotationsRef = useRef<string[]>([]); // Track last 20 placed annotations for undo
  
  // Memoize PDF options to prevent unnecessary reloads
  const pdfOptions = useMemo(() => ({
    cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
    cMapPacked: true,
  }), []);
  
  // Close tool dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.entries(toolDropdownRefs.current).forEach(([tool, ref]) => {
        if (ref && !ref.contains(event.target as Node)) {
          setShowToolDropdown(prev => ({ ...prev, [tool]: false }));
        }
      });
      
      // Handle drawing dropdown
      if (drawingDropdownRef.current && !drawingDropdownRef.current.contains(event.target as Node)) {
        setShowDrawingDropdown(false);
      }
      if (filesDropdownRef.current && !filesDropdownRef.current.contains(event.target as Node)) {
        setShowFilesDropdown(false);
      }
    };

    const hasOpenDropdown = Object.values(showToolDropdown).some(isOpen => isOpen) || showDrawingDropdown || showFilesDropdown;
    if (hasOpenDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showToolDropdown, showDrawingDropdown, showFilesDropdown]);


  // UNIFIED SELECT: Counting mode now only affects placement behavior, not tool restrictions
  // Users can freely use select tool and other drawing tools while in counting mode
  useEffect(() => {
    console.log('📊 Counting mode changed:', countingModeActive, 'activePlacementComponent:', activePlacementComponent?.name);
    if (countingModeActive) {
      console.log('✅ Counting mode active - select tool can interact with both annotation layers');
      // Switch to select tool for component placement, but don't disable other tools
      setCurrentTool('select');
      // Keep hand-drawn tools enabled - users can switch to them freely
      setHandDrawnEnabled(true);
    } else {
      console.log('✅ Counting mode off');
      setHandDrawnEnabled(true);
      // Clear last placed annotations when exiting counting mode
      lastPlacedAnnotationsRef.current = [];
    }
    
    // Clear array when switching between different components to prevent undoing wrong component
    lastPlacedAnnotationsRef.current = [];
  }, [countingModeActive, activePlacementComponent]);
  // Sync tool settings with global color/thickness when tool changes
  useEffect(() => {
    if (currentTool && toolSettings[currentTool]) {
      setHandDrawnColor(toolSettings[currentTool].color);
      setHandDrawnLineWidth(toolSettings[currentTool].thickness);
    }
  }, [currentTool, toolSettings]);
  
  // NEW: Coordinate conversion for the transform-based stage
  const getScreenToCanvasCoords = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    
    // Calculate actual coordinates accounting for internal canvas resolution vs screen size
    // getBoundingClientRect() already accounts for CSS transforms (scale/pan)
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    return { x, y };
  }, []);

  const canvasToVisualCoords = useCallback((canvasX: number, canvasY: number) => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return { x: canvasX, y: canvasY };
    
    const rect = canvas.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Ratio of screen pixels to internal pixels
    const visualScaleX = rect.width / canvas.width;
    const visualScaleY = rect.height / canvas.height;
    
    // Return coordinates relative to the viewport container
    return {
      x: (canvasX * visualScaleX) + (rect.left - containerRect.left),
      y: (canvasY * visualScaleY) + (rect.top - containerRect.top)
    };
  }, []);

  const rescaleAnnotation = useCallback((ann: Annotation, newWidth: number, newHeight: number): Annotation => {
    // If canvas dimensions aren't valid, return annotation as-is to prevent collapsing to 0,0
    if (!newWidth || !newHeight || (newWidth === 300 && newHeight === 150)) {
      return ann;
    }

    const updated = { ...ann } as any;
    if (ann.relativeStartX !== undefined && ann.relativeStartY !== undefined) {
      updated.startX = ann.relativeStartX * newWidth;
      updated.startY = ann.relativeStartY * newHeight;
    }
    if (ann.relativeX !== undefined && ann.relativeY !== undefined) {
      updated.x = ann.relativeX * newWidth;
      updated.y = ann.relativeY * newHeight;
    }
    
    // For endX/endY, prefer relative coordinates if they exist
    if (ann.relativeWidth !== undefined && ann.relativeHeight !== undefined) {
      updated.width = ann.relativeWidth * newWidth;
      updated.height = ann.relativeHeight * newHeight;
      
      if (ann.relativeStartX !== undefined && ann.relativeStartY !== undefined) {
        updated.endX = updated.startX + updated.width;
        updated.endY = updated.startY + updated.height;
      }
    } else {
      // Legacy fallback using savedCanvasWidth
      if (ann.endX !== undefined && ann.savedCanvasWidth) {
        updated.endX = (ann.endX / ann.savedCanvasWidth) * newWidth;
      }
      if (ann.endY !== undefined && ann.savedCanvasHeight) {
        updated.endY = (ann.endY / ann.savedCanvasHeight) * newHeight;
      }
      if (ann.width !== undefined && ann.savedCanvasWidth) {
        updated.width = (ann.width / ann.savedCanvasWidth) * newWidth;
      }
      if (ann.height !== undefined && ann.savedCanvasHeight) {
        updated.height = (ann.height / ann.savedCanvasHeight) * newHeight;
      }
    }

    if ((ann as any).points) {
      updated.points = (ann as any).points.map((p: any) => ({
        ...p,
        x: p.relativeX !== undefined ? p.relativeX * newWidth : p.x,
        y: p.relativeY !== undefined ? p.relativeY * newHeight : p.y,
      }));
    }
    return updated as Annotation;
  }, []);

  // Get context if available (optional - component can work without it)
  const blueprintContext = useBlueprintContext();
  const hasContext = blueprintContext !== null;
  const { acquireSaveLock, releaseSaveLock } = useAnnotationSaveContext();
  
  // Helper function to get color from electrical key or use annotation's stored color
  const getColorForAnnotation = useCallback((annotation: Annotation): string => {
    // If we have an electrical key and the annotation has a component_type
    if (electricalKey && annotation.component_type) {
      const component = electricalKey.components.find(
        c => c.name === annotation.component_type || c.symbol === annotation.component_type
      );
      if (component) {
        return component.color;
      }
    }
    // Fallback to the annotation's stored color
    return annotation.color;
  }, [electricalKey]);

  // Load saved page rotations from localStorage
  useEffect(() => {
    if (estimationId && file?.name) {
      const storageKey = `page-rotations-${estimationId}-${file.name}`;
      const savedRotations = localStorage.getItem(storageKey);
      if (savedRotations) {
        try {
          const rotations = JSON.parse(savedRotations);
          setPageRotations(rotations);
          console.log('📐 Loaded saved page rotations:', rotations);
        } catch (err) {
          console.error('Failed to parse saved rotations:', err);
        }
      }
      
      // Also load specs rotations
      const specsStorageKey = `specs-rotations-${estimationId}-${file.name}`;
      const savedSpecsRotations = localStorage.getItem(specsStorageKey);
      if (savedSpecsRotations) {
        try {
          const rotations = JSON.parse(savedSpecsRotations);
          setSpecsPageRotations(rotations);
          console.log('📐 Loaded saved specs rotations:', rotations);
        } catch (err) {
          console.error('Failed to parse saved specs rotations:', err);
        }
      }
    }
  }, [estimationId, file?.name]);

  // Save current page numbers to localStorage for persistence across mode switches
  useEffect(() => {
    if (estimationId && file?.name && activeMode === 'draw') {
      try {
        const storageKey = `page-number-${estimationId}-${file.name}`;
        localStorage.setItem(storageKey, pageNumber.toString());
      } catch { /* quota exceeded — ignore */ }
    }
  }, [pageNumber, estimationId, file?.name, activeMode]);

  // Save specs page number
  useEffect(() => {
    if (estimationId && file?.name && activeMode === 'specs') {
      try {
        const storageKey = `specs-page-number-${estimationId}-${file.name}`;
        localStorage.setItem(storageKey, specsPageNumber.toString());
      } catch { /* quota exceeded — ignore */ }
    }
  }, [specsPageNumber, estimationId, file?.name, activeMode]);

  // Save NECA page number
  useEffect(() => {
    if (estimationId && activeMode === 'neca') {
      try {
        const storageKey = `neca-page-number-${estimationId}`;
        localStorage.setItem(storageKey, necaPageNumber.toString());
      } catch { /* quota exceeded — ignore */ }
    }
  }, [necaPageNumber, estimationId, activeMode]);
  
  // Handle mode transitions with cleanup delay to prevent worker conflicts
  useEffect(() => {
    console.log('🔄 Mode transition starting:', activeMode);
    setIsTransitioning(true);
    
    // AGGRESSIVE delay to ensure old Document cleanup completes
    const timer = setTimeout(() => {
      console.log('✅ Transition complete, rendering:', activeMode);
      setIsTransitioning(false);
      
      // In draw mode, enable hand-drawn tools and default to select
      if (activeMode === 'draw') {
        setHandDrawnEnabled(true);
        setCurrentTool('select');
      } else {
        setHandDrawnEnabled(false);
        setCurrentTool('select');
      }
    }, 600); // INCREASED to 600ms for complete cleanup
    
    return () => {
      console.log('🧹 Cleaning up mode transition');
      clearTimeout(timer);
    };
  }, [activeMode]);

  // Debug calibration state changes
  useEffect(() => {
    console.log('🔧 Calibration state changed:', {
      isCalibrating,
      handDrawnEnabled,
      handDrawnWillBeDisabled: isCalibrating
    });
  }, [isCalibrating, handDrawnEnabled]);

  // Clear highlight when page changes (but not when we just set a highlight)
  const lastHighlightPageRef = useRef<number | null>(null);
  
  useEffect(() => {
    // Only clear if page changed and it's NOT from a citation click
    if (lastHighlightPageRef.current !== null && lastHighlightPageRef.current !== specsPageNumber) {
      console.log('📄 Page changed from', lastHighlightPageRef.current, 'to', specsPageNumber, '- clearing highlight');
      setHighlightBoundingBox(null);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    }
    // Update the ref for next time
    lastHighlightPageRef.current = specsPageNumber;
  }, [specsPageNumber]);

  // Reset loading state when page changes
  useEffect(() => {
    setIsPageFullyLoaded(false);
    setIsCanvasSynced(false);
    setAnnotationsLoaded(false);
    setIsPDFRendered(false);
  }, [pageNumber]);
  
  // Mark page as fully loaded when PDF is rendered and canvas is synced (annotations load in background)
  useEffect(() => {
    if (isPDFRendered && isCanvasSynced) {
      console.log('✅ Page fully loaded - PDF rendered and canvas synced');
      setIsPageFullyLoaded(true);
    }
  }, [isPDFRendered, isCanvasSynced]);

  // Load measurement calibrations from Supabase
  useEffect(() => {
    const loadCalibrations = async () => {
      if (!estimationId) return;
      
      const { data, error } = await supabase
        .from('estimations')
        .select('measurement_calibs')
        .eq('id', estimationId)
        .single();
      
      if (data && data.measurement_calibs) {
        setCalibrationData(data.measurement_calibs);
        console.log('📏 Loaded calibrations:', data.measurement_calibs);
      }
    };
    
    loadCalibrations();
  }, [estimationId]);

  // Close calibration dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calibrationDropdownRef.current && !calibrationDropdownRef.current.contains(event.target as Node)) {
        setShowCalibrationDropdown(false);
      }
    };

    if (showCalibrationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalibrationDropdown]);

  // Track Command/Ctrl key for grid snapping during calibration
  useEffect(() => {
    if (!isCalibrating) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        setIsGridSnapActive(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.ctrlKey) {
        setIsGridSnapActive(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      setIsGridSnapActive(false);
    };
  }, [isCalibrating]);

  // Calculate normalized percentage from line (coordinates normalized to 0-1 range)
  const calculateCanvasPercentage = useCallback((startX: number, startY: number, endX: number, endY: number): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    
    const dx = (endX - startX) / canvas.width;
    const dy = (endY - startY) / canvas.height;
    const normalizedLength = Math.sqrt(dx * dx + dy * dy);
    const percentage = normalizedLength * 100;
    
    return parseFloat(percentage.toFixed(2));
  }, []);

  // Save calibration to Supabase
  const saveCalibrationToSupabase = useCallback(async (
    pageNum: number, 
    canvasPercentage: number, 
    realWorldValue: number, 
    unit: string,
    referenceLine?: { startX: number; startY: number; endX: number; endY: number }
  ) => {
    if (!estimationId) return;
    
    const updatedCalibs = {
      ...calibrationData,
      [pageNum]: {
        canvasPercentage,
        realWorldValue,
        unit,
        createdAt: new Date().toISOString(),
        referenceLine,
        showReferenceLine: true // Default to showing the reference line
      }
    };
    
    const { error } = await supabase
      .from('estimations')
      .update({ measurement_calibs: updatedCalibs })
      .eq('id', estimationId);
    
    if (error) {
      console.error('Error saving calibration:', error);
      return;
    }
    
    setCalibrationData(updatedCalibs);
    console.log(`✅ Calibration saved: ${canvasPercentage}% = ${realWorldValue}${unit} for page ${pageNum}`);
  }, [estimationId, calibrationData]);

  // Handle calibration modal save
  const handleCalibrationSave = useCallback((realValue: number, unit: string) => {
    if (calibrationLine) {
      const referenceLine = {
        startX: calibrationLine.startX,
        startY: calibrationLine.startY,
        endX: calibrationLine.endX,
        endY: calibrationLine.endY
      };
      saveCalibrationToSupabase(pageNumber, calibrationLine.percentage, realValue, unit, referenceLine);
      setShowCalibrationModal(false);
      setCalibrationLine(null);
      setIsCalibrating(false);
      setShowCalibrationDropdown(false);
    }
  }, [calibrationLine, pageNumber, saveCalibrationToSupabase]);

  // Handle calibration redo
  const handleCalibrationRedo = useCallback(() => {
    setShowCalibrationModal(false);
    setCalibrationLine(null);
    // Keep isCalibrating true so user can draw again
  }, []);

  // Handle wire run completion (called from HandDrawnAnnotations)
  // When polyline is finished, save directly with the pre-selected wire type
  const handleWireRunComplete = useCallback(async (points: {x: number; y: number}[], totalLength: number, page: number) => {
    console.log('🔌 Wire run completed:', { points: points.length, totalLength, page, wireType: selectedWireType?.name });
    
    if (!selectedWireType || !estimationId || !file?.id) {
      console.error('Cannot save wire run: missing wire type or file info');
      setIsWireRunMode(false);
      setSelectedWireType(null);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      console.error('Cannot save wire run: no canvas');
      return;
    }

    // Convert relative points (0-1) to canvas coordinates, preserving relative coords
    const canvasPoints = points.map(p => ({
      x: p.x * canvas.width,
      y: p.y * canvas.height,
      relativeX: p.x,
      relativeY: p.y
    }));

    // Create annotation immediately for instant visual feedback (before DB round-trip)
    const tempId = `wire-run-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const wireRunAnnotation: Annotation = {
      id: tempId,
      type: 'wire-run',
      page: page,
      startX: canvasPoints[0]?.x || 0,
      startY: canvasPoints[0]?.y || 0,
      relativeStartX: points[0]?.x || 0,
      relativeStartY: points[0]?.y || 0,
      endX: canvasPoints[canvasPoints.length - 1]?.x,
      endY: canvasPoints[canvasPoints.length - 1]?.y,
      points: canvasPoints,
      color: selectedWireType.color,
      thickness: 3,
      timestamp: new Date().toISOString(),
      wireTypeName: selectedWireType.name,
      wireTypeColor: selectedWireType.color,
      totalLength: totalLength,
      measurementUnit: calibrationData[page]?.unit || 'ft',
      component_type: `wire-${selectedWireType.name}`
    };

    setAnnotations(prev => [...prev, wireRunAnnotation]);
    console.log('✅ Wire run annotation added immediately:', selectedWireType.name, totalLength.toFixed(2), 'ft');

    // Save to DB in background, then update annotation ID to match the database ID
    try {
      const { data: insertedRun, error } = await supabaseClient
        .from('wire_runs')
        .insert({
          estimation_id: estimationId,
          file_id: file.id,
          wire_type: selectedWireType,
          points: points,
          page_number: page,
          total_length: totalLength,
          segments: points.length - 1
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error saving wire run to database:', error);
      } else if (insertedRun?.id) {
        // Update annotation ID to match the database record
        setAnnotations(prev => prev.map(a =>
          a.id === tempId ? { ...a, id: `wire-run-${insertedRun.id}` } : a
        ));
        console.log('✅ Wire run DB ID linked:', insertedRun.id);
      }
    } catch (err) {
      console.error('Error saving wire run to database:', err);
    }

    // Trigger refresh of wire runs panel
    setWireRunRefreshTrigger(prev => prev + 1);
  }, [selectedWireType, estimationId, file?.id, calibrationData]);

  // Handle square footage completion (called from HandDrawnAnnotations)
  // rect coords are relative (0-1), matching wire-run pattern
  const handleSquareFootageComplete = useCallback((
    rect: { x: number; y: number; width: number; height: number },
    area: number,
    page: number
  ) => {
    const canvas = canvasRef.current;
    if (!canvas || !estimationId || !file?.id) return;

    const unit = calibrationData[page]?.unit || 'ft';
    const absStartX = rect.x * canvas.width;
    const absStartY = rect.y * canvas.height;
    const absEndX = (rect.x + rect.width) * canvas.width;
    const absEndY = (rect.y + rect.height) * canvas.height;
    const absWidth = rect.width * canvas.width;
    const absHeight = rect.height * canvas.height;

    const annotation: Annotation = {
      id: `sqft-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'hand-square-footage',
      page,
      startX: absStartX,
      startY: absStartY,
      endX: absEndX,
      endY: absEndY,
      width: absWidth,
      height: absHeight,
      relativeStartX: rect.x,
      relativeStartY: rect.y,
      relativeWidth: rect.width,
      relativeHeight: rect.height,
      savedCanvasWidth: canvas.width,
      savedCanvasHeight: canvas.height,
      color: '#9333EA',
      thickness: 2,
      timestamp: new Date().toISOString(),
      totalArea: area,
      areaUnit: unit,
      component_type: 'square-footage',
    };

    setAnnotations(prev => [...prev, annotation]);

    // Return to select mode after drawing the box
    setIsSquareFootageMode(false);
    setCurrentTool('select');
  }, [estimationId, file?.id, calibrationData]);

  // Handle wire type selection from modal - starts drawing mode
  const handleWireTypeSelected = useCallback((wireType: WireType) => {
    console.log('🔌 Wire type selected:', wireType.name);
    setSelectedWireType(wireType);
    setShowWireTypeModal(false);
    
    // Start wire run drawing mode (tab switch already happened before modal opened)
    setIsWireRunMode(true);
    setHandDrawnEnabled(true);
    setCurrentTool('hand-wire-run' as AnnotationTool);
  }, []);

  // UNIFIED SELECT: Handle hand-drawn stroke selection changes
  const handleStrokeSelectionChange = useCallback((strokeIds: Set<string>, append: boolean) => {
    console.log('🎨 Hand-drawn stroke selection changed:', strokeIds.size, 'strokes, append:', append);
    
    if (currentTool === 'select') {
      // When append is true, keep existing counting annotation selections
      // When append is false, clear counting annotation selections
      if (!append) {
        setSelectedAnnotation(null);
        setSelectedAnnotations(new Set());
      }
      setSelectedHandDrawnStrokes(strokeIds);
    }
  }, [currentTool]);

  // Start wire run mode - switch to counting/wires tab first, then show modal
  const startWireRunMode = useCallback(() => {
    console.log('🔌 Switching to Wires tab, then opening modal');
    
    // First switch to Counting tab -> Wires subtab
    if (onSwitchToWiresTab) {
      onSwitchToWiresTab();
    }
    
    // Then open the modal (small delay to ensure tab switch completes)
    setTimeout(() => {
      setShowWireTypeModal(true);
    }, 50);
  }, [onSwitchToWiresTab]);

  // Start square footage mode
  const startSquareFootageMode = useCallback(() => {
    if (!calibrationData[pageNumber]) {
      alert('Please calibrate the page first before measuring square footage.');
      return;
    }
    setIsSquareFootageMode(true);
    setCurrentTool('hand-square-footage');
    setHandDrawnEnabled(true);
  }, [calibrationData, pageNumber]);

  // Handle quick count - add component and start counting
  const handleStartQuickCount = useCallback(async () => {
    if (!newComponentName.trim()) return;
    
    setIsAddingComponent(true);
    
    try {
      // Create the new component
      const newComponent = {
        name: newComponentName.trim().toLowerCase(),
        color: newComponentColor,
        symbol: newComponentName.trim()
      };
      
      // Add to key via callback
      if (onAddComponentToKey) {
        await onAddComponentToKey(newComponent);
      }
      
      // Switch to counting tab
      if (onSwitchToCountingTab) {
        onSwitchToCountingTab();
      }
      
      // Close modal
      setShowNewComponentModal(false);
      setNewComponentName('');
      setNewComponentColor('#3b82f6');
      
      console.log('✅ Quick count started for:', newComponent.name);
    } catch (error) {
      console.error('Failed to start quick count:', error);
    } finally {
      setIsAddingComponent(false);
    }
  }, [newComponentName, newComponentColor, onAddComponentToKey, onSwitchToCountingTab]);

  // Handle start area selection for autocount
  const handleStartAreaSelection = useCallback(() => {
    if (!newComponentName.trim()) return;
    
    // For new components entered manually, use lowercase for consistency
    const normalizedName = newComponentName.trim().toLowerCase();
    console.log('🎯 Starting area selection for autocount:', normalizedName);
    
    // Look up component in key to get isLighting (for zoom in review modal)
    const comp = electricalKey?.components?.find((c: any) =>
      (c.name || '').toLowerCase() === normalizedName ||
      (c.symbolType === 'Lighting' && (c.symbol || c.name || '').toLowerCase() === normalizedName)
    );
    const isLighting = comp?.symbolType === 'Lighting';
    
    setPendingAutocountComponent({
      name: normalizedName,
      color: newComponentColor,
      isLighting
    });
    
    // Close the Count modal
    setShowNewComponentModal(false);
    
    // Enter area selection mode
    if (isAddingNewOnly) {
      setIsAddingNewOnlyScanMode(true);
    }
    setAutocountMode('area-selection');
    
    // Clear previous selection if any
    setAutocountSelection(null);
    setReferenceAreaImage(null);
    
  }, [newComponentName, newComponentColor, electricalKey]);

  // Retake should always return to area selection, even if the name field is temporarily empty.
  const handleRetakeAreaSelection = useCallback(() => {
    // Close modal and return to selection mode
    setShowNewComponentModal(false);
    if (isAddingNewOnly) {
      setIsAddingNewOnlyScanMode(true);
    }
    setAutocountMode('area-selection');

    // Clear previous capture so the next drag becomes the new scanned symbol
    setAutocountSelection(null);
    setReferenceAreaImage(null);
  }, [isAddingNewOnly]);

  // Handle starting key extraction from modal
  const handleStartKeyExtraction = useCallback(() => {
    console.log('🔑 Starting key extraction mode from modal');
    
    setShowNewComponentModal(false);
    setQuickCountSearch('');
    
    // Clear previous jobs and enter multi-area selection mode
    setKeyExtractionJobs([]);
    setKeyExtractionMode(true);
    setKeyExtractionSelection(null);
    setKeyExtractionSnapshot(null);
  }, []);

  // Handle key extraction area selection complete
  const handleKeyExtractionComplete = useCallback(async (snapshotBase64: string) => {
    console.log('🔑 Key area selected, extracting symbols...');
    setKeyExtractionMode(false);
    setIsExtractingKeyFromLegend(true);
    setKeyExtractionSnapshot(snapshotBase64);
    
    try {
      const response = await fetch('/api/extract-key-agentic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: snapshotBase64 })
      });
      
      const data = await response.json();
      
      if (data.success && data.symbols) {
        console.log(`✅ Extracted ${data.symbols.length} symbols from legend`);
        
        // Crop symbol images using bounding boxes
        const symbolsWithImages = await cropSymbolImages(snapshotBase64, data.symbols);
        
        // Deduplicate names within the extracted batch (e.g. two "Switch" entries with different symbols)
        const dedupedSymbols = deduplicateComponentNames(symbolsWithImages);
        console.log(`📋 Deduped extraction: ${symbolsWithImages.length} → ${dedupedSymbols.length} unique names`);
        
        setExtractedKeyPreview(dedupedSymbols);
        setSelectedKeyPreviewIndices(new Set(dedupedSymbols.map((_: any, i: number) => i)));
        setShowKeyExtractionPreview(true);
      } else {
        console.error('❌ Extraction failed:', data.error);
        alert('Failed to extract key: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ API call failed:', error);
      alert('Failed to call extraction API: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsExtractingKeyFromLegend(false);
    }
  }, []);

  // "Add & Scan Next": queue this screenshot and go back to selection mode (no API call yet)
  const handleExtractAndScanNext = useCallback(() => {
    if (!keyExtractionSnapshot) return;

    setKeyExtractionJobs(prev => [...prev, {
      id: crypto.randomUUID(),
      snapshot: keyExtractionSnapshot,
      status: 'queued' as const,
    }]);
    setShowKeyExtractionConfirmModal(false);
    setKeyExtractionSnapshot(null);
    setKeyExtractionSelection(null);
  }, [keyExtractionSnapshot]);

  // Fire all queued jobs concurrently
  const fireAllKeyExtractionJobs = useCallback(() => {
    setKeyExtractionJobs(prev => prev.map(job => {
      if (job.status !== 'queued') return job;

      const abortController = new AbortController();
      const jobId = job.id;
      const snapshot = job.snapshot;

      (async () => {
        try {
          const response = await fetch('/api/extract-key-agentic', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageBase64: snapshot }),
            signal: abortController.signal,
          });
          const data = await response.json();

          if (data.success && data.symbols) {
            const symbolsWithImages = await cropSymbolImages(snapshot, data.symbols);
            setKeyExtractionJobs(p => p.map(j =>
              j.id === jobId ? { ...j, status: 'done' as const, results: symbolsWithImages } : j
            ));
          } else {
            setKeyExtractionJobs(p => p.map(j =>
              j.id === jobId ? { ...j, status: 'error' as const, error: data.error || 'Extraction failed' } : j
            ));
          }
        } catch (err: any) {
          if (err.name === 'AbortError') return;
          setKeyExtractionJobs(p => p.map(j =>
            j.id === jobId ? { ...j, status: 'error' as const, error: err.message || 'API call failed' } : j
          ));
        }
      })();

      return { ...job, status: 'processing' as const, abortController };
    }));
  }, []);

  // Flag to trigger firing all jobs after state settles
  const pendingFireAllRef = useRef(false);

  // "Extract" / "Extract & Done": queue this snapshot if present, fire ALL jobs, exit selection mode
  const handleExtractSingle = useCallback(() => {
    if (keyExtractionSnapshot) {
      setKeyExtractionJobs(prev => [...prev, {
        id: crypto.randomUUID(),
        snapshot: keyExtractionSnapshot,
        status: 'queued' as const,
      }]);
    }
    setShowKeyExtractionConfirmModal(false);
    setKeyExtractionSnapshot(null);
    setKeyExtractionSelection(null);
    setKeyExtractionMode(false);
    pendingFireAllRef.current = true;
  }, [keyExtractionSnapshot]);

  // Fire all queued jobs once state has settled after handleExtractSingle or handleFinishKeyExtractionSelection
  useEffect(() => {
    if (!pendingFireAllRef.current) return;
    if (keyExtractionJobs.length === 0) return;
    if (!keyExtractionJobs.some(j => j.status === 'queued')) return;
    pendingFireAllRef.current = false;
    fireAllKeyExtractionJobs();
  }, [keyExtractionJobs, fireAllKeyExtractionJobs]);

  // Delete a key extraction job (abort if still processing)
  const handleDeleteKeyExtractionJob = useCallback((jobId: string) => {
    setKeyExtractionJobs(prev => {
      const job = prev.find(j => j.id === jobId);
      if (job && job.status === 'processing' && job.abortController) {
        job.abortController.abort();
      }
      return prev.filter(j => j.id !== jobId);
    });
  }, []);

  // "Extract Key" from floating panel: fire all queued jobs concurrently, exit selection mode
  const handleFinishKeyExtractionSelection = useCallback(() => {
    setKeyExtractionMode(false);
    setKeyExtractionSelection(null);
    pendingFireAllRef.current = true;
    // Force a re-render so the useEffect picks up the flag
    setKeyExtractionJobs(prev => [...prev]);
  }, []);

  // Auto-open preview when all jobs finish after user clicked Extract
  useEffect(() => {
    if (keyExtractionMode) return;
    if (keyExtractionJobs.length === 0) return;
    if (showKeyExtractionPreview) return;
    if (keyExtractionJobs.some(j => j.status === 'processing' || j.status === 'queued')) return;

    const allResults = keyExtractionJobs
      .filter(j => j.status === 'done' && j.results)
      .flatMap(j => j.results!);

    if (allResults.length === 0) {
      alert('No symbols were extracted. Try selecting the legend areas again.');
      setKeyExtractionJobs([]);
      return;
    }

    const dedupedSymbols = deduplicateComponentNames(allResults);
    console.log(`📋 All jobs complete. Combined ${allResults.length} → ${dedupedSymbols.length} unique symbols`);

    setExtractedKeyPreview(dedupedSymbols);
    setSelectedKeyPreviewIndices(new Set(dedupedSymbols.map((_: any, i: number) => i)));
    setShowKeyExtractionPreview(true);
  }, [keyExtractionJobs, keyExtractionMode, showKeyExtractionPreview]);

  // Cancel multi-area key extraction entirely
  const handleCancelKeyExtractionJobs = useCallback(() => {
    keyExtractionJobs.forEach(j => {
      if (j.status === 'processing' && j.abortController) j.abortController.abort();
    });
    setKeyExtractionJobs([]);
    setKeyExtractionMode(false);
    setKeyExtractionSelection(null);
  }, [keyExtractionJobs]);

  // Default color for power components (green)
  const POWER_COLOR = 'rgba(34, 197, 94, 0.6)';

  // Default color for lighting fixture components (gold)
  const LIGHTING_FIXTURE_COLOR = 'rgba(234, 179, 8, 0.6)';

  // Color mapping for componentType categories: Power = green, Lighting = gold
  const COMPONENT_TYPE_COLORS: Record<string, string> = {
    'Switches': POWER_COLOR,              // green (power)
    'Receptacles': POWER_COLOR,            // green (power)
    'Distribution Equipment': POWER_COLOR, // green (power)
    'Telecom': POWER_COLOR,                // green (power)
    'Lighting': LIGHTING_FIXTURE_COLOR,    // gold (lighting)
    'Fire Alarm': POWER_COLOR,             // green (power)
    'Low Voltage': POWER_COLOR,            // green (power)
    'Other': POWER_COLOR,                  // green (power)
  };

  // Handle starting lighting schedule extraction from modal
  const handleStartLightingScheduleExtraction = useCallback(() => {
    console.log('💡 Starting lighting schedule extraction mode from modal');
    
    // Close the modal
    setShowNewComponentModal(false);
    setQuickCountSearch('');
    
    // Enter lighting schedule selection mode
    setLightingScheduleMode(true);
    setLightingScheduleSelection(null);
    setLightingScheduleSnapshot(null);
  }, []);

  // Handle lighting schedule extraction (called after user confirms)
  const handleLightingScheduleComplete = useCallback(async (snapshotBase64: string) => {
    console.log('💡 Extracting lighting fixtures from schedule...');
    setIsExtractingLightingSchedule(true);
    
    try {
      const response = await fetch('/api/extract-lighting-schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: snapshotBase64 })
      });
      
      const data = await response.json();
      
      if (data.success && data.fixtures) {
        console.log(`✅ Extracted ${data.fixtures.length} lighting fixtures from schedule`);
        
        // Close confirm modal, open preview
        setShowLightingScheduleConfirmModal(false);
        setExtractedLightingFixtures(data.fixtures);
        setSelectedLightingFixtureIndices(new Set(data.fixtures.map((_: any, i: number) => i)));
        setShowLightingSchedulePreview(true);
      } else {
        console.error('❌ Lighting schedule extraction failed:', data.error);
        alert('Failed to extract lighting schedule: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ API call failed:', error);
      alert('Failed to call lighting schedule API: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsExtractingLightingSchedule(false);
    }
  }, []);

  // Handle adding extracted lighting fixtures to project key (without counting)
  const handleAddLightingFixturesToKey = useCallback(() => {
    const selectedFixtures = extractedLightingFixtures.filter((_, idx) => selectedLightingFixtureIndices.has(idx));
    if (selectedFixtures.length === 0) return;
    
    console.log('💡 Adding extracted lighting fixtures to key...');
    
    const newComponents = selectedFixtures.map((fixture) => ({
      color: LIGHTING_FIXTURE_COLOR,
      name: fixture.name,
      symbol: fixture.code,
      symbolType: 'Lighting',
      componentType: 'Lighting',
      description: fixture.description,
      pages: [] as any[],
      totalCount: 0,
      referenceImage: '',
      hasReferenceImage: false,
    }));
    
    const existingComponents = electricalKey?.components || [];
    const existingSymbols = new Set(existingComponents.map((c: any) => (c.symbol || '').toLowerCase()));
    const uniqueNew = newComponents.filter((c: any) => !existingSymbols.has((c.symbol || '').toLowerCase()));
    const mergedComponents = [...existingComponents, ...uniqueNew];
    
    const projectKey = {
      components: mergedComponents,
      extractedAt: new Date().toISOString(),
      pdfHash: (electricalKey as any)?.pdfHash || '',
    };
    
    console.log(`📦 Added ${uniqueNew.length} lighting fixtures to key (${newComponents.length - uniqueNew.length} duplicates skipped, total: ${mergedComponents.length})`);
    
    setShowLightingSchedulePreview(false);
    setExtractedLightingFixtures([]);
    setSelectedLightingFixtureIndices(new Set());
    setLightingScheduleSnapshot(null);
    
    if (onKeyExtracted) {
      onKeyExtracted(projectKey);
    }
  }, [extractedLightingFixtures, selectedLightingFixtureIndices, onKeyExtracted, electricalKey]);

  // Helper function to count a single component across selected pages (all concurrently)
  // Uses same coordinate system as handleCountAll (8000px canvas, scaled to on-screen)
  const countSingleComponent = useCallback(async (
    component: { color: string; name: string; symbol: string; referenceImage: string; symbolType?: string; componentType?: string; subscript?: string; description?: string },
    componentIndex: number,
    pagesToCount?: number[],
    options?: { keepCountingState?: boolean }
  ) => {
    if (!pdfDocument || !numPages) return;
    const shouldClearGlobalProgress = !options?.keepCountingState;
    const finalizeComponentCount = () => {
      if (!shouldClearGlobalProgress) return;
      setIsCountingAll(false);
      setCountAllProgress('');
    };
    
    const pageNumbers = pagesToCount ?? Array.from({ length: numPages }, (_, i) => i + 1);
    const pageCount = pageNumbers.length;
    
    console.log(`🔍 Counting "${component.name}" across ${pageCount} pages (concurrent)...`);
    setCountAllProgress(`Counting "${component.name}" across ${pageCount} pages...`);
    
    const isLightingComponent = component.symbolType === 'Lighting';
    const isLightingGroup = component.name === '__LIGHTING_GROUP__';
    const componentNameLower = (component.name || '').toLowerCase();
    const isReceptacleComponent = componentNameLower.includes('receptacle') || componentNameLower.includes('duplex') || (component as any).componentType === 'Receptacles';
    const isSwitchComponent = (component as any).componentType === 'Switches' || (componentNameLower.includes('switch') && !isReceptacleComponent);
    const isReceptaclesGroup = component.name === '__RECEPTACLES_GROUP__';
    const isTeleDataGroup = component.name === '__TELE_DATA_GROUP__';
    const isTeleDataComponent = isTeleDataGroup || component.name.toLowerCase().includes('tel') || component.name.toLowerCase().includes('data') || component.name.toLowerCase().includes('communication') || (component as any).componentType === 'Telecom' || (component as any).componentType === 'Low Voltage';
    const isJunctionBoxComponent = (() => {
      const n = (component.name || '').toLowerCase().replace(/[-_]/g, ' ');
      return n.includes('junction') || n.includes('jbox') || n.includes('j box') || (n.includes('j') && n.includes('box'));
    })();

    // ============================================================
    // SERVER-SIDE BATCH PATH (Streaming): Receptacles use fully server-side processing.
    // The server downloads the PDF, renders the page at 16,000px using pdfjs-dist +
    // @napi-rs/canvas, tiles with sharp, runs Roboflow, and streams back detections.
    // No browser rendering or Supabase upload needed. Pages process 3 at a time.
    // ============================================================
    if ((isReceptacleComponent || isReceptaclesGroup) && file?.url) {
      const displayName = isReceptaclesGroup ? 'RECEPTACLES' : component.name;
      console.log(`🔌 CLOUD RUN: Counting "${displayName}" on pages [${pageNumbers.join(', ')}] — single request, PDF downloaded once`);
      setCountAllProgress(`Counting "${displayName}" across ${pageCount} pages...`);

      // Get on-screen canvas dimensions for coordinate scaling
      const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
      const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;

      try {
        const allDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];

        // Build reference types for server-side classification
        const receptacleRefTypes = electricalKey?.components
          ? electricalKey.components
              .filter((c: any) =>
                c.name.toLowerCase().includes('receptacle') ||
                c.componentType === 'Receptacles' ||
                c.componentType === 'Power'
              )
              .map((c: any, idx: number) => ({
                index: idx,
                name: c.name,
                symbol: c.symbol || '',
                referenceImage: c.referenceImage || ''
              }))
          : [];

        let completedPageCount = 0;

        // For grouped receptacles, always send referenceTypes for classification but no targetComponentName
        // so the server classifies all detections without filtering any out.
        const shouldClassifyAll = isReceptaclesGroup && receptacleRefTypes.length > 1;

        // Single request to Cloud Run with ALL pages
        const response = await fetch('https://autocount-receptacles-904918874970.us-central1.run.app', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pdfUrl: file.url,
            pageNumbers,
            componentType: isReceptaclesGroup ? 'receptacle' : component.name,
            confidence: 0.01,
            referenceTypes: (shouldClassifyAll || receptacleRefTypes.length > 1) ? receptacleRefTypes : undefined,
            targetComponentName: shouldClassifyAll ? undefined : (receptacleRefTypes.length > 1 ? component.name : undefined)
          })
        });

        // Read SSE streaming response — update UI as results arrive
        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let streamBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          streamBuffer += decoder.decode(value, { stream: true });
          const events = streamBuffer.split('\n\n');
          streamBuffer = events.pop() || '';

          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;
            // Parse SSE format: "data: {...}"
            const dataLine = eventBlock.split('\n').find(l => l.startsWith('data: '));
            if (!dataLine) continue;
            try {
              const msg = JSON.parse(dataLine.slice(6));

              if (msg.event === 'progress') {
                setCountAllProgress(msg.message || '');
                if (msg.pct != null) {
                  setCountAllComponentProgress(prev => prev.map((p, idx) =>
                    idx === componentIndex ? { ...p, completedBatches: msg.pct, totalBatches: 100 } : p
                  ));
                }

              } else if (msg.event === 'page-progress') {
                setCountAllProgress(
                  `${msg.message || ''} (${allDetections.length} found)`
                );

              } else if (msg.event === 'unfiltered-batch') {
                // Unfiltered detections during Roboflow phase — show as semi-transparent "detecting"
                const batchPageNum = msg.pageNum || pageNumber;
                const newDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
                for (const pred of (msg.newPredictions || [])) {
                  const screenX = pred.relX * onScreenWidth;
                  const screenY = pred.relY * onScreenHeight;
                  const screenWidth = pred.relWidth * onScreenWidth;
                  const screenHeight = pred.relHeight * onScreenHeight;

                  newDetections.push({
                    id: `unfiltered-${batchPageNum}-${pred.relX.toFixed(5)}-${pred.relY.toFixed(5)}`,
                    thumbnail: pred.thumbnail || '',
                    prediction: {
                      ...pred,
                      x: screenX,
                      y: screenY,
                      width: screenWidth,
                      height: screenHeight,
                      page: batchPageNum,
                      unfiltered: true
                    }
                  });
                }

                if (newDetections.length > 0) {
                  allDetections.push(...newDetections);

                  setMultiComponentDetections(prev => prev.map((d, idx) =>
                    idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                  ));
                  setAllComponentSelections(prev => {
                    const newMap = new Map(prev);
                    const compKey = component.name;
                    const currentSet = new Set(newMap.get(compKey) || []);
                    newDetections.forEach(d => currentSet.add(d.id));
                    newMap.set(compKey, currentSet);
                    return newMap;
                  });
                }

                console.log(`📥 Page ${batchPageNum}: +${newDetections.length} unfiltered detections (total: ${allDetections.length})`);
                await new Promise(resolve => setTimeout(resolve, 0));

              } else if (msg.event === 'classify' && isReceptaclesGroup) {
                // Server classified a detection — update its classifiedName without removing
                const detId = `unfiltered-${msg.pageNum}-${msg.relX.toFixed(5)}-${msg.relY.toFixed(5)}`;
                const det = allDetections.find(d => d.id === detId);
                if (det) {
                  det.prediction.classifiedName = msg.classifiedName;
                  det.prediction.classifiedIndex = msg.classifiedIndex;
                  det.prediction.unfiltered = false;
                  setDetectionClassifications(prev => {
                    const newMap = new Map(prev);
                    newMap.set(detId, msg.classifiedName);
                    return newMap;
                  });
                }
                setMultiComponentDetections(prev => prev.map((d, idx) =>
                  idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                ));

              } else if (msg.event === 'remove') {
                // Classification determined these don't match — remove them
                const removals = msg.removals || [];
                if (removals.length > 0) {
                  const removalSet: Set<string> = new Set(removals.map((r: any) =>
                    `unfiltered-${r.pageNum}-${r.relX.toFixed(5)}-${r.relY.toFixed(5)}`
                  ));

                  // Remove from allDetections
                  for (let i = allDetections.length - 1; i >= 0; i--) {
                    if (removalSet.has(allDetections[i].id)) {
                      allDetections.splice(i, 1);
                    }
                  }

                  // Update state
                  setMultiComponentDetections(prev => prev.map((d, idx) =>
                    idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                  ));
                  setAllComponentSelections(prev => {
                    const newMap = new Map(prev);
                    const compKey = component.name;
                    const currentSet = newMap.get(compKey);
                    if (currentSet) {
                      removalSet.forEach((id: string) => currentSet.delete(id));
                      newMap.set(compKey, currentSet);
                    }
                    return newMap;
                  });

                  console.log(`🗑️ Removed ${removals.length} non-matching detections (remaining: ${allDetections.length})`);
                  await new Promise(resolve => setTimeout(resolve, 0));
                }

              } else if (msg.event === 'batch') {
                // Classified matching detections — these are final
                const batchPageNum = msg.pageNum || pageNumber;
                console.log(`✅ Page ${batchPageNum}: classified batch arrived`);

              } else if (msg.event === 'page-done') {
                completedPageCount++;
                console.log(`✅ Page ${msg.pageNum}: ${msg.totalPredictions} final detections`);

              } else if (msg.event === 'done') {
                // Mark all remaining detections as classified (remove "Classifying..." labels)
                const defaultClassName = receptacleRefTypes.length > 0 ? receptacleRefTypes[0].name : (isReceptaclesGroup ? 'Receptacle' : component.name);
                for (const det of allDetections) {
                  if (det.prediction.unfiltered) {
                    det.prediction.unfiltered = false;
                  }
                  // For grouped mode, set default classification on any unclassified detection
                  if (isReceptaclesGroup && !det.prediction.classifiedName) {
                    det.prediction.classifiedName = defaultClassName;
                    setDetectionClassifications(prev => {
                      const newMap = new Map(prev);
                      if (!newMap.has(det.id)) {
                        newMap.set(det.id, defaultClassName);
                      }
                      return newMap;
                    });
                  }
                }
                setMultiComponentDetections(prev => prev.map((d, idx) =>
                  idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                ));
                const displayNameDone = isReceptaclesGroup ? 'RECEPTACLES' : component.name;
                setCountAllProgress(`Found ${allDetections.length} ${displayNameDone} across ${pageNumbers.length} pages`);
                console.log(`✅ All pages done: ${msg.totalPredictions} total detections in ${msg.timing?.totalMs}ms`);

              } else if (msg.event === 'error') {
                console.error(`❌ Server error:`, msg.message);
              }
            } catch (parseErr) {
              console.warn('Failed to parse SSE event:', eventBlock);
            }
          }
        }

        console.log(`🔢 Total detections from server: ${allDetections.length}`);

        // Mark complete
        setCountAllComponentProgress(prev => prev.map((p, i) =>
          i === componentIndex ? { ...p, status: 'complete' as const, completedBatches: p.totalBatches || 1 } : p
        ));
        finalizeComponentCount();
        console.log(`✅ Finished server-side counting "${component.name}"`);
        return; // Skip the client-side tiling path below
      } catch (error) {
        console.error(`❌ Server-side batch failed, falling back to client-side:`, error);
        setCountAllProgress(`Server-side failed, counting "${component.name}" client-side...`);
      }
    }

    // ============================================================
    // SERVER-SIDE BATCH PATH (Streaming): Tele/Data outlets use Cloud Run with
    // PyMuPDF rendering + Roboflow tele-outlet-detection model. Detection only,
    // no classification. Single request, PDF downloaded once, streams per-page results.
    // ============================================================
    if (isTeleDataComponent && file?.url) {
      const displayName = isTeleDataGroup ? 'TELE/DATA OUTLETS' : component.name;
      console.log(`📡 CLOUD RUN: Counting "${displayName}" on pages [${pageNumbers.join(', ')}] — tele/data detection`);
      setCountAllProgress(`Counting "${displayName}" across ${pageCount} pages...`);

      const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
      const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;

      try {
        const allDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
        let completedPageCount = 0;

        const response = await fetch('https://autocount-tele-data-904918874970.us-central1.run.app', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pdfUrl: file.url,
            pageNumbers,
            componentType: component.name,
            confidence: 0.01,
          })
        });

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let streamBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          streamBuffer += decoder.decode(value, { stream: true });
          const events = streamBuffer.split('\n\n');
          streamBuffer = events.pop() || '';

          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;
            const dataLine = eventBlock.split('\n').find(l => l.startsWith('data: '));
            if (!dataLine) continue;
            try {
              const msg = JSON.parse(dataLine.slice(6));

              if (msg.event === 'progress') {
                setCountAllProgress(msg.message || '');
                if (msg.pct != null) {
                  setCountAllComponentProgress(prev => prev.map((p, idx) =>
                    idx === componentIndex ? { ...p, completedBatches: msg.pct, totalBatches: 100 } : p
                  ));
                }

              } else if (msg.event === 'page-progress') {
                setCountAllProgress(
                  `${msg.message || ''} (${allDetections.length} found)`
                );

              } else if (msg.event === 'unfiltered-batch') {
                const batchPageNum = msg.pageNum || pageNumber;
                const newDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
                for (const pred of (msg.newPredictions || [])) {
                  const screenX = pred.relX * onScreenWidth;
                  const screenY = pred.relY * onScreenHeight;
                  const screenWidth = pred.relWidth * onScreenWidth;
                  const screenHeight = pred.relHeight * onScreenHeight;

                  newDetections.push({
                    id: `teledata-${batchPageNum}-${pred.relX.toFixed(5)}-${pred.relY.toFixed(5)}`,
                    thumbnail: pred.thumbnail || '',
                    prediction: {
                      ...pred,
                      x: screenX,
                      y: screenY,
                      width: screenWidth,
                      height: screenHeight,
                      page: batchPageNum,
                    }
                  });
                }

                if (newDetections.length > 0) {
                  allDetections.push(...newDetections);

                  setMultiComponentDetections(prev => prev.map((d, idx) =>
                    idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                  ));
                  setAllComponentSelections(prev => {
                    const newMap = new Map(prev);
                    const compKey = component.name;
                    const currentSet = new Set(newMap.get(compKey) || []);
                    newDetections.forEach(d => currentSet.add(d.id));
                    newMap.set(compKey, currentSet);
                    return newMap;
                  });
                }

                console.log(`📥 Page ${batchPageNum}: +${newDetections.length} tele/data detections (total: ${allDetections.length})`);
                await new Promise(resolve => setTimeout(resolve, 0));

              } else if (msg.event === 'page-done') {
                completedPageCount++;
                console.log(`✅ Page ${msg.pageNum}: ${msg.totalPredictions} final detections`);

              } else if (msg.event === 'done') {
                const defaultClassName = isTeleDataGroup && teleDataGroupComponents.length > 0
                  ? teleDataGroupComponents[0].name
                  : component.name;
                for (const det of allDetections) {
                  if (!det.prediction.classifiedName) {
                    det.prediction.classifiedName = defaultClassName;
                    setDetectionClassifications(prev => {
                      const newMap = new Map(prev);
                      if (!newMap.has(det.id)) {
                        newMap.set(det.id, defaultClassName);
                      }
                      return newMap;
                    });
                  }
                }
                setMultiComponentDetections(prev => prev.map((d, idx) =>
                  idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                ));
                setCountAllProgress(`Found ${allDetections.length} ${displayName} across ${pageNumbers.length} pages`);
                console.log(`✅ All pages done: ${msg.totalPredictions} total detections in ${msg.timing?.totalMs}ms`);

              } else if (msg.event === 'error') {
                console.error(`❌ Server error:`, msg.message);
              }
            } catch (parseErr) {
              console.warn('Failed to parse SSE event:', eventBlock);
            }
          }
        }

        console.log(`🔢 Total tele/data detections from server: ${allDetections.length}`);

        setCountAllComponentProgress(prev => prev.map((p, i) =>
          i === componentIndex ? { ...p, status: 'complete' as const, completedBatches: p.totalBatches || 1 } : p
        ));
        finalizeComponentCount();
        console.log(`✅ Finished server-side counting "${component.name}"`);
        return;
      } catch (error) {
        console.error(`❌ Tele/data server-side batch failed, falling back to client-side:`, error);
        setCountAllProgress(`Server-side failed, counting "${component.name}" client-side...`);
      }
    }

    // ============================================================
    // SERVER-SIDE BATCH PATH (Streaming): Lighting uses Cloud Run with
    // PyMuPDF rendering + Document AI OCR for fixture label detection.
    // Single request, PDF downloaded once, streams per-page results.
    // ============================================================
    if ((isLightingComponent || isLightingGroup) && file?.url) {
      const displayName = isLightingGroup ? 'LIGHTING' : component.name;
      const lightingCompsForRequest = isLightingGroup ? lightingGroupComponents : [component];
      const fixtureCodes = lightingCompsForRequest
        .map((c: any) => (c.symbol || c.name || '').trim().toUpperCase())
        .filter((s: string) => s.length > 0 && s !== 'LIGHTING' && s !== 'LIGHTING FIXTURE' && s !== 'LIGHTING FIXTURES');

      console.log(`💡 CLOUD RUN: Counting "${displayName}" (${fixtureCodes.length} codes: [${fixtureCodes.join(', ')}]) on pages [${pageNumbers.join(', ')}]`);
      if (fixtureCodes.length === 0) {
        console.warn('⚠️ No valid fixture codes found for lighting count');
        setCountAllProgress('No valid lighting fixture codes found in key.');
        return;
      }
      setCountAllProgress(`Counting ${displayName} across ${pageCount} pages...`);

      const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
      const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;

      try {
        const allDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];

        const allFixtureCodes = (electricalKey?.components || [])
          .filter((c: any) => c.symbolType === 'Lighting')
          .map((c: any) => (c.symbol || c.name || '').trim().toUpperCase())
          .filter((s: string) => s.length > 0);

        const completedPageSet = new Set<number>();
        const markLightingPageDone = (pageNum: number): number => {
          completedPageSet.add(pageNum);
          return completedPageSet.size;
        };

        // Fan out: one request per page for parallel Cloud Run instances
        const CLOUD_RUN_URL = 'https://autocount-lighting-904918874970.us-central1.run.app';

        const processPageStream = async (pg: number) => {
          const response = await fetch(CLOUD_RUN_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pdfUrl: file.url,
              pageNumbers: [pg],
              fixtureCodes,
              fixtureCode: fixtureCodes[0], // Use first valid code as primary
              allFixtureCodes
            })
          });

          const reader = response.body!.getReader();
          const decoder = new TextDecoder();
          let streamBuffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            streamBuffer += decoder.decode(value, { stream: true });
            const events = streamBuffer.split('\n\n');
            streamBuffer = events.pop() || '';

            for (const eventBlock of events) {
              if (!eventBlock.trim()) continue;
              const dataLine = eventBlock.split('\n').find((l: string) => l.startsWith('data: '));
              if (!dataLine) continue;
              try {
                const msg = JSON.parse(dataLine.slice(6));

                if (msg.event === 'progress') {
                  const progressPageNum = msg.pageNum || pg;
                  const phase = msg.phase || 'scanning';
                  const pct = msg.pct || 0;
                  const unfilteredTotal = msg.unfilteredTotal || 0;
                  
                  setCountAllProgress(`Scanning for lighting fixtures... (${allDetections.length} found)`);
                  setLightingProgress(prev => {
                    const newPageProgress = new Map(prev.pageProgress);
                    
                    // Sync all pages to 'verifying' if the global phase is verifying
                    if (phase === 'verifying') {
                      newPageProgress.forEach((status, pgNum) => {
                        if (status.phase !== 'done') {
                          newPageProgress.set(pgNum, { 
                            ...status, 
                            phase: 'verifying',
                            pct: Math.max(status.pct || 0, 88) 
                          });
                        }
                      });
                    }

                    const existing = newPageProgress.get(progressPageNum) || { phase: 'scanning', pct: 0, completedTiles: 0, totalTiles: 0 };
                    if (existing.phase === 'done') {
                      return prev;
                    }
                    newPageProgress.set(progressPageNum, { ...existing, phase, pct: Math.max(existing.pct || 0, pct) });
                    
                    return {
                      ...prev,
                      pageProgress: newPageProgress,
                      overallPct: computeLightingOverallPct(newPageProgress, prev.totalPages),
                      totalCandidates: unfilteredTotal || prev.totalCandidates,
                      message: phase === 'rendering' ? `Rendering page ${progressPageNum}...`
                        : phase === 'verifying' ? `Verifying ${unfilteredTotal || prev.totalCandidates} candidates with AI...`
                        : `Scanning page ${progressPageNum}...`
                    };
                  });

                } else if (msg.event === 'page-progress') {
                  const pageProgressNum = msg.pageNum || pg;
                  const phase = msg.phase || 'scanning';
                  const pct = msg.pct || 0;
                  if (msg.completedTiles != null) {
                    setLightingProgress(prev => {
                      const newPageProgress = new Map(prev.pageProgress);
                      const existing = newPageProgress.get(pageProgressNum) || { phase: 'scanning', pct: 0, completedTiles: 0, totalTiles: 0 };
                      if (existing.phase === 'done') {
                        return prev;
                      }
                      
                      // Prevent phase regression (don't go from verifying back to scanning)
                      const newPhase = (existing.phase === 'verifying' && phase === 'scanning') ? 'verifying' : phase;
                      // Use the higher percentage to prevent jumping backwards
                      const newPct = Math.max(existing.pct || 0, pct);

                      newPageProgress.set(pageProgressNum, { 
                        ...existing, 
                        phase: newPhase, 
                        pct: newPct,
                        completedTiles: msg.completedTiles, 
                        totalTiles: msg.totalTiles 
                      });
                      return {
                        ...prev,
                        pageProgress: newPageProgress,
                        overallPct: computeLightingOverallPct(newPageProgress, prev.totalPages),
                        message: newPhase === 'verifying' ? `Verifying page ${pageProgressNum} with AI...` : `Scanning page ${pageProgressNum}... (${msg.completedTiles}/${msg.totalTiles} tiles)`
                      };
                    });
                  }

                } else if (msg.event === 'unfiltered-batch') {
                  const batchPageNum = msg.pageNum || pg;
                  const batchFixtureCode = msg.fixtureCode || '';
                  const unfilteredCount = (msg.newPredictions || []).length;
                  setCountAllProgress(`Scanning for lighting fixtures... detecting on page ${batchPageNum}... (verifying with AI before showing)`);
                  setLightingProgress(prev => ({
                    ...prev,
                    message: `Detecting on page ${batchPageNum}... verifying with AI`
                  }));
                  console.log(`📥 Page ${batchPageNum} [${batchFixtureCode}]: +${unfilteredCount} unfiltered (held for verification)`);

                } else if (msg.event === 'verified-batch') {
                  const verifiedCode = msg.fixtureCode || '';
                  const verifiedPreds = msg.predictions || [];
                  const verifiedSoFar = msg.verifiedSoFar || 0;
                  const totalToVerify = msg.totalToVerify || 0;

                  const newDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
                  for (const pred of verifiedPreds) {
                    const screenX = pred.relX * onScreenWidth;
                    const screenY = pred.relY * onScreenHeight;
                    const screenWidth = pred.relWidth * onScreenWidth;
                    const screenHeight = pred.relHeight * onScreenHeight;
                    const predPageNum = pred.pageNum || pg;

                    newDetections.push({
                      id: `lighting-${verifiedCode}-${predPageNum}-${pred.relX.toFixed(5)}-${pred.relY.toFixed(5)}`,
                      thumbnail: pred.thumbnail || '',
                      prediction: {
                        ...pred,
                        x: screenX,
                        y: screenY,
                        width: screenWidth,
                        height: screenHeight,
                        page: predPageNum,
                        classifiedName: verifiedCode,
                      }
                    });
                  }

                  if (newDetections.length > 0) {
                    allDetections.push(...newDetections);

                    setMultiComponentDetections(prev => {
                      const updated = [...prev];
                      const comp = updated[componentIndex];
                      if (comp) {
                        updated[componentIndex] = { ...comp, detections: [...allDetections] };
                      }
                      return updated;
                    });
                    const compKey = isLightingGroup ? '__LIGHTING_GROUP__' : (component.symbol || component.name);
                    setAllComponentSelections(prev => {
                      const newMap = new Map(prev);
                      const currentSet = new Set(newMap.get(compKey) || []);
                      newDetections.forEach(d => currentSet.add(d.id));
                      newMap.set(compKey, currentSet);
                      return newMap;
                    });
                    if (isLightingGroup && verifiedCode) {
                      setDetectionClassifications(prev => {
                        const newMap = new Map(prev);
                        newDetections.forEach(d => newMap.set(d.id, verifiedCode));
                        return newMap;
                      });
                    }
                  }

                  setLightingProgress(prev => {
                    const newPageProgress = new Map(prev.pageProgress);
                    
                    // Update progress for all pages that have detections in this batch
                    // or at least for the current request page 'pg'
                    const affectedPages = new Set<number>();
                    if (newDetections.length > 0) {
                      newDetections.forEach(d => affectedPages.add(d.prediction.page));
                    } else {
                      affectedPages.add(pg);
                    }

                    affectedPages.forEach(affectedPg => {
                      const pgStatus = newPageProgress.get(affectedPg) || { phase: 'verifying', pct: 88, completedTiles: 1, totalTiles: 1 };
                      if (pgStatus.phase !== 'done') {
                        // Calculate individual page percentage based on verification progress
                        // Smoothly crawl from 88% to 100%
                        const verifyRatio = totalToVerify > 0 ? verifiedSoFar / totalToVerify : 0;
                        const pagePct = 88 + (verifyRatio * 12);

                        newPageProgress.set(affectedPg, { 
                          ...pgStatus, 
                          phase: 'verifying', 
                          pct: Math.max(pgStatus.pct || 0, pagePct),
                          verifiedSoFar, 
                          totalToVerify 
                        });
                      }
                    });

                    return {
                      ...prev,
                      pageProgress: newPageProgress,
                      overallPct: computeLightingOverallPct(newPageProgress, prev.totalPages),
                      detectionsFound: allDetections.length,
                      verifiedCount: verifiedSoFar,
                      totalCandidates: totalToVerify || prev.totalCandidates,
                      message: `Verified ${allDetections.length} fixtures (${verifiedSoFar}/${totalToVerify || '?'} candidates checked)`
                    };
                  });
                  console.log(`🔍 Verified [${verifiedCode}]: ${verifiedPreds.length} kept (total: ${allDetections.length})`);

                } else if (msg.event === 'page-scanned') {
                  const scannedPageNum = msg.pageNum || pg;
                  console.log(`📡 Page ${scannedPageNum} scanned: ${msg.totalPredictions} candidates found`);
                  setLightingProgress(prev => {
                    const newPageProgress = new Map(prev.pageProgress);
                    const existing = newPageProgress.get(scannedPageNum) || { phase: 'scanning', pct: 0, completedTiles: 0, totalTiles: 0 };
                    if (existing.phase !== 'done') {
                      newPageProgress.set(scannedPageNum, { ...existing, phase: 'verifying', pct: 88 });
                    }
                    return {
                      ...prev,
                      pageProgress: newPageProgress,
                      overallPct: computeLightingOverallPct(newPageProgress, prev.totalPages),
                      message: `Page ${scannedPageNum} scanned. Verifying ${msg.totalPredictions} candidates...`
                    };
                  });

                } else if (msg.event === 'page-done') {
                  const donePageNum = msg.pageNum || pg;
                  const completedPageCount = markLightingPageDone(donePageNum);
                  console.log(`✅ Page ${donePageNum} fully done: ${msg.totalPredictions} lighting detections (${completedPageCount}/${pageNumbers.length} pages)`);
                  setCountAllProgress(`Scanning for lighting fixtures... (${allDetections.length} found, ${completedPageCount}/${pageNumbers.length} pages)`);
                  setCountAllComponentProgress(prev => prev.map((p, idx) =>
                    idx === componentIndex ? { ...p, completedBatches: Math.round((completedPageCount / pageNumbers.length) * 100), totalBatches: 100 } : p
                  ));
                  setLightingProgress(prev => {
                    const newPageProgress = new Map(prev.pageProgress);
                    newPageProgress.set(donePageNum, { phase: 'done', pct: 100, completedTiles: 1, totalTiles: 1 });
                    return {
                      ...prev,
                      pageProgress: newPageProgress,
                      overallPct: computeLightingOverallPct(newPageProgress, prev.totalPages),
                      detectionsFound: allDetections.length,
                      message: `${completedPageCount}/${pageNumbers.length} pages complete (${allDetections.length} fixtures found)`
                    };
                  });

                } else if (msg.event === 'done') {
                  // Each page request sends a terminal "done" event. Use it as a completion backstop.
                  const completedPageCount = markLightingPageDone(pg);
                  setCountAllProgress(`Finalizing lighting fixtures... (${completedPageCount}/${pageNumbers.length} pages)`);
                  setCountAllComponentProgress(prev => prev.map((p, idx) =>
                    idx === componentIndex ? { ...p, completedBatches: Math.round((completedPageCount / pageNumbers.length) * 100), totalBatches: 100 } : p
                  ));
                  setLightingProgress(prev => {
                    const newPageProgress = new Map(prev.pageProgress);
                    newPageProgress.set(pg, { phase: 'done', pct: 100, completedTiles: 1, totalTiles: 1 });
                    return {
                      ...prev,
                      pageProgress: newPageProgress,
                      overallPct: computeLightingOverallPct(newPageProgress, prev.totalPages),
                      detectionsFound: allDetections.length,
                      message: `${completedPageCount}/${pageNumbers.length} pages complete (${allDetections.length} fixtures found)`
                    };
                  });

                } else if (msg.event === 'error') {
                  console.error(`❌ Page ${pg} server error:`, msg.message);
                }
              } catch (parseErr) {
                console.warn(`⚠️ Failed to parse lighting SSE event for page ${pg}:`, parseErr);
              }
            }
          }
        };

        // Fire all pages in parallel — each hits a separate Cloud Run instance
        console.log(`🚀 Launching ${pageNumbers.length} parallel page requests`);
        await Promise.all(pageNumbers.map(pg => processPageStream(pg)));

        // Finalize
        for (const det of allDetections) {
          if (det.prediction.unfiltered) det.prediction.unfiltered = false;
        }
        
        // Final state update with all detections from all pages
        setMultiComponentDetections(prev => {
          const updated = [...prev];
          const comp = updated[componentIndex];
          if (comp) {
            updated[componentIndex] = { ...comp, detections: [...allDetections] };
          }
          return updated;
        });

        setLightingProgress(prev => {
          const newPageProgress = new Map(prev.pageProgress);
          pageNumbers.forEach((pageNum) => {
            newPageProgress.set(pageNum, { phase: 'done', pct: 100, completedTiles: 1, totalTiles: 1 });
          });
          return {
            ...prev,
            pageProgress: newPageProgress,
            overallPct: 100,
            detectionsFound: allDetections.length,
            message: `${pageNumbers.length}/${pageNumbers.length} pages complete (${allDetections.length} fixtures found)`
          };
        });
        setCountAllProgress(`Found ${allDetections.length} lighting fixtures across ${pageNumbers.length} page${pageNumbers.length > 1 ? 's' : ''}`);
        console.log(`✅ All ${pageNumbers.length} pages done: ${allDetections.length} total detections`);

        console.log(`🔢 Total lighting detections from server: ${allDetections.length}`);

        // Small delay to ensure multiComponentDetections state is flushed before setting status to complete
        // (which triggers auto-save in useEffect)
        await new Promise(resolve => setTimeout(resolve, 300));

        setCountAllComponentProgress(prev => prev.map((p, i) =>
          i === componentIndex ? { ...p, status: 'complete' as const, completedBatches: p.totalBatches || 1 } : p
        ));
        finalizeComponentCount();
        console.log(`✅ Finished server-side counting "${displayName}"`);
        return;
      } catch (error) {
        console.error(`❌ Lighting server-side batch failed:`, error);
        setCountAllProgress(`Server-side failed for "${displayName}".`);
        setLightingProgress(prev => ({ ...prev, message: 'Server-side failed. Please try again.' }));
        return; // STOP HERE. Do not fall through to client-side tiling.
      }
    }
    // ============================================================
    // SERVER-SIDE BATCH PATH (Streaming): Switches use Cloud Run with
    // PyMuPDF rendering + Roboflow object detection (switch-finder model).
    // Single request, PDF downloaded once, streams per-page results.
    // No classification — just detection + dedup.
    // ============================================================
    if (isSwitchComponent && file?.url) {
      const switchDisplayName = component.name;

      console.log(`🔌 CLOUD RUN: Counting switch "${switchDisplayName}" on pages [${pageNumbers.join(', ')}]`);
      setCountAllProgress(`Counting ${switchDisplayName} across ${pageCount} pages...`);

      const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
      const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;

      try {
        const allDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
        let completedPageCount = 0;

        const SWITCH_CLOUD_RUN_URL = 'https://autocount-switches-904918874970.us-central1.run.app';

        const processPageStream = async (pg: number) => {
          const response = await fetch(SWITCH_CLOUD_RUN_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pdfUrl: file.url,
              pageNumbers: [pg],
              componentType: 'switch',
              confidence: 0.01
            })
          });

          const reader = response.body!.getReader();
          const decoder = new TextDecoder();
          let streamBuffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            streamBuffer += decoder.decode(value, { stream: true });
            const events = streamBuffer.split('\n\n');
            streamBuffer = events.pop() || '';

            for (const eventBlock of events) {
              if (!eventBlock.trim()) continue;
              const dataLine = eventBlock.split('\n').find((l: string) => l.startsWith('data: '));
              if (!dataLine) continue;
              try {
                const msg = JSON.parse(dataLine.slice(6));

                if (msg.event === 'progress') {
                  setCountAllProgress(`Scanning for switches... (${allDetections.length} found)`);

                } else if (msg.event === 'unfiltered-batch') {
                  const batchPreds = msg.newPredictions || [];
                  const batchPageNum = msg.pageNum || pg;

                  const newDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
                  for (const pred of batchPreds) {
                    const screenX = pred.relX * onScreenWidth;
                    const screenY = pred.relY * onScreenHeight;
                    const screenWidth = pred.relWidth * onScreenWidth;
                    const screenHeight = pred.relHeight * onScreenHeight;
                    const predPageNum = pred.pageNum || batchPageNum;

                    newDetections.push({
                      id: `switch-${predPageNum}-${pred.relX.toFixed(5)}-${pred.relY.toFixed(5)}`,
                      thumbnail: pred.thumbnail || '',
                      prediction: {
                        ...pred,
                        x: screenX,
                        y: screenY,
                        width: screenWidth,
                        height: screenHeight,
                        page: predPageNum,
                      }
                    });
                  }

                  if (newDetections.length > 0) {
                    allDetections.push(...newDetections);

                    setMultiComponentDetections(prev => prev.map((d, idx) =>
                      idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                    ));
                    const compKey = component.name;
                    setAllComponentSelections(prev => {
                      const newMap = new Map(prev);
                      const currentSet = new Set(newMap.get(compKey) || []);
                      newDetections.forEach(d => currentSet.add(d.id));
                      newMap.set(compKey, currentSet);
                      return newMap;
                    });
                  }

                  console.log(`📥 Page ${batchPageNum}: +${batchPreds.length} detections (total: ${allDetections.length})`);
                  setCountAllProgress(`Scanning for switches... (${allDetections.length} found)`);

                } else if (msg.event === 'page-done') {
                  completedPageCount++;
                  console.log(`✅ Page ${msg.pageNum}: ${msg.totalPredictions} switch detections (${completedPageCount}/${pageNumbers.length} pages)`);
                  setCountAllProgress(`Scanning for switches... (${allDetections.length} found, ${completedPageCount}/${pageNumbers.length} pages)`);
                  setCountAllComponentProgress(prev => prev.map((p, idx) =>
                    idx === componentIndex ? { ...p, completedBatches: Math.round((completedPageCount / pageNumbers.length) * 100), totalBatches: 100 } : p
                  ));

                } else if (msg.event === 'error') {
                  console.error(`❌ Page ${pg} server error:`, msg.message);
                }
              } catch (parseErr) {
                // skip unparseable event
              }
            }
          }
        };

        console.log(`🚀 Launching ${pageNumbers.length} parallel switch page requests`);
        await Promise.all(pageNumbers.map(pg => processPageStream(pg)));

        // Finalize
        setMultiComponentDetections(prev => prev.map((d, idx) =>
          idx === componentIndex ? { ...d, detections: [...allDetections] } : d
        ));
        setCountAllProgress(`Found ${allDetections.length} switches across ${pageNumbers.length} page${pageNumbers.length > 1 ? 's' : ''}`);
        console.log(`✅ All ${pageNumbers.length} pages done: ${allDetections.length} total switch detections`);

        setCountAllComponentProgress(prev => prev.map((p, i) =>
          i === componentIndex ? { ...p, status: 'complete' as const, completedBatches: p.totalBatches || 1 } : p
        ));
        finalizeComponentCount();
        console.log(`✅ Finished server-side switch counting "${switchDisplayName}"`);
        return;
      } catch (error) {
        console.error(`❌ Switch server-side batch failed, falling back to client-side:`, error);
        setCountAllProgress(`Server-side failed, counting "${switchDisplayName}" client-side...`);
      }
    }
    // ============================================================
    // SERVER-SIDE BATCH PATH (Streaming): Junction Boxes use Cloud Run with
    // PyMuPDF rendering + Roboflow object detection (junction-box-detection model).
    // Single request, PDF downloaded once, streams per-page results.
    // No classification — just detection + dedup.
    // ============================================================
    if (isJunctionBoxComponent && file?.url) {
      const jboxDisplayName = component.name;

      console.log(`📦 CLOUD RUN: Counting junction box "${jboxDisplayName}" on pages [${pageNumbers.join(', ')}]`);
      setCountAllProgress(`Counting ${jboxDisplayName} across ${pageCount} pages...`);

      const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
      const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;

      try {
        const allDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
        let completedPageCount = 0;

        const JBOX_CLOUD_RUN_URL = 'https://autocount-jboxes-904918874970.us-central1.run.app';

        const processPageStream = async (pg: number) => {
          const response = await fetch(JBOX_CLOUD_RUN_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pdfUrl: file.url,
              pageNumbers: [pg],
              componentType: 'junction box',
              confidence: 0.01
            })
          });

          const reader = response.body!.getReader();
          const decoder = new TextDecoder();
          let streamBuffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            streamBuffer += decoder.decode(value, { stream: true });
            const events = streamBuffer.split('\n\n');
            streamBuffer = events.pop() || '';

            for (const eventBlock of events) {
              if (!eventBlock.trim()) continue;
              const dataLine = eventBlock.split('\n').find((l: string) => l.startsWith('data: '));
              if (!dataLine) continue;
              try {
                const msg = JSON.parse(dataLine.slice(6));

                if (msg.event === 'progress') {
                  setCountAllProgress(`Scanning for junction boxes... (${allDetections.length} found)`);

                } else if (msg.event === 'unfiltered-batch') {
                  const batchPreds = msg.newPredictions || [];
                  const batchPageNum = msg.pageNum || pg;

                  const newDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
                  for (const pred of batchPreds) {
                    const screenX = pred.relX * onScreenWidth;
                    const screenY = pred.relY * onScreenHeight;
                    const screenWidth = pred.relWidth * onScreenWidth;
                    const screenHeight = pred.relHeight * onScreenHeight;
                    const predPageNum = pred.pageNum || batchPageNum;

                    newDetections.push({
                      id: `jbox-${predPageNum}-${pred.relX.toFixed(5)}-${pred.relY.toFixed(5)}`,
                      thumbnail: pred.thumbnail || '',
                      prediction: {
                        ...pred,
                        x: screenX,
                        y: screenY,
                        width: screenWidth,
                        height: screenHeight,
                        page: predPageNum,
                      }
                    });
                  }

                  if (newDetections.length > 0) {
                    allDetections.push(...newDetections);

                    setMultiComponentDetections(prev => prev.map((d, idx) =>
                      idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                    ));
                    const compKey = component.name;
                    setAllComponentSelections(prev => {
                      const newMap = new Map(prev);
                      const currentSet = new Set(newMap.get(compKey) || []);
                      newDetections.forEach(d => currentSet.add(d.id));
                      newMap.set(compKey, currentSet);
                      return newMap;
                    });
                  }

                  console.log(`📥 Page ${batchPageNum}: +${batchPreds.length} jbox detections (total: ${allDetections.length})`);
                  setCountAllProgress(`Scanning for junction boxes... (${allDetections.length} found)`);

                } else if (msg.event === 'page-done') {
                  completedPageCount++;
                  console.log(`✅ Page ${msg.pageNum}: ${msg.totalPredictions} jbox detections (${completedPageCount}/${pageNumbers.length} pages)`);
                  setCountAllProgress(`Scanning for junction boxes... (${allDetections.length} found, ${completedPageCount}/${pageNumbers.length} pages)`);
                  setCountAllComponentProgress(prev => prev.map((p, idx) =>
                    idx === componentIndex ? { ...p, completedBatches: Math.round((completedPageCount / pageNumbers.length) * 100), totalBatches: 100 } : p
                  ));

                } else if (msg.event === 'error') {
                  console.error(`❌ Page ${pg} server error:`, msg.message);
                }
              } catch (parseErr) {
                // skip unparseable event
              }
            }
          }
        };

        console.log(`🚀 Launching ${pageNumbers.length} parallel jbox page requests`);
        await Promise.all(pageNumbers.map(pg => processPageStream(pg)));

        // Finalize
        setMultiComponentDetections(prev => prev.map((d, idx) =>
          idx === componentIndex ? { ...d, detections: [...allDetections] } : d
        ));
        setCountAllProgress(`Found ${allDetections.length} junction boxes across ${pageNumbers.length} page${pageNumbers.length > 1 ? 's' : ''}`);
        console.log(`✅ All ${pageNumbers.length} pages done: ${allDetections.length} total jbox detections`);

        setCountAllComponentProgress(prev => prev.map((p, i) =>
          i === componentIndex ? { ...p, status: 'complete' as const, completedBatches: p.totalBatches || 1 } : p
        ));
        finalizeComponentCount();
        console.log(`✅ Finished server-side junction box counting "${jboxDisplayName}"`);
        return;
      } catch (error) {
        console.error(`❌ JBox server-side batch failed, falling back to client-side:`, error);
        setCountAllProgress(`Server-side failed, counting "${jboxDisplayName}" client-side...`);
      }
    }
    // ============================================================
    // SERVER-SIDE BATCH PATH (Streaming): Generic Gemini Vision uses Cloud Run with
    // PyMuPDF rendering + Gemini 3 Flash for visual reference-based detection.
    // Single request, PDF downloaded once, streams per-page results.
    // ============================================================
    const isGenericComponent = !isLightingComponent && !isLightingGroup && !isSwitchComponent && !isReceptacleComponent && !isReceptaclesGroup && !isTeleDataComponent && !isJunctionBoxComponent;
    if (isGenericComponent && file?.url && component.referenceImage) {
      console.log(`🔮 CLOUD RUN: Counting "${component.name}" on pages [${pageNumbers.join(', ')}] — Gemini vision detection`);
      setCountAllProgress(`Counting "${component.name}" across ${pageCount} pages...`);

      const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
      const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;

      try {
        const allDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
        let completedPageCount = 0;

        const response = await fetch('https://autocount-gemini-904918874970.us-central1.run.app', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pdfUrl: file.url,
            pageNumbers,
            referenceAreaBase64: component.referenceImage,
            componentType: component.name,
            componentSymbol: component.symbol || component.name
          })
        });

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let streamBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          streamBuffer += decoder.decode(value, { stream: true });
          const events = streamBuffer.split('\n\n');
          streamBuffer = events.pop() || '';

          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;
            const dataLine = eventBlock.split('\n').find((l: string) => l.startsWith('data: '));
            if (!dataLine) continue;
            try {
              const msg = JSON.parse(dataLine.slice(6));

              if (msg.event === 'progress') {
                setCountAllProgress(msg.message || '');
                if (msg.pct != null) {
                  setCountAllComponentProgress(prev => prev.map((p, idx) =>
                    idx === componentIndex ? { ...p, completedBatches: msg.pct, totalBatches: 100 } : p
                  ));
                }

              } else if (msg.event === 'page-progress') {
                setCountAllProgress(
                  `${msg.message || ''} (${allDetections.length} found)`
                );

              } else if (msg.event === 'unfiltered-batch') {
                const batchPageNum = msg.pageNum || pageNumber;
                const newDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
                for (const pred of (msg.newPredictions || [])) {
                  const screenX = pred.relX * onScreenWidth;
                  const screenY = pred.relY * onScreenHeight;
                  const screenWidth = pred.relWidth * onScreenWidth;
                  const screenHeight = pred.relHeight * onScreenHeight;

                  newDetections.push({
                    id: `gemini-${batchPageNum}-${pred.relX.toFixed(5)}-${pred.relY.toFixed(5)}`,
                    thumbnail: pred.thumbnail || '',
                    prediction: {
                      ...pred,
                      x: screenX,
                      y: screenY,
                      width: screenWidth,
                      height: screenHeight,
                      page: batchPageNum,
                    }
                  });
                }

                if (newDetections.length > 0) {
                  allDetections.push(...newDetections);

                  setMultiComponentDetections(prev => prev.map((d, idx) =>
                    idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                  ));
                  setAllComponentSelections(prev => {
                    const newMap = new Map(prev);
                    const compKey = component.name;
                    const currentSet = new Set(newMap.get(compKey) || []);
                    newDetections.forEach(d => currentSet.add(d.id));
                    newMap.set(compKey, currentSet);
                    return newMap;
                  });
                }

                console.log(`📥 Page ${batchPageNum}: +${newDetections.length} Gemini detections (total: ${allDetections.length})`);
                await new Promise(resolve => setTimeout(resolve, 0));

              } else if (msg.event === 'page-done') {
                completedPageCount++;
                console.log(`✅ Page ${msg.pageNum}: ${msg.totalPredictions} detections (${completedPageCount}/${pageNumbers.length} pages)`);
                setCountAllProgress(`Scanning for ${component.name}... (${allDetections.length} found, ${completedPageCount}/${pageNumbers.length} pages)`);
                setCountAllComponentProgress(prev => prev.map((p, idx) =>
                  idx === componentIndex ? { ...p, completedBatches: Math.round((completedPageCount / pageNumbers.length) * 100), totalBatches: 100 } : p
                ));

              } else if (msg.event === 'done') {
                setMultiComponentDetections(prev => prev.map((d, idx) =>
                  idx === componentIndex ? { ...d, detections: [...allDetections] } : d
                ));
                setCountAllProgress(`Found ${allDetections.length} ${component.name} across ${pageNumbers.length} pages`);
                console.log(`✅ All pages done: ${msg.totalPredictions} total detections in ${msg.timing?.totalMs}ms`);

              } else if (msg.event === 'error') {
                console.error(`❌ Server error:`, msg.message);
              }
            } catch (parseErr) {
              console.warn('Failed to parse SSE event:', eventBlock);
            }
          }
        }

        console.log(`🔢 Total Gemini detections from server: ${allDetections.length}`);

        setCountAllComponentProgress(prev => prev.map((p, i) =>
          i === componentIndex ? { ...p, status: 'complete' as const, completedBatches: p.totalBatches || 1 } : p
        ));
        finalizeComponentCount();
        console.log(`✅ Finished server-side Gemini counting "${component.name}"`);
        return;
      } catch (error) {
        console.error(`❌ Gemini server-side batch failed:`, error);
        setCountAllProgress(`Server-side failed for "${component.name}".`);
        return; // STOP HERE. Do not fall through to client-side tiling.
      }
    }
    // ============================================================
    // END SERVER-SIDE BATCH PATH
    // ============================================================

    const isOcrBased = isLightingComponent || isSwitchComponent || isReceptacleComponent || isTeleDataComponent || isJunctionBoxComponent; // Higher res + 10x10 grid with overlap
    const BATCH_SIZE = isOcrBased ? 20 : 10;
    const MAX_WIDTH = isOcrBased ? 16000 : 8000; // Higher res for OCR-based detection
    
    // Get on-screen canvas dimensions for coordinate scaling (SAME AS handleCountAll)
    const onScreenPdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    const onScreenWidth = onScreenPdfCanvas?.width || canvasRef.current?.width || 8000;
    const onScreenHeight = onScreenPdfCanvas?.height || canvasRef.current?.height || 5714;
    
    // Helper to extract thumbnail from high-res canvas
    const extractThumbnailFromCanvas = (canvas: HTMLCanvasElement, prediction: any): string => {
      const centerX = prediction.relX * canvas.width;
      const centerY = prediction.relY * canvas.height;
      const boxWidth = (prediction.relWidth || 0.02) * canvas.width;
      const boxHeight = (prediction.relHeight || 0.02) * canvas.height;
      
      const padding = 30;
      const x = Math.max(0, centerX - boxWidth / 2 - padding);
      const y = Math.max(0, centerY - boxHeight / 2 - padding);
      const width = Math.min(canvas.width - x, boxWidth + padding * 2);
      const height = Math.min(canvas.height - y, boxHeight + padding * 2);
      
      const thumbCanvas = document.createElement('canvas');
      thumbCanvas.width = width;
      thumbCanvas.height = height;
      const thumbCtx = thumbCanvas.getContext('2d')!;
      thumbCtx.drawImage(canvas, x, y, width, height, 0, 0, width, height);

      // Match server behavior by drawing a green bbox on thumbnails.
      const bboxLeft = Math.max(0, centerX - boxWidth / 2 - x);
      const bboxTop = Math.max(0, centerY - boxHeight / 2 - y);
      const strokeWidth = Math.max(2, Math.round(Math.min(boxWidth, boxHeight) * 0.06));
      thumbCtx.strokeStyle = '#00FF00';
      thumbCtx.lineWidth = strokeWidth;
      thumbCtx.strokeRect(bboxLeft, bboxTop, boxWidth, boxHeight);

      return thumbCanvas.toDataURL('image/png');
    };
    
    // Process selected pages concurrently
    await Promise.all(
      pageNumbers.map(async (pageNum) => {
        try {
          // Render page at 8000px width (SAME AS handleCountAll)
          const page = await pdfDocument.getPage(pageNum);
          const baseViewport = page.getViewport({ scale: 1.0 });
          const scale = MAX_WIDTH / baseViewport.width;
          const viewport = page.getViewport({ scale });
          
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = viewport.width;
          pageCanvas.height = viewport.height;
          const pageCtx = pageCanvas.getContext('2d')!;
          await page.render({ canvasContext: pageCtx, viewport }).promise;
          
          const canvasWidth = pageCanvas.width;
          const canvasHeight = pageCanvas.height;
          
          // Create tiles - 10x10 grid for OCR-based detection (lighting + switches), 5x5 for regular
          const gridSize = isOcrBased ? 10 : 5;
          const tileWidth = Math.floor(canvasWidth / gridSize);
          const tileHeight = Math.floor(canvasHeight / gridSize);
          const overlap = isOcrBased ? Math.floor(tileWidth * 0.15) : 0; // 15% overlap for OCR-based detection
          const tiles: Array<any> = [];
          
          // DEBUG: Store tile grid params so drawAnnotations can render the grid overlay
          if (isOcrBased) {
            debugTileGridRef.current = { gridSize, tileWidth, tileHeight, overlap, hiResWidth: canvasWidth, hiResHeight: canvasHeight };
            console.log(`🔴 DEBUG: Stored tile grid params (${gridSize}x${gridSize}, tile: ${tileWidth}x${tileHeight}px, overlap: ${overlap}px)`);
            setForceRedraw(prev => prev + 1);
          }
          
          // Helper: generate tiles from a grid with a given pixel offset
          const generateTiles = (offsetX: number, offsetY: number, passLabel: string) => {
            const passTiles: Array<any> = [];
            for (let row = 0; row < gridSize; row++) {
              for (let col = 0; col < gridSize; col++) {
                const baseLeft = col * tileWidth + offsetX;
                const baseTop = row * tileHeight + offsetY;
                if (baseLeft >= canvasWidth || baseTop >= canvasHeight) continue;
                
                const captureLeft = Math.max(0, baseLeft - overlap);
                const captureTop = Math.max(0, baseTop - overlap);
                const baseRight = Math.min(canvasWidth, baseLeft + tileWidth);
                const baseBottom = Math.min(canvasHeight, baseTop + tileHeight);
                const captureRight = Math.min(canvasWidth, baseRight + overlap);
                const captureBottom = Math.min(canvasHeight, baseBottom + overlap);
                const captureW = captureRight - captureLeft;
                const captureH = captureBottom - captureTop;
                if (captureW < 100 || captureH < 100) continue; // Skip tiny edge tiles
                
                const tileCanvas = document.createElement('canvas');
                tileCanvas.width = captureW;
                tileCanvas.height = captureH;
                const tileCtx = tileCanvas.getContext('2d');
                if (!tileCtx) continue;
                tileCtx.drawImage(pageCanvas, captureLeft, captureTop, captureW, captureH, 0, 0, captureW, captureH);
                
                // Boost contrast for OCR-based detection (lighting + switches)
                if (isOcrBased) {
                  const imageData = tileCtx.getImageData(0, 0, captureW, captureH);
                  const data = imageData.data;
                  const contrast = 1.5;
                  const factor = (259 * (contrast * 128 + 255)) / (255 * (259 - contrast * 128));
                  for (let p = 0; p < data.length; p += 4) {
                    data[p] = Math.min(255, Math.max(0, factor * (data[p] - 128) + 128));
                    data[p + 1] = Math.min(255, Math.max(0, factor * (data[p + 1] - 128) + 128));
                    data[p + 2] = Math.min(255, Math.max(0, factor * (data[p + 2] - 128) + 128));
                  }
                  tileCtx.putImageData(imageData, 0, 0);
                }
                
                passTiles.push({
                  base64: tileCanvas.toDataURL('image/png'),
                  xOffset: captureLeft, yOffset: captureTop,
                  width: captureW, height: captureH,
                  row, col, index: 0 // re-indexed below
                });
              }
            }
            return passTiles;
          };
          
          // Pass 1: Normal grid
          const pass1Tiles = generateTiles(0, 0, 'pass1');
          tiles.push(...pass1Tiles);
          
          // Pass 2: Staggered grid offset by half a tile (OCR-based detection)
          // Labels on pass 1 boundaries will be centered in pass 2 tiles
          if (isOcrBased) {
            const halfW = Math.floor(tileWidth / 2);
            const halfH = Math.floor(tileHeight / 2);
            const pass2Tiles = generateTiles(halfW, halfH, 'pass2');
            tiles.push(...pass2Tiles);
            console.log(`🔀 Staggered grid: Pass 1 = ${pass1Tiles.length} tiles, Pass 2 = ${pass2Tiles.length} tiles (offset ${halfW}x${halfH}px), Total = ${tiles.length} tiles`);
          }
          
          // Re-index all tiles sequentially
          for (let ti = 0; ti < tiles.length; ti++) {
            tiles[ti].index = ti;
          }
          
          // Track all accumulated detections for cross-batch deduplication (needed for staggered grid)
          const allAccumulatedDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
          
          // Process tiles in batches
          for (let batchStart = 0; batchStart < tiles.length; batchStart += BATCH_SIZE) {
            const batch = tiles.slice(batchStart, batchStart + BATCH_SIZE);
            
            const batchPromises = batch.map(async (tile: any) => {
              try {
                // Use different API endpoint based on component type:
                // - Lighting fixtures: OCR-based label detection
                // - Switches: OCR + Gemini verification with subscript matching
                // - Receptacles: Roboflow object detection model
                // - Everything else: visual matching with reference image
                const isLighting = component.symbolType === 'Lighting';
                const isReceptacle = component.name.toLowerCase().includes('receptacle') || component.name.toLowerCase().includes('duplex') || (component as any).componentType === 'Receptacles';
                const isSwitch = (component as any).componentType === 'Switches' || (component.name.toLowerCase().includes('switch') && !isReceptacle);
                const apiEndpoint = isLighting 
                  ? '/api/autocount-lighting' 
                  : isSwitch 
                    ? '/api/autocount-switches'
                    : isReceptacle
                      ? '/api/autocount-receptacles-v2'
                      : '/api/autocount-gemini';
                // Collect all lighting fixture codes for negative-list matching
                const allFixtureCodes = isLighting
                  ? (electricalKey?.components || [])
                      .filter((c: any) => c.symbolType === 'Lighting')
                      .map((c: any) => (c.symbol || c.name || '').trim().toUpperCase())
                      .filter((s: string) => s.length > 0)
                  : [];
                // Collect all switch codes for negative-list matching (same idea as lighting)
                const allSwitchCodes = isSwitch
                  ? (electricalKey?.components || [])
                      .filter((c: any) => {
                        const n = (c.name || '').toLowerCase();
                        return c.componentType === 'Switches' || (n.includes('switch') && !n.includes('receptacle'));
                      })
                      .map((c: any) => {
                        // Build the actual OCR code: use abbreviation, or "S" + subscript
                        const abbr = (c.symbol || '').trim().toUpperCase();
                        const sub = (c.subscript || '').trim();
                        if (abbr && abbr !== c.name?.toUpperCase()) return abbr;
                        if (sub) return 'S' + sub;
                        return 'S';
                      })
                      .filter((s: string) => s.length > 0)
                  : [];
                // Build the switch code: prefer abbreviation, fall back to "S" + subscript, then just "S"
                const derivedSwitchCode = (() => {
                  const abbr = (component.symbol || '').trim().toUpperCase();
                  const sub = ((component as any).subscript || '').trim();
                  // If symbol is the same as the name (i.e., no abbreviation was extracted), build from scratch
                  if (!abbr || abbr === component.name?.toUpperCase()) {
                    return sub ? 'S' + sub : 'S';
                  }
                  return abbr;
                })();
                const derivedFixtureCode = (() => {
                  const codes = (electricalKey?.components || [])
                    .filter((c: any) => c.name === component.name)
                    .map((c: any) => (c.symbol || '').trim().toUpperCase())
                    .filter((s: string) => s.length > 0 && s !== 'LIGHTING' && s !== 'LIGHTING FIXTURE' && s !== 'LIGHTING FIXTURES');
                  return codes[0] || '';
                })();

                const requestBody = isLighting
                  ? {
                      fixtureCode: derivedFixtureCode,
                      tileBase64: tile.base64,
                      tileIndex: tile.index,
                      tileWidth: tile.width,
                      tileHeight: tile.height,
                      row: tile.row,
                      col: tile.col,
                      allFixtureCodes
                    }
                  : isSwitch
                    ? {
                        switchCode: derivedSwitchCode,
                        switchName: component.name,
                        subscript: (component as any).subscript || '',
                        description: (component as any).description || '',
                        tileBase64: tile.base64,
                        tileIndex: tile.index,
                        tileWidth: tile.width,
                        tileHeight: tile.height,
                        row: tile.row,
                        col: tile.col,
                        allSwitchCodes
                      }
                    : isReceptacle
                      ? {
                          tileBase64: tile.base64,
                          tileIndex: tile.index,
                          componentType: component.name,
                          tileWidth: tile.width,
                          tileHeight: tile.height,
                          row: tile.row,
                          col: tile.col,
                          confidence: 0.24
                        }
                      : {
                          referenceAreaBase64: component.referenceImage,
                          tileBase64: tile.base64,
                          tileIndex: tile.index,
                          componentType: component.name,
                          componentSymbol: component.name,
                          tileWidth: tile.width,
                          tileHeight: tile.height,
                          row: tile.row,
                          col: tile.col
                        };

                const response = await fetch(apiEndpoint, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(requestBody)
                });
                const data = await response.json();
                if (data.predictions?.length > 0) {
                  console.log(`📥 R${tile.row}C${tile.col} (#${tile.index}): ${data.predictions.length} predictions received`, data.predictions);
                }
                return { tile, predictions: data.predictions || [] };
              } catch (error) {
                console.warn(`⚠️ R${tile.row}C${tile.col} (#${tile.index}) FETCH FAILED:`, error);
                return { tile, predictions: [] };
              }
            });
            
            const batchResults = await Promise.all(batchPromises);
            
            // Collect all detections from this batch for a single state update
            const batchDetections: Array<{ id: string; thumbnail: string; prediction: any }> = [];
            for (const { tile, predictions } of batchResults) {
              for (const pred of predictions) {
                const pageX = pred.x + tile.xOffset;
                const pageY = pred.y + tile.yOffset;
                const relX = pageX / canvasWidth;
                const relY = pageY / canvasHeight;
                const relWidth = pred.width ? pred.width / canvasWidth : 0.02;
                const relHeight = pred.height ? pred.height / canvasHeight : 0.02;
                const screenX = relX * onScreenWidth;
                const screenY = relY * onScreenHeight;
                const screenWidth = pred.width ? (pred.width / canvasWidth) * onScreenWidth : 40;
                const screenHeight = pred.height ? (pred.height / canvasHeight) * onScreenHeight : 40;
                const thumbnail = extractThumbnailFromCanvas(pageCanvas, { relX, relY, relWidth, relHeight });
                const detection = {
                  id: `${component.name}-${pageNum}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  thumbnail,
                  prediction: {
                    ...pred,
                    relX, relY, relWidth, relHeight,
                    x: screenX,
                    y: screenY,
                    width: screenWidth,
                    height: screenHeight,
                    page: pageNum
                  }
                };
                batchDetections.push(detection);
              }
            }
            
            if (batchDetections.length > 0) {
              console.log(`🔢 Batch detections (pre-dedup): ${batchDetections.length}`, batchDetections.map(d => `${d.id.substring(0, 30)}… relX=${d.prediction.relX.toFixed(4)} relY=${d.prediction.relY.toFixed(4)}`));
            }
            
            // Hybrid dedup: check each new detection against ALL prior detections
            // (both from previous batches AND from earlier items in this same batch)
            // This fixes within-batch duplicates that were slipping through
            // Applied to all OCR-based components (lighting + switches) that use staggered grids
            const deduped: Array<{ id: string; thumbnail: string; prediction: any }> = [];
            if (isLightingComponent || isSwitchComponent || isReceptacleComponent || isTeleDataComponent) {
              for (const det of batchDetections) {
                const a = det.prediction;
                let isDup = false;
                // Check against all previously accumulated detections AND already-accepted batch items
                const checkAgainst = [...allAccumulatedDetections, ...deduped];
                for (const existing of checkAgainst) {
                  const b = existing.prediction;
                  
                  // Bounding box IoMin overlap >30%
                  const ax1 = a.relX - a.relWidth / 2, ay1 = a.relY - a.relHeight / 2;
                  const ax2 = a.relX + a.relWidth / 2, ay2 = a.relY + a.relHeight / 2;
                  const bx1 = b.relX - b.relWidth / 2, by1 = b.relY - b.relHeight / 2;
                  const bx2 = b.relX + b.relWidth / 2, by2 = b.relY + b.relHeight / 2;
                  const ix1 = Math.max(ax1, bx1), iy1 = Math.max(ay1, by1);
                  const ix2 = Math.min(ax2, bx2), iy2 = Math.min(ay2, by2);
                  if (ix2 > ix1 && iy2 > iy1) {
                    const intersection = (ix2 - ix1) * (iy2 - iy1);
                    const areaA = a.relWidth * a.relHeight;
                    const areaB = b.relWidth * b.relHeight;
                    const minArea = Math.min(areaA, areaB);
                    if (minArea > 0 && (intersection / minArea) > 0.3) {
                      isDup = true;
                      break;
                    }
                  }
                  
                  // Center proximity check (lighting + switches only, not receptacles/tele-data)
                  if (!isReceptacleComponent && !isTeleDataComponent) {
                    const maxDim = Math.max(a.relWidth, a.relHeight, b.relWidth, b.relHeight);
                    const threshold = maxDim * 0.7;
                    const dx = Math.abs(a.relX - b.relX);
                    const dy = Math.abs(a.relY - b.relY);
                    if (dx < threshold && dy < threshold) {
                      isDup = true;
                      break;
                    }
                  }
                }
                if (!isDup) {
                  deduped.push(det);
                }
              }
            } else {
              deduped.push(...batchDetections);
            }

            // Add deduped detections to the accumulated pool
            allAccumulatedDetections.push(...deduped);
            
            if (deduped.length > 0) {
              console.log(`✅ Post-dedup: ${deduped.length} detections added to state (accumulated total: ${allAccumulatedDetections.length})`, deduped.map(d => d.id.substring(0, 40)));
            }

            if (deduped.length > 0) {
              setMultiComponentDetections(prev => prev.map((d, i) => 
                i === componentIndex ? { ...d, detections: [...d.detections, ...deduped] } : d
              ));
              setAllComponentSelections(prev => {
                const newMap = new Map(prev);
                const compKey = component.symbolType === 'Lighting' ? (component.symbol || component.name) : component.name;
                const currentSet = new Set(newMap.get(compKey) || []);
                deduped.forEach(d => currentSet.add(d.id));
                newMap.set(compKey, currentSet);
                return newMap;
              });
            }
            
            // Increment completed batches (one per batch, any page)
            setCountAllComponentProgress(prev => prev.map((p, i) => 
              i === componentIndex ? { ...p, completedBatches: (p.completedBatches || 0) + 1 } : p
            ));
            await new Promise(resolve => setTimeout(resolve, 0));
          }
          
          // Free page canvas memory
          pageCanvas.width = 0;
          pageCanvas.height = 0;
        } catch (pageError) {
          console.error(`❌ Page ${pageNum} failed for "${component.name}":`, pageError);
          // Still increment completed batches for this page so progress bar advances
          const errorBatchesPerPage = isLightingComponent ? 20 : 5;
          setCountAllComponentProgress(prev => prev.map((p, idx) => 
            idx === componentIndex ? { ...p, completedBatches: (p.completedBatches || 0) + errorBatchesPerPage } : p
          ));
        }
      })
    );
    
    // Mark this component as complete
    setCountAllComponentProgress(prev => prev.map((p, i) => 
      i === componentIndex ? { ...p, status: 'complete' as const } : p
    ));
    finalizeComponentCount();
    
    console.log(`✅ Finished counting "${component.name}"`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDocument, numPages, canvasRef, file, lightingGroupComponents, teleDataGroupComponents, electricalKey]);

  // Smart page selection: auto-select pages that match the component type based on pageIndex labels
  const getSmartPageSelection = useCallback((
    component: { symbolType?: string; componentType?: string; name?: string }
  ): Set<number> => {
    const allPages = new Set(Array.from({ length: numPages || 0 }, (_, i) => i + 1));
    
    // If no pageIndex data, fall back to selecting all pages
    if (!pageIndex || pageIndex.length === 0) return allPages;

    // Determine which page labels are relevant for this component
    const nameLower = (component.name || '').toLowerCase();
    const symbolType = component.symbolType || '';
    const componentType = (component as any).componentType || '';
    
    let relevantLabels: string[] = [];

    if (symbolType === 'Lighting' || componentType === 'Switches' || (nameLower.includes('switch') && !nameLower.includes('receptacle'))) {
      relevantLabels = ['lighting', 'reflected ceiling plan', 'power'];
    } else if (componentType === 'Power' || nameLower.includes('receptacle')) {
      relevantLabels = ['power'];
    } else if (nameLower.includes('fire alarm') || nameLower.includes('fire') || componentType === 'Fire Alarm') {
      relevantLabels = ['fire alarm'];
    } else if (nameLower.includes('low voltage') || nameLower.includes('telecom') || nameLower.includes('data') || componentType === 'Low Voltage' || componentType === 'Telecom') {
      relevantLabels = ['low voltage', 'telecom', 'power'];
    } else if (nameLower.includes('panel') || componentType === 'Panel Schedules') {
      relevantLabels = ['panel schedules'];
    } else if (nameLower.includes('demo') || componentType === 'Demolition') {
      relevantLabels = ['demolition'];
    } else if (nameLower.includes('junction') || nameLower.includes('jbox') || nameLower.includes('j box') || (nameLower.includes('j') && nameLower.includes('box'))) {
      relevantLabels = ['power', 'lighting', 'reflected ceiling plan'];
    }

    // If we couldn't determine a type, select all pages
    if (relevantLabels.length === 0) return allPages;

    // Filter pages by matching labels
    const matchedPages = new Set(
      pageIndex
        .filter(p => relevantLabels.some(label => p.label.toLowerCase().includes(label)))
        .map(p => p.page)
    );

    // If no pages matched (e.g. pageIndex labels don't cover this type), fall back to all
    if (matchedPages.size === 0) return allPages;

    console.log(`🎯 Smart page selection for "${component.name}": ${matchedPages.size} pages matching [${relevantLabels.join(', ')}]`);
    return matchedPages;
  }, [numPages, pageIndex]);

  // Shared entry point: given a list of components, open page selection for the first one
  // and queue the rest for sequential counting via the same review flow.
  const startCountingComponents = useCallback((
    components: Array<{ color: string; name: string; symbol: string; referenceImage: string; symbolType?: string; componentType?: string; subscript?: string; description?: string }>,
    options?: { parallel?: boolean }
  ) => {
    if (!pdfDocument || !numPages || components.length === 0) return;
    const isParallelMode = options?.parallel === true;
    setIsParallelCountSession(isParallelMode);

    // Fresh counting session — clear classifications from any previous run
    setDetectionClassifications(new Map());

    // Separate receptacle components from non-receptacles
    const isReceptacleComp = (c: { name: string; componentType?: string }) =>
      c.name.toLowerCase().includes('receptacle') ||
      c.componentType === 'Receptacles' ||
      c.componentType === 'Power';
    const receptacleComps = components.filter(isReceptacleComp);
    const nonReceptacleComps = components.filter(c => !isReceptacleComp(c));

    let mergedComponents = [...components];

    if (receptacleComps.length >= 1) {
      const groupedReceptacle = {
        color: POWER_COLOR,
        name: '__RECEPTACLES_GROUP__',
        symbol: 'RECEPTACLES',
        referenceImage: receptacleComps[0].referenceImage,
        symbolType: undefined as string | undefined,
        componentType: 'Receptacles' as string | undefined,
        subscript: undefined as string | undefined,
        description: undefined as string | undefined,
      };
      const firstReceptacleIdx = components.findIndex(isReceptacleComp);
      mergedComponents = [...components];
      mergedComponents = mergedComponents.filter(c => !isReceptacleComp(c));
      mergedComponents.splice(firstReceptacleIdx, 0, groupedReceptacle);
      // Use only the standard class entries for review modal tabs/filters
      const standardReceptacleComps = receptacleComps.filter((c: any) => c.isStandardClass);
      setReceptacleGroupComponents(standardReceptacleComps.length > 0 ? standardReceptacleComps : receptacleComps);
      console.log(`🔌 Grouped ${receptacleComps.length} receptacle types into unified RECEPTACLES pass (${standardReceptacleComps.length} standard classes for review tabs)`);
    } else {
      setReceptacleGroupComponents([]);
    }

    // Group tele/data components into a single pass (detect once, assign to top type)
    const isTeleDataComp = (c: { name: string; componentType?: string }) =>
      c.name.toLowerCase().includes('tel') ||
      c.name.toLowerCase().includes('data') ||
      c.name.toLowerCase().includes('communication') ||
      c.componentType === 'Telecom' ||
      c.componentType === 'Low Voltage';
    const teleDataComps = mergedComponents.filter(isTeleDataComp);
    if (teleDataComps.length >= 1) {
      const groupedTeleData = {
        color: POWER_COLOR,
        name: '__TELE_DATA_GROUP__',
        symbol: 'TELE/DATA',
        referenceImage: teleDataComps[0].referenceImage,
        symbolType: undefined as string | undefined,
        componentType: 'Telecom' as string | undefined,
        subscript: undefined as string | undefined,
        description: undefined as string | undefined,
      };
      const firstTeleDataIdx = mergedComponents.findIndex(isTeleDataComp);
      mergedComponents = mergedComponents.filter(c => !isTeleDataComp(c));
      mergedComponents.splice(firstTeleDataIdx, 0, groupedTeleData);
      setTeleDataGroupComponents(teleDataComps);
      console.log(`📡 Grouped ${teleDataComps.length} tele/data types into unified TELE/DATA pass`);
    } else {
      setTeleDataGroupComponents([]);
    }

    // Group lighting components into a single pass (OCR tiles once, match all codes)
    const isLightingComp = (c: { symbolType?: string }) => c.symbolType === 'Lighting';
    const lightingComps = mergedComponents.filter(isLightingComp);
    if (lightingComps.length >= 1) {
      const groupedLighting = {
        color: LIGHTING_FIXTURE_COLOR,
        name: '__LIGHTING_GROUP__',
        symbol: '', // No longer hardcoding 'LIGHTING' as a symbol
        referenceImage: lightingComps[0].referenceImage,
        symbolType: 'Lighting' as string | undefined,
        componentType: 'Lighting' as string | undefined,
        subscript: undefined as string | undefined,
        description: undefined as string | undefined,
      };
      const firstLightingIdx = mergedComponents.findIndex(isLightingComp);
      mergedComponents = mergedComponents.filter(c => !isLightingComp(c));
      mergedComponents.splice(firstLightingIdx, 0, groupedLighting);
      setLightingGroupComponents(lightingComps);
      console.log(`💡 Grouped ${lightingComps.length} lighting types into unified LIGHTING pass`);
    } else {
      setLightingGroupComponents([]);
    }

    // Store remaining components for sequential processing (skip first)
    setPendingKeyComponents(mergedComponents.slice(1).map(c => ({
      color: c.color, name: c.name, symbol: c.symbol, referenceImage: c.referenceImage, symbolType: c.symbolType, componentType: c.componentType, subscript: c.subscript, description: c.description
    })));

    console.log(`🚀 Opening page selection for first symbol: "${mergedComponents[0].name}" (${mergedComponents.length - 1} more after)`);

    // Open page selection modal; smart-select pages matching component type
    setSelectedPagesForCount(getSmartPageSelection(mergedComponents[0]));
    setPageSelectionContext({
      component: mergedComponents[0],
      componentIndex: 0,
      newComponents: mergedComponents,
      parallelMode: isParallelMode
    });
    setPageThumbnailsForSelection(null);
    setShowPageSelectionModal(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDocument, numPages, getSmartPageSelection]);

  // Keyword-based type classification (mirrors handler.js)
  const classifyToTypeClass = useCallback((name: string): string => {
    const n = name.toLowerCase().replace(/[_\-]/g, ' ');
    if (n.includes('gfci') || n.includes('gfi') || n.includes('ground fault')) return 'gfci_receptacle';
    if (n.includes('square')) return 'duplex_receptacle';
    if (n.includes('quadruplex') || n.includes('quad') || n.includes('double duplex') || n.includes('fourplex')) return 'quadruplex_receptacle';
    if (n.includes('halfswitch') || n.includes('half switch') || n.includes('switched half')) return 'halfswitch_specific_circuit_receptacle';
    if (n.includes('dedicated') || n.includes('individual circuit') || n.includes('specified circuit') || n.includes('isolated') || n.includes('specific circuit') || n.includes('special circuit') || n.includes('special') || n.includes('emergency')) return 'dedicated_receptacle';
    return 'duplex_receptacle';
  }, []);

  // Keyword-based mounting detection
  const classifyToMounting = useCallback((name: string): string => {
    const n = name.toLowerCase().replace(/[_\-]/g, ' ');
    if (n.includes('ceiling')) return 'ceiling_mounted';
    if (n.includes('floor')) return 'floor_mounted';
    return 'wall_mounted';
  }, []);

  const RECEPTACLE_TYPES = ['duplex_receptacle', 'gfci_receptacle', 'dedicated_receptacle', 'halfswitch_specific_circuit_receptacle', 'quadruplex_receptacle'] as const;
  const MOUNTINGS = ['wall_mounted', 'ceiling_mounted', 'floor_mounted'] as const;

  // 16 combined standard classes: 5 types × 3 mountings + 1 furniture
  // Generate all standard receptacle entries — always adds all of them
  const generateStandardReceptacles = useCallback((components: Array<{ name: string; componentType?: string; referenceImage?: string; color?: string; isStandardClass?: boolean }>) => {
    // Grab the first ref image from any extracted receptacle (used for all standards)
    let defaultImage = '';
    for (const comp of components) {
      if (comp.componentType !== 'Receptacles' || (comp as any).isStandardClass) continue;
      if (comp.referenceImage) {
        defaultImage = comp.referenceImage;
        break;
      }
    }

    const standardComponents: Array<any> = [];
    for (const [combinedKey, standardName] of Object.entries(STANDARD_RECEPTACLE_CLASSES)) {
      standardComponents.push({
        color: POWER_COLOR,
        name: standardName,
        symbol: standardName,
        componentType: 'Receptacles',
        description: `Auto-generated standard class for ${combinedKey.replace(/__/g, ' + ').replace(/_/g, ' ')}`,
        subscript: '',
        pages: [] as any[],
        totalCount: 0,
        referenceImage: defaultImage,
        hasReferenceImage: !!defaultImage,
        isStandardClass: true,
      });
    }
    return standardComponents;
  }, []);

  // Handle adding extracted key components to the project key AND start counting (ONE AT A TIME)
  const handleAddToKeyAndCount = useCallback(async () => {
    if (extractedKeyPreview.length === 0) return;
    
    console.log('🔑 Adding extracted components to key and starting sequential count...');
    
    const componentsWithImages = extractedKeyPreview.filter((s, idx) => selectedKeyPreviewIndices.has(idx) && s.croppedImage);
    
    if (componentsWithImages.length === 0) {
      alert('No symbols with images to add. Try selecting the legend area again.');
      return;
    }
    
    const existingComponents = electricalKey?.components || [];
    const existingNames = existingComponents.map((c: any) => c.name);
    
    const newComponents = componentsWithImages.map((symbol) => {
      const uniqueName = getUniqueComponentName(symbol.name, existingNames);
      existingNames.push(uniqueName);
      return {
        color: COMPONENT_TYPE_COLORS[symbol.componentType] || POWER_COLOR,
        name: uniqueName,
        symbol: symbol.abbreviation || symbol.name,
        componentType: symbol.componentType || 'Other',
        description: symbol.description || '',
        subscript: symbol.subscript || '',
        pages: [] as any[],
        totalCount: 0,
        referenceImage: symbol.croppedImage as string,
        hasReferenceImage: true,
      };
    });

    // Generate standard receptacle classes from the new receptacle components
    const allReceptacleComponents = [...existingComponents, ...newComponents];
    const standardReceptacles = generateStandardReceptacles(allReceptacleComponents);
    
    // Filter out any pre-existing standard classes to avoid duplicates, 
    // and also filter out legacy standard classes that should be removed.
    const legacyNames = new Set(LEGACY_STANDARD_RECEPTACLE_NAMES);
    const filteredExisting = existingComponents.filter((c: any) => 
      !(c.isStandardClass && legacyNames.has(c.name))
    );
    
    const existingStandardNames = new Set(filteredExisting.filter((c: any) => c.isStandardClass).map((c: any) => c.name));
    const newStandards = standardReceptacles.filter(s => !existingStandardNames.has(s.name));
    if (newStandards.length > 0) {
      console.log(`⚡ Generated ${newStandards.length} standard receptacle classes: ${newStandards.map(s => s.name).join(', ')}`);
    }
    
    const mergedComponents = [...filteredExisting, ...newComponents, ...newStandards];
    
    const projectKey = {
      components: mergedComponents,
      extractedAt: new Date().toISOString(),
      pdfHash: (electricalKey as any)?.pdfHash || '',
    };
    
    console.log(`📦 Added ${newComponents.length} new components (total: ${mergedComponents.length})`);
    
    setShowKeyExtractionPreview(false);
    setExtractedKeyPreview([]);
    setSelectedKeyPreviewIndices(new Set());
    setKeyExtractionSnapshot(null);
    setKeyExtractionJobs([]);
    
    if (onKeyExtracted) {
      onKeyExtracted(projectKey);
    }
    
    if (!pdfDocument || !numPages) {
      console.warn('PDF not ready for counting');
      return;
    }
    
    startCountingComponents(newComponents.map(c => ({
      color: c.color, name: c.name, symbol: c.symbol, referenceImage: c.referenceImage, componentType: c.componentType, subscript: c.subscript, description: c.description
    })));
  }, [extractedKeyPreview, selectedKeyPreviewIndices, onKeyExtracted, pdfDocument, numPages, electricalKey, startCountingComponents]);

  // Handle adding extracted key components to the project key WITHOUT counting
  const handleAddToKeyOnly = useCallback(async () => {
    if (extractedKeyPreview.length === 0) return;
    
    console.log('🔑 Adding extracted components to key (no counting)...');
    
    const componentsWithImages = extractedKeyPreview.filter((s, idx) => selectedKeyPreviewIndices.has(idx) && s.croppedImage);
    
    if (componentsWithImages.length === 0) {
      alert('No symbols with images to add. Try selecting the legend area again.');
      return;
    }
    
    const existingComponents = electricalKey?.components || [];
    const existingNames = existingComponents.map((c: any) => c.name);
    
    const newComponents = componentsWithImages.map((symbol) => {
      const uniqueName = getUniqueComponentName(symbol.name, existingNames);
      existingNames.push(uniqueName);
      return {
        color: COMPONENT_TYPE_COLORS[symbol.componentType] || POWER_COLOR,
        name: uniqueName,
        symbol: symbol.abbreviation || symbol.name,
        componentType: symbol.componentType || 'Other',
        description: symbol.description || '',
        subscript: symbol.subscript || '',
        pages: [] as any[],
        totalCount: 0,
        referenceImage: symbol.croppedImage as string,
        hasReferenceImage: true,
      };
    });

    // Generate standard receptacle classes
    const allReceptacleComponents = [...existingComponents, ...newComponents];
    const standardReceptacles = generateStandardReceptacles(allReceptacleComponents);
    const existingStandardNames = new Set(existingComponents.filter((c: any) => c.isStandardClass).map((c: any) => c.name));
    const newStandards = standardReceptacles.filter(s => !existingStandardNames.has(s.name));
    if (newStandards.length > 0) {
      console.log(`⚡ Generated ${newStandards.length} standard receptacle classes: ${newStandards.map(s => s.name).join(', ')}`);
    }
    
    const mergedComponents = [...existingComponents, ...newComponents, ...newStandards];
    
    const projectKey = {
      components: mergedComponents,
      extractedAt: new Date().toISOString(),
      pdfHash: (electricalKey as any)?.pdfHash || '',
    };
    
    console.log(`📦 Added ${newComponents.length} new components to key only (total: ${mergedComponents.length})`);
    
    setShowKeyExtractionPreview(false);
    setExtractedKeyPreview([]);
    setSelectedKeyPreviewIndices(new Set());
    setKeyExtractionSnapshot(null);
    setKeyExtractionJobs([]);
    
    if (onKeyExtracted) {
      onKeyExtracted(projectKey);
    }
  }, [extractedKeyPreview, selectedKeyPreviewIndices, onKeyExtracted, electricalKey]);

  // Handle adding extracted lighting fixtures to project key AND start counting
  const handleAddLightingFixturesToKeyAndCount = useCallback(() => {
    const selectedFixtures = extractedLightingFixtures.filter((_, idx) => selectedLightingFixtureIndices.has(idx));
    if (selectedFixtures.length === 0) return;
    
    console.log('💡 Adding extracted lighting fixtures to key and starting count...');
    
    const newComponents = selectedFixtures.map((fixture) => ({
      color: LIGHTING_FIXTURE_COLOR,
      name: fixture.name,
      symbol: fixture.code,
      symbolType: 'Lighting',
      componentType: 'Lighting',
      description: fixture.description,
      pages: [] as any[],
      totalCount: 0,
      referenceImage: '',
      hasReferenceImage: false,
    }));
    
    const existingComponents = electricalKey?.components || [];
    const existingSymbols = new Set(existingComponents.map((c: any) => (c.symbol || '').toLowerCase()));
    const uniqueNew = newComponents.filter((c: any) => !existingSymbols.has((c.symbol || '').toLowerCase()));
    const mergedComponents = [...existingComponents, ...uniqueNew];
    
    const projectKey = {
      components: mergedComponents,
      extractedAt: new Date().toISOString(),
      pdfHash: (electricalKey as any)?.pdfHash || '',
    };
    
    console.log(`📦 Added ${uniqueNew.length} lighting fixtures to key (${newComponents.length - uniqueNew.length} duplicates skipped, total: ${mergedComponents.length})`);
    
    setShowLightingSchedulePreview(false);
    setExtractedLightingFixtures([]);
    setSelectedLightingFixtureIndices(new Set());
    setLightingScheduleSnapshot(null);
    
    if (onKeyExtracted) {
      onKeyExtracted(projectKey);
    }
    
    if (!pdfDocument || !numPages) {
      console.warn('PDF not ready for counting');
      return;
    }
    
    startCountingComponents(uniqueNew.map(c => ({
      color: c.color, name: c.name, symbol: c.symbol, referenceImage: c.referenceImage, symbolType: c.symbolType, componentType: c.componentType, description: c.description
    })));
  }, [extractedLightingFixtures, selectedLightingFixtureIndices, onKeyExtracted, electricalKey, pdfDocument, numPages, startCountingComponents]);

  // Called when user confirms page selection modal - starts counting on selected pages
  const handleConfirmPageSelection = useCallback(async () => {
    const ctx = pageSelectionContext;
    if (!ctx || selectedPagesForCount.size === 0) return;
    const pagesArray = Array.from(selectedPagesForCount).sort((a, b) => a - b);
    const { component, componentIndex, newComponents, parallelMode } = ctx;
    const isParallelMode = !!parallelMode;
    const isLightingComp = component.symbolType === 'Lighting' || component.name === '__LIGHTING_GROUP__';
    const componentNameLower = (component.name || '').toLowerCase();
    const isReceptacleComp = componentNameLower.includes('receptacle') || componentNameLower.includes('duplex') || (component as any).componentType === 'Receptacles' || component.name === '__RECEPTACLES_GROUP__';
    const isSwitchComp = (component as any).componentType === 'Switches' || (componentNameLower.includes('switch') && !isReceptacleComp);
    const isTeleDataComp = component.name.toLowerCase().includes('tel') || component.name.toLowerCase().includes('data') || component.name.toLowerCase().includes('communication') || (component as any).componentType === 'Telecom' || (component as any).componentType === 'Low Voltage';
    const isOcrBasedComp = isLightingComp || isSwitchComp || isReceptacleComp || isTeleDataComp;
    const displayName = component.name === '__RECEPTACLES_GROUP__' ? 'RECEPTACLES' : component.name === '__TELE_DATA_GROUP__' ? 'TELE/DATA OUTLETS' : component.name === '__LIGHTING_GROUP__' ? 'LIGHTING' : component.name;
    // High-res (lighting/switches/receptacles): 10x10 grid (100 tiles) + staggered pass (~100 tiles) = ~200 tiles / 10 per batch = 20 batches/page
    // Regular: 5x5 grid (25 tiles) / 5 per batch = 5 batches/page
    const batchesPerPage = isOcrBasedComp ? 20 : 5;
    const totalBatchesForSelection = pagesArray.length * batchesPerPage;

    setShowPageSelectionModal(false);
    setPageSelectionContext(null);
    setPageThumbnailsForSelection(null);

    // NOTE: Do NOT clear detectionClassifications here — previous components'
    // classifications (e.g. receptacle reclassifications) must survive across
    // sequential component counting. Each component's detections have unique IDs
    // so there's no collision risk.

    if (newComponents) {
      // First component: set up progress, detections, review modal, then count
      const getComponentKey = (c: any) => {
        if (c.name === '__RECEPTACLES_GROUP__') return '__RECEPTACLES_GROUP__';
        if (c.name === '__LIGHTING_GROUP__') return '__LIGHTING_GROUP__';
        if (c.symbolType === 'Lighting') return c.symbol || c.name;
        return c.name;
      };
      const getDisplayName = (c: any) => {
        if (c.name === '__RECEPTACLES_GROUP__') return 'RECEPTACLES';
        if (c.name === '__LIGHTING_GROUP__') return 'LIGHTING';
        return c.name;
      };
      const getTotalBatchesForComponent = (c: any) => {
        const n = (c.name || '').toLowerCase();
        const isLighting = c.symbolType === 'Lighting' || c.name === '__LIGHTING_GROUP__';
        const isSwitch = (c.componentType === 'Switches') || (n.includes('switch') && !n.includes('receptacle'));
        const isReceptacle = n.includes('receptacle') || n.includes('duplex') || c.componentType === 'Receptacles' || c.name === '__RECEPTACLES_GROUP__';
        const isTeleData = c.name === '__TELE_DATA_GROUP__' || n.includes('tel') || n.includes('data') || n.includes('communication') || c.componentType === 'Telecom' || c.componentType === 'Low Voltage';
        const isOcrBased = isLighting || isSwitch || isReceptacle || isTeleData;
        return pagesArray.length * (isOcrBased ? 20 : 5);
      };
      const initialProgress = newComponents.map((c, idx) => ({
        name: getDisplayName(c),
        color: c.color,
        referenceImage: c.referenceImage,
        status: isParallelMode ? ('processing' as const) : (idx === 0 ? 'processing' as const : 'pending' as const),
        count: 0,
        completedBatches: 0,
        totalBatches: getTotalBatchesForComponent(c),
        currentPage: 0,
        currentBatch: 0
      }));
      const allComponentResults = newComponents.map((c) => ({
        componentName: getComponentKey(c),
        componentColor: c.color,
        referenceImage: c.referenceImage,
        detections: [] as Array<{ id: string; thumbnail: string; prediction: any }>,
        isLighting: c.name === '__LIGHTING_GROUP__' || c.symbolType === 'Lighting'
      }));
      const initialSelections = new Map<string, Set<string>>(
        newComponents.map((c) => [getComponentKey(c), new Set<string>()])
      );
      setCountAllComponentProgress(initialProgress);
      setMultiComponentDetections(allComponentResults);
      setAllComponentSelections(initialSelections);
      setCurrentReviewComponentIndex(0);
      setReceptacleClassFilter('all');

      const isLightingOnly = newComponents.every((c: any) =>
        c.symbolType === 'Lighting' || c.name === '__LIGHTING_GROUP__'
      );

      if (isLightingOnly) {
        setShowLightingCountingModal(true);
        setLightingProgress({
          totalPages: pagesArray.length,
          pageProgress: new Map(),
          overallPct: 0,
          detectionsFound: 0,
          verifiedCount: 0,
          totalCandidates: 0,
          message: `Starting lighting count across ${pagesArray.length} pages...`
        });
      } else {
        setShowMultiComponentReviewModal(true);
      }

      setIsCountingAll(true);
      setCountAllProgress(
        isParallelMode
          ? `Counting ${newComponents.length} component groups across ${pagesArray.length} pages...`
          : `Counting "${displayName}" across ${pagesArray.length} pages...`
      );
      if (isParallelMode) {
        setPendingKeyComponents([]);
      }
    } else {
      // Next component: pop pending, update progress for this component, then count
      setPendingKeyComponents(prev => prev.slice(1));
      setCountAllComponentProgress(prev => prev.map((p, i) =>
        i === componentIndex ? { ...p, status: 'processing' as const, totalBatches: totalBatchesForSelection } : p
      ));
      setIsCountingAll(true);
      setCountAllProgress(`Counting "${displayName}" across ${pagesArray.length} pages...`);
    }

    await new Promise(resolve => setTimeout(resolve, 50));
    if (newComponents && isParallelMode) {
      void (async () => {
        try {
          await Promise.all(
            newComponents.map((comp, idx) =>
              countSingleComponent(comp, idx, pagesArray, { keepCountingState: true })
            )
          );
        } catch (error) {
          console.error('❌ Error in parallel power counting flow:', error);
        } finally {
          setIsCountingAll(false);
          setCountAllProgress('');
        }
      })();
      return;
    }

    try {
      await countSingleComponent(component, componentIndex, pagesArray);
    } catch (error) {
      console.error(`❌ Error counting "${displayName}":`, error);
      setIsCountingAll(false);
      setCountAllProgress('');
    }
  }, [pageSelectionContext, selectedPagesForCount, countSingleComponent]);

  // Helper function to crop symbol images from the snapshot using bounding boxes
  const cropSymbolImages = async (
    snapshotBase64: string, 
    symbols: Array<{ name: string; abbreviation: string; description: string; componentType: string; subscript: string; box_2d: number[] | null; rowIndex: number }>
  ): Promise<Array<{ name: string; abbreviation: string; description: string; componentType: string; subscript: string; box_2d: number[] | null; croppedImage: string | null; rowIndex: number }>> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const imgWidth = img.width;
        const imgHeight = img.height;
        
        console.log(`📐 Snapshot dimensions: ${imgWidth}x${imgHeight}`);
        
        const symbolsWithImages = symbols.map((symbol) => {
          if (!symbol.box_2d || symbol.box_2d.length !== 4) {
            console.log(`⚠️ No bounding box for: ${symbol.name}`);
            return { ...symbol, croppedImage: null };
          }
          
          // Convert normalized coordinates (0-1000) to pixel coordinates
          const [ymin, xmin, ymax, xmax] = symbol.box_2d;
          const x = Math.round((xmin / 1000) * imgWidth);
          const y = Math.round((ymin / 1000) * imgHeight);
          const width = Math.round(((xmax - xmin) / 1000) * imgWidth);
          const height = Math.round(((ymax - ymin) / 1000) * imgHeight);
          
          // Validate dimensions
          if (width <= 0 || height <= 0 || x < 0 || y < 0 || x + width > imgWidth || y + height > imgHeight) {
            console.log(`⚠️ Invalid bounding box for: ${symbol.name}`, { x, y, width, height });
            return { ...symbol, croppedImage: null };
          }
          
          // Create canvas for cropped image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          if (ctx) {
            ctx.drawImage(img, x, y, width, height, 0, 0, width, height);
            const croppedImage = canvas.toDataURL('image/png');
            console.log(`✅ Cropped image for: ${symbol.name} (${width}x${height})`);
            return { ...symbol, croppedImage };
          }
          
          return { ...symbol, croppedImage: null };
        });
        
        const withImages = symbolsWithImages.filter(s => s.croppedImage).length;
        console.log(`📦 Cropped ${withImages}/${symbols.length} symbol images`);
        
        resolve(symbolsWithImages);
      };
      
      img.onerror = () => {
        console.error('❌ Failed to load snapshot for cropping');
        resolve(symbols.map(s => ({ ...s, croppedImage: null })));
      };
      
      img.src = snapshotBase64;
    });
  };

  // Ref to track if we should auto-trigger from Quick Count
  const quickCountTriggerRef = useRef(false);
  
  // State to force redraw after annotations load
  const [forceRedraw, setForceRedraw] = useState(0);
  
  // Handle quick autocount from saved reference image
  const handleQuickAutocountFromReference = useCallback((
    componentName: string,
    componentColor: string,
    referenceImage: string,
    isLighting?: boolean
  ) => {
    console.log('⚡ Quick autocount:', componentName);
    
    // Close the modal
    setShowNewComponentModal(false);
    setQuickCountSearch(''); // Reset search
    
    // Set flag to auto-trigger
    quickCountTriggerRef.current = true;
    
    // Set up component and reference
    setPendingAutocountComponent({
      name: componentName,
      color: componentColor,
      isLighting: isLighting ?? false
    });
    setReferenceAreaImage(referenceImage);
  }, []);

  // Handle confirm autocount and run the detection (STREAMING VERSION)
  // Handle confirm autocount - uses shared counting flow
  const handleConfirmAddToKey = useCallback(async () => {
    if (!referenceAreaImage || !newComponentName.trim() || !electricalKey) return;
    
    setIsAddingComponent(true);
    try {
      // Assign next available color
      const COLOR_PALETTE = [
        '#FF0000', '#0080FF', '#00FF00', '#FFD700', '#FF00FF',
        '#00FFFF', '#FF6600', '#FF1493', '#7FFF00', '#FF69B4'
      ];
      const usedColors = (electricalKey.components || []).map((c: any) => c.color);
      const availableColor = COLOR_PALETTE.find(c => !usedColors.includes(c)) ||
                            COLOR_PALETTE[(electricalKey.components || []).length % COLOR_PALETTE.length];

      const componentToAdd = {
        name: newComponentName.toUpperCase().trim(),
        symbol: '',
        description: '',
        color: newComponentColor || availableColor,
        referenceImage: referenceAreaImage,
        symbolType: 'Power',
        pages: [],
        totalCount: 0
      };

      const updatedKey = {
        ...electricalKey,
        components: [...(electricalKey.components || []), componentToAdd]
      };

      // Save to Supabase
      const { error } = await supabase
        .from('estimations')
        .update({ project_key: updatedKey })
        .eq('id', estimationId);

      if (error) throw error;

      // Sync to library if user is logged in
      try {
        if (user) {
          await componentLibraryService.syncComponentToLibrary(user.id, {
            user_id: user.id,
            component_name: componentToAdd.name,
            symbol: componentToAdd.symbol,
            color: componentToAdd.color
          });
        }
      } catch (libErr) {
        console.warn('Failed to sync to library:', libErr);
      }

      console.log('✅ Component added to key with reference image:', componentToAdd.name);
      
      // Notify parent/sidebar to refresh without triggering bulk extraction UI
      // or any expensive counting logic.
      if (onAnnotationSaved) {
        // We pass an empty array to signal that no annotations were changed,
        // but the sidebar should still refresh its key data.
        await onAnnotationSaved([]);
      }

      // Close modal and reset
      setShowNewComponentModal(false);
      setNewComponentName('');
      setReferenceAreaImage(null);
      setIsAddingNewOnly(false);
      setIsAddingNewOnlyScanMode(false);
      setAutocountMode(null);
      setAutocountSelection(null);
      setPendingAutocountComponent(null);
      setQueuedComponents([]);
      setEditingQueuedName('');
      setShowAutocountConfirmModal(false);
      setShowAutocountReviewModal(false);
      setIsAutocounting(false);
      setAutocountProgress('');
      
    } catch (err) {
      console.error('Error adding component to key:', err);
      alert('Failed to add component to key. Please try again.');
    } finally {
      setIsAddingComponent(false);
    }
  }, [referenceAreaImage, newComponentName, newComponentColor, electricalKey, estimationId, onAnnotationSaved, user]);

  const handleConfirmAutocount = useCallback(() => {
    if (!referenceAreaImage || !pendingAutocountComponent || !pdfDocument || !numPages) return;
    
    console.log('🎯 Starting autocount via shared flow:', pendingAutocountComponent.name);
    
    // Close the confirm modal and area selection mode
    setShowAutocountConfirmModal(false);
    setAutocountMode(null);
    
    // Use the shared counting flow with the reference image
    startCountingComponents([{
      color: pendingAutocountComponent.color,
      name: pendingAutocountComponent.name,
      symbol: pendingAutocountComponent.name,
      referenceImage: referenceAreaImage
    }]);
  }, [referenceAreaImage, pendingAutocountComponent, pdfDocument, numPages, startCountingComponents]);

  // Auto-trigger handleConfirmAutocount when Quick Count sets state
  useEffect(() => {
    // Only trigger if both are set AND this is from Quick Count (not from manual area selection)
    if (referenceAreaImage && pendingAutocountComponent && !autocountMode && !showAutocountConfirmModal && quickCountTriggerRef.current) {
      console.log('🚀 Auto-triggering autocount from Quick Count');
      quickCountTriggerRef.current = false;
      
      // Small delay to ensure all state is settled
      setTimeout(() => {
        handleConfirmAutocount();
      }, 100);
    }
  }, [referenceAreaImage, pendingAutocountComponent, autocountMode, showAutocountConfirmModal, handleConfirmAutocount]);

  // Helper function to slice canvas into tiles
  // NOTE: Count All handlers will be added after this function is defined
  const sliceCanvasIntoTiles = useCallback(async (canvas: HTMLCanvasElement, gridSize = 5) => {
    const tileWidth = Math.floor(canvas.width / gridSize);
    const tileHeight = Math.floor(canvas.height / gridSize);
    const tiles = [];
    
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const tileCanvas = document.createElement('canvas');
        const left = col * tileWidth;
        const top = row * tileHeight;
        
        // Handle last row/column
        tileCanvas.width = (col === gridSize - 1) ? canvas.width - left : tileWidth;
        tileCanvas.height = (row === gridSize - 1) ? canvas.height - top : tileHeight;
        
        const ctx = tileCanvas.getContext('2d');
        if (!ctx) continue;
        
        // Draw tile from main canvas
        ctx.drawImage(
          canvas,
          left, top, tileCanvas.width, tileCanvas.height,
          0, 0, tileCanvas.width, tileCanvas.height
        );
        
        // Convert to base64 PNG
        const base64 = tileCanvas.toDataURL('image/png');
        
        tiles.push({
          base64,
          xOffset: left,
          yOffset: top,
          width: tileCanvas.width,
          height: tileCanvas.height,
          row,
          col,
          index: row * gridSize + col
        });
      }
    }
    
    return tiles;
  }, []);

  // NMS (Non-Maximum Suppression) - Remove duplicate detections
  const nonMaximumSuppression = useCallback((predictions: any[], iouThreshold = 0.5) => {
    if (predictions.length === 0) return [];
    
    // Sort by confidence (descending)
    const sorted = [...predictions].sort((a, b) => b.confidence - a.confidence);
    const keep = [];
    
    while (sorted.length > 0) {
      const current = sorted.shift()!;
      keep.push(current);
      
      // Remove overlapping boxes
      const remaining = [];
      for (const pred of sorted) {
        const iou = calculateIoU(current, pred);
        if (iou < iouThreshold) {
          remaining.push(pred);
        }
      }
      sorted.length = 0;
      sorted.push(...remaining);
    }
    
    return keep;
  }, []);

  // Calculate IoU (Intersection over Union)
  const calculateIoU = useCallback((box1: any, box2: any) => {
    const box1_x1 = box1.x - box1.width / 2;
    const box1_y1 = box1.y - box1.height / 2;
    const box1_x2 = box1.x + box1.width / 2;
    const box1_y2 = box1.y + box1.height / 2;
    
    const box2_x1 = box2.x - box2.width / 2;
    const box2_y1 = box2.y - box2.height / 2;
    const box2_x2 = box2.x + box2.width / 2;
    const box2_y2 = box2.y + box2.height / 2;
    
    const x1 = Math.max(box1_x1, box2_x1);
    const y1 = Math.max(box1_y1, box2_y1);
    const x2 = Math.min(box1_x2, box2_x2);
    const y2 = Math.min(box1_y2, box2_y2);
    
    const intersectionArea = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
    const box1Area = box1.width * box1.height;
    const box2Area = box2.width * box2.height;
    const unionArea = box1Area + box2Area - intersectionArea;
    
    return intersectionArea / unionArea;
  }, []);

  // Handle batch autocount for selected components - uses shared counting flow
  const handleBatchAutocount = useCallback(() => {
    if (!electricalKey?.components || !pdfDocument || !numPages) return;
    
    // Filter to only selected components (with reference images OR lighting fixtures)
    // Use symbol as key for lighting, name for regular components
    const selectedComponents = electricalKey.components.filter((c: any) => {
      const key = c.symbolType === 'Lighting' ? (c.symbol || c.name) : c.name;
      return selectedComponentsForBatch.has(key) && (c.referenceImage || c.symbolType === 'Lighting' || c.componentType === 'Switches');
    });
    
    if (selectedComponents.length === 0) {
      console.warn('No components selected');
      return;
    }
    
    console.log(`🚀 Starting batch count for ${selectedComponents.length} selected components`);
    
    // Close the modal and clear selections
    setShowNewComponentModal(false);
    setQuickCountSearch('');
    setSelectedComponentsForBatch(new Set());

    // Use the shared counting flow - pass symbolType for lighting detection, componentType/subscript/description for switches
    startCountingComponents(selectedComponents.map((c: any) => ({
      color: c.color, name: c.name, symbol: c.symbol || c.name, referenceImage: c.referenceImage || '', symbolType: c.symbolType, componentType: c.componentType, subscript: c.subscript, description: c.description
    })));
  }, [electricalKey, pdfDocument, numPages, selectedComponentsForBatch, startCountingComponents]);

  // Navigate to next component in review (show page selection, then start counting on confirm)
  const handleMultiComponentNext = useCallback(() => {
    const currentProgress = countAllComponentProgress[currentReviewComponentIndex];
    if (currentProgress?.status === 'processing') {
      return;
    }
    const nextIndex = currentReviewComponentIndex + 1;
    setCurrentReviewComponentIndex(nextIndex);
    setBulkSelectedDetections(new Set());
    setBulkReclassTarget('');
    
    if (!isParallelCountSession && pendingKeyComponents.length > 0 && numPages) {
      const nextComponent = pendingKeyComponents[0];
      console.log(`➡️ Opening page selection for next symbol: "${nextComponent.name}"`);
      setSelectedPagesForCount(getSmartPageSelection(nextComponent));
      setPageSelectionContext({ component: nextComponent, componentIndex: nextIndex });
      setPageThumbnailsForSelection(null);
      setShowPageSelectionModal(true);
    }
  }, [currentReviewComponentIndex, pendingKeyComponents, numPages, getSmartPageSelection, isParallelCountSession, countAllComponentProgress]);

  // Navigate to previous component in review
  const handleMultiComponentBack = useCallback(() => {
    setCurrentReviewComponentIndex(prev => prev - 1);
    setBulkSelectedDetections(new Set());
    setBulkReclassTarget('');
  }, []);

  // Toggle detection selection in multi-component modal
  const toggleMultiDetectionSelection = useCallback((componentName: string, detectionId: string) => {
    setAllComponentSelections(prev => {
      const newSelections = new Map(prev);
      const componentSelections = new Set(newSelections.get(componentName) || new Set<string>());
      
      if (componentSelections.has(detectionId)) {
        componentSelections.delete(detectionId);
      } else {
        componentSelections.add(detectionId);
      }
      
      newSelections.set(componentName, componentSelections);
      return newSelections;
    });
  }, []);

  const sanitizeDatasetName = useCallback((rawName: string): string => {
    const normalized = (rawName || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
    return normalized || 'unknown_symbol';
  }, []);

  const dataUrlToBytes = useCallback((dataUrl: string): Uint8Array | null => {
    if (!dataUrl || !dataUrl.includes(',')) return null;
    try {
      const base64Data = dataUrl.split(',')[1];
      if (!base64Data) return null;
      const binary = atob(base64Data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes;
    } catch (error) {
      console.warn('Failed to decode thumbnail data URL:', error);
      return null;
    }
  }, []);

  const resolveMultiDetectionDatasetClass = useCallback((componentData: any, det: any): string => {
    const classOverride = detectionClassifications.get(det.id);
    const classifiedName = det.prediction?.classifiedName;

    if (componentData.componentName === '__RECEPTACLES_GROUP__') {
      return classOverride || classifiedName || receptacleGroupComponents[0]?.name || 'unknown_symbol';
    }
    if (componentData.componentName === '__TELE_DATA_GROUP__') {
      return classOverride || classifiedName || teleDataGroupComponents[0]?.name || 'unknown_symbol';
    }
    if (componentData.componentName === '__LIGHTING_GROUP__') {
      return classOverride || classifiedName || 'lighting_unknown';
    }
    return classOverride || classifiedName || componentData.componentName || 'unknown_symbol';
  }, [detectionClassifications, receptacleGroupComponents, teleDataGroupComponents]);

  const handleExportClassificationDataset = useCallback(async (mode: 'single' | 'multi') => {
    if (isExportingDataset) return;
    setIsExportingDataset(true);

    type DatasetItem = {
      folder: string;
      fileName: string;
      bytes: Uint8Array;
    };

    try {
      const datasetItems: DatasetItem[] = [];
      const NOT_SYMBOL_FOLDER = 'not_symbol';

      if (mode === 'single') {
        for (let idx = 0; idx < autocountDetections.length; idx++) {
          const det = autocountDetections[idx];
          const bytes = dataUrlToBytes(det.thumbnail);
          if (!bytes) continue;

          const isSelected = selectedDetectionsToKeep.has(det.id);
          const resolvedClassName =
            detectionClassifications.get(det.id) ||
            det.prediction?.classifiedName ||
            pendingAutocountComponent?.name ||
            'unknown_symbol';
          const folder = isSelected ? sanitizeDatasetName(resolvedClassName) : NOT_SYMBOL_FOLDER;
          const detPage = det.prediction?.page || pageNumber;
          const safeId = sanitizeDatasetName(det.id).slice(0, 40);
          const fileName = `${sanitizeDatasetName(pendingAutocountComponent?.name || 'component')}_p${detPage}_${String(idx + 1).padStart(4, '0')}_${safeId || 'det'}.png`;

          datasetItems.push({ folder, fileName, bytes });
        }
      } else {
        for (const componentData of multiComponentDetections) {
          const selections = allComponentSelections.get(componentData.componentName) || new Set<string>();
          const componentLabel =
            componentData.componentName === '__RECEPTACLES_GROUP__'
              ? 'receptacles'
              : componentData.componentName === '__TELE_DATA_GROUP__'
                ? 'tele_data'
                : componentData.componentName === '__LIGHTING_GROUP__'
                  ? 'lighting'
                  : componentData.componentName;
          const safeComponentLabel = sanitizeDatasetName(componentLabel);

          for (let idx = 0; idx < componentData.detections.length; idx++) {
            const det = componentData.detections[idx];
            const bytes = dataUrlToBytes(det.thumbnail);
            if (!bytes) continue;

            const isSelected = selections.has(det.id);
            const resolvedClassName = resolveMultiDetectionDatasetClass(componentData, det);
            const folder = isSelected ? sanitizeDatasetName(resolvedClassName) : NOT_SYMBOL_FOLDER;
            const detPage = det.prediction?.page || pageNumber;
            const safeId = sanitizeDatasetName(det.id).slice(0, 40);
            const fileName = `${safeComponentLabel}_p${detPage}_${String(idx + 1).padStart(4, '0')}_${safeId || 'det'}.png`;

            datasetItems.push({ folder, fileName, bytes });
          }
        }
      }

      if (datasetItems.length === 0) {
        window.alert('No detection thumbnails available to export.');
        return;
      }

      const zip = new JSZip();
      datasetItems.forEach(item => {
        zip.file(`dataset/${item.folder}/${item.fileName}`, item.bytes);
      });

      const readmeLines = [
        'Classification dataset export',
        `Generated: ${new Date().toISOString()}`,
        '',
        '- Selected detections are sorted into their resolved class folders.',
        '- Deselected detections are sorted into "not_symbol".',
        '- Thumbnails include green detection bounding boxes when provided by the counting service.'
      ];
      zip.file('dataset/README.txt', readmeLines.join('\n'));

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `classification_dataset_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      console.log(`📦 Exported classification dataset with ${datasetItems.length} images`);
    } catch (error) {
      console.error('❌ Failed to export classification dataset:', error);
      window.alert('Failed to export classification dataset. Please try again.');
    } finally {
      setIsExportingDataset(false);
    }
  }, [
    isExportingDataset,
    autocountDetections,
    selectedDetectionsToKeep,
    detectionClassifications,
    pendingAutocountComponent,
    pageNumber,
    multiComponentDetections,
    allComponentSelections,
    dataUrlToBytes,
    resolveMultiDetectionDatasetClass,
    sanitizeDatasetName
  ]);

  // Run labor estimation for all components concurrently
  const runConcurrentLaborEstimation = useCallback(async (components: Array<{ name: string; count: number }>) => {
    console.log(`🚀 Starting concurrent labor estimation for ${components.length} components`);
    
    // Initialize progress
    const progressItems = components.map(c => ({
      name: c.name,
      count: c.count,
      status: 'pending' as const
    }));
    
    setLaborEstimationProgress(progressItems);
    setShowLaborEstimationModal(true);
    
    // Update status helper
    const updateStatus = (name: string, status: 'pending' | 'processing' | 'complete' | 'error') => {
      setLaborEstimationProgress(prev => 
        prev.map(item => item.name === name ? { ...item, status } : item)
      );
    };
    
    // Run all estimations concurrently
    const estimationPromises = components.map(async (component) => {
      try {
        updateStatus(component.name, 'processing');
        
        const result = await autoEstimateComponent(
          component.name,
          component.count,
          estimationId,
          (message) => {
            console.log(`[${component.name}] ${message}`);
          }
        );
        
        if (result.success) {
          updateStatus(component.name, 'complete');
        } else {
          updateStatus(component.name, 'error');
        }
        
        return result;
      } catch (error) {
        console.error(`Error estimating ${component.name}:`, error);
        updateStatus(component.name, 'error');
        return { success: false, error: 'Estimation failed' };
      }
    });
    
    const results = await Promise.allSettled(estimationPromises);
    
    console.log('✅ All labor estimations complete');
    
    // Extract successful results
    const successfulResults = results
      .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled' && r.value?.success)
      .map(r => r.value);
    
    // Collect citations from NECA lookups
    const necaCitations = successfulResults
      .filter(r => r.laborData?.page_number)
      .map(r => ({
        source: 'neca_manual',
        pageNumber: r.laborData.page_number,
        section: r.laborData.section || '',
        quote: `${r.laborData.matched_component}: ${r.laborData.measurement}`,
        componentName: r.componentName,
        laborHours: r.laborData.labor_hours
      }));
    
    if (necaCitations.length > 0) {
      console.log(`✅ Collected ${necaCitations.length} NECA citations`);
    }
  }, [estimationId]);

  // Handle finish multi-component review and save all annotations
  const handleFinishMultiComponentReview = useCallback(async () => {
    console.log('💾 Saving all reviewed components');
    console.log('🗺️ detectionClassifications at Finish:', detectionClassifications.size, 'entries');
    console.log('🗺️ detectionClassifications contents:', JSON.stringify(Array.from(detectionClassifications.entries())));
    console.log('🗺️ multiComponentDetections:', multiComponentDetections.map(cd => ({ name: cd.componentName, count: cd.detections.length })));
    console.log('🗺️ receptacleGroupComponents:', receptacleGroupComponents.map(rc => rc.name));
    console.log('🗺️ teleDataGroupComponents:', teleDataGroupComponents.map(tc => tc.name));
    console.log('🗺️ lightingGroupComponents:', lightingGroupComponents.map(lc => lc.name));
    
    // Close review modal immediately
    setIsParallelCountSession(false);
    setShowMultiComponentReviewModal(false);
    
    // Prepare data
    const componentsToEstimate: Array<{ name: string; count: number }> = [];
    
    // Get canvas dimensions once
    const annotationCanvas = canvasRef.current;
    const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    const canvasWidth = pdfCanvas?.width || annotationCanvas?.width || 8000;
    const canvasHeight = pdfCanvas?.height || annotationCanvas?.height || 5714;
    
    // Collect all new annotations first (so we don't have state batching issues)
    const allNewAnnotations: any[] = [];
    const savePromises: Promise<any>[] = [];
    let updatedAnnotationsForSave: Annotation[] | null = null;
    
    // Helper to create save promise for a single component's annotations
    // Accumulate annotations per component to avoid race conditions on concurrent saves
    const pendingSavesByComponent = new Map<string, any[]>();
    const createSavePromise = (componentName: string, newAnnotations: any[]) => {
      // Allow save without file?.id - receptacles/tele/data use file_id=null (global)
      if (!supabase || !estimationId) return null;
      const existing = pendingSavesByComponent.get(componentName) || [];
      pendingSavesByComponent.set(componentName, [...existing, ...newAnnotations]);
      return null; // actual save happens below in flushSaves
    };
    const flushSaves = async () => {
      if (pendingSavesByComponent.size === 0) return;
      console.log('ℹ️ flushSaves legacy DB path disabled; unified v2 save pipeline is authoritative');
    };

    // Prepare annotations and database save promises for each component
    for (const componentData of multiComponentDetections) {
      console.log(`🔍 Processing component: "${componentData.componentName}" (${componentData.detections.length} total detections)`);
      const selections = allComponentSelections.get(componentData.componentName) || new Set();
      const approvedDetections = componentData.detections.filter(det => selections.has(det.id));
      console.log(`  ✅ ${approvedDetections.length} approved out of ${componentData.detections.length}`);
      
      if (approvedDetections.length === 0) {
        console.log(`⏭️ Skipping ${componentData.componentName} - no detections approved`);
        continue;
      }
      
      // Grouped receptacles: split by user-editable classification
      if (componentData.componentName === '__RECEPTACLES_GROUP__' && receptacleGroupComponents.length > 0) {
        console.log('🔌 HIT RECEPTACLES GROUP BRANCH');
        const defaultName = receptacleGroupComponents[0].name;
        const colorByName = new Map(receptacleGroupComponents.map(rc => [rc.name, rc.color]));

        // Group approved detections by their final classification (use state directly, not ref - avoids effect timing issues)
        const byClass = new Map<string, typeof approvedDetections>();
        for (const det of approvedDetections) {
          const className = detectionClassifications.get(det.id) || det.prediction.classifiedName || defaultName;
          console.log(`  🏷️ det ${det.id}: classMap=${detectionClassifications.get(det.id)}, classifiedName=${det.prediction.classifiedName}, final=${className}`);
          if (!byClass.has(className)) byClass.set(className, []);
          byClass.get(className)!.push(det);
        }

        console.log(`✅ Splitting ${approvedDetections.length} grouped receptacles into ${byClass.size} types:`, JSON.stringify(Array.from(byClass.keys())));

        Array.from(byClass.entries()).forEach(([className, dets]) => {
          const baseTimestamp = Date.now() + Math.random() * 1000;
          const compColor = colorByName.get(className) || componentData.componentColor;
          const newAnnotations = dets.map((det: any, idx: number) => {
            // Use relative coordinates directly from the prediction if available.
            // This prevents scaling corruption if the canvas size changed between detection and saving.
            const relStartX = det.prediction.relX !== undefined ? (det.prediction.relX - (det.prediction.relWidth || 0) / 2) : (det.prediction.x - (det.prediction.width || 40) / 2) / canvasWidth;
            const relStartY = det.prediction.relY !== undefined ? (det.prediction.relY - (det.prediction.relHeight || 0) / 2) : (det.prediction.y - (det.prediction.height || 40) / 2) / canvasHeight;
            const relWidth = det.prediction.relWidth !== undefined ? det.prediction.relWidth : (det.prediction.width || 40) / canvasWidth;
            const relHeight = det.prediction.relHeight !== undefined ? det.prediction.relHeight : (det.prediction.height || 40) / canvasHeight;

            const boxWidth = relWidth * canvasWidth;
            const boxHeight = relHeight * canvasHeight;
            const startX = relStartX * canvasWidth;
            const startY = relStartY * canvasHeight;

            return {
              id: `auto-${baseTimestamp}-${idx}-${Math.random().toString(36).substring(2, 9)}`,
              type: 'rectangle' as const,
              component_type: className,
              page: det.prediction.page || pageNumber,
              number: idx + 1,
              startX, startY,
              width: boxWidth, height: boxHeight,
              color: compColor,
              thickness: 2,
              confidence: det.prediction.confidence,
              auto_detected: true,
              timestamp: new Date().toISOString(),
              relativeStartX: relStartX,
              relativeStartY: relStartY,
              relativeWidth: relWidth,
              relativeHeight: relHeight,
              savedCanvasWidth: canvasWidth,
              savedCanvasHeight: canvasHeight
            };
          });

          allNewAnnotations.push(...newAnnotations);
          const sp = createSavePromise(className, newAnnotations);
          if (sp) savePromises.push(sp);
          componentsToEstimate.push({ name: className, count: dets.length });
          console.log(`  📦 ${className}: ${dets.length} detections`);
        });

        // Ensure every known receptacle type has an entry in pendingSavesByComponent,
        // even if all its detections were reclassified away. This forces flushSaves to
        // overwrite (and clear) any stale auto-detected annotations left from a prior run.
        for (const rc of receptacleGroupComponents) {
          if (!pendingSavesByComponent.has(rc.name)) {
            pendingSavesByComponent.set(rc.name, []);
            console.log(`  🧹 ${rc.name}: cleared stale annotations (no detections this run)`);
          }
        }

        continue;
      }

      // Grouped tele/data: split by user-editable classification (defaults to top type)
      if (componentData.componentName === '__TELE_DATA_GROUP__' && teleDataGroupComponents.length > 0) {
        const defaultName = teleDataGroupComponents[0].name;
        const colorByName = new Map(teleDataGroupComponents.map(tc => [tc.name, tc.color]));

        const byClass = new Map<string, typeof approvedDetections>();
        for (const det of approvedDetections) {
          const className = detectionClassifications.get(det.id) || det.prediction.classifiedName || defaultName;
          if (!byClass.has(className)) byClass.set(className, []);
          byClass.get(className)!.push(det);
        }

        console.log(`✅ Splitting ${approvedDetections.length} grouped tele/data into ${byClass.size} types`);

        Array.from(byClass.entries()).forEach(([className, dets]) => {
          const baseTimestamp = Date.now() + Math.random() * 1000;
          const compColor = colorByName.get(className) || componentData.componentColor;
          const newAnnotations = dets.map((det: any, idx: number) => {
            // Use relative coordinates directly from the prediction if available.
            // This prevents scaling corruption if the canvas size changed between detection and saving.
            const relStartX = det.prediction.relX !== undefined ? (det.prediction.relX - (det.prediction.relWidth || 0) / 2) : (det.prediction.x - (det.prediction.width || 40) / 2) / canvasWidth;
            const relStartY = det.prediction.relY !== undefined ? (det.prediction.relY - (det.prediction.relHeight || 0) / 2) : (det.prediction.y - (det.prediction.height || 40) / 2) / canvasHeight;
            const relWidth = det.prediction.relWidth !== undefined ? det.prediction.relWidth : (det.prediction.width || 40) / canvasWidth;
            const relHeight = det.prediction.relHeight !== undefined ? det.prediction.relHeight : (det.prediction.height || 40) / canvasHeight;

            const boxWidth = relWidth * canvasWidth;
            const boxHeight = relHeight * canvasHeight;
            const startX = relStartX * canvasWidth;
            const startY = relStartY * canvasHeight;

            return {
              id: `auto-${baseTimestamp}-${idx}-${Math.random().toString(36).substring(2, 9)}`,
              type: 'rectangle' as const,
              component_type: className,
              page: det.prediction.page || pageNumber,
              number: idx + 1,
              startX, startY,
              width: boxWidth, height: boxHeight,
              color: compColor,
              thickness: 2,
              confidence: det.prediction.confidence,
              auto_detected: true,
              timestamp: new Date().toISOString(),
              relativeStartX: relStartX,
              relativeStartY: relStartY,
              relativeWidth: relWidth,
              relativeHeight: relHeight,
              savedCanvasWidth: canvasWidth,
              savedCanvasHeight: canvasHeight
            };
          });

          allNewAnnotations.push(...newAnnotations);
          const sp = createSavePromise(className, newAnnotations);
          if (sp) savePromises.push(sp);
          componentsToEstimate.push({ name: className, count: dets.length });
          console.log(`  📦 ${className}: ${dets.length} detections`);
        });

        continue;
      }

      // Grouped lighting: split by fixture code classification
      if (componentData.componentName === '__LIGHTING_GROUP__' && lightingGroupComponents.length > 0) {
        const colorBySymbol = new Map(lightingGroupComponents.map(lc => [lc.symbol.toUpperCase(), lc.color]));
        const nameBySymbol = new Map(lightingGroupComponents.map(lc => [lc.symbol.toUpperCase(), lc.name]));

        const byCode = new Map<string, typeof approvedDetections>();
        for (const det of approvedDetections) {
          const code = detectionClassifications.get(det.id) || det.prediction.classifiedName || '';
          if (!byCode.has(code)) byCode.set(code, []);
          byCode.get(code)!.push(det);
        }

        console.log(`✅ Splitting ${approvedDetections.length} grouped lighting into ${byCode.size} types`);

        Array.from(byCode.entries()).forEach(([code, dets]) => {
          const baseTimestamp = Date.now() + Math.random() * 1000;
          const compName = nameBySymbol.get(code) || code;
          const compColor = colorBySymbol.get(code) || componentData.componentColor;
          // Use symbol code as component_type to match manual placement (which uses symbol for lighting)
          const componentType = code;
          const newAnnotations = dets.map((det: any, idx: number) => {
            // Use relative coordinates directly from the prediction if available.
            const relStartX = det.prediction.relX !== undefined ? (det.prediction.relX - (det.prediction.relWidth || 0) / 2) : (det.prediction.x - (det.prediction.width || 40) / 2) / canvasWidth;
            const relStartY = det.prediction.relY !== undefined ? (det.prediction.relY - (det.prediction.relHeight || 0) / 2) : (det.prediction.y - (det.prediction.height || 40) / 2) / canvasHeight;
            const relWidth = det.prediction.relWidth !== undefined ? det.prediction.relWidth : (det.prediction.width || 40) / canvasWidth;
            const relHeight = det.prediction.relHeight !== undefined ? det.prediction.relHeight : (det.prediction.height || 40) / canvasHeight;

            const boxWidth = relWidth * canvasWidth;
            const boxHeight = relHeight * canvasHeight;
            const startX = relStartX * canvasWidth;
            const startY = relStartY * canvasHeight;

            return {
              id: `auto-${baseTimestamp}-${idx}-${Math.random().toString(36).substring(2, 9)}`,
              type: 'rectangle' as const,
              component_type: componentType,
              page: det.prediction.page || pageNumber,
              number: idx + 1,
              startX, startY,
              width: boxWidth, height: boxHeight,
              color: compColor,
              thickness: 2,
              confidence: det.prediction.confidence,
              auto_detected: true,
              timestamp: new Date().toISOString(),
              relativeStartX: relStartX,
              relativeStartY: relStartY,
              relativeWidth: relWidth,
              relativeHeight: relHeight,
              savedCanvasWidth: canvasWidth,
              savedCanvasHeight: canvasHeight
            };
          });

          allNewAnnotations.push(...newAnnotations);
          createSavePromise(componentType, newAnnotations);
          componentsToEstimate.push({ name: componentType, count: dets.length });
          console.log(`  💡 ${code} (${compName}): ${dets.length} detections`);
        });

        continue;
      }
      
      console.log(`⚠️ GENERIC PATH for "${componentData.componentName}" (${approvedDetections.length} detections) — NOT receptacles/tele/lighting group`);
      
      // Safety net: if any detections have been reclassified, split by classification
      const hasReclassifications = approvedDetections.some(det => detectionClassifications.has(det.id));
      console.log(`  🔀 hasReclassifications: ${hasReclassifications}`);
      if (hasReclassifications) {
        const byClass = new Map<string, typeof approvedDetections>();
        for (const det of approvedDetections) {
          const className = detectionClassifications.get(det.id) || det.prediction.classifiedName || componentData.componentName;
          if (!byClass.has(className)) byClass.set(className, []);
          byClass.get(className)!.push(det);
        }
        console.log(`🔀 Splitting ${approvedDetections.length} ${componentData.componentName} into ${byClass.size} types (fallback classification)`);
        Array.from(byClass.entries()).forEach(([className, dets]) => {
          const baseTimestamp = Date.now() + Math.random() * 1000;
          const newAnnotations = dets.map((det: any, idx: number) => {
            // Use relative coordinates directly from the prediction if available.
            const relStartX = det.prediction.relX !== undefined ? (det.prediction.relX - (det.prediction.relWidth || 0) / 2) : (det.prediction.x - (det.prediction.width || 40) / 2) / canvasWidth;
            const relStartY = det.prediction.relY !== undefined ? (det.prediction.relY - (det.prediction.relHeight || 0) / 2) : (det.prediction.y - (det.prediction.height || 40) / 2) / canvasHeight;
            const relWidth = det.prediction.relWidth !== undefined ? det.prediction.relWidth : (det.prediction.width || 40) / canvasWidth;
            const relHeight = det.prediction.relHeight !== undefined ? det.prediction.relHeight : (det.prediction.height || 40) / canvasHeight;

            const boxWidth = relWidth * canvasWidth;
            const boxHeight = relHeight * canvasHeight;
            const startX = relStartX * canvasWidth;
            const startY = relStartY * canvasHeight;

            return {
              id: `auto-${baseTimestamp}-${idx}-${Math.random().toString(36).substring(2, 9)}`,
              type: 'rectangle' as const,
              component_type: className,
              page: det.prediction.page || pageNumber,
              number: idx + 1,
              startX, startY,
              width: boxWidth, height: boxHeight,
              color: componentData.componentColor,
              thickness: 2,
              confidence: det.prediction.confidence,
              auto_detected: true,
              timestamp: new Date().toISOString(),
              relativeStartX: relStartX,
              relativeStartY: relStartY,
              relativeWidth: relWidth,
              relativeHeight: relHeight,
              savedCanvasWidth: canvasWidth,
              savedCanvasHeight: canvasHeight
            };
          });
          allNewAnnotations.push(...newAnnotations);
          const sp = createSavePromise(className, newAnnotations);
          if (sp) savePromises.push(sp);
          componentsToEstimate.push({ name: className, count: dets.length });
          console.log(`  📦 ${className}: ${dets.length} detections (fallback)`);
        });
      } else {
        const baseTimestamp = Date.now() + Math.random() * 1000;
        const newAnnotations = approvedDetections.map((det, idx: number) => {
          // Use relative coordinates directly from the prediction if available.
          const relStartX = det.prediction.relX !== undefined ? (det.prediction.relX - (det.prediction.relWidth || 0) / 2) : (det.prediction.x - (det.prediction.width || 40) / 2) / canvasWidth;
          const relStartY = det.prediction.relY !== undefined ? (det.prediction.relY - (det.prediction.relHeight || 0) / 2) : (det.prediction.y - (det.prediction.height || 40) / 2) / canvasHeight;
          const relWidth = det.prediction.relWidth !== undefined ? det.prediction.relWidth : (det.prediction.width || 40) / canvasWidth;
          const relHeight = det.prediction.relHeight !== undefined ? det.prediction.relHeight : (det.prediction.height || 40) / canvasHeight;

          const boxWidth = relWidth * canvasWidth;
          const boxHeight = relHeight * canvasHeight;
          const startX = relStartX * canvasWidth;
          const startY = relStartY * canvasHeight;
          
          return {
            id: `auto-${baseTimestamp}-${idx}-${Math.random().toString(36).substring(2, 9)}`,
            type: 'rectangle' as const,
            component_type: componentData.componentName,
            page: det.prediction.page || pageNumber,
            number: idx + 1,
            startX, startY,
            width: boxWidth, height: boxHeight,
            color: componentData.componentColor,
            thickness: 2,
            confidence: det.prediction.confidence,
            auto_detected: true,
            timestamp: new Date().toISOString(),
            relativeStartX: relStartX,
            relativeStartY: relStartY,
            relativeWidth: relWidth,
            relativeHeight: relHeight,
            savedCanvasWidth: canvasWidth,
            savedCanvasHeight: canvasHeight
          };
        });
        
        allNewAnnotations.push(...newAnnotations);
        
        const sp = createSavePromise(componentData.componentName, newAnnotations);
        if (sp) savePromises.push(sp);
        
        componentsToEstimate.push({
          name: componentData.componentName,
          count: approvedDetections.length
        });
      }
      
      // Save reference image to key (also async, non-blocking)
      if (componentData.referenceImage && estimationId) {
        const refSavePromise = (async () => {
          try {
            const { data: estimation } = await supabase
              .from('estimations')
              .select('project_key')
              .eq('id', estimationId)
              .single();
            
            if (estimation?.project_key) {
              const key = estimation.project_key;
              const component = key.components?.find((c: any) => 
                c.name.toLowerCase() === componentData.componentName.toLowerCase()
              );
              
              if (component && !component.referenceImage) {
                component.referenceImage = componentData.referenceImage;
                
                await supabase
                  .from('estimations')
                  .update({ project_key: key })
                  .eq('id', estimationId);
              }
            }
          } catch (error) {
            console.error(`Failed to save reference image for ${componentData.componentName}:`, error);
          }
        })();
        
        savePromises.push(refSavePromise);
      }
    }
    
    // Update annotations in UI immediately (don't wait for database)
    if (allNewAnnotations.length > 0) {
      const updatedAnnotations = [...annotations, ...allNewAnnotations];
      updatedAnnotationsForSave = updatedAnnotations;
      setAnnotations(updatedAnnotations);
      addToHistory(updatedAnnotations);
      // Force persistence through the unified v2 save pipeline.
      lastSaveHashRef.current = '';
      requestSaveNow(updatedAnnotations);
      console.log(`✅ Added ${allNewAnnotations.length} total annotations from ${componentsToEstimate.length} components`);
    }
    
    // Clean up state
    setMultiComponentDetections([]);
    setAllComponentSelections(new Map());
    setCurrentReviewComponentIndex(0);
    setPendingKeyComponents([]);
    setReceptacleGroupComponents([]);
    setLightingGroupComponents([]);
    setDetectionClassifications(new Map());
    setBulkSelectedDetections(new Set());
    setBulkReclassTarget('');
    
    // Show labor estimation modal IMMEDIATELY
    if (componentsToEstimate.length > 0 && estimationId) {
      // Start labor estimation right away (non-blocking)
      runConcurrentLaborEstimation(componentsToEstimate);
    }
    
    // Save reference-image side effects, then refresh key.
    if (savePromises.length > 0 || updatedAnnotationsForSave) {
      Promise.all(savePromises).then(async () => {
        console.log('✅ Side effects completed after multi-component finish');
        
        // Refetch the key from database and notify parent to refresh
        if (estimationId && onKeyExtracted) {
          try {
            const { data } = await supabase
              .from('estimations')
              .select('project_key')
              .eq('id', estimationId)
              .single();
            
            if (data?.project_key) {
              console.log('🔄 Refreshing key with', data.project_key.components?.length, 'components');
              onKeyExtracted(data.project_key);
            }
          } catch (error) {
            console.error('Failed to refresh key:', error);
          }
        }
        
      }).catch((error) => {
        console.error('❌ Error in multi-component finish side effects:', error);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiComponentDetections, allComponentSelections, pageNumber, annotations, supabase, estimationId, file, canvasRef, onAnnotationSaved, onKeyExtracted, receptacleGroupComponents, teleDataGroupComponents, lightingGroupComponents, activeMode, currentDrawingFileId, detectionClassifications]);

  // Show summary when lighting counting finishes (instead of auto-save)
  useEffect(() => {
    if (!showLightingCountingModal || isCountingAll || lightingCountingFinished) {
      return;
    }
    const allDone = countAllComponentProgress.length > 0 &&
      countAllComponentProgress.every(p => p.status === 'complete');
    if (allDone) {
      console.log('💡 Lighting counting complete — showing summary');
      setLightingProgress(prev => ({ ...prev, overallPct: 100, message: 'Counting complete' }));
      setLightingCountingFinished(true);
    }
  }, [showLightingCountingModal, isCountingAll, countAllComponentProgress, lightingCountingFinished]);

  const handleFinishLightingCount = useCallback(async () => {
    setShowLightingCountingModal(false);
    setLightingCountingFinished(false);
    setLightingProgress({ totalPages: 0, pageProgress: new Map(), overallPct: 0, detectionsFound: 0, verifiedCount: 0, totalCandidates: 0, message: '' });
    await handleFinishMultiComponentReview();
  }, [handleFinishMultiComponentReview]);

  const handleCancelLightingCount = useCallback(() => {
    setShowLightingCountingModal(false);
    setLightingCountingFinished(false);
    setLightingProgress({ totalPages: 0, pageProgress: new Map(), overallPct: 0, detectionsFound: 0, verifiedCount: 0, totalCandidates: 0, message: '' });
    // Clear the detections we just found
    setMultiComponentDetections([]);
    setAllComponentSelections(new Map());
    setCountAllComponentProgress([]);
    setIsCountingAll(false);
  }, []);

  // Handle final confirmation after review
  const handleFinalAutocountConfirm = useCallback(async () => {
    if (!pendingAutocountComponent) return;
    
    console.log('💾 Saving reviewed autocount detections (multi-page)');
    
    // Filter to only keep selected detections
    const approvedDetections = autocountDetections.filter(det => 
      selectedDetectionsToKeep.has(det.id)
    );
    
    console.log(`✅ User approved ${approvedDetections.length} of ${autocountDetections.length} detections`);
    
    // Group detections by page for proper canvas dimensions
    const detectionsByPage = approvedDetections.reduce((acc, det) => {
      const page = det.prediction.page || pageNumber; // Fallback to current page if not set
      if (!acc[page]) acc[page] = [];
      acc[page].push(det);
      return acc;
    }, {} as Record<number, typeof approvedDetections>);
    
    console.log(`📄 Detections spread across ${Object.keys(detectionsByPage).length} pages`);
    
    // Get canvas dimensions (use standard dimensions for multi-page)
    const annotationCanvas = canvasRef.current;
    const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    const canvasWidth = pdfCanvas?.width || annotationCanvas?.width || 8000;
    const canvasHeight = pdfCanvas?.height || annotationCanvas?.height || 5714;
    
    // Convert approved detections to annotations
    // CRITICAL: Preserve page numbers from predictions!
    // Convert to rectangles with bounding box data so they can be resized
    const baseTimestamp = Date.now();
    const newAnnotations = approvedDetections.map((det, idx: number) => {
      // Use relative coordinates directly from the prediction if available.
      // This prevents scaling corruption if the canvas size changed between detection and saving.
      const relStartX = det.prediction.relX !== undefined ? (det.prediction.relX - (det.prediction.relWidth || 0) / 2) : (det.prediction.x - (det.prediction.width || 40) / 2) / canvasWidth;
      const relStartY = det.prediction.relY !== undefined ? (det.prediction.relY - (det.prediction.relHeight || 0) / 2) : (det.prediction.y - (det.prediction.height || 40) / 2) / canvasHeight;
      const relWidth = det.prediction.relWidth !== undefined ? det.prediction.relWidth : (det.prediction.width || 40) / canvasWidth;
      const relHeight = det.prediction.relHeight !== undefined ? det.prediction.relHeight : (det.prediction.height || 40) / canvasHeight;

      const boxWidth = relWidth * canvasWidth;
      const boxHeight = relHeight * canvasHeight;
      const startX = relStartX * canvasWidth;
      const startY = relStartY * canvasHeight;
      
      return {
        id: `auto-${baseTimestamp}-${idx}-${Math.random().toString(36).substring(2, 9)}`,
        type: 'rectangle' as const,
        component_type: pendingAutocountComponent.name,
        page: det.prediction.page || pageNumber, // Use page from prediction!
        number: idx + 1,
        startX: startX,
        startY: startY,
        width: boxWidth,
        height: boxHeight,
        color: pendingAutocountComponent.color,
        thickness: 2,
        confidence: det.prediction.confidence,
        auto_detected: true,
        timestamp: new Date().toISOString(),
        relativeStartX: relStartX,
        relativeStartY: relStartY,
        relativeWidth: relWidth,
        relativeHeight: relHeight,
        savedCanvasWidth: canvasWidth,
        savedCanvasHeight: canvasHeight
      };
    });
    
    console.log('📊 Annotations by page:', 
      Object.entries(detectionsByPage).map(([page, dets]) => 
        `Page ${page}: ${dets.length} annotations`
      ).join(', ')
    );
    
    // Add to existing annotations
    const updatedAnnotations = [...annotations, ...newAnnotations];
    setAnnotations(updatedAnnotations);
    addToHistory(updatedAnnotations);
    // Persist via unified save pipeline (v2 RPC-backed).
    lastSaveHashRef.current = '';
    requestSaveNow(updatedAnnotations);
    
    // NOW save reference image to component in key (after annotations are saved)
    console.log('🔑 Attempting to save reference to key:', { 
      hasReferenceImage: !!referenceAreaImage, 
      hasEstimationId: !!estimationId,
      componentName: pendingAutocountComponent.name 
    });
    
    if (referenceAreaImage && estimationId) {
      try {
        console.log('🔑 Fetching current key from database...');
        // Get current key from database
        const { data: estimation, error: fetchError } = await supabase
          .from('estimations')
          .select('project_key')
          .eq('id', estimationId)
          .single();
        
        if (fetchError) {
          console.error('❌ Error fetching estimation:', fetchError);
          return;
        }
        
        console.log('🔑 Current key:', estimation?.project_key);
        
        if (estimation) {
          // Initialize key if it doesn't exist
          const currentKey = estimation.project_key || { components: [], extractedAt: new Date().toISOString() };
          
          // Check if component already exists - case-insensitive match
          const componentIndex = currentKey.components.findIndex(
            (c: any) => c.name.toLowerCase() === pendingAutocountComponent.name.toLowerCase()
          );
          
          const isNewComponent = componentIndex < 0;
          
          if (componentIndex >= 0) {
            // Update existing component with reference image
            currentKey.components[componentIndex].referenceImage = referenceAreaImage;
            console.log(`✅ Updating existing component "${pendingAutocountComponent.name}" with reference image at index`, componentIndex);
          } else {
            // Add new component with reference image - deduplicate name
            const existingNames = currentKey.components.map((c: any) => c.name);
            const uniqueName = getUniqueComponentName(pendingAutocountComponent.name, existingNames);
            currentKey.components.push({
              name: uniqueName,
              color: pendingAutocountComponent.color,
              symbol: pendingAutocountComponent.name,
              referenceImage: referenceAreaImage,
              pages: [],
              totalCount: 0
            });
            console.log(`✅ Adding new component "${pendingAutocountComponent.name}" with reference image. Total components:`, currentKey.components.length);
          }
          
          // Save updated key to database
          console.log('🔑 Saving updated key to database...');
          const { error: updateError } = await supabase
            .from('estimations')
            .update({ project_key: currentKey })
            .eq('id', estimationId);
          
          // NEW: Sync to rolling key library
          if (user?.id && !updateError) {
            if (isNewComponent) {
              // New component - sync everything including reference image
              await componentLibraryService.syncComponentToLibrary(user.id, {
                user_id: user.id,
                component_name: pendingAutocountComponent.name, // Use exact name
                symbol: pendingAutocountComponent.name,
                color: pendingAutocountComponent.color,
                reference_image: referenceAreaImage
              });
              console.log(`✅ Synced new component "${pendingAutocountComponent.name}" with reference image to library`);
            } else {
              // Existing component - just update reference image
              await componentLibraryService.syncReferenceImage(
                user.id,
                pendingAutocountComponent.name, // Use exact name
                referenceAreaImage
              );
              console.log(`✅ Synced reference image for "${pendingAutocountComponent.name}" to library`);
            }
          }
          
          if (updateError) {
            console.error('❌ Error saving key with reference image:', updateError);
          } else {
            console.log('✅ Reference image saved to database successfully');
          }
        } else {
          console.warn('⚠️ No estimation found');
        }
      } catch (error) {
        console.error('❌ Failed to save reference image:', error);
      }
    } else {
      console.warn('⚠️ Skipping key save - missing referenceImage or estimationId');
    }
    
    // Close review modal and clean up FIRST for immediate visual feedback
    setShowAutocountReviewModal(false);
    setAutocountDetections([]);
    setSelectedDetectionsToKeep(new Set());
    setAutocountTotalBatches(0);
    setAutocountCompletedBatches(0);
    
    // Notify parent that annotations were saved to trigger count refresh
    // Use requestAnimationFrame to ensure canvas has redrawn first
    if (onAnnotationSaved) {
      requestAnimationFrame(() => onAnnotationSaved());
    }
    
    // Auto-estimate component and add to estimate table (use modal instead of chat)
    if (approvedDetections.length > 0 && estimationId) {
      // Store component info before cleanup
      const componentToEstimate = {
        name: pendingAutocountComponent.name,
        count: approvedDetections.length
      };
      
      // Clean up before async work
      setPendingAutocountComponent(null);
      setReferenceAreaImage(null);
      setAutocountSelection(null);
      
      // Show labor estimation modal immediately and run estimation
      runConcurrentLaborEstimation([componentToEstimate]);
    } else {
      // No auto-estimation needed, just clean up
      setPendingAutocountComponent(null);
      setReferenceAreaImage(null);
      setAutocountSelection(null);
    }
    
    console.log('✅ Autocount complete! Added to canvas. Counts should refresh.');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autocountDetections, selectedDetectionsToKeep, pendingAutocountComponent, pageNumber, annotations, referenceAreaImage, estimationId, onAnnotationSaved]);

  // Toggle detection selection in review
  const toggleDetectionSelection = useCallback((detectionId: string) => {
    setSelectedDetectionsToKeep(prev => {
      const newSet = new Set(prev);
      if (newSet.has(detectionId)) {
        newSet.delete(detectionId);
      } else {
        newSet.add(detectionId);
      }
      return newSet;
    });
  }, []);

  // Handle start calibration
  const handleStartCalibration = useCallback(() => {
    console.log('🔧 Starting calibration mode');
    setIsCalibrating(true);
    setShowCalibrationDropdown(false);
    console.log('✅ Calibration mode enabled, hand-drawn tools disabled');
  }, []);

  // Handle copy calibration from another page
  const handleCopyCalibration = useCallback(async (fromPageNum: number) => {
    const sourceCal = calibrationData[fromPageNum];
    if (sourceCal) {
      await saveCalibrationToSupabase(
        pageNumber,
        sourceCal.canvasPercentage,
        sourceCal.realWorldValue,
        sourceCal.unit,
        sourceCal.referenceLine
      );
      setShowCalibrationDropdown(false);
    }
  }, [calibrationData, pageNumber, saveCalibrationToSupabase]);

  // Toggle reference line visibility
  const toggleReferenceLine = useCallback(async () => {
    const currentCal = calibrationData[pageNumber];
    if (!currentCal || !estimationId) return;

    const updatedCalibs = {
      ...calibrationData,
      [pageNumber]: {
        ...currentCal,
        showReferenceLine: !currentCal.showReferenceLine
      }
    };

    const { error } = await supabase
      .from('estimations')
      .update({ measurement_calibs: updatedCalibs })
      .eq('id', estimationId);

    if (!error) {
      setCalibrationData(updatedCalibs);
    }
  }, [calibrationData, pageNumber, estimationId]);

  // Helper function to calculate distance from point to line segment
  const pointToLineDistance = (px: number, py: number, x1: number, y1: number, x2: number, y2: number): number => {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    
    if (lenSq !== 0) {
      param = dot / lenSq;
    }
    
    let xx, yy;
    
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }
    
    const dx = px - xx;
    const dy = py - yy;
    
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Check if point is inside annotation (handles both radius and width/height formats)
  const isPointInAnnotation = (annotation: Annotation, x: number, y: number, padding: number = 0): boolean => {
    if (annotation.type === 'hand-square-footage') {
      const sqMinX = Math.min(annotation.startX, annotation.endX || annotation.startX) - padding;
      const sqMaxX = Math.max(annotation.startX, annotation.endX || annotation.startX) + padding;
      const sqMinY = Math.min(annotation.startY, annotation.endY || annotation.startY) - padding;
      const sqMaxY = Math.max(annotation.startY, annotation.endY || annotation.startY) + padding;
      return x >= sqMinX && x <= sqMaxX && y >= sqMinY && y <= sqMaxY;
    }
    if (annotation.type === 'rectangle' || annotation.type === 'circle') {
      // AI-generated circles have radius, manual annotations have width/height
      if (annotation.radius) {
        // AI-generated circle - use radius and center point
        const centerX = annotation.x || annotation.startX;
        const centerY = annotation.y || annotation.startY;
        const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
        return distance <= (annotation.radius + padding);
      } else {
        // Manual annotation - use width/height bounding box
        const minX = Math.min(annotation.startX, annotation.startX + (annotation.width || 0)) - padding;
        const maxX = Math.max(annotation.startX, annotation.startX + (annotation.width || 0)) + padding;
        const minY = Math.min(annotation.startY, annotation.startY + (annotation.height || 0)) - padding;
        const maxY = Math.max(annotation.startY, annotation.startY + (annotation.height || 0)) + padding;
        return x >= minX && x <= maxX && y >= minY && y <= maxY;
      }
    }
    return false;
  };

  // Helper function to find annotation at a point (returns center of annotation)
  // Only considers visible annotations (matching highlightedComponentType if set)
  const findAnnotationAtPoint = useCallback((x: number, y: number): { id: string; centerX: number; centerY: number; isWireRun?: boolean } | null => {
    const canvas = canvasRef.current;
    const cw = canvas?.width || 16000;
    const ch = canvas?.height || 10666;
    const pageAnns = annotations.filter(a => {
      if (a.page !== pageNumber) return false;
      // Filter out hidden annotations
      if (!highlightedComponentType) return true;
      if (a.type === 'piping') {
        const startAnn = annotations.find(ann => ann.id === a.startAnnotationId);
        const endAnn = annotations.find(ann => ann.id === a.endAnnotationId);
        return (startAnn?.component_type === highlightedComponentType) ||
               (endAnn?.component_type === highlightedComponentType);
      }
      return a.component_type === highlightedComponentType;
    }).map(a => rescaleAnnotation(a, cw, ch));
    
    for (const ann of pageAnns) {
      let centerX: number, centerY: number;
      
      // Handle wire runs - check if point is near any segment
      if (ann.type === 'wire-run' && ann.points && ann.points.length >= 2) {
        const hitDistance = 15; // pixels from line to register a hit
        
        for (let i = 0; i < ann.points.length - 1; i++) {
          const p1 = ann.points[i];
          const p2 = ann.points[i + 1];
          
          // Calculate distance from point to line segment
          const dx = p2.x - p1.x;
          const dy = p2.y - p1.y;
          const lengthSquared = dx * dx + dy * dy;
          
          let t = 0;
          if (lengthSquared > 0) {
            t = Math.max(0, Math.min(1, ((x - p1.x) * dx + (y - p1.y) * dy) / lengthSquared));
          }
          
          const nearestX = p1.x + t * dx;
          const nearestY = p1.y + t * dy;
          const distance = Math.sqrt((x - nearestX) ** 2 + (y - nearestY) ** 2);
          
          if (distance <= hitDistance) {
            // Find center of the wire run
            const midIndex = Math.floor(ann.points.length / 2);
            return { 
              id: ann.id, 
              centerX: ann.points[midIndex].x, 
              centerY: ann.points[midIndex].y,
              isWireRun: true
            };
          }
        }
        continue;
      }
      
      // Handle square footage rectangles - use startX/startY/endX/endY bounds
      if (ann.type === 'hand-square-footage') {
        const sqMinX = Math.min(ann.startX, ann.endX || ann.startX);
        const sqMaxX = Math.max(ann.startX, ann.endX || ann.startX);
        const sqMinY = Math.min(ann.startY, ann.endY || ann.startY);
        const sqMaxY = Math.max(ann.startY, ann.endY || ann.startY);
        const sqPad = 10;
        
        if (x >= sqMinX - sqPad && x <= sqMaxX + sqPad && y >= sqMinY - sqPad && y <= sqMaxY + sqPad) {
          return {
            id: ann.id,
            centerX: (sqMinX + sqMaxX) / 2,
            centerY: (sqMinY + sqMaxY) / 2,
          };
        }
        continue;
      }

      if (ann.type === 'circle') {
        if (ann.x !== undefined && ann.y !== undefined) {
          centerX = ann.x;
          centerY = ann.y;
        } else {
          centerX = ann.startX + (ann.width || 0) / 2;
          centerY = ann.startY + (ann.height || 0) / 2;
        }
      } else if (ann.type === 'rectangle') {
        centerX = ann.startX + (ann.width || 0) / 2;
        centerY = ann.startY + (ann.height || 0) / 2;
      } else {
        continue; // Skip non-shape annotations
      }
      
      // Check if point is within annotation bounds (with padding)
      const padding = 30;
      const minX = Math.min(ann.startX, ann.startX + (ann.width || 0)) - padding;
      const maxX = Math.max(ann.startX, ann.startX + (ann.width || 0)) + padding;
      const minY = Math.min(ann.startY, ann.startY + (ann.height || 0)) - padding;
      const maxY = Math.max(ann.startY, ann.startY + (ann.height || 0)) + padding;
      
      if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
        return { id: ann.id, centerX, centerY };
      }
    }
    
    return null;
  }, [annotations, pageNumber, highlightedComponentType]);

  // Helper function to get annotation center
  const getAnnotationCenter = useCallback((annotation: Annotation): { centerX: number; centerY: number } | null => {
    if (annotation.type === 'circle') {
      if (annotation.x !== undefined && annotation.y !== undefined) {
        return { centerX: annotation.x, centerY: annotation.y };
      } else {
        return {
          centerX: annotation.startX + (annotation.width || 0) / 2,
          centerY: annotation.startY + (annotation.height || 0) / 2
        };
      }
    } else if (annotation.type === 'rectangle') {
      return {
        centerX: annotation.startX + (annotation.width || 0) / 2,
        centerY: annotation.startY + (annotation.height || 0) / 2
      };
    }
    return null;
  }, []);

  // Helper function to find PDF element at a point (simplified for SVG bounding boxes)
  const findPDFElementAtPoint = useCallback((x: number, y: number, elements: PDFVectorElement[]): PDFVectorElement | null => {
    const TOLERANCE = 10; // pixels
    
    // Prioritize: 1) xobjects (electrical symbols), 2) text, 3) curves, 4) everything else
    const prioritized = [...elements].sort((a, b) => {
      const priorityA = a.type === 'xobject' ? 0 : a.type === 'text' ? 1 : a.type === 'curve' ? 2 : 3;
      const priorityB = b.type === 'xobject' ? 0 : b.type === 'text' ? 1 : b.type === 'curve' ? 2 : 3;
      return priorityA - priorityB;
    });
    
    // Simple bounding box hit test for all elements
    for (const element of prioritized) {
      const { x: ex, y: ey, width, height } = element.bounds;
      
      if (x >= ex - TOLERANCE && x <= ex + width + TOLERANCE &&
          y >= ey - TOLERANCE && y <= ey + height + TOLERANCE) {
        return element;
      }
    }
    
    return null;
  }, []);

  // Helper function to calculate real-world measurement from a pixel-space pipe
  const calculatePipeMeasurement = useCallback((canvasDistance: number, pxDx?: number, pxDy?: number): { value: number; unit: string } | null => {
    const currentCal = calibrationData[pageNumber];
    if (!currentCal) return null;
    
    const canvas = canvasRef.current;
    if (!canvas) return null;
    
    let normalizedPercent: number;
    if (pxDx !== undefined && pxDy !== undefined) {
      const ndx = pxDx / canvas.width;
      const ndy = pxDy / canvas.height;
      normalizedPercent = Math.sqrt(ndx * ndx + ndy * ndy) * 100;
    } else {
      // Fallback: treat canvasDistance as if horizontal for backward compat
      normalizedPercent = (canvasDistance / canvas.width) * 100;
    }
    
    const realWorldDistance = (normalizedPercent / currentCal.canvasPercentage) * currentCal.realWorldValue;
    
    return {
      value: parseFloat(realWorldDistance.toFixed(2)),
      unit: currentCal.unit
    };
  }, [calibrationData, pageNumber]);

  // Helper function to update pipes connected to moved annotations
  const updateConnectedPipes = useCallback((annotationsList: Annotation[], movedAnnotationIds: Set<string>): Annotation[] => {
    return annotationsList.map(ann => {
      if (ann.type === 'piping') {
        let updated = false;
        let newStartX = ann.startX;
        let newStartY = ann.startY;
        let newEndX = ann.endX;
        let newEndY = ann.endY;
        let newMeasurement = ann.measurement;
        let newMeasurementUnit = ann.measurementUnit;
        
        // Check if start annotation moved
        if (ann.startAnnotationId && movedAnnotationIds.has(ann.startAnnotationId)) {
          const startAnn = annotationsList.find(a => a.id === ann.startAnnotationId);
          if (startAnn) {
            const center = getAnnotationCenter(startAnn);
            if (center) {
              newStartX = center.centerX;
              newStartY = center.centerY;
              updated = true;
            }
          }
        }
        
        // Check if end annotation moved
        if (ann.endAnnotationId && movedAnnotationIds.has(ann.endAnnotationId)) {
          const endAnn = annotationsList.find(a => a.id === ann.endAnnotationId);
          if (endAnn) {
            const center = getAnnotationCenter(endAnn);
            if (center) {
              newEndX = center.centerX;
              newEndY = center.centerY;
              updated = true;
            }
          }
        }
        
        // Recalculate measurement if pipe was updated
        if (updated) {
          const dx = (newEndX || newStartX) - newStartX;
          const dy = (newEndY || newStartY) - newStartY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const measurement = calculatePipeMeasurement(distance, dx, dy);
          
          if (measurement) {
            newMeasurement = measurement.value;
            newMeasurementUnit = measurement.unit;
          }
          
          return {
            ...ann,
            startX: newStartX,
            startY: newStartY,
            endX: newEndX,
            endY: newEndY,
            measurement: newMeasurement,
            measurementUnit: newMeasurementUnit
          };
        }
      }
      return ann;
    });
  }, [getAnnotationCenter, calculatePipeMeasurement]);

  // Load annotations from database
  const loadAnnotations = useCallback(async () => {
    if (!estimationId) return;
    
    // Don't reset loaded flag here - we handle that at page level
    // This prevents breaking the loading state when canvas sync triggers a reload

    // Load from Supabase - multiple records by component_type
    if (supabase) {
      try {
        if (activeMode === 'draw' && !currentDrawingFileId) {
          // Wait for file context before loading scoped annotations.
          console.log('⚠️ Draw mode but no file_id yet - waiting for file to load...');
          return;
        }

        const { data, error } = await loadGroupedAnnotationsFromV2({
          supabase: supabase as any,
          estimationId,
          activeMode,
          currentDrawingFileId: currentDrawingFileId || null,
          pageNumber,
        });

        if (error) {
          console.error('Error loading annotations:', error);
          // Fall back to localStorage if there's an error
          const localData = localStorage.getItem(`annotations_${estimationId}`);
          if (localData) {
            try {
              const localAnnotations = JSON.parse(localData);
              setAnnotations(localAnnotations);
              setHistory([localAnnotations]);
              setHistoryIndex(0);
              setAnnotationsLoaded(true);
              persistedAnnotationSignaturesRef.current = new Map(
                (Array.isArray(localAnnotations) ? localAnnotations : [])
                  .filter((annotation: any) => annotation?.id)
                  .map((annotation: any) => [String(annotation.id), getAnnotationSignature(annotation as Annotation)])
              );
              lastSaveHashRef.current = getAnnotationsHash(Array.isArray(localAnnotations) ? localAnnotations : []);
            } catch (e) {
              console.error('Error parsing local annotations:', e);
            }
          }
          return;
        }

        if (data && data.length > 0) {
          // Debug logging
          console.log(`📥 Loading annotations from ${data.length} database records:`);

          // Track unique IDs to detect duplicates
          const seenIds = new Set<string>();
          const duplicateIds = new Set<string>();

          // Get current canvas dimensions for scaling
          const canvas = canvasRef.current;
          if (!canvas) {
            console.warn('⚠️ Canvas not ready for loading annotations');
            setAnnotationsLoaded(false); // Mark as not loaded so retry can trigger
            return;
          }

          console.log('📐 Current canvas dimensions for loading:', {
            width: canvas.width,
            height: canvas.height
          });

          // Combine all annotations from different component types
          const allAnnotations: Annotation[] = [];

          for (const record of data) {
            if (record.annotations) {
              let annotations = record.annotations;
              // Parse if it's a string
              if (typeof annotations === 'string') {
                try {
                  annotations = JSON.parse(annotations);
                } catch (e) {
                  console.error('Error parsing annotations:', e);
                  continue;
                }
              }

              if (Array.isArray(annotations)) {
                console.log(`  - ${record.component_type}: ${annotations.length} annotations`);

                // Process each annotation and scale if needed
                for (const savedAnn of annotations) {
                  if (seenIds.has(savedAnn.id)) {
                    duplicateIds.add(savedAnn.id);
                    console.warn(`⚠️ Duplicate annotation ID found: ${savedAnn.id}`);
                  } else {
                    seenIds.add(savedAnn.id);
                    
                    let loadedAnnotation: Annotation = { ...savedAnn } as Annotation;
                    
                    // Check if we have relative positions (new format)
                    if (savedAnn.relativeX !== undefined || savedAnn.relativeStartX !== undefined) {
                      // Use relative positions to calculate current absolute positions
                      const newX = savedAnn.relativeX ? savedAnn.relativeX * canvas.width : savedAnn.x;
                      const newY = savedAnn.relativeY ? savedAnn.relativeY * canvas.height : savedAnn.y;
                      const newStartX = savedAnn.relativeStartX ? savedAnn.relativeStartX * canvas.width : savedAnn.startX;
                      const newStartY = savedAnn.relativeStartY ? savedAnn.relativeStartY * canvas.height : savedAnn.startY;
                      
                      console.log('📍 Loading annotation with relative coords:', {
                        id: savedAnn.id,
                        type: savedAnn.type,
                        saved: {
                          relative: {
                            relativeX: savedAnn.relativeX,
                            relativeY: savedAnn.relativeY,
                            relativeStartX: savedAnn.relativeStartX,
                            relativeStartY: savedAnn.relativeStartY
                          },
                          absolute: {
                            x: savedAnn.x,
                            y: savedAnn.y,
                            startX: savedAnn.startX,
                            startY: savedAnn.startY
                          },
                          savedCanvas: {
                            width: savedAnn.savedCanvasWidth,
                            height: savedAnn.savedCanvasHeight
                          }
                        },
                        calculated: {
                          newX,
                          newY,
                          newStartX,
                          newStartY,
                          currentCanvas: {
                            width: canvas.width,
                            height: canvas.height
                          }
                        },
                        comparison: {
                          xDiff: savedAnn.x ? Math.abs(newX - savedAnn.x) : 0,
                          yDiff: savedAnn.y ? Math.abs(newY - savedAnn.y) : 0,
                          wouldBeWrongWithoutScaling: savedAnn.savedCanvasWidth !== canvas.width
                        }
                      });
                      
                      loadedAnnotation = {
                        ...savedAnn,
                        x: newX,
                        y: newY,
                        startX: newStartX,
                        startY: newStartY,
                        width: savedAnn.relativeWidth ? savedAnn.relativeWidth * canvas.width : savedAnn.width,
                        height: savedAnn.relativeHeight ? savedAnn.relativeHeight * canvas.height : savedAnn.height
                      } as Annotation;
                    } else if (savedAnn.savedCanvasWidth) {
                      // Fallback: scale from saved canvas size
                      const scaleX = canvas.width / savedAnn.savedCanvasWidth;
                      const scaleY = canvas.height / savedAnn.savedCanvasHeight;
                      
                      console.log('📏 Scaling annotation from saved canvas size:', {
                        id: savedAnn.id,
                        savedCanvas: {
                          width: savedAnn.savedCanvasWidth,
                          height: savedAnn.savedCanvasHeight
                        },
                        currentCanvas: {
                          width: canvas.width,
                          height: canvas.height
                        },
                        scale: { x: scaleX, y: scaleY }
                      });
                      
                      loadedAnnotation = {
                        ...savedAnn,
                        x: savedAnn.x ? savedAnn.x * scaleX : savedAnn.x,
                        y: savedAnn.y ? savedAnn.y * scaleY : savedAnn.y,
                        startX: savedAnn.startX ? savedAnn.startX * scaleX : savedAnn.startX,
                        startY: savedAnn.startY ? savedAnn.startY * scaleY : savedAnn.startY,
                        width: savedAnn.width ? savedAnn.width * scaleX : savedAnn.width,
                        height: savedAnn.height ? savedAnn.height * scaleY : savedAnn.height
                      } as Annotation;
                    } else {
                      // Old format - assume 16000px canvas and scale if needed
                      if (canvas.width !== 16000) {
                        const assumedScale = canvas.width / 16000;
                        
                        console.log('⚠️ Loading old format annotation, scaling from assumed 16000px:', {
                          id: savedAnn.id,
                          assumedOriginalCanvas: 16000,
                          currentCanvas: canvas.width,
                          scale: assumedScale,
                          before: {
                            x: savedAnn.x,
                            y: savedAnn.y,
                            startX: savedAnn.startX,
                            startY: savedAnn.startY
                          }
                        });
                        
                        loadedAnnotation = {
                          ...savedAnn,
                          x: savedAnn.x ? savedAnn.x * assumedScale : savedAnn.x,
                          y: savedAnn.y ? savedAnn.y * assumedScale : savedAnn.y,
                          startX: savedAnn.startX ? savedAnn.startX * assumedScale : savedAnn.startX,
                          startY: savedAnn.startY ? savedAnn.startY * assumedScale : savedAnn.startY,
                          width: savedAnn.width ? savedAnn.width * assumedScale : savedAnn.width,
                          height: savedAnn.height ? savedAnn.height * assumedScale : savedAnn.height
                        } as Annotation;
                      }
                    }
                    
                    allAnnotations.push(loadedAnnotation);
                  }
                }
              }
            }
          }

          if (duplicateIds.size > 0) {
            console.error(`❌ Found ${duplicateIds.size} duplicate annotation IDs in database!`);
            console.error('Duplicate IDs:', Array.from(duplicateIds));
          }

          // Recalculate pipe positions based on their connected annotations
          const recalculatedAnnotations = allAnnotations.map(ann => {
            if (ann.type === 'piping' && ann.startAnnotationId && ann.endAnnotationId) {
              // Find the connected annotations
              const startAnn = allAnnotations.find(a => a.id === ann.startAnnotationId);
              const endAnn = allAnnotations.find(a => a.id === ann.endAnnotationId);
              
              if (startAnn && endAnn) {
                // Recalculate centers
                const startCenter = getAnnotationCenter(startAnn);
                const endCenter = getAnnotationCenter(endAnn);
                
                if (startCenter && endCenter) {
                  // Calculate new distance and measurement inline
                  // (avoids depending on calculatePipeMeasurement which changes with pageNumber)
                  const dx = endCenter.centerX - startCenter.centerX;
                  const dy = endCenter.centerY - startCenter.centerY;
                  const distance = Math.sqrt(dx * dx + dy * dy);
                  
                  // Inline pipe measurement: read calibration for the pipe's page
                  let measurement: { value: number; unit: string } | null = null;
                  const pipePage = ann.page || 1;
                  const pipeCal = calibrationData[pipePage];
                  if (pipeCal && canvas) {
                    const ndx = dx / canvas.width;
                    const ndy = dy / canvas.height;
                    const normalizedPercent = Math.sqrt(ndx * ndx + ndy * ndy) * 100;
                    const realWorldDistance = (normalizedPercent / pipeCal.canvasPercentage) * pipeCal.realWorldValue;
                    measurement = {
                      value: parseFloat(realWorldDistance.toFixed(2)),
                      unit: pipeCal.unit
                    };
                  }
                  
                  console.log(`📏 Recalculated pipe ${ann.id}:`, {
                    oldStart: { x: ann.startX, y: ann.startY },
                    newStart: { x: startCenter.centerX, y: startCenter.centerY },
                    oldEnd: { x: ann.endX, y: ann.endY },
                    newEnd: { x: endCenter.centerX, y: endCenter.centerY },
                    measurement: measurement ? `${measurement.value}${measurement.unit}` : 'none'
                  });
                  
                  return {
                    ...ann,
                    startX: startCenter.centerX,
                    startY: startCenter.centerY,
                    endX: endCenter.centerX,
                    endY: endCenter.centerY,
                    measurement: measurement?.value,
                    measurementUnit: measurement?.unit
                  };
                }
              }
            }
            return ann;
          });

          setAnnotations(recalculatedAnnotations);
          setHistory([recalculatedAnnotations]);
          setHistoryIndex(0);
          setAnnotationsLoaded(true);
          persistedAnnotationSignaturesRef.current = new Map(
            recalculatedAnnotations
              .filter((annotation) => annotation?.id)
              .map((annotation) => [String(annotation.id), getAnnotationSignature(annotation)])
          );

          // Update the last save hash to prevent immediate re-save
          lastSaveHashRef.current = getAnnotationsHash(recalculatedAnnotations);

          console.log(`✅ Loaded ${recalculatedAnnotations.length} unique annotations from database`);
        } else {
          // No annotations in database - set flag to prevent saveAnnotations from deleting
          lastLoadWasEmptyRef.current = true;
          setAnnotations([]);
          setHistory([[]]);
          setHistoryIndex(0);
          setAnnotationsLoaded(true);
          persistedAnnotationSignaturesRef.current = new Map();
          lastSaveHashRef.current = getAnnotationsHash([]);
        }
      } catch (error) {
        console.error('Could not load from Supabase:', error);
      }
    }
  }, [estimationId, activeMode, currentDrawingFileId, pageNumber, getAnnotationCenter, calibrationData]);
  
  // Load saved annotations from Supabase on mount or when estimationId changes
  useEffect(() => {
    loadAnnotations();
  }, [loadAnnotations]);
  
  // Reload annotations when switching drawing files in draw mode
  const prevDrawingFileIdRef = useRef<string | undefined>(currentDrawingFileId);
  useEffect(() => {
    if (activeMode === 'draw' && currentDrawingFileId && prevDrawingFileIdRef.current !== currentDrawingFileId) {
      console.log('🔄 Drawing file changed from', prevDrawingFileIdRef.current, 'to', currentDrawingFileId);
      console.log('   Clearing current annotations and reloading...');
      
      // Clear current annotations first
      setAnnotations([]);
      setHistory([[]]);
      setHistoryIndex(0);
      setAnnotationsLoaded(false); // Reset flag so retry mechanism works
      
      // Then reload for the new file
      loadAnnotations();
    }
    prevDrawingFileIdRef.current = currentDrawingFileId;
  }, [currentDrawingFileId, activeMode, loadAnnotations]);
  
  // Reset annotations loaded flag when switching to draw mode to trigger reload
  const prevActiveModeRef = useRef(activeMode);
  useEffect(() => {
    if (activeMode === 'draw' && prevActiveModeRef.current !== 'draw') {
      console.log('🔄 Switched to draw mode - resetting annotations loaded flag');
      setAnnotationsLoaded(false);
    }
    prevActiveModeRef.current = activeMode;
  }, [activeMode]);

  // Retry loading annotations when canvas becomes ready after tab switch
  // This fixes the issue where annotations fail to load because canvas isn't ready yet
  useEffect(() => {
    // Debug: Log the state of all conditions
    if (activeMode === 'draw' && !annotationsLoaded && !isTransitioning) {
      console.log('🔍 Retry conditions:', {
        activeMode,
        hasCanvas: !!canvasRef.current,
        currentDrawingFileId,
        isPDFRendered,
        isTransitioning,
        annotationsLoaded
      });
    }
    
    if (
      activeMode === 'draw' && 
      !isTransitioning &&
      canvasRef.current && 
      currentDrawingFileId && 
      isPDFRendered && 
      !annotationsLoaded
    ) {
      console.log('🔄 Canvas now ready after tab switch - retrying annotation load');
      loadAnnotations().then(() => {
        // Trigger a redraw by incrementing forceRedraw state
        setTimeout(() => {
          console.log('🎨 Triggering redraw after annotation load');
          setForceRedraw(prev => prev + 1);
        }, 50);
      });
    }
  }, [activeMode, isTransitioning, canvasRef.current, currentDrawingFileId, isPDFRendered, annotationsLoaded, loadAnnotations]);
  
  // Removed: Electrical key reload useEffect
  // The key only affects annotation colors, not positions or data
  // Annotations automatically redraw with new colors because drawAnnotations depends on electricalKey
  // Reloading from database was causing race conditions where freshly saved annotations
  // would be deleted if the reload happened before the save transaction committed

  // Track if we've already prompted for auto-annotation on each page
  const promptedPagesRef = useRef<Set<number>>(new Set());
  
  // Expose method to mark page as prompted (for when user dismisses)
  useEffect(() => {
    if (ref && typeof ref === 'object' && 'current' in ref && ref.current) {
      ref.current.markPageAsPrompted = (page: number) => {
        promptedPagesRef.current.add(page);
        console.log(`Marked page ${page} as prompted (won't ask again)`);
      };
    }
  }, [ref]);
  
  // DISABLED: Auto-annotation feature - commented out for now
  // Check for auto-annotation when page changes
  // useEffect(() => {
  //   const checkAndTriggerAutoAnnotation = async () => {
  //     if (!estimationId || !pageNumber || !onAutoAnnotationTrigger || !supabase) return;
  //     
  //     // Wait for annotations to be loaded from database before checking
  //     if (!annotationsLoaded) {
  //       console.log('⏳ Waiting for annotations to load before checking auto-annotation...');
  //       return;
  //     }
  //     
  //     // Don't prompt again if we already prompted for this page in this session
  //     if (promptedPagesRef.current.has(pageNumber)) {
  //       console.log(`Already prompted for auto-annotation on page ${pageNumber} in this session, skipping`);
  //       return;
  //     }
  //     
  //     // Check if page has any message history - don't show auto-annotation if there are any messages
  //     try {
  //       const { data: pageMessages, error: messagesError } = await supabase
  //         .from('estimation_chat_messages')
  //         .select('id')
  //         .eq('estimation_id', estimationId)
  //         .eq('page_number', pageNumber);
  //       
  //       if (messagesError) {
  //         console.error('Error checking page messages:', messagesError);
  //         return;
  //       }
  //       
  //       if (pageMessages && pageMessages.length > 0) {
  //         console.log(`Page ${pageNumber} has ${pageMessages.length} message(s), skipping auto-annotation prompt`);
  //         promptedPagesRef.current.add(pageNumber); // Mark as prompted for this session too
  //         return;
  //       }
  //     } catch (error) {
  //       console.error('Error checking page messages:', error);
  //       return;
  //     }
  //     
  //     // Wait a bit to ensure annotations are loaded from database
  //     await new Promise(resolve => setTimeout(resolve, 800));
  //     
  //     // Check current in-memory annotations first (most accurate)
  //     const pageHasAnnotations = annotations.some(a => a.page === pageNumber);
  //     if (pageHasAnnotations) {
  //       console.log(`Page ${pageNumber} has ${annotations.filter(a => a.page === pageNumber).length} annotations in memory, skipping auto-annotation prompt`);
  //       return;
  //     }
  //     
  //     // Double-check with database
  //     const { shouldRun, electricalKey } = await shouldAutoAnnotate(
  //       pageNumber,
  //       estimationId
  //     );
  //     
  //     if (shouldRun && electricalKey) {
  //       console.log(`🎯 Triggering auto-annotation prompt for page ${pageNumber}`);
  //       // Note: We'll add to promptedPagesRef when user dismisses OR accepts
  //       onAutoAnnotationTrigger({
  //         pageNumber,
  //         electricalKey,
  //         estimationId,
  //         onDismiss: () => {
  //           promptedPagesRef.current.add(pageNumber);
  //           console.log(`User dismissed auto-annotation for page ${pageNumber}`);
  //         }
  //       });
  //     }
  //   };
  //   
  //   checkAndTriggerAutoAnnotation();
  // }, [pageNumber, estimationId, onAutoAnnotationTrigger, annotations, annotationsLoaded]);

  // Handle sending snapshot to agent
  const handleSendToAgent = async () => {
    await captureSnapshotToClipboard();
    setSnapshotSelection(null);
    setShowSnapshotActions(false);
  };

  // Handle extracting key from snapshot
  const handleExtractKey = async () => {
    if (!canvasRef.current || !snapshotSelection) return;

    const annotationCanvas = canvasRef.current;
    const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    
    if (!pdfCanvas) {
      console.error('PDF canvas not found');
      alert('Could not capture PDF area. Please try again.');
      return;
    }

    // Calculate the selection bounds
    const x = Math.min(snapshotSelection.startX, snapshotSelection.endX);
    const y = Math.min(snapshotSelection.startY, snapshotSelection.endY);
    const width = Math.abs(snapshotSelection.endX - snapshotSelection.startX);
    const height = Math.abs(snapshotSelection.endY - snapshotSelection.startY);

    // Create temporary canvas to capture the selected area
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext('2d');

    if (!tempCtx) return;

    // Draw the selected portion of the PDF
    tempCtx.drawImage(pdfCanvas, x, y, width, height, 0, 0, width, height);

    // Convert to data URL
    const snapshotDataUrl = tempCanvas.toDataURL('image/jpeg', 0.95);

    // Clear snapshot UI immediately
    setSnapshotSelection(null);
    setShowSnapshotActions(false);

    // Send progress updates
    const updateProgress = (msg: string) => {
      if (onKeyExtractionProgress) {
        onKeyExtractionProgress(msg);
      }
      if (onScreenshotCaptured) {
        onScreenshotCaptured(msg);
      }
    };

    updateProgress('Understanding table...');

    // Import and call the key extraction service
    const { extractKeyFromSnapshot, convertToProjectKeyFormat } = await import('../services/keyExtractionService');
    
    try {
      console.log('🔑 Extracting key from snapshot...');
      
      const extractedKey = await extractKeyFromSnapshot(snapshotDataUrl);
      
      updateProgress('Extracting elements...');
      
      const projectKey = convertToProjectKeyFormat(extractedKey);
      
      console.log('✅ Key extracted successfully:', projectKey);
      
      // Call the onKeyExtracted callback
      if (onKeyExtracted) {
        onKeyExtracted(projectKey);
      }
      
      // Send success message - brief confirmation
      updateProgress(`Extracted ${projectKey.components.length} symbols`);
      
    } catch (error) {
      console.error('❌ Error extracting key:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      
      updateProgress(`❌ Extraction failed: ${errorMsg}`);
    }
  };

  // Handle canceling snapshot
  const handleCancelSnapshot = () => {
    setSnapshotSelection(null);
    setShowSnapshotActions(false);
  };

  // Capture snapshot to clipboard
  const captureSnapshotToClipboard = async () => {
    if (!canvasRef.current || !snapshotSelection) return;

    const annotationCanvas = canvasRef.current;
    const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    
    if (!pdfCanvas) {
      console.error('PDF canvas not found');
      return;
    }

    // Calculate the selection bounds (handle negative width/height)
    const x = Math.min(snapshotSelection.startX, snapshotSelection.endX);
    const y = Math.min(snapshotSelection.startY, snapshotSelection.endY);
    const width = Math.abs(snapshotSelection.endX - snapshotSelection.startX);
    const height = Math.abs(snapshotSelection.endY - snapshotSelection.startY);

    // Don't capture if the selection is too small
    if (width < 10 || height < 10) {
      console.log('Selection too small');
      return;
    }
    
    // Define minimum size for AI recognition
    // 300px gives Claude much better material to work with
    const MIN_SIZE = 300;

    console.log('📸 Capturing area:', { x, y, width, height });
    console.log('📐 Canvas resolution:', {
      annotationCanvas: `${annotationCanvas.width}x${annotationCanvas.height}`,
      pdfCanvas: `${pdfCanvas.width}x${pdfCanvas.height}`,
      capturedArea: `${width}x${height} pixels`
    });

    try {
      // Create a combined canvas that includes both PDF and annotations
      const combinedCanvas = document.createElement('canvas');
      combinedCanvas.width = annotationCanvas.width;
      combinedCanvas.height = annotationCanvas.height;
      const combinedCtx = combinedCanvas.getContext('2d')!;

      // Draw the PDF content first
      combinedCtx.drawImage(pdfCanvas, 0, 0, annotationCanvas.width, annotationCanvas.height);
      
      // Draw the annotations on top
      combinedCtx.drawImage(annotationCanvas, 0, 0);

      // Create a temporary canvas for the cropped area
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const tempCtx = tempCanvas.getContext('2d')!;

      // Draw the selected area from the combined canvas
      tempCtx.drawImage(
        combinedCanvas,
        x, y, width, height,  // Source rectangle
        0, 0, width, height   // Destination rectangle
      );

      // Smart upscaling for small images
      let finalCanvas = tempCanvas;
      let finalCtx = tempCtx;
      
      if (width < MIN_SIZE || height < MIN_SIZE) {
        console.log(`🔍 Image too small (${width}x${height}), upscaling for better recognition...`);
        
        // Calculate scale factor to reach minimum size while maintaining aspect ratio
        const scale = Math.max(MIN_SIZE / width, MIN_SIZE / height);
        const upscaledWidth = Math.round(width * scale);
        const upscaledHeight = Math.round(height * scale);
        
        // Create upscaled canvas
        const upscaledCanvas = document.createElement('canvas');
        upscaledCanvas.width = upscaledWidth;
        upscaledCanvas.height = upscaledHeight;
        const upscaledCtx = upscaledCanvas.getContext('2d')!;
        
        // For electrical symbols, we want sharp edges
        // Try different interpolation methods based on content
        
        // Use bicubic-like interpolation for smoother upscaling
        // This preserves the shape much better than nearest neighbor
        upscaledCtx.imageSmoothingEnabled = true;
        upscaledCtx.imageSmoothingQuality = 'high';
        upscaledCtx.drawImage(tempCanvas, 0, 0, upscaledWidth, upscaledHeight);
        
        // Only apply mild enhancement - don't destroy the image!
        if (width < 100 || height < 100) {
          console.log('🔍 Applying mild enhancement for clarity...');
          const imageData = upscaledCtx.getImageData(0, 0, upscaledWidth, upscaledHeight);
          const data = imageData.data;
          
          // Just a gentle contrast boost - nothing aggressive
          const factor = 1.2; // Much gentler than before
          for (let i = 0; i < data.length; i += 4) {
            // Enhance contrast slightly
            data[i] = Math.min(255, Math.max(0, (data[i] - 128) * factor + 128));
            data[i + 1] = Math.min(255, Math.max(0, (data[i + 1] - 128) * factor + 128));
            data[i + 2] = Math.min(255, Math.max(0, (data[i + 2] - 128) * factor + 128));
          }
          
          upscaledCtx.putImageData(imageData, 0, 0);
        }
        
        console.log(`📐 Upscaled from ${width}x${height} to ${upscaledWidth}x${upscaledHeight} (${scale.toFixed(1)}x)`);
        finalCanvas = upscaledCanvas;
        finalCtx = upscaledCtx;
      }

      // Convert to blob
      const blob = await new Promise<Blob | null>(resolve => {
        finalCanvas.toBlob(resolve, 'image/png');
      });

      if (blob) {
        // Convert blob to base64 data URL
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = reader.result as string;
          
          // If we have a callback, send the image directly to the chat
          if (onScreenshotCaptured) {
            onScreenshotCaptured(base64);
            
            // Log whether image was upscaled
            if (width < MIN_SIZE || height < MIN_SIZE) {
              const scale = Math.max(MIN_SIZE / width, MIN_SIZE / height);
              console.log(`✅ Screenshot sent to chat (upscaled ${scale.toFixed(1)}x for better recognition)`);
            } else {
              console.log('✅ Screenshot sent directly to chat!');
            }
          }
          
          // Optionally try to copy to clipboard (will fail on HTTP, but that's OK)
          try {
            // Only attempt clipboard if we're on HTTPS
            if (window.location.protocol === 'https:' && navigator.clipboard && window.ClipboardItem) {
              await navigator.clipboard.write([
                new ClipboardItem({
                  'image/png': blob
                })
              ]);
              console.log('✅ Screenshot also copied to clipboard!');
            }
          } catch (error) {
            // Silently ignore clipboard errors - it's just a nice-to-have feature
            // The main functionality (sending to chat) already worked
          }

          // Show success feedback
          const ctx = annotationCanvas.getContext('2d');
          if (ctx) {
            // Flash the selection area briefly
            ctx.save();
            ctx.fillStyle = 'rgba(0, 255, 0, 0.3)';
            ctx.fillRect(x, y, width, height);
            ctx.restore();

            // Clear the flash after a moment
            setTimeout(() => {
              drawAnnotations();
            }, 200);
          }
        };
        
        reader.readAsDataURL(blob);
      } else {
        console.error('Failed to create screenshot blob');
      }
    } catch (error) {
      console.error('Error capturing screenshot:', error);
    }
  };

  const lastSaveHashRef = useRef<string>('');
  const lastLoadWasEmptyRef = useRef<boolean>(false);
  const annotationsRef = useRef<Annotation[]>([]);
  const annotationsLoadedRef = useRef<boolean>(false);
  const persistedAnnotationSignaturesRef = useRef<Map<string, string>>(new Map());
  const pendingSaveSnapshotRef = useRef<Annotation[] | null>(null);
  const pendingSaveAllowPruneRef = useRef<boolean>(false);
  const saveAnnotationsRef = useRef<(snapshot?: Annotation[], allowPrune?: boolean) => Promise<void>>(async () => {});
  const isCoalescedSaveRunningRef = useRef<boolean>(false);
  const hasPendingCoalescedSaveRef = useRef<boolean>(false);

  // Generate a hash of annotations to detect if they've actually changed
  const getAnnotationsHash = (anns: Annotation[]): string => {
    const toNumberOrNull = (value: any): number | null =>
      typeof value === 'number' && Number.isFinite(value) ? Number(value.toFixed(3)) : null;

    const sorted = [...anns].sort((a, b) => a.id.localeCompare(b.id));
    return JSON.stringify(sorted.map(a => ({
      id: a.id,
      page: a.page,
      componentType: a.component_type,
      annotationType: a.type,
      number: a.number ?? null,
      x: toNumberOrNull(a.x),
      y: toNumberOrNull(a.y),
      startX: toNumberOrNull(a.startX),
      startY: toNumberOrNull(a.startY),
      endX: toNumberOrNull(a.endX),
      endY: toNumberOrNull(a.endY),
      width: toNumberOrNull(a.width),
      height: toNumberOrNull(a.height),
      radius: toNumberOrNull(a.radius),
      relativeX: toNumberOrNull(a.relativeX),
      relativeY: toNumberOrNull(a.relativeY),
      relativeStartX: toNumberOrNull(a.relativeStartX),
      relativeStartY: toNumberOrNull(a.relativeStartY),
      relativeWidth: toNumberOrNull(a.relativeWidth),
      relativeHeight: toNumberOrNull(a.relativeHeight),
      pointsCount: Array.isArray((a as any).points) ? (a as any).points.length : 0
    })));
  };

  const getAnnotationSignature = (annotation: Annotation): string => {
    const toNumberOrNull = (value: any): number | null =>
      typeof value === 'number' && Number.isFinite(value) ? Number(value.toFixed(3)) : null;

    return JSON.stringify({
      id: annotation.id,
      page: annotation.page,
      componentType: annotation.component_type ?? null,
      annotationType: annotation.type ?? null,
      number: annotation.number ?? null,
      text: annotation.text ?? null,
      color: annotation.color ?? null,
      thickness: toNumberOrNull(annotation.thickness),
      x: toNumberOrNull(annotation.x),
      y: toNumberOrNull(annotation.y),
      startX: toNumberOrNull(annotation.startX),
      startY: toNumberOrNull(annotation.startY),
      endX: toNumberOrNull(annotation.endX),
      endY: toNumberOrNull(annotation.endY),
      width: toNumberOrNull(annotation.width),
      height: toNumberOrNull(annotation.height),
      radius: toNumberOrNull(annotation.radius),
      relativeX: toNumberOrNull(annotation.relativeX),
      relativeY: toNumberOrNull(annotation.relativeY),
      relativeStartX: toNumberOrNull(annotation.relativeStartX),
      relativeStartY: toNumberOrNull(annotation.relativeStartY),
      relativeWidth: toNumberOrNull(annotation.relativeWidth),
      relativeHeight: toNumberOrNull(annotation.relativeHeight),
      savedCanvasWidth: toNumberOrNull(annotation.savedCanvasWidth),
      savedCanvasHeight: toNumberOrNull(annotation.savedCanvasHeight),
      measurement: toNumberOrNull(annotation.measurement),
      measurementUnit: annotation.measurementUnit ?? null,
      totalLength: toNumberOrNull(annotation.totalLength),
      totalArea: toNumberOrNull(annotation.totalArea),
      areaUnit: annotation.areaUnit ?? null,
      wireTypeColor: annotation.wireTypeColor ?? null,
      wireTypeName: annotation.wireTypeName ?? null,
      auto_detected: annotation.auto_detected ?? null,
      confidence: toNumberOrNull(annotation.confidence),
      points: Array.isArray((annotation as any).points)
        ? (annotation as any).points.map((point: any) => ({
            x: toNumberOrNull(point?.x),
            y: toNumberOrNull(point?.y),
            relativeX: toNumberOrNull(point?.relativeX),
            relativeY: toNumberOrNull(point?.relativeY),
          }))
        : null,
    });
  };

  // Save annotations to Supabase with deduplication and locking
  const saveAnnotations = async (
    annotationsSnapshot: Annotation[] = annotationsRef.current,
    allowPrune: boolean = false
  ) => {
    if (!supabase || !estimationId) return;
    if (!annotationsLoadedRef.current) {
      console.log('⏭️ Skipping save - annotations not fully loaded');
      return;
    }

    // Check if annotations have actually changed
    const currentHash = getAnnotationsHash(annotationsSnapshot);
    if (currentHash === lastSaveHashRef.current) {
      console.log('✨ Annotations unchanged, skipping save...');
      return;
    }

    await acquireSaveLock();
    setIsSaving(true);

    try {
      // IMPORTANT: Never perform implicit delete-all from autosave state.
      // Empty snapshots can occur transiently during file/page transitions.
      // But allow explicit prune/delete flows to persist true empty state.
      if (annotationsSnapshot.length === 0 && !allowPrune) {
        console.log('⏭️ Skipping save for empty snapshot (no implicit DB delete)');
        lastSaveHashRef.current = currentHash;
        return;
      }


      // Get canvas dimensions for relative position calculation
      const canvas = canvasRef.current;
      if (!canvas || canvas.width <= 0 || canvas.height <= 0 || (canvas.width === 300 && canvas.height === 150)) {
        console.log('⏭️ Skipping save - canvas not yet fully initialized:', {
          exists: !!canvas,
          width: canvas?.width,
          height: canvas?.height
        });
        return;
      }

      console.log('📐 Current canvas dimensions for saving:', {
        width: canvas.width,
        height: canvas.height
      });

      const enhancedAnnotations = annotationsSnapshot.map(annotation => {
        // If the annotation already has relative coordinates, PRESERVE THEM.
        // Do not recalculate from absolute coordinates unless they are missing.
        // This prevents the "re-normalization" pass from corrupting data during canvas resizes/races.
        if (annotation.relativeX !== undefined && annotation.relativeY !== undefined && annotation.relativeStartX !== undefined) {
          return {
            ...annotation,
            savedCanvasWidth: canvas.width,
            savedCanvasHeight: canvas.height
          };
        }

        // Only rescale and recalculate if relative coordinates are missing (legacy or new placement)
        const rescaled = rescaleAnnotation(annotation, canvas.width, canvas.height);
        return {
          ...rescaled,
          // Compute relative positions from the rescaled (correct) absolute positions
          relativeX: annotation.relativeX !== undefined ? annotation.relativeX : (rescaled.x ? rescaled.x / canvas.width : 0),
          relativeY: annotation.relativeY !== undefined ? annotation.relativeY : (rescaled.y ? rescaled.y / canvas.height : 0),
          relativeStartX: annotation.relativeStartX !== undefined ? annotation.relativeStartX : (rescaled.startX ? rescaled.startX / canvas.width : 0),
          relativeStartY: annotation.relativeStartY !== undefined ? annotation.relativeStartY : (rescaled.startY ? rescaled.startY / canvas.height : 0),
          relativeWidth: annotation.relativeWidth !== undefined ? annotation.relativeWidth : (rescaled.width ? rescaled.width / canvas.width : 0),
          relativeHeight: annotation.relativeHeight !== undefined ? annotation.relativeHeight : (rescaled.height ? rescaled.height / canvas.height : 0),
          savedCanvasWidth: canvas.width,
          savedCanvasHeight: canvas.height,
          ...(rescaled.points ? {
            points: rescaled.points.map((p: any) => ({
              ...p,
              relativeX: p.relativeX !== undefined ? p.relativeX : p.x / canvas.width,
              relativeY: p.relativeY !== undefined ? p.relativeY : p.y / canvas.height
            }))
          } : {})
        };
      });

      const nextPersistedSignatures = new Map(persistedAnnotationSignaturesRef.current);
      const changedAnnotations = enhancedAnnotations.filter((annotation) => {
        const annotationId = String(annotation.id || '').trim();
        if (!annotationId) return true;
        const signature = getAnnotationSignature(annotation as Annotation);
        const previousSignature = nextPersistedSignatures.get(annotationId);
        const changed = previousSignature !== signature;
        if (changed) {
          nextPersistedSignatures.set(annotationId, signature);
        }
        return changed;
      });

      if (changedAnnotations.length === 0) {
        console.log('✨ No annotation deltas detected, skipping DB upsert');
        lastSaveHashRef.current = currentHash;
        return;
      }

      const { changedComponentTypes } = await saveAnnotationSnapshotV2({
        supabase: supabase as any,
        estimationId,
        activeMode,
        currentDrawingFileId: currentDrawingFileId || null,
        annotations: changedAnnotations as any[],
        allowPrune
      });

      console.log('✅ Annotations saved successfully');
      persistedAnnotationSignaturesRef.current = nextPersistedSignatures;
      lastSaveHashRef.current = currentHash;
      
      // Notify parent that annotations were saved (for count refresh).
      // Keep this inside the lock so save + estimate sync happen atomically.
      if (onAnnotationSaved) {
        console.log('📢 Notifying parent of annotation save');
        await Promise.resolve(onAnnotationSaved(changedComponentTypes));
      }

    } catch (error) {
      console.error('❌ Error saving annotations:', error);
      // Try to save to localStorage as fallback (may fail if quota exceeded)
      try {
        localStorage.setItem(`annotations_${estimationId}`, JSON.stringify(annotationsSnapshot));
        console.log('💾 Saved to localStorage as fallback');
      } catch (storageError) {
        console.warn('⚠️ localStorage fallback also failed (quota exceeded):', storageError);
      }
    } finally {
      releaseSaveLock();
      setTimeout(() => setIsSaving(false), 1000);
    }
  };

  // Keep a ref to the latest save function so coalesced runs always use fresh state/closures
  saveAnnotationsRef.current = saveAnnotations;

  // Run at most one save at a time; if more changes arrive while saving, run exactly one follow-up save.
  const runCoalescedSave = useCallback(async () => {
    if (isCoalescedSaveRunningRef.current) {
      hasPendingCoalescedSaveRef.current = true;
      return;
    }

    isCoalescedSaveRunningRef.current = true;
    try {
      do {
        hasPendingCoalescedSaveRef.current = false;
        const snapshot = pendingSaveSnapshotRef.current
          ? [...pendingSaveSnapshotRef.current]
          : [...annotationsRef.current];
        const allowPrune = pendingSaveAllowPruneRef.current;
        pendingSaveSnapshotRef.current = null;
        pendingSaveAllowPruneRef.current = false;
        await saveAnnotationsRef.current(snapshot, allowPrune);
      } while (hasPendingCoalescedSaveRef.current);
    } finally {
      isCoalescedSaveRunningRef.current = false;
    }
  }, []);

  const requestSaveNow = useCallback((snapshot?: Annotation[], options?: { allowPrune?: boolean }) => {
    if (!annotationsLoadedRef.current) {
      console.log('⏭️ requestSaveNow ignored - annotations not loaded');
      return;
    }
    pendingSaveSnapshotRef.current = snapshot ? [...snapshot] : [...annotationsRef.current];
    pendingSaveAllowPruneRef.current = pendingSaveAllowPruneRef.current || Boolean(options?.allowPrune);
    void runCoalescedSave();
  }, [runCoalescedSave]);

  const persistDeletedAnnotations = useCallback(async (
    annotationIds: string[],
    knownComponentTypes?: string[]
  ) => {
    if (!supabase || !estimationId) return;
    const ids = Array.from(new Set((annotationIds || []).map((id) => String(id)).filter(Boolean)));
    if (!ids.length) return;

    await acquireSaveLock();
    setIsSaving(true);
    try {
      const { changedComponentTypes } = await deleteAnnotationsByIdV2({
        supabase: supabase as any,
        estimationId,
        activeMode,
        currentDrawingFileId: currentDrawingFileId || null,
        annotationIds: ids,
        knownComponentTypes,
      });

      if (onAnnotationSaved) {
        await Promise.resolve(onAnnotationSaved(changedComponentTypes));
      }

      ids.forEach((id) => persistedAnnotationSignaturesRef.current.delete(id));

      // Force next snapshot save to re-evaluate after explicit deletions.
      lastSaveHashRef.current = '';
    } catch (error) {
      console.error('❌ Error persisting explicit annotation delete:', error);
    } finally {
      releaseSaveLock();
      setTimeout(() => setIsSaving(false), 1000);
    }
  }, [
    supabase,
    estimationId,
    activeMode,
    currentDrawingFileId,
    acquireSaveLock,
    releaseSaveLock,
    onAnnotationSaved,
  ]);

  // Helper function to calculate pop-in animation scale
  const getAnimationScale = useCallback((annotationId: string): number => {
    const timestamp = annotationTimestamps.get(annotationId);
    if (!timestamp) return 1;
    
    const elapsed = Date.now() - timestamp;
    const duration = 700; // 700ms animation for smoother, more visible effect
    
    if (elapsed >= duration) return 1;
    
    // Ease-out elastic effect with bounce
    const progress = elapsed / duration;
    // Creates a bounce effect: overshoots slightly then settles
    const scale = 1 + Math.sin(progress * Math.PI) * 0.3 * (1 - progress);
    
    return scale;
  }, [annotationTimestamps]);

  // Draw annotations on canvas
  const drawAnnotations = useCallback((skipUI = false) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // In element detection mode, skip drawing regular annotations
    // Only draw the PDF element highlight
    if (isElementDetectionMode) {
      // Skip all regular annotation drawing - jump to PDF element highlight
      // (at the end of this function)
    } else {
    
    // No rotation transform needed - the PDF itself is rotated
    // and the canvas dimensions match the rotated PDF
    
    // Draw each annotation for current page
    // Always resolve positions from relative coords at draw time to handle canvas resizes
    const pageAnnotations = annotations
      .filter(a => a.page === pageNumber)
      .map(a => rescaleAnnotation(a, canvas.width, canvas.height));
    
    if (pageAnnotations.length > 0 && !skipUI) {
      console.log('🎨 Drawing annotations on canvas:', {
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        annotationCount: pageAnnotations.length,
        firstAnnotation: pageAnnotations[0]
      });
    }
    
    const isBeaconViewMode = Boolean(viewAllMode && highlightedComponentType);

    if (!skipUI && isBeaconViewMode && pageAnnotations.length > 0) {
      // In view-all mode, dim the PDF and punch transparent holes through detection bounds.
      ctx.save();
      ctx.fillStyle = 'rgba(6, 12, 20, 0.56)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'destination-out';

      pageAnnotations.forEach(annotation => {
        if (annotation.type === 'rectangle') {
          const x = Math.min(annotation.startX, annotation.startX + (annotation.width || 0));
          const y = Math.min(annotation.startY, annotation.startY + (annotation.height || 0));
          const width = Math.abs(annotation.width || 0);
          const height = Math.abs(annotation.height || 0);
          if (width > 0 && height > 0) {
            ctx.fillRect(x, y, width, height);
          }
          return;
        }

        if (annotation.type === 'circle') {
          const centerX = annotation.x ?? (annotation.startX + (annotation.width || 0) / 2);
          const centerY = annotation.y ?? (annotation.startY + (annotation.height || 0) / 2);
          const radiusFromNumber = annotation.number
            ? (String(annotation.number).length === 1 ? 24 : String(annotation.number).length === 2 ? 28 : 32)
            : null;
          const radius = radiusFromNumber || annotation.radius || Math.max(8, Math.min(Math.abs(annotation.width || 0), Math.abs(annotation.height || 0)) / 2);
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
          ctx.fill();
          return;
        }

        if (annotation.type === 'hand-square-footage') {
          const x = annotation.startX;
          const y = annotation.startY;
          const width = (annotation.endX || annotation.startX) - annotation.startX;
          const height = (annotation.endY || annotation.startY) - annotation.startY;
          if (Math.abs(width) > 0 && Math.abs(height) > 0) {
            ctx.fillRect(x, y, width, height);
          }
        }
      });

      ctx.restore();

      // Add neutral interior light so beacon windows read brighter than the base PDF.
      ctx.save();
      pageAnnotations.forEach(annotation => {
        const isHighlighted = highlightedComponentType === annotation.component_type;
        const interiorLightAlpha = isHighlighted ? 0.22 : 0.12;
        ctx.fillStyle = `rgba(255, 255, 255, ${interiorLightAlpha})`;

        if (annotation.type === 'rectangle') {
          const x = Math.min(annotation.startX, annotation.startX + (annotation.width || 0));
          const y = Math.min(annotation.startY, annotation.startY + (annotation.height || 0));
          const width = Math.abs(annotation.width || 0);
          const height = Math.abs(annotation.height || 0);
          if (width > 0 && height > 0) {
            ctx.fillRect(x, y, width, height);
          }
          return;
        }

        if (annotation.type === 'circle') {
          const centerX = annotation.x ?? (annotation.startX + (annotation.width || 0) / 2);
          const centerY = annotation.y ?? (annotation.startY + (annotation.height || 0) / 2);
          const radiusFromNumber = annotation.number
            ? (String(annotation.number).length === 1 ? 24 : String(annotation.number).length === 2 ? 28 : 32)
            : null;
          const radius = radiusFromNumber || annotation.radius || Math.max(8, Math.min(Math.abs(annotation.width || 0), Math.abs(annotation.height || 0)) / 2);
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
          ctx.fill();
          return;
        }

        if (annotation.type === 'hand-square-footage') {
          const x = annotation.startX;
          const y = annotation.startY;
          const width = (annotation.endX || annotation.startX) - annotation.startX;
          const height = (annotation.endY || annotation.startY) - annotation.startY;
          if (Math.abs(width) > 0 && Math.abs(height) > 0) {
            ctx.fillRect(x, y, width, height);
          }
        }
      });
      ctx.restore();
    }

    pageAnnotations.forEach(annotation => {
      const isHighlighted = highlightedComponentType === annotation.component_type;
      const isAnimating = animatingAnnotations.has(annotation.id);
      const isMultiSelected = selectedAnnotations.has(annotation.id);
      
      // If a component type is highlighted, only show annotations of that type
      // (unless viewAllMode is on, in which case show others ghosted)
      let isGhosted = false;
      if (highlightedComponentType) {
        if (annotation.type === 'wire-run') {
          // Wire runs are always visible (ghosted when another type is highlighted)
          isGhosted = true;
        } else if (annotation.type === 'piping') {
          const startAnn = annotations.find(a => a.id === annotation.startAnnotationId);
          const endAnn = annotations.find(a => a.id === annotation.endAnnotationId);
          const shouldShowPipe = 
            (startAnn?.component_type === highlightedComponentType) ||
            (endAnn?.component_type === highlightedComponentType);
          
          if (!shouldShowPipe) {
            if (viewAllMode) {
              isGhosted = true;
            } else {
              return;
            }
          }
        } else if (annotation.component_type !== highlightedComponentType) {
          if (viewAllMode) {
            isGhosted = true;
          } else {
            return;
          }
        }
      }

      if (isGhosted) {
        ctx.save();
        ctx.globalAlpha = isBeaconViewMode ? 0.85 : 0.5;
      }

      const shouldShowOnlyNumber = isHighlighted && !isAnimating;

      // Skip drawing the shape if we're only showing the number (for circles only)
      // Rectangles should ALWAYS be drawn - they show a number badge in the corner instead of being replaced
      if (!shouldShowOnlyNumber || annotation.type === 'rectangle') {
        // Check if this annotation is selected (either single or multi)
        const isSelected = isMultiSelected || (annotation.id === selectedAnnotation);
        
        if (isSelected) {
          // Draw a highly visible dotted blue outline for selected annotations
          ctx.save();
          ctx.strokeStyle = '#0066ff'; // Bright blue
          ctx.lineWidth = 6; // Thick line for better visibility
          ctx.setLineDash([12, 6]); // Larger dotted pattern: 12px dash, 6px gap
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          
          // Add a stronger shadow for better visibility
          ctx.shadowColor = '#0066ff';
          ctx.shadowBlur = 8;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
          
          // Draw the selection outline based on annotation type
          if (annotation.type === 'rectangle') {
            ctx.strokeRect(
              annotation.startX - 8,
              annotation.startY - 8,
              (annotation.width || 0) + 16,
              (annotation.height || 0) + 16
            );
          } else if (annotation.type === 'circle') {
            let centerX, centerY, radius;
            if (annotation.x !== undefined && annotation.y !== undefined) {
              centerX = annotation.x;
              centerY = annotation.y;
              radius = (annotation.radius || 5) + 8;
            } else {
              centerX = annotation.startX + (annotation.width || 0) / 2;
              centerY = annotation.startY + (annotation.height || 0) / 2;
              // If it's a numbered annotation, use the actual drawn radius
              if (annotation.number) {
                const text = String(annotation.number);
                radius = (text.length === 1 ? 12 : text.length === 2 ? 14 : 16) + 6;
              } else {
                radius = Math.min(annotation.width || 0, annotation.height || 0) / 2 + 8;
              }
            }
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
            ctx.stroke();
          } else if (annotation.type === 'line') {
            // Draw dotted rectangles at the endpoints
            const size = 14;
            ctx.strokeRect(annotation.startX - size/2, annotation.startY - size/2, size, size);
            ctx.strokeRect((annotation.endX || annotation.startX) - size/2, (annotation.endY || annotation.startY) - size/2, size, size);
          }
          ctx.restore();
        }
        
        // Now draw the annotation with its normal color
        const annotationColor = getColorForAnnotation(annotation);
        ctx.strokeStyle = annotationColor;
        ctx.lineWidth = annotation.thickness;

        // Set fill style with semi-transparency (20% opacity)
        const colorMatch = annotationColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (colorMatch) {
          ctx.fillStyle = `rgba(${colorMatch[1]}, ${colorMatch[2]}, ${colorMatch[3]}, 0.2)`;
        } else {
          ctx.fillStyle = annotationColor + '33'; // Add 20% opacity in hex
        }

        switch (annotation.type) {
          case 'rectangle':
            // Get animation scale for pop-in effect
            const rectAnimScale = isAnimating ? getAnimationScale(annotation.id) : 1;
            const rectColor = getColorForAnnotation(annotation);
            
            // Convert color to semi-transparent for fill
            let rectFillColor = rectColor;
            const rectColorMatch = rectColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
            if (rectColorMatch) {
              rectFillColor = `rgba(${rectColorMatch[1]}, ${rectColorMatch[2]}, ${rectColorMatch[3]}, 0.25)`;
            } else if (rectColor.startsWith('#')) {
              const hex = rectColor.replace('#', '');
              const r = parseInt(hex.substring(0, 2), 16);
              const g = parseInt(hex.substring(2, 4), 16);
              const b = parseInt(hex.substring(4, 6), 16);
              rectFillColor = `rgba(${r}, ${g}, ${b}, 0.25)`;
            }
            
            if (isBeaconViewMode) {
              const x = Math.min(annotation.startX, annotation.startX + (annotation.width || 0));
              const y = Math.min(annotation.startY, annotation.startY + (annotation.height || 0));
              const width = Math.abs(annotation.width || 0);
              const height = Math.abs(annotation.height || 0);
              const beaconBorderColor = (isHighlighted || isSelected) ? rectColor : 'rgba(255, 255, 255, 0.96)';

              ctx.save();
              ctx.strokeStyle = beaconBorderColor;
              ctx.lineWidth = isHighlighted ? 4.5 : 2.25;
              ctx.shadowColor = beaconBorderColor;
              ctx.shadowBlur = (isHighlighted || isSelected) ? 28 : 10;
              ctx.strokeRect(x, y, width, height);
              ctx.restore();
            } else {
              // Fill first with semi-transparent color
              ctx.fillStyle = rectFillColor;
              ctx.fillRect(
                annotation.startX,
                annotation.startY,
                annotation.width || 0,
                annotation.height || 0
              );
              
              // Then stroke the border with thicker line
              ctx.strokeStyle = rectColor;
              ctx.lineWidth = 3;
              ctx.strokeRect(
                annotation.startX,
                annotation.startY,
                annotation.width || 0,
                annotation.height || 0
              );
            }
            
            // COMMENTED OUT: Number display inside rectangles
            // if (annotation.number) {
            //   const numText = String(annotation.number);
            //   const centerX = annotation.startX + (annotation.width || 0) / 2;
            //   const centerY = annotation.startY + (annotation.height || 0) / 2;
            //   const rectSize = Math.min(annotation.width || 40, annotation.height || 40);
            //   const baseFontSize = Math.max(24, Math.min(rectSize * 0.6, 48));
            //   const animatedFontSize = baseFontSize * rectAnimScale;
            //   ctx.font = `bold ${animatedFontSize}px Arial`;
            //   ctx.textAlign = 'center';
            //   ctx.textBaseline = 'middle';
            //   ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
            //   ctx.lineWidth = 4;
            //   ctx.lineJoin = 'round';
            //   ctx.miterLimit = 2;
            //   ctx.strokeText(numText, centerX, centerY);
            //   ctx.fillStyle = 'white';
            //   ctx.fillText(numText, centerX, centerY);
            // }
            
            // Draw resize handles if this rectangle is selected
            const isThisSelected = (annotation.id === selectedAnnotation) || selectedAnnotations.has(annotation.id);
            if (isThisSelected && currentTool === 'select') {
              const handleSize = 10;
              const handles = [
                { x: annotation.startX, y: annotation.startY, cursor: 'nw-resize' }, // top-left
                { x: annotation.startX + (annotation.width || 0), y: annotation.startY, cursor: 'ne-resize' }, // top-right
                { x: annotation.startX, y: annotation.startY + (annotation.height || 0), cursor: 'sw-resize' }, // bottom-left
                { x: annotation.startX + (annotation.width || 0), y: annotation.startY + (annotation.height || 0), cursor: 'se-resize' }, // bottom-right
              ];
              
              handles.forEach(handle => {
                // White fill
                ctx.fillStyle = 'white';
                ctx.fillRect(handle.x - handleSize/2, handle.y - handleSize/2, handleSize, handleSize);
                // Blue border
                ctx.strokeStyle = '#0066ff';
                ctx.lineWidth = 2;
                ctx.strokeRect(handle.x - handleSize/2, handle.y - handleSize/2, handleSize, handleSize);
              });
            }
            break;
          
          case 'circle':
            // Determine center point based on annotation format
            let centerX, centerY;
            
            if (annotation.x !== undefined && annotation.y !== undefined) {
              // AI-generated format (x, y, radius)
              centerX = annotation.x;
              centerY = annotation.y;
            } else {
              // User-drawn format (startX, startY, width, height)
              centerX = annotation.startX + (annotation.width || 0) / 2;
              centerY = annotation.startY + (annotation.height || 0) / 2;
            }

            // Get animation scale for pop-in effect
            const animScale = isAnimating ? getAnimationScale(annotation.id) : 1;

            // Draw circle with number inside
            if (annotation.number) {
              const text = String(annotation.number);
              
              // Determine circle size based on number of digits
              const radius = text.length === 1 ? 24 : text.length === 2 ? 28 : 32;
              
              // Apply animation scale to radius
              const animatedRadius = radius * animScale;
              
              // Draw filled circle with semi-transparent background
              const circleColor = getColorForAnnotation(annotation);
              
              // Convert color to semi-transparent (40% opacity)
              let fillColor = circleColor;
              const colorMatch = circleColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
              if (colorMatch) {
                fillColor = `rgba(${colorMatch[1]}, ${colorMatch[2]}, ${colorMatch[3]}, 0.4)`;
              } else if (circleColor.startsWith('#')) {
                // Convert hex to rgba with opacity
                const hex = circleColor.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                fillColor = `rgba(${r}, ${g}, ${b}, 0.4)`;
              }
              
              ctx.beginPath();
              ctx.arc(centerX, centerY, animatedRadius, 0, 2 * Math.PI);
              if (isBeaconViewMode) {
                const beaconBorderColor = (isHighlighted || isSelected) ? circleColor : 'rgba(255, 255, 255, 0.96)';
                ctx.strokeStyle = beaconBorderColor;
                ctx.lineWidth = isHighlighted ? 4 : 2;
                ctx.shadowColor = beaconBorderColor;
                ctx.shadowBlur = (isHighlighted || isSelected) ? 24 : 9;
                ctx.stroke();
                ctx.shadowBlur = 0;
              } else {
                // Draw semi-transparent fill
                ctx.fillStyle = fillColor;
                ctx.fill();
                
                // Draw solid color border
                ctx.strokeStyle = circleColor;
                ctx.lineWidth = 2;
                ctx.stroke();
              }
              
              // COMMENTED OUT: Number display inside circles
              // const baseFontSize = text.length === 1 ? 28 : 24;
              // const animatedFontSize = baseFontSize * animScale;
              // ctx.font = `bold ${animatedFontSize}px Arial`;
              // ctx.textAlign = 'center';
              // ctx.textBaseline = 'middle';
              // ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
              // ctx.lineWidth = 3;
              // ctx.lineJoin = 'round';
              // ctx.miterLimit = 2;
              // ctx.strokeText(text, centerX, centerY);
              // ctx.fillStyle = 'white';
              // ctx.fillText(text, centerX, centerY);
              
              // Reset styles
              ctx.strokeStyle = circleColor;
              ctx.lineWidth = annotation.thickness || 2;
            } else {
              // Draw small dot (5px radius) if no number
              const circleColor = getColorForAnnotation(annotation);
              
              // Apply animation scale to small dot radius too
              const animatedDotRadius = 5 * animScale;
              
              // Convert to semi-transparent
              let fillColor = circleColor;
              const colorMatch = circleColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
              if (colorMatch) {
                fillColor = `rgba(${colorMatch[1]}, ${colorMatch[2]}, ${colorMatch[3]}, 0.4)`;
              } else if (circleColor.startsWith('#')) {
                const hex = circleColor.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                fillColor = `rgba(${r}, ${g}, ${b}, 0.4)`;
              }
              
              ctx.beginPath();
              ctx.arc(centerX, centerY, animatedDotRadius, 0, 2 * Math.PI);
              if (isBeaconViewMode) {
                const beaconBorderColor = (isHighlighted || isSelected) ? circleColor : 'rgba(255, 255, 255, 0.96)';
                ctx.strokeStyle = beaconBorderColor;
                ctx.lineWidth = isHighlighted ? 3 : 1.5;
                ctx.shadowColor = beaconBorderColor;
                ctx.shadowBlur = (isHighlighted || isSelected) ? 20 : 8;
                ctx.stroke();
                ctx.shadowBlur = 0;
              } else {
                ctx.fillStyle = fillColor;
                ctx.fill();
                ctx.strokeStyle = circleColor;
                ctx.lineWidth = 1;
                ctx.stroke();
              }
            }
            break;
          
          case 'line':
            ctx.beginPath();
            ctx.moveTo(annotation.startX, annotation.startY);
            ctx.lineTo(annotation.endX || annotation.startX, annotation.endY || annotation.startY);
            ctx.stroke();
            break;

          case 'pen':
            if (annotation.points && annotation.points.length > 0) {
              ctx.beginPath();
              ctx.moveTo(annotation.points[0].x, annotation.points[0].y);
              annotation.points.forEach(point => {
                ctx.lineTo(point.x, point.y);
              });
              ctx.stroke();
            }
            break;

          case 'text':
            if (annotation.text) {
              ctx.font = '16px Arial';
              ctx.fillText(annotation.text, annotation.startX, annotation.startY);
            }
            break;
          
          case 'piping':
            // Draw selection highlight if selected
            if (isSelected) {
              ctx.save();
              ctx.strokeStyle = '#0066ff'; // Bright blue
              ctx.lineWidth = 8; // Thick outline
              ctx.setLineDash([12, 6]);
              ctx.lineCap = 'round';
              ctx.shadowColor = '#0066ff';
              ctx.shadowBlur = 8;
              
              ctx.beginPath();
              ctx.moveTo(annotation.startX, annotation.startY);
              ctx.lineTo(annotation.endX || annotation.startX, annotation.endY || annotation.startY);
              ctx.stroke();
              
              ctx.setLineDash([]);
              ctx.restore();
            }
            
            // Draw the pipe line
            ctx.beginPath();
            ctx.moveTo(annotation.startX, annotation.startY);
            ctx.lineTo(annotation.endX || annotation.startX, annotation.endY || annotation.startY);
            ctx.stroke();
            
            // Draw measurement label if available
            if (annotation.measurement && annotation.measurementUnit) {
              const midX = (annotation.startX + (annotation.endX || annotation.startX)) / 2;
              const midY = (annotation.startY + (annotation.endY || annotation.startY)) / 2;
              
              const label = `${annotation.measurement}${annotation.measurementUnit}`;
              
              // Draw background for text
              ctx.font = 'bold 24px Arial';
              const textMetrics = ctx.measureText(label);
              const padding = 10;
              
              ctx.fillStyle = 'white';
              ctx.fillRect(
                midX - textMetrics.width/2 - padding,
                midY - 16,
                textMetrics.width + padding * 2,
                32
              );
              
              // Draw text
              ctx.fillStyle = annotation.color;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(label, midX, midY);
            }
            
            // Draw connection points
            ctx.fillStyle = annotation.color;
            ctx.beginPath();
            ctx.arc(annotation.startX, annotation.startY, 6, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(annotation.endX || annotation.startX, annotation.endY || annotation.startY, 6, 0, Math.PI * 2);
            ctx.fill();
            break;
          
          case 'wire-run':
            // Draw wire/conduit run polyline
            if (!annotation.points || annotation.points.length < 2) break;
            
            const wireColor = annotation.wireTypeColor || annotation.color;
            
            // Draw selection highlight if selected
            if (isSelected) {
              ctx.save();
              ctx.strokeStyle = '#0066ff';
              ctx.lineWidth = 8;
              ctx.setLineDash([12, 6]);
              ctx.lineCap = 'round';
              ctx.lineJoin = 'round';
              ctx.shadowColor = '#0066ff';
              ctx.shadowBlur = 8;
              
              ctx.beginPath();
              ctx.moveTo(annotation.points[0].x, annotation.points[0].y);
              for (let i = 1; i < annotation.points.length; i++) {
                ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
              }
              ctx.stroke();
              ctx.setLineDash([]);
              ctx.restore();
            }
            
            // Draw the wire run polyline
            ctx.strokeStyle = wireColor;
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.globalAlpha = 1;
            
            ctx.beginPath();
            ctx.moveTo(annotation.points[0].x, annotation.points[0].y);
            for (let i = 1; i < annotation.points.length; i++) {
              ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
            }
            ctx.stroke();
            
            // Draw small dots at vertices
            annotation.points.forEach((point, index) => {
              ctx.beginPath();
              ctx.arc(point.x, point.y, index === 0 ? 5 : 4, 0, Math.PI * 2);
              ctx.fillStyle = wireColor;
              ctx.fill();
            });
            
            // Draw measurement label
            if (annotation.totalLength !== undefined) {
              const midIndex = Math.floor(annotation.points.length / 2);
              const labelPoint = annotation.points[midIndex];
              
              ctx.font = 'bold 16px Arial';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              
              const unit = annotation.measurementUnit || 'ft';
              const text = `${annotation.totalLength.toFixed(1)} ${unit}`;
              const textWidth = ctx.measureText(text).width + 20;
              
              // Background - colored with white border
              ctx.fillStyle = wireColor;
              ctx.beginPath();
              ctx.roundRect(labelPoint.x - textWidth / 2, labelPoint.y - 14, textWidth, 28, 4);
              ctx.fill();
              
              // White border
              ctx.strokeStyle = '#fff';
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.roundRect(labelPoint.x - textWidth / 2, labelPoint.y - 14, textWidth, 28, 4);
              ctx.stroke();
              
              // Text
              ctx.fillStyle = '#fff';
              ctx.fillText(text, labelPoint.x, labelPoint.y);
            }
            
            // Draw wire type name near start
            if (annotation.wireTypeName) {
              const startPoint = annotation.points[0];
              ctx.font = 'bold 14px Arial';
              ctx.textAlign = 'left';
              ctx.textBaseline = 'bottom';
              
              const nameText = annotation.wireTypeName;
              const nameWidth = ctx.measureText(nameText).width + 12;
              
              // Background
              ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
              ctx.beginPath();
              ctx.roundRect(startPoint.x - 2, startPoint.y - 28, nameWidth, 22, 3);
              ctx.fill();
              
              // Text
              ctx.fillStyle = wireColor;
              ctx.fillText(nameText, startPoint.x + 4, startPoint.y - 10);
            }
            break;
          
          case 'hand-square-footage': {
            const sqftX = annotation.startX;
            const sqftY = annotation.startY;
            const sqftW = (annotation.endX || annotation.startX) - annotation.startX;
            const sqftH = (annotation.endY || annotation.startY) - annotation.startY;
            const sqftColor = annotation.color || '#9333EA';
            
            if (isSelected) {
              ctx.save();
              ctx.strokeStyle = '#0066ff';
              ctx.lineWidth = 4;
              ctx.setLineDash([8, 4]);
              ctx.shadowColor = '#0066ff';
              ctx.shadowBlur = 6;
              ctx.strokeRect(sqftX, sqftY, sqftW, sqftH);
              ctx.setLineDash([]);
              ctx.restore();
            }
            
            // Light fill
            ctx.globalAlpha = 0.06;
            ctx.fillStyle = sqftColor;
            ctx.fillRect(sqftX, sqftY, sqftW, sqftH);
            
            // 1-unit grid inside the rectangle
            const pageCal = calibrationData[annotation.page];
            if (pageCal && canvas) {
              const pxPerUnit = (pageCal.canvasPercentage / 100) * canvas.width / pageCal.realWorldValue;
              if (pxPerUnit > 5) {
                ctx.save();
                ctx.beginPath();
                ctx.rect(sqftX, sqftY, sqftW, sqftH);
                ctx.clip();
                
                ctx.globalAlpha = 0.18;
                ctx.strokeStyle = sqftColor;
                ctx.lineWidth = 0.5;
                ctx.setLineDash([3, 3]);
                
                const gridLeft = sqftW > 0 ? sqftX : sqftX + sqftW;
                const gridTop = sqftH > 0 ? sqftY : sqftY + sqftH;
                const gridRight = sqftW > 0 ? sqftX + sqftW : sqftX;
                const gridBottom = sqftH > 0 ? sqftY + sqftH : sqftY;
                
                for (let gx = gridLeft + pxPerUnit; gx < gridRight; gx += pxPerUnit) {
                  ctx.beginPath();
                  ctx.moveTo(gx, gridTop);
                  ctx.lineTo(gx, gridBottom);
                  ctx.stroke();
                }
                for (let gy = gridTop + pxPerUnit; gy < gridBottom; gy += pxPerUnit) {
                  ctx.beginPath();
                  ctx.moveTo(gridLeft, gy);
                  ctx.lineTo(gridRight, gy);
                  ctx.stroke();
                }
                
                ctx.setLineDash([]);
                ctx.restore();
              }
            }
            
            // Solid border
            ctx.globalAlpha = 0.6;
            ctx.strokeStyle = sqftColor;
            ctx.lineWidth = 1.5;
            ctx.strokeRect(sqftX, sqftY, sqftW, sqftH);
            
            // Area label
            ctx.globalAlpha = 1;
            if (annotation.totalArea !== undefined) {
              const cx = sqftX + sqftW / 2;
              const cy = sqftY + sqftH / 2;
              const unit = annotation.areaUnit || 'ft';
              const areaText = `${annotation.totalArea.toFixed(1)} sq ${unit}`;
              
              ctx.font = 'bold 14px Arial';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              
              const tw = ctx.measureText(areaText).width + 16;
              
              ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
              ctx.beginPath();
              ctx.roundRect(cx - tw / 2, cy - 12, tw, 24, 4);
              ctx.fill();
              
              ctx.fillStyle = '#fff';
              ctx.fillText(areaText, cx, cy);
            }
            break;
          }
        }
      }

      // Draw enlarged number when highlighted (or animating) - ONLY for circles, not rectangles
      // Rectangles already show their number badge in the main drawing code
      if ((shouldShowOnlyNumber || isAnimating) && annotation.number && annotation.type === 'circle') {
        // Determine center point based on annotation format
        let centerX, centerY;
        
        if (annotation.x !== undefined && annotation.y !== undefined) {
          centerX = annotation.x;
          centerY = annotation.y;
        } else if (annotation.startX !== undefined && annotation.startY !== undefined) {
          centerX = annotation.startX + (annotation.width || 0) / 2;
          centerY = annotation.startY + (annotation.height || 0) / 2;
        } else {
          return; // Can't draw without position
        }

        // Calculate animation scale
        let scale = 1;
        if (isAnimating) {
          // Pulse animation - grows then shrinks back
          const animationProgress = (Date.now() % 800) / 800; // 0 to 1 over 800ms
          if (animationProgress < 0.5) {
            scale = 1 + (animationProgress * 2) * 4; // Grow to 5x
          } else {
            scale = 5 - ((animationProgress - 0.5) * 2) * 2; // Shrink back to 3x
          }
        } else if (shouldShowOnlyNumber) {
          scale = 3; // Show at 3x size when highlighted
        }

        const text = String(annotation.number);
        const baseRadius = text.length === 1 ? 12 : text.length === 2 ? 14 : 16;
        const radius = baseRadius * scale;
        const fontSize = Math.floor((text.length === 1 ? 16 : 14) * scale);
        
        // Draw enlarged circle with semi-transparent fill
        const enlargedColor = getColorForAnnotation(annotation);
        
        // Convert to semi-transparent
        let fillColor = enlargedColor;
        const colorMatch = enlargedColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (colorMatch) {
          fillColor = `rgba(${colorMatch[1]}, ${colorMatch[2]}, ${colorMatch[3]}, 0.4)`;
        } else if (enlargedColor.startsWith('#')) {
          const hex = enlargedColor.replace('#', '');
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          fillColor = `rgba(${r}, ${g}, ${b}, 0.4)`;
        }
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        if (isBeaconViewMode) {
          // Keep beacon mode hollow; only stroke + glow.
          ctx.strokeStyle = enlargedColor;
          ctx.lineWidth = 4 * scale;
          ctx.shadowColor = enlargedColor;
          ctx.shadowBlur = 18;
          ctx.stroke();
          ctx.shadowBlur = 0;
        } else {
          ctx.fillStyle = fillColor;
          ctx.fill();
          
          // Add solid color border
          ctx.strokeStyle = enlargedColor;
          ctx.lineWidth = 3 * scale;
          ctx.stroke();
        }

        // COMMENTED OUT: Number display in enlarged/highlighted circles
        // ctx.font = `bold ${fontSize}px Arial`;
        // ctx.textAlign = 'center';
        // ctx.textBaseline = 'middle';
        // ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
        // ctx.lineWidth = 3 * (scale / 2);
        // ctx.lineJoin = 'round';
        // ctx.miterLimit = 2;
        // ctx.strokeText(text, centerX, centerY);
        // ctx.fillStyle = 'white';
        // ctx.fillText(text, centerX, centerY);
      }

      if (isGhosted) {
        ctx.restore();
      }
    });

    if (!skipUI && isBeaconViewMode && hoveredAnnotationPoint && hoveredAnnotation && highlightedComponentType) {
      const hovered = pageAnnotations.find(a => a.id === hoveredAnnotation);
      if (hovered && hovered.component_type && hovered.component_type !== highlightedComponentType) {
        const label = hovered.component_type;
        ctx.save();
        ctx.font = '600 12px Inter, system-ui, sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';

        const textWidth = ctx.measureText(label).width;
        const boxWidth = textWidth + 16;
        const boxHeight = 24;
        const tooltipX = Math.min(hoveredAnnotationPoint.x + 12, canvas.width - boxWidth - 8);
        const tooltipY = Math.max(12, hoveredAnnotationPoint.y - 26);

        ctx.fillStyle = 'rgba(12, 18, 28, 0.9)';
        ctx.beginPath();
        ctx.roundRect(tooltipX, tooltipY, boxWidth, boxHeight, 6);
        ctx.fill();

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
        ctx.fillText(label, tooltipX + 8, tooltipY + boxHeight / 2);
        ctx.restore();
      }
    }
    
    // Draw current annotation being created
    if (isDrawing && currentAnnotation) {
      ctx.strokeStyle = currentColor;
      ctx.fillStyle = currentColor;
      ctx.lineWidth = thickness;
      
      switch (currentAnnotation.type) {
        case 'rectangle':
          ctx.strokeRect(
            currentAnnotation.startX || 0,
            currentAnnotation.startY || 0,
            currentAnnotation.width || 0,
            currentAnnotation.height || 0
          );
          break;
          
        case 'circle':
          // Show numbered circle preview while drawing
          const centerX = (currentAnnotation.startX || 0) + (currentAnnotation.width || 0) / 2;
          const centerY = (currentAnnotation.startY || 0) + (currentAnnotation.height || 0) / 2;
          
          // Calculate what number this would be
          const componentType = currentAnnotation.component_type || 'annotation';
          const existingCount = annotations.filter(
            a => a.component_type === componentType && a.page === pageNumber
          ).length;
          const nextNumber = existingCount + 1;
          const text = String(nextNumber);
          
          // Determine circle size based on number of digits
          const radius = text.length === 1 ? 24 : text.length === 2 ? 28 : 32;
          
          // Draw filled circle with semi-transparent background
          // Convert to semi-transparent
          let fillColor = currentColor;
          const colorMatch = currentColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          if (colorMatch) {
            fillColor = `rgba(${colorMatch[1]}, ${colorMatch[2]}, ${colorMatch[3]}, 0.4)`;
          } else if (currentColor.startsWith('#')) {
            const hex = currentColor.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            fillColor = `rgba(${r}, ${g}, ${b}, 0.4)`;
          }
          
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
          ctx.fillStyle = fillColor;
          ctx.fill();
          ctx.strokeStyle = currentColor;
          ctx.lineWidth = 2;
          ctx.stroke();
          
          // Draw white number with dark outline
          ctx.font = `bold ${text.length === 1 ? '14' : '12'}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // Draw text outline
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
          ctx.lineWidth = 3;
          ctx.lineJoin = 'round';
          ctx.miterLimit = 2;
          ctx.strokeText(text, centerX, centerY);
          
          // Draw white fill
          ctx.fillStyle = 'white';
          ctx.fillText(text, centerX, centerY);
          break;
          
        case 'line':
          ctx.beginPath();
          ctx.moveTo(currentAnnotation.startX || 0, currentAnnotation.startY || 0);
          ctx.lineTo(currentAnnotation.endX || 0, currentAnnotation.endY || 0);
          ctx.stroke();
          break;
          
        case 'pen':
          if (currentAnnotation.points && currentAnnotation.points.length > 0) {
            ctx.beginPath();
            ctx.moveTo(currentAnnotation.points[0].x, currentAnnotation.points[0].y);
            currentAnnotation.points.forEach(point => {
              ctx.lineTo(point.x, point.y);
            });
            ctx.stroke();
          }
          break;
      }
    }

    // Draw selection rectangle
    if (selectionRect && isSelecting) {
      ctx.strokeStyle = 'rgba(0, 123, 255, 0.8)'; // Blue selection rectangle
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]); // Dashed line

      const width = selectionRect.endX - selectionRect.startX;
      const height = selectionRect.endY - selectionRect.startY;

      ctx.strokeRect(
        selectionRect.startX,
        selectionRect.startY,
        width,
        height
      );

      // Fill with semi-transparent blue
      ctx.fillStyle = 'rgba(0, 123, 255, 0.1)';
      ctx.fillRect(
        selectionRect.startX,
        selectionRect.startY,
        width,
        height
      );

      ctx.setLineDash([]); // Reset dash pattern
    }

    // Draw autocount selection rectangle
    if (!skipUI && autocountSelection && autocountMode === 'area-selection') {
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.9)'; // Blue for AI autocount
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]); // Dashed line

      const x = Math.min(autocountSelection.startX, autocountSelection.endX);
      const y = Math.min(autocountSelection.startY, autocountSelection.endY);
      const width = Math.abs(autocountSelection.endX - autocountSelection.startX);
      const height = Math.abs(autocountSelection.endY - autocountSelection.startY);

      ctx.strokeRect(x, y, width, height);

      // Fill with semi-transparent blue
      ctx.fillStyle = 'rgba(59, 130, 246, 0.15)';
      ctx.fillRect(x, y, width, height);

      ctx.setLineDash([]); // Reset dash pattern
    }

    // Draw key extraction selection rectangle
    if (!skipUI && keyExtractionSelection && keyExtractionMode) {
      ctx.strokeStyle = 'rgba(139, 92, 246, 0.9)'; // Purple for key extraction
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]); // Dashed line

      const x = Math.min(keyExtractionSelection.startX, keyExtractionSelection.endX);
      const y = Math.min(keyExtractionSelection.startY, keyExtractionSelection.endY);
      const width = Math.abs(keyExtractionSelection.endX - keyExtractionSelection.startX);
      const height = Math.abs(keyExtractionSelection.endY - keyExtractionSelection.startY);

      ctx.strokeRect(x, y, width, height);

      // Fill with semi-transparent purple
      ctx.fillStyle = 'rgba(139, 92, 246, 0.15)';
      ctx.fillRect(x, y, width, height);

      ctx.setLineDash([]); // Reset dash pattern
    }

    // Draw lighting schedule selection rectangle
    if (!skipUI && lightingScheduleSelection && lightingScheduleMode) {
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.9)'; // Amber for lighting schedule
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]); // Dashed line

      const x = Math.min(lightingScheduleSelection.startX, lightingScheduleSelection.endX);
      const y = Math.min(lightingScheduleSelection.startY, lightingScheduleSelection.endY);
      const width = Math.abs(lightingScheduleSelection.endX - lightingScheduleSelection.startX);
      const height = Math.abs(lightingScheduleSelection.endY - lightingScheduleSelection.startY);

      ctx.strokeRect(x, y, width, height);

      // Fill with semi-transparent amber
      ctx.fillStyle = 'rgba(245, 158, 11, 0.15)';
      ctx.fillRect(x, y, width, height);

      ctx.setLineDash([]); // Reset dash pattern
    }

    // Draw snapshot selection rectangle (show it even after mouse up, until cleared)
    if (!skipUI && (snapshotSelection || showSnapshotActions)) {
      if (snapshotSelection) {
      const width = Math.abs(snapshotSelection.endX - snapshotSelection.startX);
      const height = Math.abs(snapshotSelection.endY - snapshotSelection.startY);
      const x = Math.min(snapshotSelection.startX, snapshotSelection.endX);
      const y = Math.min(snapshotSelection.startY, snapshotSelection.endY);
      
      // Check if image will be upscaled
      const MIN_SIZE = 300;
      const willUpscale = width < MIN_SIZE || height < MIN_SIZE;
      
      // Draw semi-transparent fill for better visibility
      const fillColor = willUpscale ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)';
      ctx.fillStyle = fillColor;
      ctx.fillRect(x, y, width, height);
      
      // Draw solid border with high visibility
      ctx.strokeStyle = willUpscale ? 'rgba(245, 158, 11, 1)' : 'rgba(16, 185, 129, 1)'; // Orange if upscaling, green otherwise
      ctx.lineWidth = 3;
      ctx.setLineDash([]); // Solid line for better visibility

      ctx.strokeRect(x, y, width, height);
      
      // Draw corner handles for better visibility
      const handleSize = 8;
      ctx.fillStyle = willUpscale ? 'rgba(245, 158, 11, 1)' : 'rgba(16, 185, 129, 1)';
      // Top-left
      ctx.fillRect(x - handleSize/2, y - handleSize/2, handleSize, handleSize);
      // Top-right
      ctx.fillRect(x + width - handleSize/2, y - handleSize/2, handleSize, handleSize);
      // Bottom-left
      ctx.fillRect(x - handleSize/2, y + height - handleSize/2, handleSize, handleSize);
      // Bottom-right
      ctx.fillRect(x + width - handleSize/2, y + height - handleSize/2, handleSize, handleSize);

      // Add size indicator with upscaling info
      if (width > 10 && height > 10) {
        // Background for text
        const textBg = willUpscale ? 'rgba(245, 158, 11, 0.9)' : 'rgba(0, 123, 255, 0.9)';
        const text = willUpscale 
          ? `${Math.round(width)}×${Math.round(height)}px → ${Math.max(MIN_SIZE, Math.round(width * Math.max(MIN_SIZE/width, MIN_SIZE/height)))}×${Math.max(MIN_SIZE, Math.round(height * Math.max(MIN_SIZE/width, MIN_SIZE/height)))}px`
          : `${Math.round(width)}×${Math.round(height)}px`;
        
        ctx.font = '12px Inter, system-ui, sans-serif';
        const textWidth = ctx.measureText(text).width;
        
        ctx.fillStyle = textBg;
        ctx.fillRect(x, y - 22, textWidth + 8, 20);
        
        ctx.fillStyle = 'white';
        ctx.fillText(text, x + 4, y - 8);
      }

      ctx.setLineDash([]); // Reset dash pattern
      }
    }

    // Draw saved reference line (in yellow) if it exists for this page
    const currentPageCalibration = calibrationData[pageNumber];
    if (currentPageCalibration?.referenceLine && currentPageCalibration?.showReferenceLine !== false) {
      ctx.save();
      const refLine = currentPageCalibration.referenceLine;
      
      ctx.strokeStyle = '#fbbf24'; // Yellow color
      ctx.lineWidth = 3;
      ctx.setLineDash([10, 5]); // Dashed line
      
      // Draw the reference line
      ctx.beginPath();
      ctx.moveTo(refLine.startX, refLine.startY);
      ctx.lineTo(refLine.endX, refLine.endY);
      ctx.stroke();
      
      // Draw endpoints
      ctx.fillStyle = '#fbbf24';
      ctx.beginPath();
      ctx.arc(refLine.startX, refLine.startY, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(refLine.endX, refLine.endY, 4, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw label showing the calibration
      const midX = (refLine.startX + refLine.endX) / 2;
      const midY = (refLine.startY + refLine.endY) / 2;
      const label = `${currentPageCalibration.realWorldValue}${currentPageCalibration.unit}`;
      
      ctx.font = 'bold 14px Arial';
      ctx.fillStyle = '#fbbf24';
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 3;
      ctx.strokeText(label, midX, midY - 15);
      ctx.fillText(label, midX, midY - 15);
      
      ctx.setLineDash([]);
      ctx.restore();
    }

    // Draw angle guides when snapping is active
    if (isCalibrating && isGridSnapActive && calibrationLine) {
      ctx.save();
      const guideLength = Math.max(canvas.width, canvas.height);
      
      // Draw all 15-degree angle guides
      for (let angle = 0; angle < 360; angle += 15) {
        const radians = angle * (Math.PI / 180);
        const endX = calibrationLine.startX + guideLength * Math.cos(radians);
        const endY = calibrationLine.startY + guideLength * Math.sin(radians);
        
        // Make major angles (0, 90, 180, 270) more prominent
        const isMajorAngle = angle % 90 === 0;
        ctx.strokeStyle = isMajorAngle ? 'rgba(37, 99, 235, 0.3)' : 'rgba(37, 99, 235, 0.15)';
        ctx.lineWidth = isMajorAngle ? 1.5 : 1;
        ctx.setLineDash(isMajorAngle ? [8, 4] : [4, 4]);
        
        ctx.beginPath();
        ctx.moveTo(calibrationLine.startX, calibrationLine.startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
      }
      
      ctx.setLineDash([]);
      ctx.restore();
    }

    // Draw snap target highlight
    if (pipingSnapTarget && currentTool === 'piping') {
      const snapAnnotation = annotations.find(a => a.id === pipingSnapTarget.annotationId);
      if (snapAnnotation) {
        ctx.save();
        
        // Draw pulsing highlight around the snap target
        const pulseScale = 1 + Math.sin(Date.now() / 150) * 0.15;
        
        ctx.strokeStyle = 'rgba(34, 197, 94, 0.8)'; // Green
        ctx.lineWidth = 4 * pulseScale;
        ctx.setLineDash([8, 4]);
        ctx.shadowColor = 'rgba(34, 197, 94, 0.6)';
        ctx.shadowBlur = 15 * pulseScale;
        
        if (snapAnnotation.type === 'circle') {
          const centerX = snapAnnotation.x !== undefined ? snapAnnotation.x : snapAnnotation.startX + (snapAnnotation.width || 0) / 2;
          const centerY = snapAnnotation.y !== undefined ? snapAnnotation.y : snapAnnotation.startY + (snapAnnotation.height || 0) / 2;
          const radius = snapAnnotation.radius || Math.max(snapAnnotation.width || 0, snapAnnotation.height || 0) / 2;
          
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius + 15 * pulseScale, 0, Math.PI * 2);
          ctx.stroke();
        } else if (snapAnnotation.type === 'rectangle') {
          ctx.strokeRect(
            snapAnnotation.startX - 10 * pulseScale,
            snapAnnotation.startY - 10 * pulseScale,
            (snapAnnotation.width || 0) + 20 * pulseScale,
            (snapAnnotation.height || 0) + 20 * pulseScale
          );
        }
        
        ctx.setLineDash([]);
        ctx.restore();
      }
    }

    // Draw piping preview
    if (pipingPreview && currentTool === 'piping') {
      ctx.save();
      ctx.strokeStyle = pipingSnapTarget ? 'rgba(34, 197, 94, 0.8)' : 'rgba(220, 38, 38, 0.6)';
      ctx.lineWidth = pipingSnapTarget ? 5 : 4;
      ctx.setLineDash([10, 5]);
      
      ctx.beginPath();
      ctx.moveTo(pipingPreview.startX, pipingPreview.startY);
      ctx.lineTo(pipingPreview.endX, pipingPreview.endY);
      ctx.stroke();
      
      ctx.setLineDash([]);
      
      // Draw start point
      ctx.fillStyle = 'rgb(220, 38, 38)';
      ctx.beginPath();
      ctx.arc(pipingPreview.startX, pipingPreview.startY, 6, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw end point if snapped
      if (pipingSnapTarget) {
        ctx.fillStyle = 'rgba(34, 197, 94, 1)';
        ctx.beginPath();
        ctx.arc(pipingPreview.endX, pipingPreview.endY, 8, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Calculate and show preview measurement
      const dx = pipingPreview.endX - pipingPreview.startX;
      const dy = pipingPreview.endY - pipingPreview.startY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const measurement = calculatePipeMeasurement(distance);
      
      if (measurement) {
        const midX = (pipingPreview.startX + pipingPreview.endX) / 2;
        const midY = (pipingPreview.startY + pipingPreview.endY) / 2;
        const label = `${measurement.value}${measurement.unit}`;
        
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = pipingSnapTarget ? 'rgba(34, 197, 94, 1)' : 'rgb(220, 38, 38)';
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 4;
        ctx.strokeText(label, midX, midY);
        ctx.fillText(label, midX, midY);
      }
      
      ctx.restore();
    }

    // Draw calibration line if active
    if (calibrationLine) {
      ctx.save();
      ctx.strokeStyle = '#f59e0b'; // Orange color
      ctx.lineWidth = 3;
      ctx.setLineDash([]);
      
      // Draw the line
      ctx.beginPath();
      ctx.moveTo(calibrationLine.startX, calibrationLine.startY);
      ctx.lineTo(calibrationLine.endX, calibrationLine.endY);
      ctx.stroke();
      
      // Draw endpoints
      ctx.fillStyle = '#f59e0b';
      ctx.beginPath();
      ctx.arc(calibrationLine.startX, calibrationLine.startY, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(calibrationLine.endX, calibrationLine.endY, 5, 0, Math.PI * 2);
      ctx.fill();
      
      // Calculate angle for display
      const dx = calibrationLine.endX - calibrationLine.startX;
      const dy = calibrationLine.endY - calibrationLine.startY;
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);
      const displayAngle = Math.round(angle);
      
      // Draw percentage and angle label
      const midX = (calibrationLine.startX + calibrationLine.endX) / 2;
      const midY = (calibrationLine.startY + calibrationLine.endY) / 2;
      const text = isGridSnapActive 
        ? `${calibrationLine.percentage}% (${displayAngle}°)`
        : `${calibrationLine.percentage}%`;
      
      ctx.font = 'bold 16px Arial';
      ctx.fillStyle = 'white';
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 4;
      ctx.strokeText(text, midX, midY - 10);
      ctx.fillText(text, midX, midY - 10);
      
      ctx.restore();
    }
    
    } // End of non-detection-mode drawing

    // Draw hovered PDF element highlight (always drawn, even in detection mode)
    if (isElementDetectionMode && hoveredPDFElement) {
      ctx.save();
      
      // Different colors for different element types
      let highlightColor = 'rgba(255, 215, 0, 0.9)'; // Gold for paths/lines
      let fillColor = 'rgba(255, 215, 0, 0.2)';
      
      if (hoveredPDFElement.type === 'xobject') {
        highlightColor = 'rgba(255, 0, 255, 0.9)'; // Magenta for symbols!
        fillColor = 'rgba(255, 0, 255, 0.3)';
      } else if (hoveredPDFElement.type === 'text') {
        highlightColor = 'rgba(0, 255, 255, 0.9)'; // Cyan for text
        fillColor = 'rgba(0, 255, 255, 0.2)';
      }
      
      ctx.strokeStyle = highlightColor;
      ctx.lineWidth = 5;
      ctx.shadowColor = highlightColor.replace('0.9', '0.6');
      ctx.shadowBlur = 20;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Draw bounding box for all elements (SVG gives us perfect bounds!)
      const { x, y, width, height } = hoveredPDFElement.bounds;
      
      // Draw filled highlight
      ctx.fillStyle = fillColor;
      ctx.fillRect(x, y, width, height);
      
      // Draw outline
      ctx.strokeRect(x, y, width, height);
      
      // Show label above the box for symbols and text
      if (hoveredPDFElement.text || hoveredPDFElement.objectName) {
        const label = hoveredPDFElement.text || hoveredPDFElement.objectName || '';
        ctx.font = 'bold 16px Arial';
        ctx.fillStyle = 'white';
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.lineWidth = 3;
        ctx.strokeText(label.substring(0, 40), x, y - 10);
        ctx.fillText(label.substring(0, 40), x, y - 10);
      }
      
      ctx.restore();
    }

    // No restore needed since we're not applying rotation transforms
    
    // DEBUG: Draw red tile grid overlay if toggle is on
    // If no count has been run yet, compute default 10x10 lighting grid from canvas dimensions
    if (showDebugTileGrid && !debugTileGridRef.current && canvas) {
      const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      if (pdfCanvas) {
        // Estimate hi-res dimensions based on MAX_WIDTH = 16000
        const aspect = pdfCanvas.height / pdfCanvas.width;
        const hrW = 16000;
        const hrH = Math.round(hrW * aspect);
        const gs = 10;
        const tw = Math.floor(hrW / gs);
        const th = Math.floor(hrH / gs);
        const ov = Math.floor(tw * 0.15);
        debugTileGridRef.current = { gridSize: gs, tileWidth: tw, tileHeight: th, overlap: ov, hiResWidth: hrW, hiResHeight: hrH };
      }
    }
    const gridParams = debugTileGridRef.current;
    if (showDebugTileGrid && gridParams && canvas) {
      const { gridSize: gs, tileWidth: tw, tileHeight: th, overlap: ov, hiResWidth: hrW, hiResHeight: hrH } = gridParams;
      const sX = canvas.width / hrW;
      const sY = canvas.height / hrH;
      
      ctx.save();
      // Draw base grid lines (solid red)
      ctx.strokeStyle = 'red';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      for (let i = 1; i < gs; i++) {
        const vx = Math.round(i * tw * sX);
        ctx.beginPath(); ctx.moveTo(vx, 0); ctx.lineTo(vx, canvas.height); ctx.stroke();
        const hy = Math.round(i * th * sY);
        ctx.beginPath(); ctx.moveTo(0, hy); ctx.lineTo(canvas.width, hy); ctx.stroke();
      }
      
      // Draw overlap zones (dashed red, semi-transparent)
      if (ov > 0) {
        ctx.strokeStyle = 'rgba(255, 0, 0, 0.4)';
        ctx.lineWidth = 1;
        ctx.setLineDash([6, 4]);
        const ovX = ov * sX;
        const ovY = ov * sY;
        for (let i = 1; i < gs; i++) {
          const vx = Math.round(i * tw * sX);
          ctx.beginPath(); ctx.moveTo(vx - ovX, 0); ctx.lineTo(vx - ovX, canvas.height); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(vx + ovX, 0); ctx.lineTo(vx + ovX, canvas.height); ctx.stroke();
          const hy = Math.round(i * th * sY);
          ctx.beginPath(); ctx.moveTo(0, hy - ovY); ctx.lineTo(canvas.width, hy - ovY); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(0, hy + ovY); ctx.lineTo(canvas.width, hy + ovY); ctx.stroke();
        }
      }
      
      // Draw tile labels
      ctx.setLineDash([]);
      ctx.font = 'bold 14px monospace';
      ctx.fillStyle = 'red';
      ctx.textBaseline = 'top';

      const drawPass = (offsetX: number, offsetY: number, passColor: string, passLabel: string) => {
        const cols = Math.ceil((hrW - offsetX) / tw);
        const rows = Math.ceil((hrH - offsetY) / th);

        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const baseLeft = c * tw + offsetX;
            const baseTop = r * th + offsetY;
            if (baseLeft >= hrW || baseTop >= hrH) continue;

            const lx = Math.round(baseLeft * sX);
            const ly = Math.round(baseTop * sY);
            const bw = Math.round(Math.min(hrW - baseLeft, tw) * sX);
            const bh = Math.round(Math.min(hrH - baseTop, th) * sY);

            // Draw tile boundary
            ctx.strokeStyle = passColor;
            ctx.lineWidth = passLabel === 'P1' ? 2 : 1;
            if (passLabel === 'P2') ctx.setLineDash([4, 4]);
            else ctx.setLineDash([]);
            ctx.strokeRect(lx, ly, bw, bh);

            // Draw overlap zones for this tile
            if (ov > 0) {
              const rgba = passColor.replace('rgb', 'rgba').replace(')', ', 0.3)');
              ctx.strokeStyle = rgba;
              ctx.lineWidth = 1;
              ctx.setLineDash([2, 2]);
              
              const cLeft = Math.max(0, baseLeft - ov) * sX;
              const cTop = Math.max(0, baseTop - ov) * sY;
              const cRight = Math.min(hrW, baseLeft + tw + ov) * sX;
              const cBottom = Math.min(hrH, baseTop + th + ov) * sY;
              
              ctx.strokeRect(cLeft, cTop, cRight - cLeft, cBottom - cTop);
            }

            // Draw label
            const label = `${passLabel}:R${r}C${c}`;
            const metrics = ctx.measureText(label);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.setLineDash([]);
            ctx.fillRect(lx + 2, ly + 2, metrics.width + 4, 16);
            ctx.fillStyle = passColor;
            ctx.fillText(label, lx + 4, ly + 4);
          }
        }
      };

      // Pass 1: Red
      drawPass(0, 0, 'rgb(255, 0, 0)', 'P1');

      // Pass 2: Blue (if applicable)
      if (ov > 0) {
        drawPass(Math.floor(tw / 2), Math.floor(th / 2), 'rgb(0, 0, 255)', 'P2');
      }

      ctx.restore();
    }
  }, [annotations, pageNumber, selectedAnnotation, isDrawing, currentAnnotation, currentColor, thickness, highlightedComponentType, viewAllMode, animatingAnnotations, annotationTimestamps, selectionRect, isSelecting, selectedAnnotations, isCapturingSnapshot, snapshotSelection, calibrationLine, isCalibrating, isGridSnapActive, calibrationData, getColorForAnnotation, getAnimationScale, pipingPreview, pipingSnapTarget, currentTool, calculatePipeMeasurement, isElementDetectionMode, hoveredPDFElement, autocountSelection, autocountMode, keyExtractionSelection, keyExtractionMode, lightingScheduleSelection, lightingScheduleMode, showDebugTileGrid, rescaleAnnotation]);

  // Redraw canvas when annotations, scale, electrical key, filter, or forceRedraw change
  useEffect(() => {
    drawAnnotations();
  }, [drawAnnotations, scale, annotations, electricalKey, forceRedraw]); // Added electricalKey and forceRedraw dependencies

  // Sync annotation canvas dimensions with PDF canvas using MutationObserver
  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let syncTimeout: NodeJS.Timeout | null = null;

    const syncCanvasDimensions = () => {
      const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      
      if (pdfCanvas && canvasRef.current) {
        // Reduced logging for performance - only log when dimensions actually change
        const needsSync = canvasRef.current.width !== pdfCanvas.width || 
                         canvasRef.current.height !== pdfCanvas.height;
        
        // Check if dimensions actually changed
        if (needsSync) {
          
          console.log('📐 PDF canvas dimensions changed, syncing annotation canvas:', {
            oldDimensions: {
              width: canvasRef.current.width,
              height: canvasRef.current.height
            },
            newDimensions: {
              width: pdfCanvas.width,
              height: pdfCanvas.height
            },
            rotation: pageRotations[pageNumber] || 0
          });
          
          // Match the PDF canvas dimensions exactly
          canvasRef.current.width = pdfCanvas.width;
          canvasRef.current.height = pdfCanvas.height;
          
          // CRITICAL: Also match the visual display size
          // This ensures the annotation canvas overlays the PDF exactly
          const pdfStyles = window.getComputedStyle(pdfCanvas);
          canvasRef.current.style.width = pdfStyles.width;
          canvasRef.current.style.height = pdfStyles.height;
          
          console.log('📏 Syncing visual dimensions:', {
            canvasInternalDimensions: { width: pdfCanvas.width, height: pdfCanvas.height },
            canvasVisualDimensions: { width: pdfStyles.width, height: pdfStyles.height }
          });
          
          // Update tracked dimensions
          setCurrentCanvasWidth(pdfCanvas.width);
          setCurrentCanvasHeight(pdfCanvas.height);
          
          // Recalculate annotation positions locally from relative coords
          // (avoids a Supabase round-trip on every canvas resize)
          const newW = pdfCanvas.width;
          const newH = pdfCanvas.height;
          setAnnotations(prev => prev.map(ann => rescaleAnnotation(ann, newW, newH)));
          
          // Mark canvas as synced
          setIsCanvasSynced(true);
        } else {
          // Canvas is already synced
          setIsCanvasSynced(true);
        }
      }
    };

    // Watch for PDF canvas being added/changed in DOM
    mutationObserver = new MutationObserver((mutations) => {
      // Debounce to avoid multiple rapid syncs
      if (syncTimeout) clearTimeout(syncTimeout);
      syncTimeout = setTimeout(() => {
        syncCanvasDimensions();
        
        // Also set up ResizeObserver on the PDF canvas once it exists
        const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
        if (pdfCanvas && !resizeObserver) {
          resizeObserver = new ResizeObserver(() => {
            syncCanvasDimensions();
          });
          resizeObserver.observe(pdfCanvas);
        }
      }, 50); // Small debounce delay
    });

    // Start observing the container for canvas changes
    if (containerRef.current) {
      mutationObserver.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['width', 'height', 'style']
      });
    }

    // Initial sync attempt
    syncCanvasDimensions();

    // Cleanup
    return () => {
      if (syncTimeout) clearTimeout(syncTimeout);
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
    };
  }, [pageNumber, pageRotations, drawAnnotations]);

  // Animation loop for smooth number animations
  useEffect(() => {
    if (animatingAnnotations.size > 0) {
      let animationId: number;
      let isRunning = true;

      const animate = () => {
        if (isRunning && animatingAnnotations.size > 0) {
          drawAnnotations();
          animationId = requestAnimationFrame(animate);
        }
      };

      animationId = requestAnimationFrame(animate);

      return () => {
        isRunning = false;
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    }
  }, [animatingAnnotations.size, drawAnnotations]);

  // Animation loop for piping snap target pulsing
  useEffect(() => {
    if (pipingSnapTarget) {
      let animationId: number;
      let isRunning = true;

      const animate = () => {
        if (isRunning && pipingSnapTarget) {
          drawAnnotations();
          animationId = requestAnimationFrame(animate);
        }
      };

      animationId = requestAnimationFrame(animate);

      return () => {
        isRunning = false;
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    }
  }, [pipingSnapTarget, drawAnnotations]);

  // Keyboard shortcuts moved after deleteSelected definition

  // Handle mouse down
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isElementDetectionMode) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const canvasRect = canvas.getBoundingClientRect();
    const isOnClickCanvas = (
      e.clientX >= canvasRect.left &&
      e.clientX <= canvasRect.right &&
      e.clientY >= canvasRect.top &&
      e.clientY <= canvasRect.bottom
    );

    const { x, y } = getScreenToCanvasCoords(e.clientX, e.clientY);

    if (currentTool !== 'select' && currentTool !== 'drag' && hoveredAnnotation) {
      setHoveredAnnotation(null);
      setHoveredAnnotationPoint(null);
    }
    
    // Middle mouse or Spacebar (handled via tool === 'drag') or Right-click pan
    // OR: Left click on the background (not on canvas)
    if ((e.button === 2 || e.button === 1 || currentTool === 'drag' || (!isOnClickCanvas && e.button === 0)) && !isWireRunMode) {
      if (e.button === 2) {
        const hitAnnotation = findAnnotationAtPoint(x, y);
        if (hitAnnotation) return;
      }
      e.preventDefault();
      if (e.button === 2) setIsRightClickPanning(true);
      setIsPanning(true);
      setPanStart({
        x: e.clientX,
        y: e.clientY,
        panX: panX,
        panY: panY
      });
      return;
    }

    // Only proceed with drawing/selection if we clicked on the canvas
    if (!isOnClickCanvas) return;


    // Initialize scan/extraction drag-selection modes before normal select behavior.
    // Without this, clicks fall through to selection/highlight logic instead of scan capture.
    if (autocountMode === 'area-selection') {
      setAutocountSelection({
        startX: x,
        startY: y,
        endX: x,
        endY: y
      });
      return;
    }

    if (keyExtractionMode) {
      setKeyExtractionSelection({
        startX: x,
        startY: y,
        endX: x,
        endY: y
      });
      return;
    }

    if (lightingScheduleMode) {
      setLightingScheduleSelection({
        startX: x,
        startY: y,
        endX: x,
        endY: y
      });
      return;
    }

    // Log click coordinates (but don't set them - they're tracked on hover now)
    console.log('🎯 Click at canvas coordinates:', { x: Math.round(x), y: Math.round(y) });
    
    // Handle calibration tool
    console.log('🔍 Mouse down - isCalibrating:', isCalibrating, 'handDrawnEnabled:', handDrawnEnabled);
    
    if (isCalibrating) {
      console.log('📏 Calibration: Starting line at', { x: Math.round(x), y: Math.round(y) });
      setCalibrationLine({
        startX: x,
        startY: y,
        endX: x,
        endY: y,
        percentage: 0
      });
      setIsDrawing(true);
      console.log('✅ Calibration line started, now drag to set endpoint');
      return;
    }
    
    // Handle piping tool
    if (currentTool === 'piping') {
      if (!pipingStart) {
        // Start piping - must click on an annotation
        const annotation = findAnnotationAtPoint(x, y);
        if (annotation) {
          setPipingStart({
            annotationId: annotation.id,
            x: annotation.centerX,
            y: annotation.centerY
          });
          setPipingPreview({
            startX: annotation.centerX,
            startY: annotation.centerY,
            endX: annotation.centerX,
            endY: annotation.centerY
          });
          setPipingSnapTarget(null);
          console.log('🔧 Started piping from annotation:', annotation.id);
        }
      } else {
        // End piping - use snap target if available, otherwise try to find annotation at click point
        let targetAnnotation = pipingSnapTarget ? {
          id: pipingSnapTarget.annotationId,
          centerX: pipingSnapTarget.x,
          centerY: pipingSnapTarget.y
        } : findAnnotationAtPoint(x, y);
        
        if (targetAnnotation && targetAnnotation.id !== pipingStart.annotationId) {
          // End piping at this annotation
          const dx = targetAnnotation.centerX - pipingStart.x;
          const dy = targetAnnotation.centerY - pipingStart.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          const measurement = calculatePipeMeasurement(distance);
          
          const newPipe: Annotation = {
            id: `pipe-${Date.now()}`,
            type: 'piping',
            page: pageNumber,
            startX: pipingStart.x,
            startY: pipingStart.y,
            endX: targetAnnotation.centerX,
            endY: targetAnnotation.centerY,
            color: 'rgb(220, 38, 38)', // Red color for pipes
            thickness: 4,
            timestamp: new Date().toISOString(),
            startAnnotationId: pipingStart.annotationId,
            endAnnotationId: targetAnnotation.id,
            measurement: measurement?.value,
            measurementUnit: measurement?.unit
          };
          
          const newAnnotations = [...annotationsRef.current, newPipe];
          annotationsRef.current = newAnnotations;
          setAnnotations(newAnnotations);
          addToHistory(newAnnotations);
          
          // Reset piping state
          setPipingStart(null);
          setPipingPreview(null);
          setPipingSnapTarget(null);
          
          console.log('🔧 Created pipe:', measurement ? `${measurement.value}${measurement.unit}` : 'no calibration');
        }
      }
      return; // Don't process other interactions in piping mode
    }
    
    // If snapshot actions are showing and user clicks outside, cancel
    if (showSnapshotActions && snapshotSelection) {
      const isInsideSelection = 
        x >= Math.min(snapshotSelection.startX, snapshotSelection.endX) &&
        x <= Math.max(snapshotSelection.startX, snapshotSelection.endX) &&
        y >= Math.min(snapshotSelection.startY, snapshotSelection.endY) &&
        y <= Math.max(snapshotSelection.startY, snapshotSelection.endY);
      
      if (!isInsideSelection) {
        handleCancelSnapshot();
        return;
      }
    }

    // Clear any existing snapshot selection when clicking with a different tool
    if (currentTool !== 'snapshot' && snapshotSelection) {
      setSnapshotSelection(null);
    }

    // Close context menus if open
    if (wireRunContextMenu) {
      setWireRunContextMenu(null);
    }

    // UNIFIED SELECT: Handle component placement when in counting mode with select tool
    // Note: User can freely switch to other tools (pen, highlighter, etc.) without placing components
    // Now supports click-and-drag to create rectangles for better bounding box placement
    console.log('🔍 Placement check:', { 
      activePlacementComponent: activePlacementComponent?.name, 
      currentTool, 
      countingModeActive 
    });
    if (activePlacementComponent && currentTool === 'select' && countingModeActive) {
      console.log('✅ Starting component rectangle drawing!');
      
      // Start drawing a rectangle for component placement
      setIsDrawing(true);
      // For lighting fixtures, use symbol as component_type (unique per fixture)
      const placementComponentType = activePlacementComponent.symbolType === 'Lighting'
        ? (activePlacementComponent.symbol || activePlacementComponent.name)
        : activePlacementComponent.name;
      setCurrentAnnotation({
        id: `manual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'rectangle',
        startX: x,
        startY: y,
        width: 0,
        height: 0,
        color: activePlacementComponent.color,
        thickness: 3,
        timestamp: new Date().toISOString(),
        page: pageNumber,
        component_type: placementComponentType
      });
      
      return; // Don't process other select interactions
    }

    if (currentTool === 'select') {
      const canvas = canvasRef.current;
      const cw = canvas?.width || 16000;
      const ch = canvas?.height || 10666;

      // Check if clicking on a resize handle of a selected rectangle
      // Only check visible annotations (matching highlightedComponentType if set)
      const handleSize = 10;
      const selectedRectangles = annotations.filter(a => 
        a.page === pageNumber && 
        a.type === 'rectangle' && 
        (selectedAnnotations.has(a.id) || a.id === selectedAnnotation) &&
        (!highlightedComponentType || a.component_type === highlightedComponentType)
      ).map(a => rescaleAnnotation(a, cw, ch));
      
      for (const rect of selectedRectangles) {
        const handles = [
          { key: 'nw' as const, x: rect.startX, y: rect.startY },
          { key: 'ne' as const, x: rect.startX + (rect.width || 0), y: rect.startY },
          { key: 'sw' as const, x: rect.startX, y: rect.startY + (rect.height || 0) },
          { key: 'se' as const, x: rect.startX + (rect.width || 0), y: rect.startY + (rect.height || 0) },
        ];
        
        for (const handle of handles) {
          if (Math.abs(x - handle.x) < handleSize && Math.abs(y - handle.y) < handleSize) {
            // Clicked on a resize handle
            console.log('🔲 Started resizing rectangle from handle:', handle.key);
            setResizeHandle({
              annotationId: rect.id,
              handle: handle.key,
              startX: x,
              startY: y,
              origRect: {
                startX: rect.startX,
                startY: rect.startY,
                width: rect.width || 0,
                height: rect.height || 0
              }
            });
            return; // Don't process other interactions
          }
        }
      }
      
      // UNIFIED SELECT: Works on both counting annotations AND hand-drawn annotations
      // First check if clicking on ANY annotation (for precise selection)
      // Filter out hidden annotations (those not matching highlightedComponentType)
      const hitCanvas = canvasRef.current;
      const hitCw = hitCanvas?.width || 16000;
      const hitCh = hitCanvas?.height || 10666;
      const clickedAnnotation = annotations
        .filter(a => a.page === pageNumber)
        .filter(a => {
          // If no component type is highlighted, all annotations are visible
          if (!highlightedComponentType) return true;
          
          // For pipes, check if either connected annotation matches
          if (a.type === 'piping') {
            const startAnn = annotations.find(ann => ann.id === a.startAnnotationId);
            const endAnn = annotations.find(ann => ann.id === a.endAnnotationId);
            return (startAnn?.component_type === highlightedComponentType) ||
                   (endAnn?.component_type === highlightedComponentType);
          }
          
          // For other annotations, check if component_type matches
          return a.component_type === highlightedComponentType;
        })
        .map(a => rescaleAnnotation(a, hitCw, hitCh))
        .find(a => {
          if (a.type === 'rectangle' || a.type === 'circle') {
            return isPointInAnnotation(a, x, y, HIT_BOX_PADDING);
          }
          if (a.type === 'line' || a.type === 'piping') {
            const dist = pointToLineDistance(x, y, a.startX, a.startY, a.endX || a.startX, a.endY || a.startY);
            return dist < HIT_BOX_PADDING;
          }
          if (a.type === 'pen' && a.points) {
            for (let i = 1; i < a.points.length; i++) {
              const dist = pointToLineDistance(x, y, a.points[i-1].x, a.points[i-1].y, a.points[i].x, a.points[i].y);
              if (dist < HIT_BOX_PADDING) return true;
            }
          }
          // Wire run detection
          if (a.type === 'wire-run' && a.points && a.points.length >= 2) {
            for (let i = 0; i < a.points.length - 1; i++) {
              const dist = pointToLineDistance(x, y, a.points[i].x, a.points[i].y, a.points[i+1].x, a.points[i+1].y);
              if (dist < HIT_BOX_PADDING) return true;
            }
          }
          return false;
        });

      if (clickedAnnotation) {
        // Check if it's a wire run - switch to Wires tab
        if (clickedAnnotation.type === 'wire-run' && onSwitchToWiresTab) {
          console.log('🔌 Wire run clicked, switching to Wires tab');
          onSwitchToWiresTab();
        }
        
        // Check if clicking on an already selected annotation (to move the group)
        if (selectedAnnotations.has(clickedAnnotation.id) && selectedAnnotations.size > 0) {
          // Start dragging the selected group
          setDragStart({ x, y });
        } else {
          // Precise single selection - select just this annotation
          setSelectedAnnotation(clickedAnnotation.id);
          setSelectedAnnotations(new Set([clickedAnnotation.id]));
          setDragStart({ x, y }); // Allow immediate dragging of the newly selected item
        }
      } else {
        // No annotation clicked - start selection rectangle
        setIsSelecting(true);
        setSelectionRect({ startX: x, startY: y, endX: x, endY: y });
        setSelectedAnnotations(new Set()); // Clear previous selection
        setSelectedAnnotation(null);
      }
    } else if (currentTool === 'drag' && e.button === 0) {
      // Check if clicking on an annotation to move it (skip pipes and hidden annotations)
      const clickedAnnotation = annotations
        .filter(a => a.page === pageNumber)
        .filter(a => {
          // Filter out hidden annotations (not matching highlightedComponentType)
          if (!highlightedComponentType) return true;
          return a.component_type === highlightedComponentType;
        })
        .find(a => {
          // Skip pipes - they can't be dragged
          if (a.type === 'piping') return false;
          
          if (a.type === 'rectangle' || a.type === 'circle') {
            return isPointInAnnotation(a, x, y, 0);
          }
          if (a.type === 'line') {
            // Check if click is near the line (within 5 pixels)
            const dist = pointToLineDistance(
              x, y,
              a.startX, a.startY,
              a.endX || a.startX, a.endY || a.startY
            );
            return dist < 5;
          }
          if (a.type === 'pen' && a.points) {
            // Check if click is near any segment of the pen path
            for (let i = 1; i < a.points.length; i++) {
              const dist = pointToLineDistance(
                x, y,
                a.points[i-1].x, a.points[i-1].y,
                a.points[i].x, a.points[i].y
              );
              if (dist < 5) return true;
            }
          }
          return false;
        });

      if (clickedAnnotation) {
        setSelectedAnnotation(clickedAnnotation.id);
        setDragStart({ x, y });
      } else {
        // Handled by the generic pan logic at top of function
        setSelectedAnnotation(null);
        setIsPanning(true);
        setPanStart({
          x: e.clientX,
          y: e.clientY,
          panX: panX,
          panY: panY
        });
      }
    } else if (currentTool === 'snapshot') {
      // Start snapshot selection
      setIsCapturingSnapshot(true);
      setSnapshotSelection({
        startX: x,
        startY: y,
        endX: x,
        endY: y
      });
    } else {
      // Start drawing new annotation
      setIsDrawing(true);
      const newAnnotation: Partial<Annotation> = {
        id: `annotation-${Date.now()}`,
        type: currentTool,
        page: pageNumber,
        startX: x,
        startY: y,
        color: currentColor,
        thickness: thickness,
        timestamp: new Date().toISOString()
      };

      if (currentTool === 'pen') {
        newAnnotation.points = [{ x, y }];
      }

      setCurrentAnnotation(newAnnotation);
    }
  };

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    // Track last mouse position for keyboard zoom
    lastMousePosRef.current = { x: e.clientX, y: e.clientY };

    if (isPanning && panStart) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      setPanX(panStart.panX + dx);
      setPanY(panStart.panY + dy);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getScreenToCanvasCoords(e.clientX, e.clientY);


    // Handle rectangle resizing
    if (resizeHandle) {
      const { annotationId, handle, origRect } = resizeHandle;
      const dx = x - resizeHandle.startX;
      const dy = y - resizeHandle.startY;
      
      setAnnotations(prev => prev.map(ann => {
        if (ann.id !== annotationId) return ann;
        
        let newStartX = origRect.startX;
        let newStartY = origRect.startY;
        let newWidth = origRect.width;
        let newHeight = origRect.height;
        
        // Apply resize based on which handle is being dragged
        switch (handle) {
          case 'nw': // top-left: move start, adjust size
            newStartX = origRect.startX + dx;
            newStartY = origRect.startY + dy;
            newWidth = origRect.width - dx;
            newHeight = origRect.height - dy;
            break;
          case 'ne': // top-right: move startY, adjust width
            newStartY = origRect.startY + dy;
            newWidth = origRect.width + dx;
            newHeight = origRect.height - dy;
            break;
          case 'sw': // bottom-left: move startX, adjust height
            newStartX = origRect.startX + dx;
            newWidth = origRect.width - dx;
            newHeight = origRect.height + dy;
            break;
          case 'se': // bottom-right: just adjust size
            newWidth = origRect.width + dx;
            newHeight = origRect.height + dy;
            break;
        }
        
        // Ensure minimum size
        if (newWidth < 20) {
          if (handle === 'nw' || handle === 'sw') {
            newStartX = origRect.startX + origRect.width - 20;
          }
          newWidth = 20;
        }
        if (newHeight < 20) {
          if (handle === 'nw' || handle === 'ne') {
            newStartY = origRect.startY + origRect.height - 20;
          }
          newHeight = 20;
        }
        
        return {
          ...ann,
          startX: newStartX,
          startY: newStartY,
          width: newWidth,
          height: newHeight,
          // Clear stale relative coords so save/rescale recomputes from new absolute positions
          relativeStartX: undefined,
          relativeStartY: undefined,
          relativeWidth: undefined,
          relativeHeight: undefined
        };
      }));
      return;
    }
    
    // Handle autocount area selection dragging
    if (autocountMode === 'area-selection' && autocountSelection) {
      setAutocountSelection({
        ...autocountSelection,
        endX: x,
        endY: y
      });
      return;
    }
    
    // Handle key extraction area selection dragging
    if (keyExtractionMode && keyExtractionSelection) {
      setKeyExtractionSelection({
        ...keyExtractionSelection,
        endX: x,
        endY: y
      });
      return;
    }

    // Handle lighting schedule area selection dragging
    if (lightingScheduleMode && lightingScheduleSelection) {
      setLightingScheduleSelection({
        ...lightingScheduleSelection,
        endX: x,
        endY: y
      });
      return;
    }
    
    // Check if hovering over a resize handle (for cursor change)
    // Only check visible annotations (matching highlightedComponentType if set)
    if (currentTool === 'select' && !resizeHandle) {
      const handleSize = 12;
      let foundCursor = 'default';
      
      const selectedRectangles = annotations.filter(a => 
        a.page === pageNumber && 
        a.type === 'rectangle' && 
        (selectedAnnotations.has(a.id) || a.id === selectedAnnotation) &&
        (!highlightedComponentType || a.component_type === highlightedComponentType)
      );
      
      for (const rect of selectedRectangles) {
        const handles = [
          { key: 'nw', x: rect.startX, y: rect.startY, cursor: 'nw-resize' },
          { key: 'ne', x: rect.startX + (rect.width || 0), y: rect.startY, cursor: 'ne-resize' },
          { key: 'sw', x: rect.startX, y: rect.startY + (rect.height || 0), cursor: 'sw-resize' },
          { key: 'se', x: rect.startX + (rect.width || 0), y: rect.startY + (rect.height || 0), cursor: 'se-resize' },
        ];
        
        for (const handle of handles) {
          if (Math.abs(x - handle.x) < handleSize && Math.abs(y - handle.y) < handleSize) {
            foundCursor = handle.cursor;
            break;
          }
        }
        if (foundCursor !== 'default') break;
      }
      
      if (foundCursor !== hoverCursor) {
        setHoverCursor(foundCursor);
      }
    }

    // Update hover coordinates - show visual coords (not canvas coords)
    // This gives users coordinates that match what they see on screen
    const displayCoords = canvasToVisualCoords(x, y);
    setClickCoords({ 
      x: Math.round(displayCoords.x / scale), // Remove zoom effect for display
      y: Math.round(displayCoords.y / scale)
    });

    // Element detection mode - check for PDF vectors
    if (isElementDetectionMode) {
      const hoveredElement = findPDFElementAtPoint(x, y, pdfVectorElements);
      setHoveredPDFElement(hoveredElement);
      return; // Don't process other interactions
    }

    // Handle piping preview with magnetic snapping
    // Only snap to visible annotations (matching highlightedComponentType if set)
    if (currentTool === 'piping' && pipingStart && pipingPreview) {
      const SNAP_DISTANCE = 100; // pixels - distance from center to snap
      
      // Find the closest annotation center
      let snapTarget: { annotationId: string; x: number; y: number } | null = null;
      let closestDistance = SNAP_DISTANCE;
      
      const pageAnns = annotations.filter(a => {
        if (a.page !== pageNumber) return false;
        if (!highlightedComponentType) return true;
        return a.component_type === highlightedComponentType;
      });
      
      for (const ann of pageAnns) {
        // Skip the start annotation
        if (ann.id === pipingStart.annotationId) continue;
        
        // Skip non-shape annotations
        if (ann.type !== 'circle' && ann.type !== 'rectangle') continue;
        
        // Get annotation center
        let centerX: number, centerY: number;
        
        if (ann.type === 'circle') {
          if (ann.x !== undefined && ann.y !== undefined) {
            centerX = ann.x;
            centerY = ann.y;
          } else {
            centerX = ann.startX + (ann.width || 0) / 2;
            centerY = ann.startY + (ann.height || 0) / 2;
          }
        } else if (ann.type === 'rectangle') {
          centerX = ann.startX + (ann.width || 0) / 2;
          centerY = ann.startY + (ann.height || 0) / 2;
        } else {
          continue;
        }
        
        // Calculate distance from mouse to this center
        const dx = x - centerX;
        const dy = y - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // If this is the closest annotation within snap distance, use it
        if (distance < closestDistance) {
          closestDistance = distance;
          snapTarget = {
            annotationId: ann.id,
            x: centerX,
            y: centerY
          };
        }
      }
      
      setPipingSnapTarget(snapTarget);
      setPipingPreview({
        ...pipingPreview,
        endX: snapTarget ? snapTarget.x : x,
        endY: snapTarget ? snapTarget.y : y
      });
      return;
    }

    // Handle calibration line drawing
    if (isCalibrating && isDrawing && calibrationLine) {
      let endX = x;
      let endY = y;
      
      // Snap to angles when Command/Ctrl key is held (like Photoshop)
      if (e.metaKey || e.ctrlKey) {
        const dx = x - calibrationLine.startX;
        const dy = y - calibrationLine.startY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        
        // Snap to nearest 15-degree angle (0, 15, 30, 45, 60, 75, 90, etc.)
        const snapAngle = Math.round(angle / 15) * 15;
        const radians = snapAngle * (Math.PI / 180);
        
        // Calculate snapped position
        endX = calibrationLine.startX + distance * Math.cos(radians);
        endY = calibrationLine.startY + distance * Math.sin(radians);
      }
      
      const percentage = calculateCanvasPercentage(
        calibrationLine.startX,
        calibrationLine.startY,
        endX,
        endY
      );
      
      setCalibrationLine({
        ...calibrationLine,
        endX,
        endY,
        percentage
      });
      return;
    }

    // Handle selection rectangle dragging
    if (currentTool === 'select' && isSelecting && selectionRect) {
      setSelectionRect({ ...selectionRect, endX: x, endY: y });

      // Check which annotations are within the selection rectangle
      // Only consider visible annotations (matching highlightedComponentType if set)
      const minX = Math.min(selectionRect.startX, x);
      const maxX = Math.max(selectionRect.startX, x);
      const minY = Math.min(selectionRect.startY, y);
      const maxY = Math.max(selectionRect.startY, y);

      const selected = new Set<string>();
      annotations
        .filter(a => a.page === pageNumber)
        .filter(a => {
          if (!highlightedComponentType) return true;
          if (a.type === 'piping') {
            const startAnn = annotations.find(ann => ann.id === a.startAnnotationId);
            const endAnn = annotations.find(ann => ann.id === a.endAnnotationId);
            return (startAnn?.component_type === highlightedComponentType) ||
                   (endAnn?.component_type === highlightedComponentType);
          }
          return a.component_type === highlightedComponentType;
        })
        .forEach(a => {
          let isInside = false;

          if (a.type === 'rectangle' || a.type === 'circle') {
            // Check if annotation overlaps with selection box
            // For radius-based circles, check if any point in selection box is inside
            if (a.radius) {
              const centerX = a.x || a.startX;
              const centerY = a.y || a.startY;
              // Check if center is in selection or if selection overlaps circle
              const closestX = Math.max(minX, Math.min(maxX, centerX));
              const closestY = Math.max(minY, Math.min(maxY, centerY));
              const distance = Math.sqrt(Math.pow(closestX - centerX, 2) + Math.pow(closestY - centerY, 2));
              isInside = distance <= a.radius;
            } else {
              const aMinX = Math.min(a.startX, a.startX + (a.width || 0));
              const aMaxX = Math.max(a.startX, a.startX + (a.width || 0));
              const aMinY = Math.min(a.startY, a.startY + (a.height || 0));
              const aMaxY = Math.max(a.startY, a.startY + (a.height || 0));
              isInside = !(aMaxX < minX || aMinX > maxX || aMaxY < minY || aMinY > maxY);
            }
          } else if (a.type === 'line') {
            // Check if either endpoint is within selection
            isInside = (a.startX >= minX && a.startX <= maxX && a.startY >= minY && a.startY <= maxY) ||
                      ((a.endX || 0) >= minX && (a.endX || 0) <= maxX && (a.endY || 0) >= minY && (a.endY || 0) <= maxY);
          } else if (a.type === 'pen' && a.points) {
            // Check if any point is within selection
            isInside = a.points.some(p => p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY);
          }

          if (isInside) {
            selected.add(a.id);
          }
        });

      setSelectedAnnotations(selected);
      return;
    }

    // Handle dragging multiple selected annotations
    if (currentTool === 'select' && dragStart && selectedAnnotations.size > 0 && !isSelecting) {
      const dx = x - dragStart.x;
      const dy = y - dragStart.y;

      setAnnotations(prev => {
        // Track which annotations actually moved (exclude pipes)
        const movedAnnotationIds = new Set<string>();
        
        let updated = prev.map(a => {
          if (selectedAnnotations.has(a.id) && a.type !== 'piping') {
            movedAnnotationIds.add(a.id);
            
            const updatedAnn: Annotation = {
              ...a,
              startX: a.startX + dx,
              startY: a.startY + dy,
              // Clear stale relative coords so save/rescale recomputes from new absolute positions
              relativeStartX: undefined,
              relativeStartY: undefined,
              // AI-generated annotations: also update center point coordinates
              x: a.x !== undefined ? a.x + dx : undefined,
              y: a.y !== undefined ? a.y + dy : undefined,
              relativeX: a.x !== undefined ? undefined : a.relativeX,
              relativeY: a.y !== undefined ? undefined : a.relativeY,
            };

            if (a.endX !== undefined) {
              updatedAnn.endX = a.endX + dx;
            }
            if (a.endY !== undefined) {
              updatedAnn.endY = a.endY + dy;
            }

            if (a.type === 'pen' && a.points) {
              updatedAnn.points = a.points.map(p => ({
                x: p.x + dx,
                y: p.y + dy
              }));
            }

            return updatedAnn;
          }
          return a;
        });
        
        // Update pipes connected to moved annotations
        updated = updateConnectedPipes(updated, movedAnnotationIds);
        return updated;
      });

      setDragStart({ x, y });
      return;
    }

    // Check for hover over annotations when in select mode
    // Only hover over visible annotations (matching highlightedComponentType if set)
    if (currentTool === 'select' && !dragStart && !isSelecting) {
      const hoveredAnno = annotations
        .filter(a => a.page === pageNumber)
        .filter(a => {
          if (!highlightedComponentType) return true;
          if (a.type === 'piping') {
            const startAnn = annotations.find(ann => ann.id === a.startAnnotationId);
            const endAnn = annotations.find(ann => ann.id === a.endAnnotationId);
            return (startAnn?.component_type === highlightedComponentType) ||
                   (endAnn?.component_type === highlightedComponentType);
          }
          return a.component_type === highlightedComponentType || viewAllMode;
        })
        .find(a => {
          if (a.type === 'rectangle' || a.type === 'circle') {
            return isPointInAnnotation(a, x, y, HIT_BOX_PADDING);
          }
          if (a.type === 'line') {
            const dist = pointToLineDistance(x, y, a.startX, a.startY, a.endX || a.startX, a.endY || a.startY);
            return dist < HIT_BOX_PADDING;
          }
          if (a.type === 'pen' && a.points) {
            for (let i = 1; i < a.points.length; i++) {
              const dist = pointToLineDistance(x, y, a.points[i-1].x, a.points[i-1].y, a.points[i].x, a.points[i].y);
              if (dist < HIT_BOX_PADDING) return true;
            }
          }
          return false;
        });
      setHoveredAnnotation(hoveredAnno?.id || null);
      setHoveredAnnotationPoint(hoveredAnno ? { x, y } : null);
    }

    // Check for hover over annotations when in drag mode
    // Only hover over visible annotations (matching highlightedComponentType if set)
    if (currentTool === 'drag' && !dragStart && !isPanning) {
      const hoveredAnno = annotations
        .filter(a => a.page === pageNumber)
        .filter(a => {
          if (!highlightedComponentType) return true;
          return a.component_type === highlightedComponentType;
        })
        .find(a => {
          // Skip pipes - they can't be dragged
          if (a.type === 'piping') return false;
          
          if (a.type === 'rectangle' || a.type === 'circle') {
            return isPointInAnnotation(a, x, y, HIT_BOX_PADDING);
          }
          if (a.type === 'line') {
            const dist = pointToLineDistance(x, y, a.startX, a.startY, a.endX || a.startX, a.endY || a.startY);
            return dist < HIT_BOX_PADDING;
          }
          if (a.type === 'pen' && a.points) {
            for (let i = 1; i < a.points.length; i++) {
              const dist = pointToLineDistance(x, y, a.points[i-1].x, a.points[i-1].y, a.points[i].x, a.points[i].y);
              if (dist < HIT_BOX_PADDING) return true;
            }
          }
          return false;
        });
      setHoveredAnnotation(hoveredAnno?.id || null);
      setHoveredAnnotationPoint(hoveredAnno ? { x, y } : null);
    }

    if (currentTool === 'drag' && dragStart && selectedAnnotation) {
      // Check if the selected annotation is a pipe - if so, don't move it
      const selectedAnn = annotations.find(a => a.id === selectedAnnotation);
      if (selectedAnn && selectedAnn.type === 'piping') {
        // Don't move pipes
        return;
      }
      
      // Move selected annotation
      const dx = x - dragStart.x;
      const dy = y - dragStart.y;
      
      setAnnotations(prev => {
        let updated = prev.map(a => {
          if (a.id === selectedAnnotation) {
            const updatedAnn: Annotation = {
              ...a,
              startX: a.startX + dx,
              startY: a.startY + dy,
              endX: a.endX ? a.endX + dx : undefined,
              endY: a.endY ? a.endY + dy : undefined,
              // Clear stale relative coords so save/rescale recomputes from new absolute positions
              relativeStartX: undefined,
              relativeStartY: undefined,
              // AI-generated annotations: also update center point coordinates
              x: a.x !== undefined ? a.x + dx : undefined,
              y: a.y !== undefined ? a.y + dy : undefined,
              relativeX: a.x !== undefined ? undefined : a.relativeX,
              relativeY: a.y !== undefined ? undefined : a.relativeY,
            };
            
            // If it's a pen annotation, also move all points
            if (a.type === 'pen' && a.points) {
              updatedAnn.points = a.points.map(point => ({
                x: point.x + dx,
                y: point.y + dy
              }));
            }
            
            return updatedAnn;
          }
          return a;
        });
        
        // Update pipes connected to the moved annotation
        updated = updateConnectedPipes(updated, new Set([selectedAnnotation]));
        return updated;
      });
      
      setDragStart({ x, y });
    } else if (isCapturingSnapshot && snapshotSelection) {
      // Update snapshot selection rectangle
      setSnapshotSelection({
        ...snapshotSelection,
        endX: x,
        endY: y
      });
    } else if (isDrawing && currentAnnotation) {
      // Update current annotation being drawn
      switch (currentAnnotation.type) {
        case 'rectangle':
        case 'circle':
          setCurrentAnnotation({
            ...currentAnnotation,
            width: x - (currentAnnotation.startX || 0),
            height: y - (currentAnnotation.startY || 0)
          });
          break;

        case 'line':
          setCurrentAnnotation({
            ...currentAnnotation,
            endX: x,
            endY: y
          });
          break;

        case 'pen':
          setCurrentAnnotation({
            ...currentAnnotation,
            points: [...(currentAnnotation.points || []), { x, y }]
          });
          break;
      }
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Don't show context menu if right-click panning
    if (isRightClickPanning || isPanning) {
      return;
    }
    
    const { x, y } = getScreenToCanvasCoords(e.clientX, e.clientY);
    
    // Right-click on an annotation = instant delete
    const hitAnnotation = findAnnotationAtPoint(x, y);
    if (hitAnnotation) {
      console.log('🗑️ Right-click delete annotation:', hitAnnotation.id);
      const currentAnnotations = annotationsRef.current;
      const deletedIds = new Set<string>([hitAnnotation.id]);
      currentAnnotations.forEach((annotation) => {
        if (annotation.type !== 'piping') return;
        const startDeleted = annotation.startAnnotationId && deletedIds.has(annotation.startAnnotationId);
        const endDeleted = annotation.endAnnotationId && deletedIds.has(annotation.endAnnotationId);
        if (startDeleted || endDeleted) deletedIds.add(annotation.id);
      });
      const filtered = currentAnnotations.filter((annotation) => !deletedIds.has(annotation.id));
      const newAnnotations = renumberAnnotations(filtered);
      const deletedComponentTypes = Array.from(new Set(
        currentAnnotations
          .filter((annotation) => deletedIds.has(annotation.id))
          .map((annotation) => annotation.component_type)
          .filter(Boolean)
      )) as string[];
      annotationsRef.current = newAnnotations;
      setAnnotations(newAnnotations);
      addToHistory(newAnnotations);
      setSelectedAnnotation(null);
      void persistDeletedAnnotations(Array.from(deletedIds), deletedComponentTypes);
      requestSaveNow(newAnnotations);
      return;
    }
    
    console.log('🖱️ Right-click detected!', { activePlacementComponent: activePlacementComponent?.name, countingModeActive, isWireRunMode });
    
    // Wire run mode context menu - same behavior as counting mode
    if (isWireRunMode && selectedWireType) {
      console.log('✅ Showing wire run context menu at:', e.clientX, e.clientY);
      setWireRunContextMenu({ x: e.clientX, y: e.clientY });
      return;
    }
  };

  // Handle mouse up
  const handleMouseUp = async () => {
    // Handle resize completion
    if (resizeHandle) {
      console.log('🔲 Finished resizing rectangle');
      addToHistory(annotations);
      // Coalesced save: avoid queueing stale intermediate states
      setTimeout(() => requestSaveNow(annotationsRef.current), 0);
      setResizeHandle(null);
      return;
    }
    
    // Handle calibration completion
    if (isCalibrating && calibrationLine && isDrawing) {
      console.log('📏 Calibration: Line complete', {
        percentage: calibrationLine.percentage,
        willShowModal: calibrationLine.percentage > 0
      });
      setIsDrawing(false);
      if (calibrationLine.percentage > 0) {
        console.log('✅ Opening calibration modal for real-world value input');
        setShowCalibrationModal(true);
      } else {
        console.log('⚠️ Calibration line too short (0%), not showing modal');
      }
      return;
    }
    
    if (isCapturingSnapshot && snapshotSelection) {
      // If auto-extract is enabled, extract key automatically
      if (autoExtractKeyOnSnapshot) {
        setIsCapturingSnapshot(false);
        // Trigger extraction automatically
        await handleExtractKey();
      } else {
        // Show action buttons instead of capturing immediately
        setIsCapturingSnapshot(false);
        setShowSnapshotActions(true);
      }
    } else if (isDrawing && currentAnnotation) {
      // In counting placement mode: after at least one placement, a plain left-click exits placement mode.
      // This preserves drag-to-place rectangles while providing a quick single-click "done" gesture.
      if (countingModeActive && activePlacementComponent && currentTool === 'select' && currentAnnotation.type === 'rectangle') {
        const width = Math.abs(currentAnnotation.width || 0);
        const height = Math.abs(currentAnnotation.height || 0);
        const CLICK_EXIT_THRESHOLD = 5;
        const isPlainClick = width <= CLICK_EXIT_THRESHOLD && height <= CLICK_EXIT_THRESHOLD;

        if (isPlainClick && lastPlacedAnnotationsRef.current.length > 0) {
          console.log('✅ Plain click detected after placement - exiting placement mode');
          onPlacementComplete?.();
          setIsDrawing(false);
          setCurrentAnnotation(null);
          setDragStart(null);
          return;
        }
      }

      // Add completed annotation with proper numbering
      const completedAnnotation = { ...currentAnnotation } as Annotation;
      
      // Normalize rectangle dimensions (handle negative width/height from dragging)
      if (completedAnnotation.type === 'rectangle') {
        const width = completedAnnotation.width || 0;
        const height = completedAnnotation.height || 0;
        
        // Normalize if drawn right-to-left or bottom-to-top
        if (width < 0) {
          completedAnnotation.startX = (completedAnnotation.startX || 0) + width;
          completedAnnotation.width = Math.abs(width);
        }
        if (height < 0) {
          completedAnnotation.startY = (completedAnnotation.startY || 0) + height;
          completedAnnotation.height = Math.abs(height);
        }
        
        // Ensure minimum size (at least 20x20)
        const minSize = 20;
        if ((completedAnnotation.width || 0) < minSize) {
          completedAnnotation.width = minSize;
        }
        if ((completedAnnotation.height || 0) < minSize) {
          completedAnnotation.height = minSize;
        }
      }
      
      const baseAnnotations = annotationsRef.current;

      // Assign a number based on component type (for both circles and rectangles)
      if (completedAnnotation.type === 'circle' || completedAnnotation.type === 'rectangle') {
        const componentType = completedAnnotation.component_type || 'annotation';
        const existingCount = baseAnnotations.filter(
          a => a.component_type === componentType && a.page === pageNumber
        ).length;
        completedAnnotation.number = existingCount + 1;
        
        // Set a default component_type if not specified
        if (!completedAnnotation.component_type) {
          completedAnnotation.component_type = 'annotation';
        }
      }
      
      const newAnnotations = [...baseAnnotations, completedAnnotation];
      annotationsRef.current = newAnnotations;
      setAnnotations(newAnnotations);
      addToHistory(newAnnotations);
      
      // If this is a component placement rectangle (from counting mode), handle tracking and saving
      if (completedAnnotation.component_type && countingModeActive && activePlacementComponent) {
        setSelectedAnnotation(completedAnnotation.id);
        
        // Track this annotation for undo (keep last 20)
        lastPlacedAnnotationsRef.current = [...lastPlacedAnnotationsRef.current, completedAnnotation.id].slice(-20);
        
        console.log(`📍 Placed ${completedAnnotation.component_type} rectangle at (${Math.round(completedAnnotation.startX || 0)}, ${Math.round(completedAnnotation.startY || 0)}) size ${Math.round(completedAnnotation.width || 0)}x${Math.round(completedAnnotation.height || 0)}`);
        
        // Immediately save to database so counts update right away
        console.log('💾 Immediate save triggered for counting mode');
        requestSaveNow(newAnnotations);
      }
    }

    // Handle autocount area selection completion
    if (autocountMode === 'area-selection' && autocountSelection) {
      let width = Math.abs(autocountSelection.endX - autocountSelection.startX);
      let height = Math.abs(autocountSelection.endY - autocountSelection.startY);
      
      const MIN_SIZE = 50;
      
      // Expand selection to minimum size if needed
      let selectionToUse = autocountSelection;
      if (width < MIN_SIZE || height < MIN_SIZE) {
        console.log(`📏 Expanding small selection from ${width}x${height} to minimum ${MIN_SIZE}x${MIN_SIZE}`);
        
        // Calculate center of selection
        const centerX = (autocountSelection.startX + autocountSelection.endX) / 2;
        const centerY = (autocountSelection.startY + autocountSelection.endY) / 2;
        
        // Expand to minimum size while keeping center
        const expandedWidth = Math.max(width, MIN_SIZE);
        const expandedHeight = Math.max(height, MIN_SIZE);
        
        // Create expanded selection
        selectionToUse = {
          startX: centerX - expandedWidth / 2,
          startY: centerY - expandedHeight / 2,
          endX: centerX + expandedWidth / 2,
          endY: centerY + expandedHeight / 2
        };
        
        // Update state for visual feedback
        setAutocountSelection(selectionToUse);
        
        // Update width/height for extraction
        width = expandedWidth;
        height = expandedHeight;
      }
      
      console.log('✅ Autocount area selected:', { width, height });
      
      // Extract reference area image (combine PDF + annotations)
      const annotationCanvas = canvasRef.current;
      const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      
      if (annotationCanvas && pdfCanvas) {
        const x = Math.min(selectionToUse.startX, selectionToUse.endX);
        const y = Math.min(selectionToUse.startY, selectionToUse.endY);
        
        console.log('📸 Capturing reference area:', { x, y, width, height });
        
        // Temporarily redraw canvas without UI elements for capture
        drawAnnotations(true);
        
        // Create a combined canvas that includes both PDF and annotations
        const combinedCanvas = document.createElement('canvas');
        combinedCanvas.width = annotationCanvas.width;
        combinedCanvas.height = annotationCanvas.height;
        const combinedCtx = combinedCanvas.getContext('2d');
        
        if (combinedCtx) {
          // Draw the PDF content first
          combinedCtx.drawImage(pdfCanvas, 0, 0, annotationCanvas.width, annotationCanvas.height);
          
          // Draw the annotations on top (now without selection UI)
          combinedCtx.drawImage(annotationCanvas, 0, 0);
          
          // Create temp canvas for cropped area
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = width;
          tempCanvas.height = height;
          const tempCtx = tempCanvas.getContext('2d');
          
          if (tempCtx) {
            // Draw selected portion from combined canvas
            tempCtx.drawImage(combinedCanvas, x, y, width, height, 0, 0, width, height);
            
            // Convert to base64
            const referenceImage = tempCanvas.toDataURL('image/png');
            setReferenceAreaImage(referenceImage);
            
            // Pre-fill the name editor with pending component name
            setEditingQueuedName(pendingAutocountComponent?.name || '');
            
            // Exit area selection mode
            setAutocountMode(null);
            setAutocountSelection(null);

            // Show confirmation modal
            if (isAddingNewOnlyScanMode) {
              setShowNewComponentModal(true);
            } else if (isAddingNewOnly) {
              // Safety: if we are in the sidebar flow but scan mode was lost, still show new component modal
              setShowNewComponentModal(true);
            } else {
              setShowAutocountConfirmModal(true);
            }
            
            console.log('📸 Reference area image extracted from combined canvas');
          }
        }
        
        // Restore UI on canvas
        drawAnnotations();
      }
      
      return;
    }

    // Handle key extraction area selection completion
    if (keyExtractionMode && keyExtractionSelection) {
      let width = Math.abs(keyExtractionSelection.endX - keyExtractionSelection.startX);
      let height = Math.abs(keyExtractionSelection.endY - keyExtractionSelection.startY);
      
      const MIN_SIZE = 50;
      
      // Check minimum size
      if (width < MIN_SIZE || height < MIN_SIZE) {
        console.log('❌ Key extraction selection too small, canceling');
        setKeyExtractionSelection(null);
        return;
      }
      
      console.log('✅ Key extraction area selected:', { width, height });
      
      // Extract the selected area as an image
      const annotationCanvas = canvasRef.current;
      const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      
      if (annotationCanvas && pdfCanvas) {
        const x = Math.min(keyExtractionSelection.startX, keyExtractionSelection.endX);
        const y = Math.min(keyExtractionSelection.startY, keyExtractionSelection.endY);
        
        console.log('📸 Capturing key area:', { x, y, width, height });
        
        // Temporarily redraw canvas without UI elements for capture
        drawAnnotations(true);
        
        // Create a combined canvas that includes both PDF and annotations
        const combinedCanvas = document.createElement('canvas');
        combinedCanvas.width = annotationCanvas.width;
        combinedCanvas.height = annotationCanvas.height;
        const combinedCtx = combinedCanvas.getContext('2d');
        
        if (combinedCtx) {
          // Draw the PDF content first
          combinedCtx.drawImage(pdfCanvas, 0, 0, annotationCanvas.width, annotationCanvas.height);
          
          // Draw the annotations on top (optional for key extraction, but keeps consistency)
          combinedCtx.drawImage(annotationCanvas, 0, 0);
          
          // Create temp canvas for cropped area
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = width;
          tempCanvas.height = height;
          const tempCtx = tempCanvas.getContext('2d');
          
          if (tempCtx) {
            tempCtx.drawImage(combinedCanvas, x, y, width, height, 0, 0, width, height);
            const keyImage = tempCanvas.toDataURL('image/png');
            
            // Store snapshot and show confirmation modal
            setKeyExtractionSnapshot(keyImage);
            setShowKeyExtractionConfirmModal(true);
            
            console.log('📸 Key area image captured, showing confirmation modal');
          }
        }
        
        // Restore UI on canvas
        drawAnnotations();
      }
      
      return;
    }

    // Handle lighting schedule area selection completion
    if (lightingScheduleMode && lightingScheduleSelection) {
      let width = Math.abs(lightingScheduleSelection.endX - lightingScheduleSelection.startX);
      let height = Math.abs(lightingScheduleSelection.endY - lightingScheduleSelection.startY);
      
      const MIN_SIZE = 50;
      
      // Check minimum size
      if (width < MIN_SIZE || height < MIN_SIZE) {
        console.log('❌ Lighting schedule selection too small, canceling');
        setLightingScheduleSelection(null);
        return;
      }
      
      console.log('✅ Lighting schedule area selected:', { width, height });
      
      // Extract the selected area as an image
      const annotationCanvas = canvasRef.current;
      const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      
      if (annotationCanvas && pdfCanvas) {
        const x = Math.min(lightingScheduleSelection.startX, lightingScheduleSelection.endX);
        const y = Math.min(lightingScheduleSelection.startY, lightingScheduleSelection.endY);
        
        console.log('📸 Capturing lighting schedule area:', { x, y, width, height });
        
        // Temporarily redraw canvas without UI elements for capture
        drawAnnotations(true);
        
        // Create a combined canvas that includes both PDF and annotations
        const combinedCanvas = document.createElement('canvas');
        combinedCanvas.width = annotationCanvas.width;
        combinedCanvas.height = annotationCanvas.height;
        const combinedCtx = combinedCanvas.getContext('2d');
        
        if (combinedCtx) {
          // Draw the PDF content first
          combinedCtx.drawImage(pdfCanvas, 0, 0, annotationCanvas.width, annotationCanvas.height);
          
          // Draw the annotations on top
          combinedCtx.drawImage(annotationCanvas, 0, 0);
          
          // Create temp canvas for cropped area
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = width;
          tempCanvas.height = height;
          const tempCtx = tempCanvas.getContext('2d');
          
          if (tempCtx) {
            // Draw selected portion from combined canvas
            tempCtx.drawImage(combinedCanvas, x, y, width, height, 0, 0, width, height);
            
            // Convert to base64 and show confirmation modal
            const scheduleImage = tempCanvas.toDataURL('image/png');
            
            setLightingScheduleSnapshot(scheduleImage);
            setShowLightingScheduleConfirmModal(true);
            
            console.log('📸 Lighting schedule image captured, showing confirmation modal');
          }
        }
        
        // Restore UI on canvas
        drawAnnotations();
      }
      
      return;
    }

    // Add to history if we were dragging selected annotations
    if (dragStart && selectedAnnotations.size > 0 && currentTool === 'select') {
      addToHistory(annotations);
    }

    // Handle selection rectangle completion
    if (isSelecting && selectionRect) {
      setIsSelecting(false);
      
      // Find all annotations within the selection rectangle
      // Only consider visible annotations (matching highlightedComponentType if set)
      const minX = Math.min(selectionRect.startX, selectionRect.endX);
      const maxX = Math.max(selectionRect.startX, selectionRect.endX);
      const minY = Math.min(selectionRect.startY, selectionRect.endY);
      const maxY = Math.max(selectionRect.startY, selectionRect.endY);
      
      const selectedIds = new Set<string>();
      
      annotations
        .filter(a => a.page === pageNumber)
        .filter(a => {
          if (!highlightedComponentType) return true;
          if (a.type === 'piping') {
            const startAnn = annotations.find(ann => ann.id === a.startAnnotationId);
            const endAnn = annotations.find(ann => ann.id === a.endAnnotationId);
            return (startAnn?.component_type === highlightedComponentType) ||
                   (endAnn?.component_type === highlightedComponentType);
          }
          return a.component_type === highlightedComponentType;
        })
        .forEach(annotation => {
          let isInRect = false;
          
          if (annotation.type === 'rectangle' || annotation.type === 'circle') {
            // Check if annotation overlaps with selection rectangle
            const annMinX = Math.min(annotation.startX, annotation.startX + (annotation.width || 0));
            const annMaxX = Math.max(annotation.startX, annotation.startX + (annotation.width || 0));
            const annMinY = Math.min(annotation.startY, annotation.startY + (annotation.height || 0));
            const annMaxY = Math.max(annotation.startY, annotation.startY + (annotation.height || 0));
            
            // Check for overlap
            isInRect = !(annMaxX < minX || annMinX > maxX || annMaxY < minY || annMinY > maxY);
          } else if (annotation.type === 'line') {
            // Check if either endpoint is within the rectangle
            const startInRect = annotation.startX >= minX && annotation.startX <= maxX && 
                               annotation.startY >= minY && annotation.startY <= maxY;
            const endInRect = (annotation.endX || annotation.startX) >= minX && 
                             (annotation.endX || annotation.startX) <= maxX &&
                             (annotation.endY || annotation.startY) >= minY && 
                             (annotation.endY || annotation.startY) <= maxY;
            isInRect = startInRect || endInRect;
          } else if (annotation.x !== undefined && annotation.y !== undefined) {
            // AI-generated circle format
            isInRect = annotation.x >= minX && annotation.x <= maxX && 
                      annotation.y >= minY && annotation.y <= maxY;
          }
          
          if (isInRect) {
            selectedIds.add(annotation.id);
          }
        });
      
      setSelectedAnnotations(selectedIds);
      
      // Update single selection state
      if (selectedIds.size === 1) {
        setSelectedAnnotation(Array.from(selectedIds)[0]);
      } else if (selectedIds.size === 0) {
        setSelectedAnnotation(null);
      } else {
        setSelectedAnnotation(null); // Clear single selection when multiple are selected
      }
      
      console.log(`📦 Selected ${selectedIds.size} annotations with rectangle`);
    }

    setIsDrawing(false);
    setCurrentAnnotation(null);
    setDragStart(null);
    
    // Reset right-click panning
    if (isRightClickPanning) {
      console.log('🖱️ Right-click pan ended');
      setIsRightClickPanning(false);
    }
    
    setIsPanning(false);
    setPanStart(null);
    setSelectionRect(null);
  };

  // Handle drop from electrical key
  const handleCanvasDrop = (e: React.DragEvent<HTMLCanvasElement>) => {
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Get component data from drag
    const componentData = e.dataTransfer.getData('component');
    if (!componentData) return;

    try {
      const component = JSON.parse(componentData);

      // Calculate drop position
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;

      // For lighting fixtures, use symbol as component_type (unique per fixture)
      const dropComponentType = component.symbolType === 'Lighting'
        ? (component.symbol || component.name)
        : component.name;

      const baseAnnotations = annotationsRef.current;

      // Get count for this component type
      const existingCount = baseAnnotations.filter(
        a => a.component_type === dropComponentType && a.page === pageNumber
      ).length;

      // Create new rectangle annotation (default 60x60 size, centered at drop point)
      const defaultSize = 60;
      const newAnnotation: Annotation = {
        id: `manual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'rectangle',
        startX: x - defaultSize / 2,
        startY: y - defaultSize / 2,
        width: defaultSize,
        height: defaultSize,
        color: component.color,
        thickness: 3,
        timestamp: new Date().toISOString(),
        page: pageNumber,
        component_type: dropComponentType,
        number: existingCount + 1
      };

      // Add annotation and update history
      const newAnnotations = [...baseAnnotations, newAnnotation];
      annotationsRef.current = newAnnotations;
      setAnnotations(newAnnotations);
      addToHistory(newAnnotations);

      // Select the new annotation
      setSelectedAnnotation(newAnnotation.id);
      
      // Persist immediately so rapid navigation can't drop placement.
      requestSaveNow(newAnnotations);

      console.log(`📍 Dropped ${component.name} at (${Math.round(x)}, ${Math.round(y)})`);
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  // History management
  const addToHistory = (newAnnotations: Annotation[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newAnnotations);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setAnnotations(history[newIndex]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setAnnotations(history[newIndex]);
    }
  };

  const clearAll = async () => {
    setAnnotations([]);
    addToHistory([]);
    setSelectedAnnotation(null);
    
    // Clear the prompted pages cache so user can get prompt again
    promptedPagesRef.current.clear();
    console.log('🔄 Cleared auto-annotation prompt cache');
    
    // Also clear auto-annotation prompt history from database
    if (supabase && estimationId) {
      try {
        // Delete messages that contain auto-annotation prompt history
        const { error } = await supabase
          .from('estimation_chat_messages')
          .delete()
          .eq('estimation_id', estimationId)
          .eq('agent', 'System')
          .or('content.ilike.%Auto-annotation%');
        
        if (error) {
          console.error('Error clearing auto-annotation history:', error);
        } else {
          console.log('✅ Cleared auto-annotation prompt history from database');
        }
      } catch (error) {
        console.error('Error clearing prompt history:', error);
      }
    }

    // Directly delete all annotations from database
    if (!supabase || !estimationId) return;

    console.log('🗑️ Clearing all annotations from database...');
    try {
      const { data: deletedRows, error } = await supabase
        .from('annotation_entities_v2')
        .delete()
        .eq('estimation_id', estimationId)
        .select('component_type');

      if (error) {
        console.error('❌ Error deleting all annotations:', error);
      } else {
        console.log('✅ All annotations cleared from database successfully');
        if (onAnnotationSaved) {
          const deletedTypes = Array.from(
            new Set((deletedRows || []).map((r: any) => r.component_type).filter(Boolean))
          );
          await Promise.resolve(onAnnotationSaved(deletedTypes));
        }
      }
    } catch (err) {
      console.error('Failed to clear annotations:', err);
    }
  };

  // NEW: Photoshop-style Zoom to Point
  const zoomAtPoint = useCallback((zoomFactor: number, screenX: number, screenY: number) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    
    // Position relative to viewport
    const rx = screenX - rect.left;
    const ry = screenY - rect.top;

    setScale(prev => {
      const newScale = Math.max(MIN_VIEW_SCALE, Math.min(10, prev * zoomFactor));
      const actualFactor = newScale / prev;
      
      // Adjust pan to keep point under cursor
      setPanX(rx - (rx - panX) * actualFactor);
      setPanY(ry - (ry - panY) * actualFactor);
      
      return newScale;
    });
  }, [panX, panY]);

  const zoomIn = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    
    // Use last mouse position if available, otherwise center of viewport
    const zoomX = lastMousePosRef.current?.x ?? (rect.left + rect.width / 2);
    const zoomY = lastMousePosRef.current?.y ?? (rect.top + rect.height / 2);
    
    zoomAtPoint(1.5, zoomX, zoomY);
  }, [zoomAtPoint]);

  const zoomOut = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    
    // Use last mouse position if available, otherwise center of viewport
    const zoomX = lastMousePosRef.current?.x ?? (rect.left + rect.width / 2);
    const zoomY = lastMousePosRef.current?.y ?? (rect.top + rect.height / 2);
    
    zoomAtPoint(1/1.5, zoomX, zoomY);
  }, [zoomAtPoint]);

  const centerView = useCallback((targetScale?: number) => {
    const container = containerRef.current;
    if (!container) return;
    const viewport = container.querySelector('.pdf-viewport') as HTMLDivElement | null;
    const annotationCanvas = canvasRef.current;
    const pdfCanvas = container.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement | null;
    if (!viewport || (!annotationCanvas && !pdfCanvas)) return;

    const contentWidth =
      (annotationCanvas?.offsetWidth || annotationCanvas?.clientWidth || 0) ||
      (pdfCanvas?.offsetWidth || pdfCanvas?.clientWidth || 0);
    const contentHeight =
      (annotationCanvas?.offsetHeight || annotationCanvas?.clientHeight || 0) ||
      (pdfCanvas?.offsetHeight || pdfCanvas?.clientHeight || 0);
    if (!contentWidth || !contentHeight) return;

    const effectiveScale = targetScale ?? scale;
    setPanX((viewport.clientWidth - contentWidth * effectiveScale) / 2);
    setPanY((viewport.clientHeight - contentHeight * effectiveScale) / 2);
  }, [scale]);

  const resetZoom = useCallback(() => {
    setScale(DEFAULT_VIEW_SCALE);
    setTimeout(() => centerView(DEFAULT_VIEW_SCALE), 0);
  }, [centerView]);

  const handleDownloadOriginalPDF = useCallback(async () => {
    if (!file?.url) return;
    try {
      const response = await fetch(file.url);
      const pdfBytes = await response.arrayBuffer();

      // Build annotation map grouped by component_type
      const canvas = canvasRef.current;
      const annotationsByComponent: Record<string, any[]> = {};
      for (const ann of annotations) {
        const componentType = ann.component_type || 'unknown';
        const enhanced = {
          ...ann,
          relativeX: ann.x && canvas ? ann.x / canvas.width : ann.relativeX,
          relativeY: ann.y && canvas ? ann.y / canvas.height : ann.relativeY,
          relativeStartX: ann.startX && canvas ? ann.startX / canvas.width : ann.relativeStartX,
          relativeStartY: ann.startY && canvas ? ann.startY / canvas.height : ann.relativeStartY,
          relativeWidth: ann.width && canvas ? ann.width / canvas.width : ann.relativeWidth,
          relativeHeight: ann.height && canvas ? ann.height / canvas.height : ann.relativeHeight,
          savedCanvasWidth: canvas?.width,
          savedCanvasHeight: canvas?.height,
        };
        if (!annotationsByComponent[componentType]) annotationsByComponent[componentType] = [];
        annotationsByComponent[componentType].push(enhanced);
      }

      // Pull reference images directly from the project key (stored inline as base64)
      const refImages: Record<string, string> = {};
      if (electricalKey?.components) {
        for (const comp of electricalKey.components as any[]) {
          if (comp.referenceImage) {
            refImages[comp.name] = comp.referenceImage;
          }
        }
      }

      const metadata = buildMetadataPayload({
        projectKey: electricalKey ? { components: electricalKey.components, extractedAt: (electricalKey as any).extractedAt, pdfHash: (electricalKey as any).pdfHash } : null,
        annotationsByComponent,
        referenceImages: refImages,
        fileId: file.id,
        fileName: file.name,
      });

      const modifiedPdf = await embedMetadataInPDF(pdfBytes, metadata);
      const blob = new Blob([modifiedPdf as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name || 'drawing.pdf';
      a.click();
      URL.revokeObjectURL(url);
      console.log(`✅ Downloaded PDF with embedded bidflow metadata (${annotations.length} annotations, ${Object.keys(refImages).length} reference images)`);
    } catch (err) {
      console.error('Failed to download PDF:', err);
    }
  }, [file, annotations, electricalKey, estimationId]);

  // Function to rotate the current page
  const rotateCurrentPage = useCallback(() => {
    setPageRotations(prev => {
      const currentRotation = prev[pageNumber] || 0;
      const newRotation = (currentRotation + 90) % 360;
      console.log(`🔄 Rotating page ${pageNumber}: ${currentRotation}° → ${newRotation}°`);

      // Save rotation to localStorage for persistence
      const updatedRotations = { ...prev, [pageNumber]: newRotation };
      if (estimationId && file?.name) {
        try {
          const storageKey = `page-rotations-${estimationId}-${file.name}`;
          localStorage.setItem(storageKey, JSON.stringify(updatedRotations));
        } catch { /* quota exceeded — ignore */ }
      }

      // Re-center the view after rotation
      setTimeout(() => {
        centerView();
        console.log('📍 Re-centered after rotation');
      }, 500); // Wait for rotation animation to complete

      return updatedRotations;
    });

    // Redraw annotations with new rotation
    setTimeout(() => drawAnnotations(), 10);
  }, [pageNumber, estimationId, file?.name, drawAnnotations, centerView]);

  // Function to rotate specs pages
  const rotateSpecsPage = useCallback(() => {
    setSpecsPageRotations(prev => {
      const currentRotation = prev[specsPageNumber] || 0;
      const newRotation = (currentRotation + 90) % 360;
      console.log(`🔄 Rotating specs page ${specsPageNumber}: ${currentRotation}° → ${newRotation}°`);

      // Save rotation to localStorage for persistence
      const updatedRotations = { ...prev, [specsPageNumber]: newRotation };
      if (estimationId && file?.name) {
        try {
          const storageKey = `specs-rotations-${estimationId}-${file.name}`;
          localStorage.setItem(storageKey, JSON.stringify(updatedRotations));
        } catch { /* quota exceeded — ignore */ }
      }

      return updatedRotations;
    });
  }, [specsPageNumber, estimationId, file]);

  const deleteSelected = async () => {
    // UNIFIED SELECT: Delete from both annotation layers
    const hasCountingAnnotations = selectedAnnotation || selectedAnnotations.size > 0;
    const hasHandDrawnStrokes = selectedHandDrawnStrokes.size > 0;
    
    if (hasCountingAnnotations || hasHandDrawnStrokes) {
      // Delete counting annotations
      let newAnnotations;
      let deletedIds: Set<string>;
      const currentAnnotations = annotationsRef.current;

      if (hasCountingAnnotations) {
        if (selectedAnnotations.size > 0) {
          // Delete multiple selected annotations
          deletedIds = new Set(selectedAnnotations);
          newAnnotations = currentAnnotations.filter(a => !selectedAnnotations.has(a.id));
          setSelectedAnnotations(new Set());
        } else {
          // Delete single selected annotation
          deletedIds = new Set([selectedAnnotation!]);
          newAnnotations = currentAnnotations.filter(a => a.id !== selectedAnnotation);
        }
      } else {
        newAnnotations = currentAnnotations;
        deletedIds = new Set();
      }

      // Also mark pipes connected to deleted annotations for explicit DB hard-delete.
      currentAnnotations.forEach((annotation) => {
        if (annotation.type !== 'piping') return;
        const startDeleted = annotation.startAnnotationId && deletedIds.has(annotation.startAnnotationId);
        const endDeleted = annotation.endAnnotationId && deletedIds.has(annotation.endAnnotationId);
        if (startDeleted || endDeleted) {
          deletedIds.add(annotation.id);
        }
      });

      // Check if any deleted annotations are wire runs and delete from database
      for (const id of Array.from(deletedIds)) {
        if (id.startsWith('wire-run-')) {
          const wireRunDbId = id.replace('wire-run-', '');
          console.log('🗑️ Deleting wire run from database:', wireRunDbId);
          
          try {
            const { error } = await supabase
              .from('wire_runs')
              .delete()
              .eq('id', wireRunDbId);
            
            if (error) {
              console.error('Error deleting wire run from database:', error);
            } else {
              console.log('✅ Wire run deleted from database');
            }
          } catch (err) {
            console.error('Error deleting wire run:', err);
          }
        }
      }
      
      // Trigger refresh of WireRunsPanel if any wire runs were deleted
      const hasWireRuns = Array.from(deletedIds).some(id => id.startsWith('wire-run-'));
      if (hasWireRuns) {
        setWireRunRefreshTrigger(prev => prev + 1);
      }

      // Also delete any pipes connected to the deleted annotations
      newAnnotations = newAnnotations.filter(a => {
        if (a.type === 'piping') {
          // Delete pipe if either endpoint was deleted
          const startDeleted = a.startAnnotationId && deletedIds.has(a.startAnnotationId);
          const endDeleted = a.endAnnotationId && deletedIds.has(a.endAnnotationId);
          return !startDeleted && !endDeleted;
        }
        return true;
      });

      // Renumber annotations by component type
      newAnnotations = renumberAnnotations(newAnnotations);

      const deletedComponentTypes = Array.from(new Set(
        currentAnnotations
          .filter((annotation) => deletedIds.has(annotation.id))
          .map((annotation) => annotation.component_type)
          .filter(Boolean)
      )) as string[];

      annotationsRef.current = newAnnotations;
      setAnnotations(newAnnotations);
      addToHistory(newAnnotations);
      setSelectedAnnotation(null);

      void persistDeletedAnnotations(Array.from(deletedIds), deletedComponentTypes);
      requestSaveNow(newAnnotations);
      
      // Delete hand-drawn strokes if any selected
      if (hasHandDrawnStrokes) {
        console.log('🗑️ Deleting', selectedHandDrawnStrokes.size, 'hand-drawn strokes');
        // Call the hand-drawn deletion function
        if ((window as any).handDrawnDeleteSelected) {
          (window as any).handDrawnDeleteSelected();
        }
        setSelectedHandDrawnStrokes(new Set());
      }
    }
  };

  // Keyboard shortcuts and mouse wheel for zoom
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      // Handle Ctrl+C for snapshot copy
      // Handle Escape to cancel snapshot selection
      if (e.key === 'Escape' && (snapshotSelection || showSnapshotActions)) {
        handleCancelSnapshot();
        return;
      }

      // Handle Ctrl+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        
        // If in wire run mode and actively drawing, undo last wire point
        if (isWireRunMode && (window as any).handDrawnGetWirePointCount) {
          const pointCount = (window as any).handDrawnGetWirePointCount();
          if (pointCount > 0) {
            console.log('⬅️ Ctrl+Z: Undoing last wire point');
            if ((window as any).handDrawnUndoWirePoint) {
              (window as any).handDrawnUndoWirePoint();
            }
            return;
          }
        }
        
        // If in counting mode and we have placed annotations, remove the last one
        if (countingModeActive && activePlacementComponent && lastPlacedAnnotationsRef.current.length > 0) {
          // Pop the last annotation ID from the stack
          const lastAnnotationId = lastPlacedAnnotationsRef.current.pop();
          
          if (lastAnnotationId) {
            console.log('⬅️ Ctrl+Z: Undoing last placed component:', lastAnnotationId, `(${lastPlacedAnnotationsRef.current.length} more in undo stack)`);
            
            // Find and remove the last placed annotation
            const currentAnnotations = annotationsRef.current;
            const annotationToRemove = currentAnnotations.find(a => a.id === lastAnnotationId);
            
            if (annotationToRemove) {
              const deletedIds = new Set<string>([lastAnnotationId]);
              currentAnnotations.forEach((annotation) => {
                if (annotation.type !== 'piping') return;
                const startDeleted = annotation.startAnnotationId && deletedIds.has(annotation.startAnnotationId);
                const endDeleted = annotation.endAnnotationId && deletedIds.has(annotation.endAnnotationId);
                if (startDeleted || endDeleted) deletedIds.add(annotation.id);
              });

              const newAnnotations = currentAnnotations.filter(a => !deletedIds.has(a.id));
              annotationsRef.current = newAnnotations;
              setAnnotations(newAnnotations);
              addToHistory(newAnnotations);
              setSelectedAnnotation(null);
              
              console.log(`✅ Removed ${annotationToRemove.component_type} annotation`);
              
              const deletedComponentTypes = annotationToRemove.component_type
                ? [annotationToRemove.component_type]
                : [];
              void persistDeletedAnnotations(Array.from(deletedIds), deletedComponentTypes);
              requestSaveNow(newAnnotations);
            }
          }
        }
        
        return;
      }

      // Handle delete/backspace for deleting selected annotations
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedAnnotation || selectedAnnotations.size > 0) {
          e.preventDefault();
          deleteSelected();
        }
      }

      // Handle zoom shortcuts
      if ((e.ctrlKey || e.metaKey)) {
        const now = Date.now();
        if (e.key === '=' || e.key === '+' || e.key === '-' || e.key === '_') {
          if (now - lastWheelTimeRef.current < 30) return;
          lastWheelTimeRef.current = now;
        }

        if (e.key === '=' || e.key === '+') {
          e.preventDefault();
          zoomIn();
        } else if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          zoomOut();
        } else if (e.key === '0') {
          e.preventDefault();
          resetZoom();
        }
      }
    };

    const handleWheel = (e: WheelEvent) => {
      const container = containerRef.current;
      if (!container || !container.contains(e.target as Node)) return;
      
      e.preventDefault();
      
      if (e.ctrlKey || e.metaKey) {
        // Throttle zoom events to prevent "shifting" during rapid scroll
        const now = Date.now();
        if (now - lastWheelTimeRef.current < 50) return; // Limit zoom speed to prevent shifting
        lastWheelTimeRef.current = now;

        // Zoom
        const factor = e.deltaY > 0 ? 0.8 : 1.25;
        zoomAtPoint(factor, e.clientX, e.clientY);
      } else if (e.shiftKey) {
        // Shift + wheel pans horizontally (Photoshop-like behavior)
        const horizontalDelta = Math.abs(e.deltaX) > 0 ? e.deltaX : e.deltaY;
        setPanX(prev => prev - horizontalDelta);
      } else {
        // Pan
        setPanX(prev => prev - e.deltaX);
        setPanY(prev => prev - e.deltaY);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    // Listen on document in capture phase so we run before the scrollable container sees the event
    document.addEventListener('wheel', handleWheel, { passive: false, capture: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('wheel', handleWheel, true);
    };
  }, [selectedAnnotation, selectedAnnotations, deleteSelected, snapshotSelection, captureSnapshotToClipboard, zoomIn, zoomOut, resetZoom, zoomAtPoint, isWireRunMode, countingModeActive, activePlacementComponent, annotations, addToHistory, requestSaveNow]);

  // Helper function to renumber annotations maintaining order within each component type
  const renumberAnnotations = (annotationList: Annotation[]): Annotation[] => {
    // Group by component type and page
    const grouped = new Map<string, Map<number, Annotation[]>>();

    for (const annotation of annotationList) {
      const componentType = annotation.component_type || 'unknown';
      const page = annotation.page;

      if (!grouped.has(componentType)) {
        grouped.set(componentType, new Map<number, Annotation[]>());
      }
      if (!grouped.get(componentType)!.has(page)) {
        grouped.get(componentType)!.set(page, []);
      }

      grouped.get(componentType)!.get(page)!.push(annotation);
    }

    // Renumber within each group
    const renumbered: Annotation[] = [];

    // Convert to array for ES5 compatibility
    const groupedArray = Array.from(grouped.entries());

    for (let i = 0; i < groupedArray.length; i++) {
      const [componentType, pages] = groupedArray[i];
      let componentCounter = 1;

      // Sort pages with explicit typing
      const sortedPages = Array.from(pages.keys()).sort((a: number, b: number) => a - b);

      for (const page of sortedPages) {
        const pageAnnotations = pages.get(page)!;

        // Sort by position (top to bottom, left to right) with explicit typing
        pageAnnotations.sort((a: Annotation, b: Annotation) => {
          const yDiff = (a.startY || 0) - (b.startY || 0);
          if (Math.abs(yDiff) > 20) return yDiff; // If significant Y difference, sort by Y
          return (a.startX || 0) - (b.startX || 0); // Otherwise sort by X
        });

        // Assign new numbers
        for (const annotation of pageAnnotations) {
          annotation.number = componentCounter++;
          if (annotation.text) {
            // Update label if it contains a number
            annotation.text = `${componentType} #${annotation.number}`;
          }
          renumbered.push(annotation);
        }
      }
    }

    return renumbered;
  };

  // Export annotated PDF
  const exportPDF = async () => {
    if (!file) return;
    
    try {
      const response = await fetch(file.url);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      
      // Add annotations to PDF
      annotations.forEach((annotation) => {
        const pageIndex = annotation.page - 1;
        const page = pages[pageIndex];
        if (!page) return;
        
        const { width, height } = page.getSize();
        
        // Convert canvas coordinates to PDF coordinates
        const pdfY = height - annotation.startY;
        
        // Parse color from rgba string
        const parseColor = (colorStr: string) => {
          const match = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          if (match) {
            return rgb(
              parseInt(match[1]) / 255,
              parseInt(match[2]) / 255,
              parseInt(match[3]) / 255
            );
          }
          return rgb(1, 0, 0);
        };
        
        const color = parseColor(annotation.color);
        
        switch (annotation.type) {
          case 'rectangle':
            page.drawRectangle({
              x: annotation.startX,
              y: pdfY - (annotation.height || 0),
              width: annotation.width || 0,
              height: annotation.height || 0,
              borderColor: color,
              borderWidth: annotation.thickness
            });
            break;
            
          case 'line':
            page.drawLine({
              start: { x: annotation.startX, y: pdfY },
              end: { x: annotation.endX || annotation.startX, y: height - (annotation.endY || annotation.startY) },
              color: color,
              thickness: annotation.thickness
            });
            break;
            
          case 'text':
            if (annotation.text) {
              page.drawText(annotation.text, {
                x: annotation.startX,
                y: pdfY,
                size: 16,
                font: helveticaFont,
                color: color
              });
            }
            break;
        }
      });
      
      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([new Uint8Array(modifiedPdfBytes)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `annotated_${file.name}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };

  // Export current page as PNG at 8k resolution
  const exportPageAsPNG = async () => {
    if (!file || !pdfDocument) return;
    
    try {
      console.log('📸 Exporting page at 8k resolution...');
      
      // Get the current page from PDF
      const pdfPage = await pdfDocument.getPage(pageNumber);
      const rotation = pageRotations[pageNumber] || 0;
      const viewport = pdfPage.getViewport({ scale: 1.0, rotation });
      
      // Calculate scale to achieve 8000px on longest edge
      const maxRes = 8000;
      const longestSide = Math.max(viewport.width, viewport.height);
      const scale = maxRes / longestSide;
      const scaledViewport = pdfPage.getViewport({ scale, rotation });
      
      console.log(`📐 Export dimensions: ${scaledViewport.width}x${scaledViewport.height}`);
      
      // Create high-res canvas for PDF
      const pdfCanvas = document.createElement('canvas');
      pdfCanvas.width = scaledViewport.width;
      pdfCanvas.height = scaledViewport.height;
      const pdfCtx = pdfCanvas.getContext('2d')!;
      
      // Render PDF at high resolution
      await pdfPage.render({
        canvasContext: pdfCtx,
        viewport: scaledViewport
      }).promise;
      
      // Create combined canvas
      const combinedCanvas = document.createElement('canvas');
      combinedCanvas.width = scaledViewport.width;
      combinedCanvas.height = scaledViewport.height;
      const ctx = combinedCanvas.getContext('2d')!;
      
      // Draw the high-res PDF
      ctx.drawImage(pdfCanvas, 0, 0);
      
      // Get current canvas dimensions to calculate scale factor
      const currentCanvas = canvasRef.current;
      if (currentCanvas) {
        const scaleX = scaledViewport.width / currentCanvas.width;
        const scaleY = scaledViewport.height / currentCanvas.height;
        
        // Draw annotations scaled up to 8k resolution
        const pageAnnotations = annotations.filter(a => a.page === pageNumber);
        
        pageAnnotations.forEach(annotation => {
          const annotationColor = getColorForAnnotation(annotation);
          ctx.strokeStyle = annotationColor;
          ctx.lineWidth = annotation.thickness * scaleX;
          
          // Set fill style with semi-transparency
          const colorMatch = annotationColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          if (colorMatch) {
            ctx.fillStyle = `rgba(${colorMatch[1]}, ${colorMatch[2]}, ${colorMatch[3]}, 0.4)`;
          }
          
          switch (annotation.type) {
            case 'circle':
              let centerX, centerY;
              if (annotation.x !== undefined && annotation.y !== undefined) {
                centerX = annotation.x * scaleX;
                centerY = annotation.y * scaleY;
              } else {
                centerX = (annotation.startX + (annotation.width || 0) / 2) * scaleX;
                centerY = (annotation.startY + (annotation.height || 0) / 2) * scaleY;
              }
              
              if (annotation.number) {
                const text = String(annotation.number);
                const radius = (text.length === 1 ? 24 : text.length === 2 ? 28 : 32) * scaleX;
                
                // Draw filled circle
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
                ctx.fill();
                ctx.strokeStyle = annotationColor;
                ctx.lineWidth = 2 * scaleX;
                ctx.stroke();
                
                // Draw number
                const fontSize = (text.length === 1 ? 28 : 24) * scaleX;
                ctx.font = `bold ${fontSize}px Arial`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
                ctx.lineWidth = 3 * scaleX;
                ctx.strokeText(text, centerX, centerY);
                ctx.fillStyle = 'white';
                ctx.fillText(text, centerX, centerY);
              } else {
                // Small dot
                ctx.beginPath();
                ctx.arc(centerX, centerY, 5 * scaleX, 0, 2 * Math.PI);
                ctx.fill();
                ctx.strokeStyle = annotationColor;
                ctx.lineWidth = 1 * scaleX;
                ctx.stroke();
              }
              break;
              
            case 'rectangle':
              ctx.fillRect(
                annotation.startX * scaleX,
                annotation.startY * scaleY,
                (annotation.width || 0) * scaleX,
                (annotation.height || 0) * scaleY
              );
              ctx.strokeRect(
                annotation.startX * scaleX,
                annotation.startY * scaleY,
                (annotation.width || 0) * scaleX,
                (annotation.height || 0) * scaleY
              );
              break;
              
            case 'line':
              ctx.beginPath();
              ctx.moveTo(annotation.startX * scaleX, annotation.startY * scaleY);
              ctx.lineTo((annotation.endX || annotation.startX) * scaleX, (annotation.endY || annotation.startY) * scaleY);
              ctx.stroke();
              break;
              
            case 'pen':
              if (annotation.points && annotation.points.length > 0) {
                ctx.beginPath();
                ctx.moveTo(annotation.points[0].x * scaleX, annotation.points[0].y * scaleY);
                annotation.points.forEach(point => {
                  ctx.lineTo(point.x * scaleX, point.y * scaleY);
                });
                ctx.stroke();
              }
              break;
          }
        });
      }
      
      // Convert to blob and download
      combinedCanvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${file.name.replace('.pdf', '')}_page_${pageNumber}_8k.png`;
          a.click();
          URL.revokeObjectURL(url);
          console.log(`✅ Exported 8k PNG: ${scaledViewport.width}x${scaledViewport.height}`);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Error exporting PNG:', error);
    }
  };

  // Export takeoff report with counts summary and annotated blueprints
  const exportCountTakeoffReport = async () => {
    if (!file || !pdfDocument) {
      console.error('Missing file or PDF document');
      return;
    }
    
    try {
      console.log('📊 Generating takeoff report with counts...');
      console.log('📊 Total annotations in state:', annotations.length);
      
      // Load ALL annotations from database
      let allAnnotations: Annotation[] = [];
      
      console.log('📊 Supabase available:', !!supabase);
      console.log('📊 Estimation ID:', estimationId);
      
      if (supabase) {
        console.log('📊 Loading annotations from database...');
        const { data, error } = await loadGroupedAnnotationsFromV2({
          supabase: supabase as any,
          estimationId,
          activeMode: 'estimate',
          currentDrawingFileId: null,
        });
        
        console.log('📊 Database query result:', { data, error });
        
        if (error) {
          console.error('❌ Error loading annotations from database:', error);
        } else if (data) {
          console.log(`📊 Loaded ${data.length} component records from database`);
          if (data.length === 0) {
            console.warn('⚠️ No records found in database for estimation_id:', estimationId);
          }
          // Combine all annotations from all component types
          data.forEach(record => {
            console.log(`📊 Record:`, record);
            if (record.annotations && Array.isArray(record.annotations)) {
              console.log(`📊 Adding ${record.annotations.length} annotations from ${record.component_type}`);
              allAnnotations = allAnnotations.concat(record.annotations as Annotation[]);
            } else {
              console.warn(`⚠️ No annotations array in record for ${record.component_type}`);
            }
          });
        }
      } else {
        console.error('❌ Supabase not available!');
      }
      
      // If no annotations in database, use current state
      if (allAnnotations.length === 0) {
        console.log('📊 No annotations in database, using state annotations:', annotations.length);
        allAnnotations = annotations;
      }
      
      console.log('📊 Total annotations to export:', allAnnotations.length);
      console.log('📊 Sample annotation:', allAnnotations[0]);
      
      const pdfDoc = await PDFDocument.create();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      
      // ========================================
      // PART 1: Generate Summary Report Page(s)
      // ========================================
      
      // Group counts by symbol type and component
      const countsByComponent: any = {};
      // Use numbered annotations if available, otherwise use all circle annotations
      let numberedAnnotations = allAnnotations.filter(a => a.number !== undefined);
      console.log('📊 Numbered annotations:', numberedAnnotations.length);
      
      // If no numbered annotations, use all circles
      if (numberedAnnotations.length === 0) {
        numberedAnnotations = allAnnotations.filter(a => a.type === 'circle');
        console.log('📊 Using all circle annotations instead:', numberedAnnotations.length);
      }
      
      console.log('📊 Sample numbered annotation:', numberedAnnotations[0]);
      
      // If we have annotations, create summary page
      if (numberedAnnotations.length > 0) {
        console.log('📊 Creating summary page with annotations...');
        numberedAnnotations.forEach((a, index) => {
        console.log(`📊 Processing annotation ${index + 1}:`, {
          type: a.type,
          component_type: a.component_type,
          number: a.number,
          page: a.page
        });
        
        const componentName = a.component_type || 'Unnamed';
        
        // Look up symbol type from electrical key
        let symbolType = 'Other';
        if (electricalKey && a.component_type) {
          const keyComponent = electricalKey.components.find(
            (c: any) => c.name === a.component_type || c.symbol === a.component_type
          );
          if (keyComponent?.symbolType) {
            // Capitalize and format the symbol type
            symbolType = keyComponent.symbolType
              .split('_')
              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
          }
        }
        
        const key = `${symbolType}_${componentName}`;
        if (!countsByComponent[key]) {
          countsByComponent[key] = {
            symbolType,
            componentName,
            items: []
          };
        }
        countsByComponent[key].items.push({
          page: a.page,
          number: a.number || index + 1, // Use index if no number
          x: Math.round(a.x || a.startX || 0),
          y: Math.round(a.y || a.startY || 0)
        });
      });
      
      console.log('📊 Counts by component:', countsByComponent);
      
      // Skip summary page if no valid components (will just show blueprints)
      const hasValidComponents = Object.keys(countsByComponent).length > 0;
      
      if (hasValidComponents) {
        // Create summary page
        const summaryPage = pdfDoc.addPage([612, 792]); // Letter size
        let yPosition = 750;
      
      // Draw a test rectangle to verify page is rendering
      summaryPage.drawRectangle({
        x: 40,
        y: 40,
        width: 532,
        height: 712,
        borderColor: rgb(0.8, 0.8, 0.8),
        borderWidth: 1,
      });
      
      // Title
      summaryPage.drawText('ELECTRICAL TAKEOFF SUMMARY', {
        x: 50,
        y: yPosition,
        size: 18,
        font: boldFont,
        color: rgb(0, 0, 0)
      });
      
      yPosition -= 30;
      summaryPage.drawText(`Project: ${file.name}`, {
        x: 50,
        y: yPosition,
        size: 12,
        font: font,
        color: rgb(0.3, 0.3, 0.3)
      });
      
      yPosition -= 10;
      summaryPage.drawText(`Date: ${new Date().toLocaleDateString()}`, {
        x: 50,
        y: yPosition,
        size: 12,
        font: font,
        color: rgb(0.3, 0.3, 0.3)
      });
      
      yPosition -= 40;
      
      // Table header
      summaryPage.drawText('SYMBOL TYPE', { x: 50, y: yPosition, size: 10, font: boldFont });
      summaryPage.drawText('COMPONENT', { x: 200, y: yPosition, size: 10, font: boldFont });
      summaryPage.drawText('COUNT', { x: 450, y: yPosition, size: 10, font: boldFont });
      summaryPage.drawText('PAGES', { x: 510, y: yPosition, size: 10, font: boldFont });
      
      yPosition -= 20;
      
      // Draw line
      summaryPage.drawLine({
        start: { x: 50, y: yPosition },
        end: { x: 560, y: yPosition },
        thickness: 1,
        color: rgb(0, 0, 0)
      });
      
      yPosition -= 15;
      
      // List each component
      Object.values(countsByComponent).forEach((component: any) => {
        const count = component.items.length;
        const pages = Array.from(new Set(component.items.map((i: any) => i.page))).sort((a: any, b: any) => (a as number) - (b as number)).join(', ');
        
        // Check if we need a new page
        if (yPosition < 100) {
          pdfDoc.addPage([612, 792]);
          yPosition = 750;
        }
        
        // Symbol type with color coding
        let typeColor = rgb(0, 0, 0);
        if (component.symbolType === 'Power') typeColor = rgb(0.13, 0.77, 0.37);
        else if (component.symbolType === 'Lighting') typeColor = rgb(0.92, 0.70, 0.03);
        else if (component.symbolType === 'Fire Alarm') typeColor = rgb(0.94, 0.27, 0.27);
        else if (component.symbolType === 'Communications') typeColor = rgb(0.23, 0.51, 0.95);
        
        summaryPage.drawText(component.symbolType, {
          x: 50,
          y: yPosition,
          size: 10,
          font: font,
          color: typeColor
        });
        
        summaryPage.drawText(component.componentName, {
          x: 200,
          y: yPosition,
          size: 10,
          font: font
        });
        
        summaryPage.drawText(String(count), {
          x: 460,
          y: yPosition,
          size: 10,
          font: boldFont
        });
        
        summaryPage.drawText(pages, {
          x: 510,
          y: yPosition,
          size: 8,
          font: font,
          color: rgb(0.5, 0.5, 0.5)
        });
        
        yPosition -= 20;
      });
      
      // Total count
      yPosition -= 10;
      summaryPage.drawLine({
        start: { x: 50, y: yPosition },
        end: { x: 560, y: yPosition },
        thickness: 1,
        color: rgb(0, 0, 0)
      });
      yPosition -= 20;
      
      summaryPage.drawText('TOTAL', {
        x: 200,
        y: yPosition,
        size: 12,
        font: boldFont
      });
      
        summaryPage.drawText(String(numberedAnnotations.length), {
          x: 460,
          y: yPosition,
          size: 12,
          font: boldFont
        });
      }
      } else {
        console.log('⚠️ No annotations for summary page, skipping it');
      }
      
      // ========================================
      // PART 2: Screenshot Each Page with Annotations
      // ========================================
      
      if (!numPages) {
        console.error('No pages to capture');
        return;
      }
      
      console.log(`📊 Capturing screenshots of ${numPages} pages...`);
      
      // Save current page to restore later
      const currentPage = pageNumber;
      
      // Capture each page
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        console.log(`📊 Capturing page ${pageNum}/${numPages}...`);
        
        // Switch to this page
        setPageNumber(pageNum);
        
        // Wait for the page to render
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Get the annotation canvas
        const annotationCanvas = canvasRef.current;
        if (!annotationCanvas) {
          console.error(`No canvas found for page ${pageNum}`);
          continue;
        }
        
        console.log(`📊 Page ${pageNum}: Canvas size ${annotationCanvas.width}x${annotationCanvas.height}`);
        
        // Get the PDF page and render it to a temporary canvas
        const pdfPage = await pdfDocument.getPage(pageNum);
        const rotation = pageRotations[pageNum] || 0;
        
        // Match the viewport to the current canvas size
        const scale = annotationCanvas.width / pdfPage.getViewport({ scale: 1.0, rotation }).width;
        const viewport = pdfPage.getViewport({ scale, rotation });
        
        // Create a temporary canvas for the PDF
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = annotationCanvas.width;
        tempCanvas.height = annotationCanvas.height;
        const tempCtx = tempCanvas.getContext('2d')!;
        
        // Render PDF to temp canvas
        await pdfPage.render({
          canvasContext: tempCtx,
          viewport: viewport
        }).promise;
        
        // Now draw the annotations on top
        tempCtx.drawImage(annotationCanvas, 0, 0);
        
        console.log(`📊 Page ${pageNum}: Combined PDF + annotations`);
        
        // Convert combined canvas to image
        const imageData = tempCanvas.toDataURL('image/png');
        const imageBytes = await fetch(imageData).then(res => res.arrayBuffer());
        
        // Embed in PDF
        const image = await pdfDoc.embedPng(imageBytes);
        const page = pdfDoc.addPage([tempCanvas.width, tempCanvas.height]);
        page.drawImage(image, {
          x: 0,
          y: 0,
          width: tempCanvas.width,
          height: tempCanvas.height,
        });
        
        console.log(`📊 Page ${pageNum}: Added to PDF (${tempCanvas.width}x${tempCanvas.height})`);
      }
      
      // Restore original page
      setPageNumber(currentPage);
      
      // Save PDF
      console.log(`📊 Saving PDF with ${pdfDoc.getPageCount()} pages...`);
      const modifiedPdfBytes = await pdfDoc.save();
      console.log(`📊 PDF bytes generated: ${modifiedPdfBytes.length} bytes`);
      const blob = new Blob([new Uint8Array(modifiedPdfBytes)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${file.name.replace('.pdf', '')}_takeoff_report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      
      console.log('✅ Takeoff report generated!');
    } catch (error) {
      console.error('Error generating takeoff report:', error);
    }
  };

  function onDocumentLoadSuccess(pdf: any) {
    setPdfDocument(pdf);
    setNumPages(pdf.numPages);
    setSpecsNumPages(pdf.numPages); // Same document for specs mode
    activeRenderTokenRef.current += 1;
    shouldCenterAfterRenderRef.current = true;
    
    // Restore saved page number instead of always using 1
    if (estimationId && file?.name) {
      const storageKey = `page-number-${estimationId}-${file.name}`;
      const savedPage = localStorage.getItem(storageKey);
      if (savedPage) {
        const pageNum = parseInt(savedPage, 10);
        if (pageNum >= 1 && pageNum <= pdf.numPages) {
          setPageNumber(pageNum);
          console.log(`📖 Restored saved page number: ${pageNum}`);
        } else {
          setPageNumber(1);
        }
      } else {
        setPageNumber(1);
      }
    } else {
      setPageNumber(1);
    }
    
    // Update context if available
    if (hasContext && blueprintContext?.setTotalPages) {
      blueprintContext.setTotalPages(pdf.numPages);
    }
    
  }

  function onPageLoadSuccess(page: any) {
    // Parse PDF as SVG for element detection mode (DISABLED - feature not in use)
    // This was causing slow page loads by parsing elements that are never used
    // TODO: Re-enable if element detection mode is implemented
    // parsePDFAsSVG(page).then(elements => {
    //   setPdfVectorElements(elements);
    // });
    
    // Calculate scale to achieve 8000px on longest edge for maximum quality
    if (canvasRef.current) {
      // Apply PDF's native rotation if no manual rotation has been set
      // PDFs can have metadata rotation (0, 90, 180, 270) that should be respected
      const nativeRotation = page.rotate || 0;
      
      // Check if we have a saved rotation for this page
      const savedRotation = pageRotations[pageNumber];
      
      // If no saved rotation exists, use the PDF's native rotation
      if (savedRotation === undefined && nativeRotation !== 0) {
        console.log(`📄 Page ${pageNumber} has native rotation: ${nativeRotation}°`);
        setPageRotations(prev => {
          const updatedRotations = {
            ...prev,
            [pageNumber]: nativeRotation
          };
          
          // Save to localStorage for persistence
          if (estimationId && file?.name) {
            try {
              const storageKey = `page-rotations-${estimationId}-${file.name}`;
              localStorage.setItem(storageKey, JSON.stringify(updatedRotations));
            } catch { /* quota exceeded — ignore */ }
          }
          
          return updatedRotations;
        });
      }
      
      // Get current rotation for this page (either saved or native)
      const rotation = savedRotation !== undefined ? savedRotation : nativeRotation;
      
      // Two-pass rendering: fast low-res first, high-res upgrade in background
      const baseViewport = page.getViewport({ scale: 1, rotation });
      const longestSide = Math.max(baseViewport.width, baseViewport.height);

      // Cancel any in-progress background high-res render from a previous page
      highResRenderRef.current.cancel = true;
      highResRenderRef.current = { cancel: false };
      setIsHighResReady(false);
      setIsUpgradingRes(false);

      // Store the page object for the background upgrade
      pdfPageRef.current = page;

      // Phase 1: Low-res for fast initial display
      const renderScale = FAST_RES / longestSide;
      setPdfRenderScale(renderScale);
      
      console.log('📐 Phase 1 (fast render):', {
        rotation,
        renderScale,
        targetResolution: `${FAST_RES}px on longest edge`,
        baseViewport: { width: baseViewport.width, height: baseViewport.height },
        longestSide,
        expectedDimensions: {
          width: Math.round(baseViewport.width * renderScale),
          height: Math.round(baseViewport.height * renderScale)
        }
      });
      
      // Get viewport for page size (use FULL_RES for context so annotations map correctly at final resolution)
      const viewport = page.getViewport({ scale: renderScale, rotation });
      
      // Update context if available
      if (hasContext && blueprintContext?.setPageSize) {
        blueprintContext.setPageSize({
          width: viewport.width,
          height: viewport.height
        });
      }
      
      // DISABLED: Base64 conversion that was creating 12MB images on every page load
      // This data was never actually used anywhere in the codebase
      // If needed in the future, this should be done on-demand, not eagerly
      // Original code created massive performance overhead for unused feature
      // const canvas = document.createElement('canvas');
      // page.render(...).then(() => { /* base64 conversion */ });
    }
  }

  // Phase 2: Background high-res upgrade
  const upgradeToHighRes = useCallback(async (cancelToken: { cancel: boolean }, renderToken: number) => {
    const page = pdfPageRef.current;
    if (!page || cancelToken.cancel || renderToken !== activeRenderTokenRef.current) return;

    setIsUpgradingRes(true);

    try {
      const rotation = pageRotations[pageNumber] || 0;
      const baseViewport = page.getViewport({ scale: 1, rotation });
      const longestSide = Math.max(baseViewport.width, baseViewport.height);
      const fullScale = FULL_RES / longestSide;

      const effectiveDPR = Math.min(window.devicePixelRatio, DPR_CAP);
      const viewport = page.getViewport({ scale: fullScale * effectiveDPR, rotation });

      const offscreen = document.createElement('canvas');
      offscreen.width = viewport.width;
      offscreen.height = viewport.height;
      const ctx = offscreen.getContext('2d');
      if (!ctx) return;

      console.log(`🔄 Phase 2: Rendering high-res ${offscreen.width}x${offscreen.height} in background...`);
      await page.render({ canvasContext: ctx, viewport }).promise;

      if (cancelToken.cancel || renderToken !== activeRenderTokenRef.current) {
        console.log('⏹️ High-res render cancelled (page changed)');
        return;
      }

      const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      if (pdfCanvas) {
        // Preserve on-screen size from phase 1 so quality upgrade doesn't change visual zoom.
        const previousDisplayWidth = pdfCanvas.clientWidth || parseFloat(getComputedStyle(pdfCanvas).width) || 0;
        const previousDisplayHeight = pdfCanvas.clientHeight || parseFloat(getComputedStyle(pdfCanvas).height) || 0;

        pdfCanvas.width = offscreen.width;
        pdfCanvas.height = offscreen.height;

        const displayWidth = previousDisplayWidth > 0 ? previousDisplayWidth : (offscreen.width / effectiveDPR);
        const displayHeight = previousDisplayHeight > 0 ? previousDisplayHeight : (offscreen.height / effectiveDPR);
        pdfCanvas.style.width = `${displayWidth}px`;
        pdfCanvas.style.height = `${displayHeight}px`;

        pdfCanvas.getContext('2d')?.drawImage(offscreen, 0, 0);

        if (canvasRef.current) {
          canvasRef.current.width = offscreen.width;
          canvasRef.current.height = offscreen.height;
          canvasRef.current.style.width = `${displayWidth}px`;
          canvasRef.current.style.height = `${displayHeight}px`;

          setAnnotations(prev => prev.map(ann => rescaleAnnotation(ann, offscreen.width, offscreen.height)));
          setCurrentCanvasWidth(offscreen.width);
          setCurrentCanvasHeight(offscreen.height);
        }

        setIsHighResReady(true);
        if (shouldCenterAfterRenderRef.current && renderToken === activeRenderTokenRef.current) {
          requestAnimationFrame(() => {
            if (renderToken !== activeRenderTokenRef.current) return;
            centerView(DEFAULT_VIEW_SCALE);
            shouldCenterAfterRenderRef.current = false;
          });
        }
        console.log(`✅ Phase 2 complete: ${offscreen.width}x${offscreen.height}`);
      }
    } catch (err) {
      console.error('❌ High-res upgrade failed:', err);
    } finally {
      setIsUpgradingRes(false);
    }
  }, [pageNumber, pageRotations, rescaleAnnotation, FULL_RES, DPR_CAP, centerView]);

  // Called when Phase 1 (low-res) render completes
  const handlePhase1Complete = useCallback((renderedPage?: any) => {
    if (renderedPage?.pageNumber && renderedPage.pageNumber !== pageNumber) {
      return;
    }
    const renderToken = activeRenderTokenRef.current;
    console.log('✅ Phase 1 rendered (fast)');
    setIsPDFRendered(true);
    setIsCanvasSynced(true);

    // Center after phase 1 for fast feedback; final centering is confirmed in phase 2.
    if (shouldCenterAfterRenderRef.current) {
      requestAnimationFrame(() => {
        if (renderToken !== activeRenderTokenRef.current) return;
        centerView(DEFAULT_VIEW_SCALE);
      });
    }

    if (!isHighResReady && pdfPageRef.current) {
      const cancelToken = highResRenderRef.current;
      const schedule = window.requestIdleCallback || ((cb: IdleRequestCallback) => setTimeout(cb, 100));
      schedule(() => {
        if (cancelToken.cancel || renderToken !== activeRenderTokenRef.current) return;
        upgradeToHighRes(cancelToken, renderToken);
      });
    }
  }, [isHighResReady, upgradeToHighRes, centerView, pageNumber]);

  // Get canvas screenshot as base64 (combines PDF and annotations)
  const getCanvasScreenshot = useCallback((): string | null => {
    const canvas = canvasRef.current;
    const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;

    if (!canvas || !pdfCanvas) {
      console.warn('⚠️ Canvas not available for screenshot');
      return null;
    }

    console.log('📸 Canvas dimensions for screenshot:', {
      annotationCanvas: { width: canvas.width, height: canvas.height },
      pdfCanvas: { width: pdfCanvas.width, height: pdfCanvas.height }
    });

    // Create a new canvas that combines PDF and annotations
    const combinedCanvas = document.createElement('canvas');
    
    // Canvas dimensions already match the rotated PDF
    combinedCanvas.width = canvas.width;
    combinedCanvas.height = canvas.height;

    const ctx = combinedCanvas.getContext('2d');
    if (!ctx) return null;
    
    // No rotation transform needed - PDF is already rotated

    // Check if we need to scale the PDF canvas to match annotation canvas
    if (pdfCanvas.width !== canvas.width || pdfCanvas.height !== canvas.height) {
      console.log('⚠️ Canvas size mismatch, scaling PDF to match annotation canvas');
      ctx.drawImage(pdfCanvas, 0, 0, pdfCanvas.width, pdfCanvas.height, 0, 0, canvas.width, canvas.height);
    } else {
      // Draw the PDF page first
      ctx.drawImage(pdfCanvas, 0, 0);
    }

    // Draw the annotations on top
    ctx.drawImage(canvas, 0, 0);

    // No restore needed - no rotation transform applied

    // Convert to base64
    const dataUrl = combinedCanvas.toDataURL('image/png');
    const base64 = dataUrl.split(',')[1];
    console.log('📸 Canvas screenshot captured:', {
      bytes: base64?.length,
      dimensions: { width: combinedCanvas.width, height: combinedCanvas.height }
    });
    return base64;
  }, [pageNumber, pageRotations]);
  
  // Function to get current page as base64
  const getCurrentPageAsBase64 = useCallback(() => {
    const pageElement = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    if (pageElement) {
      try {
        const dataUrl = pageElement.toDataURL('image/png');
        const base64 = dataUrl.split(',')[1];
        console.log('🖼️ PDFAnnotator: Manual base64 extraction', {
          hasCanvas: true,
          base64Length: base64?.length || 0
        });
        return base64;
      } catch (error) {
        console.error('Error getting canvas as base64:', error);
      }
    } else {
      console.warn('⚠️ PDFAnnotator: No PDF canvas found');
    }
    return null;
  }, []);

  // Function to navigate to a specific page (resets zoom + recenters, matching toolbar behavior)
  const navigateToPage = useCallback((targetPage: number) => {
    if (targetPage >= 1 && targetPage <= (numPages || 1)) {
      console.log(`📖 PDFAnnotator: Navigating to page ${targetPage}`);
      activeRenderTokenRef.current += 1;
      shouldCenterAfterRenderRef.current = true;
      setPageNumber(targetPage);
      setScale(DEFAULT_VIEW_SCALE);
      return true;
    } else {
      console.warn(`⚠️ PDFAnnotator: Invalid page number ${targetPage}. Valid range: 1-${numPages}`);
      return false;
    }
  }, [numPages]);

  // Function to get all pages as base64 thumbnails (concurrent batches for speed)
  const getAllPagesAsThumbnails = useCallback(async () => {
    if (!pdfDocument || !numPages) {
      console.warn('⚠️ PDFAnnotator: No PDF document loaded');
      return null;
    }

    console.log(`📸 PDFAnnotator: Capturing thumbnails for ${numPages} pages (concurrent)...`);
    const BATCH_SIZE = 8;

    try {
      const renderPage = async (pageNum: number) => {
        const page = await pdfDocument.getPage(pageNum);
        const viewport = page.getViewport({ scale: 0.10 });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return null;
        
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;

        const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
        const base64 = dataUrl.split(',')[1];
        
        // Free canvas memory
        canvas.width = 0;
        canvas.height = 0;
        
        return { pageNumber: pageNum, base64 };
      };

      const allResults: { pageNumber: number; base64: string }[] = [];
      const pageNumbers = Array.from({ length: numPages }, (_, i) => i + 1);

      for (let i = 0; i < pageNumbers.length; i += BATCH_SIZE) {
        const batch = pageNumbers.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.all(batch.map(renderPage));
        for (const result of batchResults) {
          if (result) allResults.push(result);
        }
      }

      // Sort by page number (batches may resolve in any order)
      allResults.sort((a, b) => a.pageNumber - b.pageNumber);

      console.log(`✅ PDFAnnotator: Captured ${allResults.length} page thumbnails`);
      return allResults;
    } catch (error) {
      console.error('❌ PDFAnnotator: Error capturing page thumbnails:', error);
      return null;
    }
  }, [pdfDocument, numPages]);

  // Load page thumbnails when page selection modal opens
  useEffect(() => {
    if (showPageSelectionModal && pageSelectionContext && !pageThumbnailsForSelection && pdfDocument && numPages) {
      getAllPagesAsThumbnails().then((thumbnails) => {
        if (thumbnails) setPageThumbnailsForSelection(thumbnails);
      });
    }
    if (!showPageSelectionModal) setPageThumbnailsForSelection(null);
  }, [showPageSelectionModal, pageSelectionContext, pdfDocument, numPages, getAllPagesAsThumbnails]);

  // Helper function to render any page at any scale completely off-screen
  // This does NOT affect what the user is currently viewing
  const renderPageOffScreen = useCallback(async (
    targetPage: number,
    maxWidth: number
  ): Promise<HTMLCanvasElement> => {
    if (!pdfDocument) {
      throw new Error('PDF not loaded');
    }

    console.log(`📸 Rendering page ${targetPage} off-screen at max width ${maxWidth}px...`);

    // IMPORTANT: Fetch the target page directly from pdfDocument
    // This does NOT affect what the user is currently viewing
    const pdfPage = await pdfDocument.getPage(targetPage);
    const viewport = pdfPage.getViewport({ scale: 1.0 });

    // Calculate scale to achieve target width
    const scale = maxWidth / viewport.width;
    const scaledViewport = pdfPage.getViewport({ scale });

    // Create a brand new off-screen canvas
    // This canvas is never attached to the DOM
    const offScreenCanvas = document.createElement('canvas');
    offScreenCanvas.width = scaledViewport.width;
    offScreenCanvas.height = scaledViewport.height;

    const context = offScreenCanvas.getContext('2d')!;

    // Render the page to our off-screen canvas
    await pdfPage.render({
      canvasContext: context,
      viewport: scaledViewport
    }).promise;

    console.log(`✅ Rendered page ${targetPage} off-screen: ${offScreenCanvas.width}x${offScreenCanvas.height}`);

    return offScreenCanvas;
  }, [pdfDocument]);

  // Expose state and methods to parent components via ref
  useImperativeHandle(ref, () => {
    // Get the actual canvas dimensions from the PDF page
    const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    const currentCanvasWidth = pdfCanvas?.width || canvasRef.current?.width || 0;
    const currentCanvasHeight = pdfCanvas?.height || canvasRef.current?.height || 0;
    
    // console.log('🔍 Exposing canvas dimensions to parent:', {
    //   width: currentCanvasWidth,
    //   height: currentCanvasHeight,
    //   hasCanvas: !!canvasRef.current,
    //   hasPdfCanvas: !!pdfCanvas
    // });
    
    return {
      annotations,
      getAnnotations: () => annotations,  // ALWAYS RETURN FRESH STATE!
      currentPage: pageNumber,
      totalPages: numPages,
      canvasWidth: currentCanvasWidth,
      canvasHeight: currentCanvasHeight,
      getCurrentPageAsBase64,
      getCanvasScreenshot,
      navigateToPage,
      handleStartLightingScheduleExtraction,
      // Trigger autocount with saved reference image
      triggerAutocount: async (componentName: string, componentColor: string, savedReferenceImage: string, isLighting?: boolean) => {
        console.log('🤖 Triggering autocount from saved reference:', componentName);
        
        setPendingAutocountComponent({
          name: componentName,
          color: componentColor,
          isLighting: isLighting ?? false
        });
        setReferenceAreaImage(savedReferenceImage);
        
        // Run autocount directly
        await handleConfirmAutocount();
      },
      navigateToSpecsPage: (page: number, boundingBox?: { top: number; left: number; width: number; height: number }) => {
        console.log('🎯 navigateToSpecsPage called:', { page, boundingBox, currentPage: specsPageNumber });
        
        // Clear any existing highlight timeout
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
          highlightTimeoutRef.current = null;
        }
        
        // Update the last highlight page ref to prevent clearing
        lastHighlightPageRef.current = page;
        
        setSpecsPageNumber(page);
        setHighlightBoundingBox(boundingBox || null);
        console.log('📦 Set highlightBoundingBox to:', boundingBox);
        
        // Auto-clear highlight after 30 seconds
        if (boundingBox) {
          highlightTimeoutRef.current = setTimeout(() => {
            console.log('⏰ Clearing bounding box highlight');
            setHighlightBoundingBox(null);
            highlightTimeoutRef.current = null;
          }, 2500);
        }
      },
      navigateToNecaPage: (page: number) => {
        console.log('📖 navigateToNecaPage called:', { page });
        // For NECA manual, navigate to the page using NECA-specific state
        setNecaPageNumber(page);
        // Scroll to top of viewer to show the page
        const pdfContainer = document.querySelector('.pdf-container');
        if (pdfContainer) {
          pdfContainer.scrollTop = 0;
        }
      },
      getAllPagesAsThumbnails,
      getBaseScreenshot: async (targetPage: number) => {
        // CRITICAL: Never navigate or affect user's current view
        // Render entirely off-screen using pdfDocument directly
        const canvas = await renderPageOffScreen(targetPage, 1092);
        return canvas.toDataURL('image/png').split(',')[1];
      },
      getMediumResolutionScreenshot: async (targetPage: number) => {
        // CRITICAL: Never navigate or affect user's current view
        // Render entirely off-screen using pdfDocument directly
        const canvas = await renderPageOffScreen(targetPage, 8000);
        return canvas.toDataURL('image/png').split(',')[1];
      },
      getFullResolutionScreenshot: async (targetPage: number) => {
        // CRITICAL: Never navigate or affect user's current view
        // Render entirely off-screen using pdfDocument directly
        const canvas = await renderPageOffScreen(targetPage, 16000);
        return canvas.toDataURL('image/png').split(',')[1];
      },
      addAnnotation: (annotation: Annotation) => {
        setAnnotations(prev => [...prev, annotation]);
        
        // Trigger pop-in animation for the new annotation
        if (annotation.id) {
          setAnimatingAnnotations(prev => new Set(prev).add(annotation.id));
          setAnnotationTimestamps(prev => new Map(prev).set(annotation.id, Date.now()));
          
          // Remove from animating set after animation completes (700ms)
          setTimeout(() => {
            setAnimatingAnnotations(prev => {
              const next = new Set(prev);
              next.delete(annotation.id);
              return next;
            });
            // Clean up timestamp after a bit longer
            setTimeout(() => {
              setAnnotationTimestamps(prev => {
                const next = new Map(prev);
                next.delete(annotation.id);
                return next;
              });
            }, 100);
          }, 700);
        }
        // Redraw will happen automatically due to annotations state change
      },
      removeAnnotation: async (id: string) => {
        // Check if this is a wire run annotation
        if (id.startsWith('wire-run-')) {
          const wireRunDbId = id.replace('wire-run-', '');
          console.log('🗑️ Deleting wire run from database:', wireRunDbId);
          
          try {
            const { error } = await supabase
              .from('wire_runs')
              .delete()
              .eq('id', wireRunDbId);
            
            if (error) {
              console.error('Error deleting wire run from database:', error);
            } else {
              console.log('✅ Wire run deleted from database');
              // Trigger refresh of WireRunsPanel
              setWireRunRefreshTrigger(prev => prev + 1);
            }
          } catch (err) {
            console.error('Error deleting wire run:', err);
          }
        }
        
        const currentAnnotations = annotationsRef.current;
        const deletedIds = new Set<string>([id]);
        currentAnnotations.forEach((annotation) => {
          if (annotation.type !== 'piping') return;
          const startDeleted = annotation.startAnnotationId && deletedIds.has(annotation.startAnnotationId);
          const endDeleted = annotation.endAnnotationId && deletedIds.has(annotation.endAnnotationId);
          if (startDeleted || endDeleted) deletedIds.add(annotation.id);
        });

        const nextAnnotations = currentAnnotations.filter(a => !deletedIds.has(a.id));
        const deletedComponentTypes = Array.from(new Set(
          currentAnnotations
            .filter((annotation) => deletedIds.has(annotation.id))
            .map((annotation) => annotation.component_type)
            .filter(Boolean)
        )) as string[];
        annotationsRef.current = nextAnnotations;
        setAnnotations(nextAnnotations);
        void persistDeletedAnnotations(Array.from(deletedIds), deletedComponentTypes);
        requestSaveNow(nextAnnotations);
      },
      setAnnotations: (newAnnotations: Annotation[]) => {
        setAnnotations(newAnnotations);
        // Reset the hash to force save on next call
        lastSaveHashRef.current = '';
      },
      saveAnnotations: requestSaveNow,
      clearAnnotations: clearAll,
      loadAnnotations,
      // Trigger autocount with saved reference image
      triggerAutocountWithReference: async (componentName: string, componentColor: string, savedReferenceImage: string, isLighting?: boolean) => {
        console.log('🤖 Triggering autocount from saved reference:', componentName);
        
        setPendingAutocountComponent({
          name: componentName,
          color: componentColor,
          isLighting: isLighting ?? false
        });
        setReferenceAreaImage(savedReferenceImage);
        
        // Small delay to ensure state is set, then trigger
        setTimeout(async () => {
          await handleConfirmAutocount();
        }, 100);
      },
      setViewAllMode: (enabled: boolean) => {
        setViewAllMode(enabled);
      },
      viewAllMode,
      highlightComponentType: (componentType: string | null) => {
        setHighlightedComponentType(componentType);
        if (componentType) {
          // Start animation for all annotations of this type
          const annotationsToAnimate = annotations
            .filter(a => a.component_type === componentType)
            .map(a => a.id);
          setAnimatingAnnotations(new Set(annotationsToAnimate));

          // Stop animation after 800ms
          setTimeout(() => {
            setAnimatingAnnotations(new Set());
          }, 800);
        }
      },
      openAddNewComponentModal: () => {
        console.log('🆕 Opening Add New Component modal from sidebar');
        setNewComponentName('');
        setCountingModalMode('manual'); // Default to manual/add-to-key mode
        setIsAddingNewOnly(true);
        setIsAddingNewOnlyScanMode(false);
        setShowNewComponentModal(true);
      },
      setCurrentTool: (tool: AnnotationTool) => {
        setCurrentTool(tool);
        console.log(`🔧 Tool changed to: ${tool}`);
      },
      // Wire run methods
      startWireRunMode,
      isWireRunMode,
      hasCalibration: !!calibrationData[pageNumber],
      calibrationData,
      wireRunRefreshTrigger,
      openCalibrationDropdown: () => setShowCalibrationDropdown(true),
      deleteWireRunAnnotation: (runId: string, _pageNumber: number) => {
        // Delete the wire run annotation that matches this database ID
        // Annotation ID format: `wire-run-${databaseId}`
        const annotationId = `wire-run-${runId}`;
        console.log('🗑️ Deleting wire run annotation:', annotationId);
        
        setAnnotations(prev => {
          const newAnnotations = prev.filter(a => a.id !== annotationId);
          console.log('🗑️ Annotations after delete:', newAnnotations.length, 'removed:', prev.length - newAnnotations.length);
          return newAnnotations;
        });
        
        // Trigger a redraw after state update (via setTimeout to let React batch the update)
        setTimeout(() => {
          drawAnnotations();
        }, 50);
      },
      currentDrawingFileId: file?.id,
      startCountingComponents: (components: Array<{ color: string; name: string; symbol: string; referenceImage: string; symbolType?: string; componentType?: string; subscript?: string; description?: string }>) => {
        startCountingComponents(components);
      },
      startCountingComponentsParallel: (components: Array<{ color: string; name: string; symbol: string; referenceImage: string; symbolType?: string; componentType?: string; subscript?: string; description?: string }>) => {
        startCountingComponents(components, { parallel: true });
      }
    };
  }, [annotations, pageNumber, numPages, scale, containerRef, getCurrentPageAsBase64, navigateToPage, getAllPagesAsThumbnails, renderPageOffScreen, requestSaveNow, persistDeletedAnnotations, clearAll, loadAnnotations, startWireRunMode, isWireRunMode, calibrationData, wireRunRefreshTrigger, file?.id, drawAnnotations, startCountingComponents]);
  
  // Update context with page changes
  useEffect(() => {
    if (hasContext && blueprintContext?.setCurrentPage) {
      blueprintContext.setCurrentPage(pageNumber);
    }
  }, [pageNumber, hasContext, blueprintContext]);
  
  // Update context with annotations
  useEffect(() => {
    if (hasContext && blueprintContext?.setAnnotations) {
      blueprintContext.setAnnotations(annotations);
    }
  }, [annotations, hasContext, blueprintContext]);

  useEffect(() => {
    annotationsRef.current = annotations;
  }, [annotations]);

  useEffect(() => {
    annotationsLoadedRef.current = annotationsLoaded;
  }, [annotationsLoaded]);

  // Auto-save annotations when they change (with debounce)
  useEffect(() => {
    // Skip initial mount
    if (!estimationId) return;
    
    // Don't save until annotations have been loaded from database
    // This prevents the auto-save from deleting all annotations on initial load
    if (!annotationsLoaded) {
      console.log('⏭️ Skipping auto-save - annotations not loaded yet');
      return;
    }

    const timeoutId = setTimeout(() => {
      console.log('🔄 Auto-saving annotations after change...');
      requestSaveNow();
    }, 100); // 100ms debounce; coalesced scheduler still prevents save flooding

    return () => clearTimeout(timeoutId);
  }, [annotations, annotationsLoaded, requestSaveNow]); // Trigger on any annotation changes including renumbering

  const drawingOptions = drawingFiles || [];
  const drawingSelectValue =
    currentDrawingFile?.id || (drawingOptions.length > 0 ? drawingOptions[0].id : '');

  return (
    <div className="pdf-annotator-container">
      {/* Toolbar */}
      <div className={`pdf-annotator-toolbar ${!isAgentChatCollapsed ? 'chat-expanded' : ''}`}>
        {/* Left: Icons - Better Order */}
        <div className="toolbar-section toolbar-icons-left">
          <TooltipButton
            className="toolbar-btn"
            onClick={onHomeClick}
            tooltip="Home"
          >
            <ArrowLeft size={16} />
          </TooltipButton>
          
          <TooltipButton
            className={`toolbar-btn ${activeMode === 'draw' ? 'active' : ''}`}
            onClick={() => {
              console.log('🚀 Switching to Takeoff mode');
              onModeChange?.('draw');
            }}
            tooltip="Takeoff"
          >
            <Rocket size={16} />
          </TooltipButton>
          
          <TooltipButton
            className={`toolbar-btn ${activeMode === 'specs' ? 'active' : ''}`}
            onClick={() => {
              console.log('📄 Switching to Specs mode');
              onModeChange?.('specs');
            }}
            tooltip="Specifications"
          >
            <FileText size={16} />
          </TooltipButton>
          
          <TooltipButton
            className={`toolbar-btn ${activeMode === 'rfi' ? 'active' : ''}`}
            onClick={() => {
              console.log('💬 Switching to RFI mode');
              onModeChange?.('rfi');
            }}
            tooltip="RFI"
          >
            <MessageSquare size={16} />
          </TooltipButton>
          
          <TooltipButton
            className={`toolbar-btn ${activeMode === 'neca' ? 'active' : ''}`}
            onClick={() => {
              console.log('👷 Switching to NECA mode');
              onModeChange?.('neca');
            }}
            tooltip="NECA Labor Manual"
          >
            <HardHat size={16} />
          </TooltipButton>
          
          <TooltipButton
            className={`toolbar-btn ${activeMode === 'estimate' ? 'active' : ''}`}
            onClick={() => {
              console.log('💰 Switching to Estimate mode');
              onModeChange?.('estimate');
            }}
            tooltip="Estimate"
          >
            <Calculator size={16} />
          </TooltipButton>
          
          <div
            ref={filesDropdownRef}
            style={{ position: 'relative', display: 'none' }}
          >
            <TooltipButton
              className="toolbar-btn"
              onClick={(e) => {
                if (!e) return;
                setShowFilesDropdown(!showFilesDropdown);
              }}
              tooltip="Files"
            >
              <FolderOpen size={16} />
            </TooltipButton>
          </div>

        </div>

        {/* Center: Navigation - Show different controls based on mode */}
        {activeMode === 'specs' && (
        <div className="toolbar-section toolbar-center-tools">
          {/* File Selector (if multiple specs files) */}
          {specsFiles && specsFiles.length > 1 && (
            <>
              <select
                value={currentSpecsFile?.id || ''}
                onChange={(e) => onSpecsFileChange?.(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-primary)',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {specsFiles.map((file) => (
                  <option key={file.id} value={file.id}>
                    {file.file_name || file.name}
                  </option>
                ))}
              </select>
            </>
          )}
          
          {/* Specs Navigation */}
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setSpecsPageNumber(prev => Math.max(prev - 1, 1))}
            disabled={specsPageNumber <= 1}
            tooltip="Previous Page"
          >
            <ChevronLeft size={16} />
          </TooltipButton>
          <span className="page-info">
            Page {specsPageNumber} of {specsNumPages || '...'}
          </span>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setSpecsPageNumber(prev => Math.min(prev + 1, specsNumPages || 1))}
            disabled={specsPageNumber >= (specsNumPages || 1)}
            tooltip="Next Page"
          >
            <ChevronRight size={16} />
          </TooltipButton>

          {/* Specs Zoom */}
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setSpecsScale(prev => Math.max(prev - 0.2, 0.5))}
            tooltip="Zoom Out"
          >
            <ZoomOut size={16} />
          </TooltipButton>
          <span className="page-info" style={{ minWidth: '60px' }}>
            {Math.round(specsScale * 100)}%
          </span>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setSpecsScale(prev => Math.min(prev + 0.2, 3.0))}
            tooltip="Zoom In"
          >
            <ZoomIn size={16} />
          </TooltipButton>

          {/* Specs Rotation */}
          <TooltipButton
            className="toolbar-btn"
            onClick={() => rotateSpecsPage()}
            tooltip="Rotate Page 90°"
          >
            <RotateCw size={16} />
          </TooltipButton>
        </div>
      )}

      {/* NECA Mode Navigation */}
      {activeMode === 'neca' && (
        <div className="toolbar-section toolbar-center-tools">
          {/* NECA Navigation */}
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setNecaPageNumber(prev => Math.max(prev - 1, 1))}
            disabled={necaPageNumber <= 1}
            tooltip="Previous Page"
          >
            <ChevronLeft size={16} />
          </TooltipButton>
          <span className="page-info">
            Page {necaPageNumber} of {necaNumPages || '...'}
          </span>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setNecaPageNumber(prev => Math.min(prev + 1, necaNumPages || 1))}
            disabled={necaPageNumber >= (necaNumPages || 1)}
            tooltip="Next Page"
          >
            <ChevronRight size={16} />
          </TooltipButton>

          {/* NECA Zoom */}
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setNecaScale(prev => Math.max(prev - 0.2, 0.5))}
            tooltip="Zoom Out"
          >
            <ZoomOut size={16} />
          </TooltipButton>
          <span className="page-info" style={{ minWidth: '60px' }}>
            {Math.round(necaScale * 100)}%
          </span>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setNecaScale(prev => Math.min(prev + 0.2, 3.0))}
            tooltip="Zoom In"
          >
            <ZoomIn size={16} />
          </TooltipButton>
        </div>
      )}

        {/* Center: Most Tools - Always show in draw mode */}
        {activeMode === 'draw' && (
        <div className="toolbar-section toolbar-center-tools">
          {/* Navigation */}
          <TooltipButton
            className="toolbar-btn"
            onClick={() => {
              activeRenderTokenRef.current += 1;
              shouldCenterAfterRenderRef.current = true;
              setPageNumber(prev => Math.max(prev - 1, 1));
              setScale(DEFAULT_VIEW_SCALE);
            }}
            disabled={pageNumber <= 1}
            tooltip="Previous Page - Navigate to the previous page"
          >
            <ChevronLeft size={16} />
          </TooltipButton>
          <div className="page-number-container">
            <input
              type="text"
              className="page-number-input"
              value={pageInputValue}
              onChange={(e) => {
                const value = e.target.value;
                setPageInputValue(value);
                const newPage = parseInt(value);
                if (!isNaN(newPage) && newPage >= 1 && newPage <= (numPages || 1)) {
                  activeRenderTokenRef.current += 1;
                  shouldCenterAfterRenderRef.current = true;
                  setPageNumber(newPage);
                  setScale(DEFAULT_VIEW_SCALE);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }
              }}
              onBlur={() => {
                // Reset to current page number if invalid
                setPageInputValue(pageNumber.toString());
              }}
            />
            <span className="page-total">of {numPages || '...'}</span>
          </div>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => {
              activeRenderTokenRef.current += 1;
              shouldCenterAfterRenderRef.current = true;
              setPageNumber(prev => Math.min(prev + 1, numPages || 1));
              setScale(DEFAULT_VIEW_SCALE);
            }}
            disabled={!numPages || pageNumber >= numPages}
            tooltip="Next Page - Navigate to the next page"
          >
            <ChevronRight size={16} />
          </TooltipButton>

          {/* Zoom */}
          <TooltipButton
            className="toolbar-btn"
            onClick={zoomOut}
            tooltip="Zoom Out - Decrease zoom level (Ctrl/Cmd + -)"
          >
            <ZoomOut size={16} />
          </TooltipButton>
          <span className="page-info" style={{ minWidth: '60px' }}>
            {Math.round(scale * 100)}%
          </span>
          <TooltipButton
            className="toolbar-btn"
            onClick={zoomIn}
            tooltip="Zoom In - Increase zoom level (Ctrl/Cmd + +)"
          >
            <ZoomIn size={16} />
          </TooltipButton>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => rotateCurrentPage()}
            tooltip="Rotate Page - Rotate current page 90 degrees clockwise"
          >
            <RotateCw size={16} />
          </TooltipButton>
          <TooltipButton
            className="toolbar-btn primary"
            onClick={() => centerView()}
            tooltip="Center View - Center the canvas in the viewport"
          >
            <Crosshair size={16} />
          </TooltipButton>
          <TooltipButton
            className="toolbar-btn"
            onClick={handleDownloadOriginalPDF}
            tooltip="Download PDF - Save PDF with embedded annotation data (re-upload to auto-populate a new project)"
          >
            <Download size={16} />
          </TooltipButton>

          {/* Hand-Drawn Tools - Always show in draw mode */}
          <>
              <TooltipButton
                className={`toolbar-btn ${currentTool === 'select' ? 'active' : ''}`}
                onClick={() => setCurrentTool('select')}
                tooltip="Select - Works on all annotations (counting + hand-drawn). Right-click to pan. Press Delete to remove."
              >
                <MousePointer size={16} />
              </TooltipButton>
              <TooltipButton
                className={`toolbar-btn ${viewAllMode ? 'active' : ''}`}
                onClick={() => setViewAllMode(prev => !prev)}
                tooltip={viewAllMode ? 'Hide other components' : 'View all components (read-only)'}
              >
                {viewAllMode ? <EyeOff size={16} /> : <Eye size={16} />}
              </TooltipButton>
              {/* Pan tool hidden - use right-click to pan instead */}
              {/* Manual Count Button - COMMENTED OUT (redundant, use Counting tab instead) */}
              {/* <TooltipButton
                className={`toolbar-btn ${countingModeActive && countingModalMode === 'manual' ? 'active counting-active' : ''}`}
                onClick={() => {
                  console.log('🔢 Opening Manual Count modal');
                  setCountingModalMode('manual');
                  setShowNewComponentModal(true);
                  setNewComponentName('');
                  setNewComponentColor('#3b82f6');
                  setSelectedComponentsForBatch(new Set()); // Clear selections when switching modes
                }}
                tooltip="Manual Count"
              >
                <Hash size={16} />
              </TooltipButton> */}

              {/* AI Autocount Button - Removed */}
              
              {/* Debug Tile Grid Toggle */}
              <TooltipButton
                className={`toolbar-btn ${showDebugTileGrid ? 'active' : ''}`}
                onClick={() => {
                  setShowDebugTileGrid(prev => !prev);
                  // Clear stored grid params when turning off so it recalculates on next toggle
                  if (showDebugTileGrid) {
                    debugTileGridRef.current = null;
                  }
                }}
                tooltip={showDebugTileGrid ? "Hide Tile Grid" : "Show Tile Grid (Debug)"}
              >
                <Grid3X3 size={16} style={showDebugTileGrid ? { color: 'red' } : undefined} />
              </TooltipButton>

              {/* Wire Run Tool - Next to counting */}
              <TooltipButton
                className={`toolbar-btn ${isWireRunMode ? 'active wire-run-active' : ''}`}
                onClick={() => {
                  console.log('🔌 Wire Run button clicked - switching to Wires tab then opening modal');
                  startWireRunMode();
                }}
                tooltip="Measure Wire/Conduit Runs"
              >
                <Cable size={16} />
              </TooltipButton>

              {/* Square Footage Tool */}
              <TooltipButton
                className={`toolbar-btn ${isSquareFootageMode ? 'active' : ''}`}
                onClick={() => {
                  if (isSquareFootageMode) {
                    setIsSquareFootageMode(false);
                    setCurrentTool('select');
                  } else {
                    startSquareFootageMode();
                  }
                }}
                tooltip="Measure Square Footage"
              >
                <Scan size={16} />
              </TooltipButton>
            

            {/* Drawing tools - now always enabled, counting mode doesn't block them */}
            <div 
              style={{ 
                display: 'flex',
                gap: '4px',
                opacity: 1, // Always fully visible
                transition: 'opacity 0.2s ease',
                cursor: 'default',
                position: 'relative'
              }}
            >
            {/* Highlighter tool removed */}
            
            {/* Eraser tool removed */}
            
            {/* Hand-drawn Line tool - hidden */}
            {/* Hand-drawn Arrow tool - hidden */}
              {/* Shape Tools - Commented out */}
              {/* <TooltipButton
                className={`toolbar-btn ${currentTool === 'hand-rectangle' ? 'active' : ''}`}
                onClick={() => setCurrentTool('hand-rectangle')}
                tooltip="Hand-Drawn Rectangle - Draw rectangles"
              >
                <Square size={16} />
              </TooltipButton>
              <TooltipButton
                className={`toolbar-btn ${currentTool === 'hand-ellipse' ? 'active' : ''}`}
                onClick={() => setCurrentTool('hand-ellipse')}
                tooltip="Hand-Drawn Ellipse - Draw circles/ellipses"
              >
                <Circle size={16} />
              </TooltipButton>
              <TooltipButton
                className={`toolbar-btn ${currentTool === 'hand-cloud' ? 'active' : ''}`}
                onClick={() => setCurrentTool('hand-cloud')}
                tooltip="Hand-Drawn Cloud - Draw cloud shapes"
              >
              <Cloud size={16} />
            </TooltipButton> */}
            
            <div 
              ref={fontDropdownRef}
              style={{ position: 'relative' }}
            >
              <TooltipButton
                className={`toolbar-btn ${currentTool === 'hand-text' ? 'active' : ''}`}
                onClick={(e) => {
                  if (!e) return;
                  setCurrentTool('hand-text');
                  const rect = e.currentTarget.getBoundingClientRect();
                  setDropdownPositions(prev => ({
                    ...prev,
                    'hand-text': { top: rect.bottom + 4, left: rect.left }
                  }));
                  setShowToolDropdown({
                    'hand-text': !showToolDropdown['hand-text']
                  });
                }}
                tooltip="Text Box - Click to add text and adjust size"
              >
                <Type size={16} />
              </TooltipButton>
              
              {showToolDropdown['hand-text'] && dropdownPositions['hand-text'] && (
                <div
                  style={{
                    position: 'fixed',
                    top: dropdownPositions['hand-text'].top,
                    left: dropdownPositions['hand-text'].left,
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '12px',
                    zIndex: 20000,
                    minWidth: '140px'
                  }}
                >
                  <div style={{ marginBottom: '8px' }}>
                    <label style={{ 
                      fontSize: '11px', 
                      color: 'var(--text-muted)', 
                      fontWeight: '600', 
                      display: 'block', 
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Font Size
                    </label>
                    {[
                      { value: 0.2, label: 'Tiny' },
                      { value: 0.5, label: 'Small' },
                      { value: 1.0, label: 'Normal' },
                      { value: 2.5, label: 'Large' },
                      { value: 4.0, label: 'Huge' }
                    ].map(option => (
                      <div
                        key={option.value}
                        onClick={() => {
                          setHandDrawnFontSize(option.value);
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          background: handDrawnFontSize === option.value ? 'var(--blue-tint-light)' : 'transparent',
                          color: handDrawnFontSize === option.value ? '#2563eb' : 'var(--text-secondary)',
                          fontSize: '13px',
                          fontWeight: handDrawnFontSize === option.value ? '600' : '500',
                          borderRadius: '4px',
                          marginBottom: '2px',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (handDrawnFontSize !== option.value) {
                            e.currentTarget.style.background = 'var(--bg-secondary)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (handDrawnFontSize !== option.value) {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

              {/* Annotation Tools - Commented out */}
              {/* <TooltipButton
                className={`toolbar-btn ${currentTool === 'hand-note' ? 'active' : ''}`}
                onClick={() => setCurrentTool('hand-note')}
                tooltip="Sticky Note - Click to add note"
              >
                <StickyNote size={16} />
              </TooltipButton>
              <TooltipButton
                className={`toolbar-btn ${currentTool === 'hand-callout' ? 'active' : ''}`}
                onClick={() => setCurrentTool('hand-callout')}
                tooltip="Callout - Drag for pointer with text"
              >
                <MessageCircle size={16} />
              </TooltipButton>
              <TooltipButton
                className={`toolbar-btn ${currentTool === 'hand-stamp' ? 'active' : ''}`}
                onClick={() => setCurrentTool('hand-stamp')}
                tooltip="Stamp - Click to add approval stamp"
              >
                <StampIcon size={16} />
              </TooltipButton> */}
              <div 
                ref={(el) => { toolDropdownRefs.current['hand-dimension'] = el; }}
                style={{ position: 'relative' }}
              >
                <TooltipButton
                  className={`toolbar-btn ${isCalibrating ? 'active' : ''}`}
                  onClick={() => {
                    setIsCalibrating(true);
                    setShowToolDropdown({});
                  }}
                  tooltip="Set Scale / Calibration"
                >
                  <Ruler size={16} />
                </TooltipButton>
              </div>

            <TooltipButton
                className="toolbar-btn"
                onClick={() => {
                  if ((window as any).handDrawnUndo) {
                    (window as any).handDrawnUndo();
                  }
                }}
                disabled={!canUndoHandDrawn}
                tooltip="Undo - Revert last hand-drawn change"
              >
                <Undo size={16} />
              </TooltipButton>
              <TooltipButton
                className="toolbar-btn"
                onClick={() => {
                  if ((window as any).handDrawnRedo) {
                    (window as any).handDrawnRedo();
                  }
                }}
                disabled={!canRedoHandDrawn}
                tooltip="Redo - Restore undone hand-drawn change"
              >
                <Redo size={16} />
              </TooltipButton>
              <TooltipButton
                className="toolbar-btn"
                onClick={() => {
                  if ((window as any).handDrawnClear) {
                    if (window.confirm('Clear all hand-drawn annotations on this page?')) {
                      (window as any).handDrawnClear();
                    }
                  }
                }}
                tooltip="Clear All - Remove all hand-drawn annotations from this page"
              >
                <><Trash2 size={16} /><span style={{ fontSize: '11px', fontWeight: 600 }}></span></>
              </TooltipButton>
            </div>
          </>
        </div>
        )}

        {/* Right: Theme Toggle + Agent */}
        <div 
          className="toolbar-section toolbar-right-section"
        >
          <div
            className={`theme-switch-wrapper ${theme === 'dark' ? 'dark' : 'light'}`}
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            <div className="theme-switch-handle">
              {theme === 'dark' ? (
                <Moon size={12} color="#a855f7" fill="#a855f7" />
              ) : (
                <Sun size={12} color="#eab308" fill="#eab308" />
              )}
            </div>
          </div>
          {isAgentChatCollapsed && (
            <TooltipButton
              className="toolbar-btn chat-toggle-btn"
              onClick={onChatToggle}
              tooltip="Open AI Assistant"
            >
              <Sparkles size={16} />
            </TooltipButton>
          )}
        </div>
      </div>
      
      {/* Show placeholder when no file - but not in estimate or rfi mode */}
      {!file && activeMode !== 'estimate' && activeMode !== 'rfi' && (
        <div className="pdf-annotator-empty" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: 'var(--text-placeholder)', fontSize: '14px' }}>No file selected</p>
        </div>
      )}
      
      {/* Specs Mode - Transitioning state */}
      {activeMode === 'specs' && isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'var(--bg-primary)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid var(--border-color)',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Loading Specifications...</p>
          </div>
        </div>
      )}

      {/* Specs Mode - Completely separate viewer */}
      {file && activeMode === 'specs' && !isTransitioning && (
        <div 
          className="pdf-annotator-wrapper"
          style={{
            background: 'var(--border-color)',
            padding: '2rem'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}>
            <div className="pdf-page-container">
              <Document
                key="specs-document"
                file={file.url}
                onLoadSuccess={(pdf: any) => {
                  setSpecsNumPages(pdf.numPages);
                  // Restore saved specs page number
                  if (estimationId && file?.name) {
                    const storageKey = `specs-page-number-${estimationId}-${file.name}`;
                    const savedPage = localStorage.getItem(storageKey);
                    if (savedPage) {
                      const pageNum = parseInt(savedPage, 10);
                      if (pageNum >= 1 && pageNum <= pdf.numPages) {
                        setSpecsPageNumber(pageNum);
                        console.log(`📖 Restored saved specs page number: ${pageNum}`);
                      } else {
                        setSpecsPageNumber(1);
                      }
                    } else {
                      setSpecsPageNumber(1);
                    }
                  } else {
                    setSpecsPageNumber(1);
                  }
                }}
                loading={
                  <div style={{
                    position: 'fixed',
                    transform: 'translateX(-15%)',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'var(--bg-primary)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '1rem',
                    zIndex: 9999
                  }}>
                    <div style={{
                      animation: 'pulse-blue 2s ease-in-out infinite'
                    }}>
                      <FileText size={48} color="#3b82f6" />
                    </div>
                    <p style={{
                      fontSize: '0.875rem',
                      color: 'var(--text-muted)',
                      margin: 0
                    }}>Loading specifications...</p>
                  </div>
                }
                error={
                  <div className="pdf-error">
                    <p>Failed to load specifications PDF</p>
                  </div>
                }
              >
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Page
                    pageNumber={specsPageNumber}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    scale={specsScale}
                    rotate={specsPageRotations[specsPageNumber] || 0}
                    onLoadSuccess={(page: any) => {
                      // Apply PDF's native rotation if no manual rotation has been set
                      const nativeRotation = page.rotate || 0;
                      const savedRotation = specsPageRotations[specsPageNumber];
                      
                      if (savedRotation === undefined && nativeRotation !== 0) {
                        console.log(`📄 Specs page ${specsPageNumber} has native rotation: ${nativeRotation}°`);
                        setSpecsPageRotations(prev => {
                          const updatedRotations = {
                            ...prev,
                            [specsPageNumber]: nativeRotation
                          };
                          
                          // Save to localStorage for persistence
                          if (estimationId && file?.name) {
                            try {
                              const storageKey = `specs-rotations-${estimationId}-${file.name}`;
                              localStorage.setItem(storageKey, JSON.stringify(updatedRotations));
                            } catch { /* quota exceeded — ignore */ }
                          }
                          
                          return updatedRotations;
                        });
                      }
                    }}
                  />
                  
                  {/* Bounding box highlight overlay */}
                  {highlightBoundingBox && (() => {
                    console.log('🟡 Rendering bounding box highlight:', highlightBoundingBox);
                    return (
                      <div
                      style={{
                        position: 'absolute',
                        top: `${highlightBoundingBox.top * 100}%`,
                        left: `${highlightBoundingBox.left * 100}%`,
                        width: `${highlightBoundingBox.width * 100}%`,
                        height: `${highlightBoundingBox.height * 100}%`,
                        backgroundColor: 'rgba(254, 240, 138, 0.5)', // Yellow with transparency
                        pointerEvents: 'none',
                        zIndex: 1000,
                        animation: 'quote-highlight-pulse 0.5s ease-in-out'
                      }}
          />
                    );
                  })()}
        </div>
              </Document>
            </div>
          </div>
        </div>
      )}

      {/* NECA Mode - Transitioning state */}
      {activeMode === 'neca' && isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'var(--bg-primary)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid var(--border-color)',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Loading NECA Manual...</p>
          </div>
        </div>
      )}

      {/* NECA Mode - Labor Manual viewer */}
      {activeMode === 'neca' && !isTransitioning && (
        <div 
          className="pdf-annotator-wrapper"
          style={{
            padding: 0,
            margin: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden'
          }}
        >
          {(() => {
            console.log('🔍 NECA Mode Rendering:', {
              activeMode,
              isTransitioning,
              file: file,
              fileUrl: file?.url,
              fileName: file?.name
            });
            return null;
          })()}
          
          {!file?.url ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: '1rem'
            }}>
              <FileText size={48} color="#ccc" />
              <p style={{ color: 'var(--text-muted)' }}>NECA Manual not loaded</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-placeholder)' }}>File URL: {file?.url || 'undefined'}</p>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              position: 'relative',
              width: '100%',
              height: '100%',
              overflow: 'auto',
              background: 'var(--border-color)'
            }}>
              <div className="pdf-page-container" style={{ padding: '2rem 0' }}>
                <Document
                  key="neca-manual-document"
                  file={file.url}
                  onLoadSuccess={(pdf: any) => {
                    console.log('✅ NECA PDF loaded successfully:', pdf.numPages, 'pages');
                    setNecaNumPages(pdf.numPages);
                    // Restore saved NECA page number
                    if (estimationId) {
                      const storageKey = `neca-page-number-${estimationId}`;
                      const savedPage = localStorage.getItem(storageKey);
                      if (savedPage) {
                        const pageNum = parseInt(savedPage, 10);
                        if (pageNum >= 1 && pageNum <= pdf.numPages) {
                          setNecaPageNumber(pageNum);
                          console.log(`📖 Restored saved NECA page number: ${pageNum}`);
                        } else {
                          setNecaPageNumber(1);
                        }
                      } else {
                        setNecaPageNumber(1);
                      }
                    } else {
                      setNecaPageNumber(1);
                    }
                  }}
                  onLoadError={(error: any) => {
                    console.error('❌ NECA PDF load error:', error);
                  }}
                  options={pdfOptions}
                  loading={
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '3rem'
                    }}>
                      <div className="loading-spinner" style={{ 
                        width: '40px', 
                        height: '40px', 
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #666',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    </div>
                  }
                >
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    {necaNumPages && necaPageNumber <= necaNumPages ? (
                      <Page
                        pageNumber={necaPageNumber}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        scale={necaScale}
                        loading={<div />}
                        error={<div />}
                        onLoadSuccess={(page: any) => {
                          // Apply PDF's native rotation if no manual rotation has been set
                          const nativeRotation = page.rotate || 0;
                          
                          // For NECA mode, we don't have per-page rotations, so just log it
                          if (nativeRotation !== 0) {
                            console.log(`📄 NECA page ${necaPageNumber} has native rotation: ${nativeRotation}°`);
                          }
                        }}
                      />
                    ) : null}
                  </div>
                </Document>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Estimate Mode - Transitioning state */}
      {activeMode === 'estimate' && isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'var(--bg-primary)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid var(--border-color)',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Loading Estimate...</p>
          </div>
        </div>
      )}

      {/* Estimate Mode - Simplified Estimate Table (Labor Agent Based) */}
      {activeMode === 'estimate' && !isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{ background: 'var(--bg-secondary)', padding: 0, overflow: 'auto' }}>
          <SimplifiedEstimateTable
            estimationId={estimationId}
            projectName={estimation?.name}
          />
        </div>
      )}

      {/* Draw Mode - Transitioning state */}
      {activeMode === 'draw' && isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'var(--bg-primary)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid var(--border-color)',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Loading Drawing...</p>
          </div>
        </div>
      )}

      {/* Draw Mode - Main canvas with annotations */}
      {file && activeMode === 'draw' && !isTransitioning && (
      <div className="pdf-draw-layout">
        <PageIndexSidebar
          numPages={numPages}
          pageNumber={pageNumber}
          pageIndex={pageIndex}
          pdfDocument={pdfDocument}
          onNavigateToPage={navigateToPage}
          onReindex={onReindex}
        />
      <div 
        className="pdf-annotator-wrapper"
        ref={containerRef}
      >
        
        {!isPageFullyLoaded && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'var(--bg-primary)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            zIndex: 9999
          }}>
            <div style={{
              transform: 'translateX(-200%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <Loader2 size={32} color="#3b82f6" style={{
                animation: 'spin 1s linear infinite'
              }} />
              <p style={{
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
                margin: 0
              }}>Loading drawing...</p>
            </div>
          </div>
        )}
        
        <div 
          className="pdf-viewport" 
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            background: 'var(--bg-tertiary)'
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => {
            handleMouseUp();
            setClickCoords(null);
            setHoverCursor('default');
            setHoveredAnnotation(null);
            setHoveredAnnotationPoint(null);
          }}
          onContextMenu={handleContextMenu}
        >
          <div className="pdf-stage" style={{
            transform: `translate(${panX}px, ${panY}px) scale(${scale})`,
            transformOrigin: '0 0',
            position: 'absolute',
            transition: isPanning ? 'none' : 'transform 0.1s ease-out'
          }}>
            <div style={{
              boxShadow: '0 10px 30px rgba(0,0,0,0.15), 0 1px 8px rgba(0,0,0,0.1)',
              background: 'var(--bg-primary)',
              position: 'relative',
              display: 'inline-block'
            }}>
          <Document
            file={file.url}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<div />}
            error={
              <div className="pdf-error">
                <p>Failed to load PDF</p>
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              onLoadSuccess={onPageLoadSuccess}
              onRenderSuccess={handlePhase1Complete}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              scale={pdfRenderScale}
              devicePixelRatio={DPR_CAP}
              rotate={pageRotations[pageNumber] || 0}
            />
          </Document>
          
          <canvas
            ref={canvasRef}
            className="annotation-canvas"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleCanvasDrop}
            style={{
              cursor: autocountMode === 'area-selection' ? 'crosshair' :
                     lightingScheduleMode ? 'crosshair' :
                     isElementDetectionMode ? 'crosshair' :
                     isGridSnapActive ? 'crosshair' :
                     isPanning ? 'grabbing' :
                     resizeHandle ? `${resizeHandle.handle}-resize` :
                     (hoverCursor !== 'default' && currentTool === 'select') ? hoverCursor :
                     isSelecting ? 'default' :
                     isCapturingSnapshot ? 'crosshair' :
                     (activePlacementComponent && countingModeActive) ? 'crosshair' :
                     currentTool === 'piping' ? 'crosshair' :
                     (currentTool === 'select' && dragStart) ? 'move' :
                     (hoveredAnnotation && selectedAnnotations.has(hoveredAnnotation) && currentTool === 'select' && annotations.find(a => a.id === hoveredAnnotation)?.type !== 'piping') ? 'move' :
                     (hoveredAnnotation && annotations.find(a => a.id === hoveredAnnotation)?.type === 'piping') ? 'pointer' :
                     (hoveredAnnotation ? 'pointer' :
                     (currentTool === 'select' ? 'default' :
                     (currentTool === 'drag' ? 'grab' :
                     (currentTool === 'snapshot' ? 'crosshair' : 'crosshair'))))
            }}
          />

          {isUpgradingRes && (
            <div style={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              background: 'rgba(0,0,0,0.5)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: 4,
              fontSize: 11,
              zIndex: 20,
              pointerEvents: 'none',
            }}>
              Enhancing resolution…
            </div>
          )}
          
          <HandDrawnAnnotations
            isEnabled={handDrawnEnabled && !isCalibrating && !autocountMode && !lightingScheduleMode}
            canvasRef={canvasRef}
            currentTool={currentTool}
            selectedColor={isWireRunMode && selectedWireType ? selectedWireType.color : handDrawnColor}
            lineWidth={handDrawnLineWidth}
            fontSize={handDrawnFontSize}
            highlightOpacity={0.3}
            estimationId={estimationId}
            fileId={file?.id || ''}
            pageNumber={pageNumber}
            currentPageCalibration={calibrationData[pageNumber]}
            onDrop={handleCanvasDrop}
            externalSelectedStrokeIds={currentTool === 'select' ? selectedHandDrawnStrokes : undefined}
            onStrokeSelectionChange={handleStrokeSelectionChange}
            onStrokesQuery={() => (window as any).getHandDrawnStrokes?.() || []}
            onContextMenu={(e) => {
              e.preventDefault();
              
              // Don't show context menu if right-click panning
              if (isRightClickPanning || isPanning) {
                return;
              }
              
              // Right-click on an annotation = instant delete
              const canvas = canvasRef.current;
              if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const scaleX = canvas.width / rect.width;
                const scaleY = canvas.height / rect.height;
                const cx = (e.clientX - rect.left) * scaleX;
                const cy = (e.clientY - rect.top) * scaleY;
                const hitAnnotation = findAnnotationAtPoint(cx, cy);
                if (hitAnnotation) {
                  console.log('🗑️ Right-click delete annotation (from HandDrawn layer):', hitAnnotation.id);
                  const currentAnnotations = annotationsRef.current;
                  const deletedIds = new Set<string>([hitAnnotation.id]);
                  currentAnnotations.forEach((annotation) => {
                    if (annotation.type !== 'piping') return;
                    const startDeleted = annotation.startAnnotationId && deletedIds.has(annotation.startAnnotationId);
                    const endDeleted = annotation.endAnnotationId && deletedIds.has(annotation.endAnnotationId);
                    if (startDeleted || endDeleted) deletedIds.add(annotation.id);
                  });
                  const filtered = currentAnnotations.filter((annotation) => !deletedIds.has(annotation.id));
                  const newAnnotations = renumberAnnotations(filtered);
                  const deletedComponentTypes = Array.from(new Set(
                    currentAnnotations
                      .filter((annotation) => deletedIds.has(annotation.id))
                      .map((annotation) => annotation.component_type)
                      .filter(Boolean)
                  )) as string[];
                  annotationsRef.current = newAnnotations;
                  setAnnotations(newAnnotations);
                  addToHistory(newAnnotations);
                  setSelectedAnnotation(null);
                  void persistDeletedAnnotations(Array.from(deletedIds), deletedComponentTypes);
                  requestSaveNow(newAnnotations);
                  return;
                }
              }
              
              console.log('🖱️ Right-click forwarded from HandDrawnAnnotations!', { activePlacementComponent: activePlacementComponent?.name, countingModeActive, isWireRunMode });
              
              // Show wire run context menu if in wire run mode (but not actively drawing)
              if (isWireRunMode && selectedWireType) {
                console.log('✅ Showing wire run context menu at:', e.clientX, e.clientY);
                setWireRunContextMenu({ x: e.clientX, y: e.clientY });
                return;
              }
              
            }}
            onHistoryChange={(canUndo, canRedo) => {
              setCanUndoHandDrawn(canUndo);
              setCanRedoHandDrawn(canRedo);
            }}
            isWireRunMode={isWireRunMode}
            onWireRunComplete={handleWireRunComplete}
            wireRunType={selectedWireType ? { name: selectedWireType.name, color: selectedWireType.color } : null}
            onWireRunClick={(x, y) => {
              // Check if clicking on an existing wire run annotation
              const wireRunAnnotation = annotations
                .filter(a => a.page === pageNumber && a.type === 'wire-run')
                .find(a => {
                  if (!a.points || a.points.length < 2) return false;
                  for (let i = 0; i < a.points.length - 1; i++) {
                    const dist = pointToLineDistance(x, y, a.points[i].x, a.points[i].y, a.points[i+1].x, a.points[i+1].y);
                    if (dist < 15) return true;
                  }
                  return false;
                });
              
              if (wireRunAnnotation) {
                console.log('🔌 Clicked on existing wire run, switching to Wires tab');
                if (onSwitchToWiresTab) {
                  onSwitchToWiresTab();
                }
              }
            }}
            onWireRunContextMenu={(e, canFinish, pointCount, finishCallback, cancelCallback) => {
              console.log('🔌 Wire run right-click - points:', pointCount, 'canFinish:', canFinish);
              // Immediately finish the wire run on right-click if we have enough points
              if (canFinish) {
                console.log('🔌 Finishing wire run automatically on right-click');
                finishCallback();
                // Clean up wire run mode
                setIsWireRunMode(false);
                setSelectedWireType(null);
                setHandDrawnEnabled(false);
                setCurrentTool('select');
              }
            }}
            onRightClickPan={(e, phase) => {
              if (phase === 'start') {
                console.log('🖱️ Right-click pan START from HandDrawnAnnotations');
                setIsRightClickPanning(true);
                setIsPanning(true);
                setPanStart({
                  x: e.clientX,
                  y: e.clientY,
                  panX: panX,
                  panY: panY
                });
              } else if (phase === 'move') {
                if (panStart) {
                  const deltaX = e.clientX - panStart.x;
                  const deltaY = e.clientY - panStart.y;
                  setPanX(panStart.panX + deltaX);
                  setPanY(panStart.panY + deltaY);
                }
              } else if (phase === 'end') {
                console.log('🖱️ Right-click pan END from HandDrawnAnnotations');
                setIsRightClickPanning(false);
                setIsPanning(false);
                setPanStart(null);
              }
            }}
            isSquareFootageMode={isSquareFootageMode}
            onSquareFootageComplete={handleSquareFootageComplete}
          />
          </div>

          {/* Snapshot Action Buttons */}
          {showSnapshotActions && snapshotSelection && (() => {
            const canvas = canvasRef.current;
            if (!canvas) return null;
            
            // Use utility function to convert canvas coords to visual coords
            const visualCoords = canvasToVisualCoords(
              Math.min(snapshotSelection.startX, snapshotSelection.endX),
              Math.min(snapshotSelection.startY, snapshotSelection.endY)
            );
            
            const left = visualCoords.x;
            const top = visualCoords.y - 50;
            
            // Debug logging with dynamic info
            console.log('🎯 Snapshot Button Positioning:', {
              canvasSize: { width: canvas.width, height: canvas.height },
              zoomScale: scale,
              selection: {
                minX: Math.min(snapshotSelection.startX, snapshotSelection.endX),
                minY: Math.min(snapshotSelection.startY, snapshotSelection.endY)
              },
              buttonPosition: { left, top }
            });
            
            return (
              <div 
                className="snapshot-actions"
                style={{
                  position: 'absolute',
                  left: left,
                  top: top,
                  zIndex: 1000,
                  display: 'flex',
                  gap: '8px',
                  background: 'var(--bg-primary)',
                  padding: '8px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  border: '1px solid var(--border-color)',
                  transform: `scale(${1 / scale})`,
                  transformOrigin: 'top left'
                }}
            >
              {/* Only show Extract Key button if not in auto-extract mode */}
              {!autoExtractKeyOnSnapshot && (
                <button
                  className="snapshot-action-btn primary"
                  onClick={handleExtractKey}
                  style={{
                    padding: '6px 12px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Key size={14} />
                  Extract Power Legend
                </button>
              )}
              <button
                className="snapshot-action-btn primary"
                onClick={handleSendToAgent}
                style={{
                  padding: '6px 12px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Send size={14} />
                Send to Agent
              </button>
              <button
                className="snapshot-action-btn"
                onClick={handleCancelSnapshot}
                style={{
                  padding: '6px 12px',
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
            );
          })()}
          </div>
        </div>
      </div>
      </div>
      )}

      {/* RFI Mode - Transitioning state */}
      {activeMode === 'rfi' && isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'var(--bg-primary)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid var(--border-color)',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Loading RFIs...</p>
          </div>
        </div>
      )}

      {/* RFI Mode - Grid View or Editor */}
      {activeMode === 'rfi' && !isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{
          background: 'var(--bg-secondary)',
          display: 'flex',
          padding: '0',
          overflow: 'hidden'
        }}>
          <RFIManager
            estimationId={estimationId}
            projectName={estimation?.name}
            contractNumber={estimation?.contract_number}
          />
        </div>
      )}

      {/* Wire Run Context Menu (when NOT actively drawing) */}
      {wireRunContextMenu && isWireRunMode && selectedWireType && (
        <div
          className="placement-context-menu"
          style={{
            position: 'fixed',
            left: wireRunContextMenu.x,
            top: wireRunContextMenu.y,
            zIndex: 100000
          }}
        >
          <div className="context-menu-header">
            <span style={{ color: selectedWireType.color }}>●</span>
            {selectedWireType.name}
          </div>
          <button
            onClick={() => {
              console.log('🔌 Stopping wire run mode');
              setIsWireRunMode(false);
              setSelectedWireType(null);
              setHandDrawnEnabled(false);
              setCurrentTool('select');
              setWireRunContextMenu(null);
            }}
          >
            ✕ Stop Drawing Wires
          </button>
          <button
            onClick={() => {
              console.log('🔌 Changing wire type');
              setWireRunContextMenu(null);
              setShowWireTypeModal(true);
            }}
          >
            🔀 Change Wire Type
          </button>
          <button
            onClick={() => setWireRunContextMenu(null)}
          >
            ↩ Continue Drawing
          </button>
        </div>
      )}

      {/* Wire Run Drawing Context Menu - REMOVED: Right-click now auto-finishes */}

      {/* Calibration Dropdown */}
      <CalibrationDropdown
        isOpen={showCalibrationDropdown}
        dropdownRef={calibrationDropdownRef}
        currentPageNumber={pageNumber}
        currentPageCalibration={calibrationData[pageNumber]}
        allCalibrations={calibrationData}
        onStartCalibration={handleStartCalibration}
        onCopyFromPage={handleCopyCalibration}
        onToggleReferenceLine={toggleReferenceLine}
      />

      {/* Calibration Modal */}
      <CalibrationModal
        isOpen={showCalibrationModal}
        percentage={calibrationLine?.percentage || 0}
        onClose={() => {
          setShowCalibrationModal(false);
          setIsCalibrating(false);
          setCurrentTool('select');
        }}
        onSave={handleCalibrationSave}
        onRedo={handleCalibrationRedo}
      />

      {/* New Component Modal for Count */}
      {showNewComponentModal && (
        <div 
          className="new-component-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100001
          }}
          onClick={() => {
            setShowNewComponentModal(false);
            setSelectedComponentsForBatch(new Set());
            setIsAddingNewOnly(false);
          }}
        >
          <div 
            className="new-component-modal"
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '12px',
              padding: '24px',
              width: '90vw',
              maxWidth: isAddingNewOnly ? '400px' : '760px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)',
              border: '1px solid var(--border-color)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '18px', fontWeight: 600 }}>
                {isAddingNewOnly ? 'Add New Component' : (countingModalMode === 'manual' ? (newComponentName ? 'Add to Key' : 'Manual Count') : 'AI Autocount')}
              </h3>
              <button
                onClick={() => {
                  setShowNewComponentModal(false);
                  setQuickCountSearch('');
                  setSelectedComponentsForBatch(new Set());
                  setIsAddingNewOnly(false);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Extract Buttons - Auto mode only (power legend extraction disabled — standard classes auto-added on load) */}
            {countingModalMode === 'auto' && !isAddingNewOnly && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px', flexShrink: 0 }}>
                <button
                  onClick={handleStartLightingScheduleExtraction}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
                  }}
                >
                  <Lightbulb size={16} />
                  Extract Lighting Schedule
                </button>
              </div>
            )}

            {/* Two-Column Layout */}
            <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              
              {/* LEFT COLUMN — Existing Components */}
              {!isAddingNewOnly && electricalKey && electricalKey.components && (() => {
                const allAvailable = countingModalMode === 'auto'
                  ? electricalKey.components.filter((c: any) => c.referenceImage || c.symbolType === 'Lighting' || c.componentType === 'Switches')
                  : electricalKey.components;
                
                const regularComponents = allAvailable.filter((c: any) => (c.symbolType || '') !== 'Lighting');
                const lightingComponents = allAvailable.filter((c: any) => c.symbolType === 'Lighting');
                const availableComponents = autocountModalTab === 'lighting' ? lightingComponents : regularComponents;
                
                const filteredComponents = availableComponents.filter((c: any) => {
                  const q = quickCountSearch.toLowerCase();
                  return c.name.toLowerCase().includes(q) ||
                    (c.symbol && c.symbol.toLowerCase().includes(q)) ||
                    (c.description && c.description.toLowerCase().includes(q));
                });
                
                return (regularComponents.length > 0 || lightingComponents.length > 0) ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '6px',
                      color: 'var(--text-secondary)', 
                      fontSize: '13px', 
                      marginBottom: '8px', 
                      fontWeight: 500,
                      flexShrink: 0
                    }}>
                      <Sparkles size={14} style={{ color: '#3b82f6' }} />
                      Existing Components
                    </label>

                    {/* Power / Lighting Tab Bar */}
                    <div style={{
                      display: 'flex',
                      gap: '0px',
                      marginBottom: '8px',
                      background: 'var(--bg-tertiary)',
                      borderRadius: '8px',
                      padding: '3px',
                      flexShrink: 0
                    }}>
                      <button
                        onClick={() => { setAutocountModalTab('components'); setSelectedComponentsForBatch(new Set()); }}
                        style={{
                          flex: 1,
                          padding: '7px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'all 0.2s',
                          background: autocountModalTab === 'components' ? 'var(--bg-primary)' : 'transparent',
                          color: autocountModalTab === 'components' ? 'var(--text-primary)' : 'var(--text-muted)',
                          boxShadow: autocountModalTab === 'components' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        }}
                      >
                        <Key size={13} />
                        Power ({regularComponents.length})
                      </button>
                      <button
                        onClick={() => { setAutocountModalTab('lighting'); setSelectedComponentsForBatch(new Set()); }}
                        style={{
                          flex: 1,
                          padding: '7px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'all 0.2s',
                          background: autocountModalTab === 'lighting' ? 'var(--bg-primary)' : 'transparent',
                          color: autocountModalTab === 'lighting' ? 'var(--text-primary)' : 'var(--text-muted)',
                          boxShadow: autocountModalTab === 'lighting' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        }}
                      >
                        <Lightbulb size={13} />
                        Lighting ({lightingComponents.length})
                      </button>
                    </div>
                    
                    {/* Search Bar */}
                    <div style={{ marginBottom: '8px', flexShrink: 0 }}>
                      <input
                        type="text"
                        value={quickCountSearch}
                        onChange={(e) => setQuickCountSearch(e.target.value)}
                        placeholder="Search components..."
                        style={{
                          width: '100%',
                          padding: '8px 12px 8px 36px',
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-strong)',
                          borderRadius: '8px',
                          color: 'var(--text-primary)',
                          fontSize: '13px',
                          outline: 'none',
                          boxSizing: 'border-box',
                          backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%236b7280\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Ccircle cx=\'11\' cy=\'11\' r=\'8\'%3E%3C/circle%3E%3Cpath d=\'m21 21-4.35-4.35\'%3E%3C/path%3E%3C/svg%3E")',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: '12px center'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#3b82f6';
                          e.currentTarget.style.background = 'var(--bg-primary)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'var(--border-strong)';
                          e.currentTarget.style.background = 'var(--bg-secondary)';
                        }}
                      />
                    </div>

                    {/* Select All / Deselect All Button - Auto mode only */}
                    {countingModalMode === 'auto' && filteredComponents.length > 0 && (() => {
                      const allSelected = selectedComponentsForBatch.size === filteredComponents.length && filteredComponents.length > 0;
                      return (
                        <button
                          onClick={() => {
                            if (allSelected) {
                              setSelectedComponentsForBatch(new Set());
                            } else {
                              const allKeys = new Set(filteredComponents.map((c: any) => 
                                c.symbolType === 'Lighting' ? (c.symbol || c.name) : c.name
                              ));
                              setSelectedComponentsForBatch(allKeys);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            marginBottom: '8px',
                            background: 'var(--bg-secondary)',
                            border: '1px solid var(--border-strong)',
                            borderRadius: '8px',
                            color: 'var(--text-secondary)',
                            fontSize: '12px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            transition: 'all 0.2s',
                            flexShrink: 0
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--bg-primary)';
                            e.currentTarget.style.borderColor = '#3b82f6';
                            e.currentTarget.style.color = '#3b82f6';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'var(--bg-secondary)';
                            e.currentTarget.style.borderColor = 'var(--border-strong)';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={allSelected}
                            readOnly
                            style={{ marginRight: '4px' }}
                          />
                          {allSelected ? `Deselect All (${filteredComponents.length})` : `Select All (${filteredComponents.length})`}
                        </button>
                      );
                    })()}
                    
                    {/* Scrollable Component List */}
                    <div style={{
                      flex: 1,
                      overflowY: 'auto',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      background: 'var(--bg-secondary)',
                      minHeight: 0
                    }}>
                      {filteredComponents.length > 0 ? (
                        filteredComponents.map((component: any, idx: number) => {
                          const selectionKey = component.symbolType === 'Lighting' ? (component.symbol || component.name) : component.name;
                          return (
                          <div
                            key={selectionKey + '-' + idx}
                            onClick={() => {
                              if (countingModalMode === 'auto') {
                                setSelectedComponentsForBatch(prev => {
                                  const newSet = new Set(prev);
                                  if (newSet.has(selectionKey)) {
                                    newSet.delete(selectionKey);
                                  } else {
                                    newSet.add(selectionKey);
                                  }
                                  return newSet;
                                });
                              } else {
                                setNewComponentName(component.name);
                                setNewComponentColor(component.color);
                                handleStartQuickCount();
                              }
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: idx < filteredComponents.length - 1 ? '1px solid var(--border-color)' : 'none',
                              transition: 'all 0.2s',
                              background: countingModalMode === 'auto' && selectedComponentsForBatch.has(selectionKey) ? 'var(--blue-tint-light)' : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = countingModalMode === 'auto' && selectedComponentsForBatch.has(selectionKey) ? 'var(--blue-tint-medium)' : 'var(--bg-primary)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = countingModalMode === 'auto' && selectedComponentsForBatch.has(selectionKey) ? 'var(--blue-tint-light)' : 'transparent';
                            }}
                          >
                            {countingModalMode === 'auto' && (
                              <input
                                type="checkbox"
                                checked={selectedComponentsForBatch.has(selectionKey)}
                                readOnly
                                style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                              />
                            )}

                            {component.symbolType === 'Lighting' ? (
                              <div style={{
                                minWidth: '36px', height: '36px', borderRadius: '6px',
                                background: 'linear-gradient(135deg, var(--yellow-tint-medium) 0%, var(--yellow-tint-strong) 100%)',
                                border: '2px solid #f59e0b',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: '13px', color: 'var(--yellow-text-dark)', flexShrink: 0
                              }}>
                                {component.symbol || '?'}
                              </div>
                            ) : component.referenceImage ? (
                              <img 
                                src={component.referenceImage} 
                                alt={component.name}
                                style={{
                                  width: '36px', height: '36px', objectFit: 'cover',
                                  borderRadius: '6px', border: `2px solid ${component.color}`,
                                  flexShrink: 0, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '36px', height: '36px', borderRadius: '6px',
                                backgroundColor: component.color, flexShrink: 0,
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                border: '2px solid rgba(0, 0, 0, 0.1)'
                              }} />
                            )}
                            
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {component.symbolType === 'Lighting' ? (component.symbol || component.name) : component.name}
                              </span>
                              {component.symbolType === 'Lighting' && (
                                <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', lineHeight: '1.3', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {component.description || component.name}
                                </span>
                              )}
                            </div>
                            
                            {countingModalMode === 'manual' && (
                              <ChevronRight size={16} style={{ color: 'var(--text-placeholder)' }} />
                            )}
                          </div>
                          );
                        })
                      ) : (
                        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-placeholder)', fontSize: '13px' }}>
                          No components found matching "{quickCountSearch}"
                        </div>
                      )}
                    </div>

                    {/* Count Components Button - Auto mode only */}
                    {countingModalMode === 'auto' && filteredComponents.length > 0 && (
                      <button
                        onClick={handleBatchAutocount}
                        disabled={selectedComponentsForBatch.size === 0}
                        style={{
                          width: '100%',
                          padding: '12px 20px',
                          marginTop: '10px',
                          background: selectedComponentsForBatch.size > 0 
                            ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                            : 'var(--border-strong)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: selectedComponentsForBatch.size > 0 ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          boxShadow: selectedComponentsForBatch.size > 0 
                            ? '0 4px 12px rgba(59, 130, 246, 0.3)'
                            : 'none',
                          transition: 'all 0.2s',
                          opacity: selectedComponentsForBatch.size > 0 ? 1 : 0.6,
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          if (selectedComponentsForBatch.size > 0) {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedComponentsForBatch.size > 0) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                          }
                        }}
                      >
                        <Rocket size={16} />
                        Count ({selectedComponentsForBatch.size})
                      </button>
                    )}
                  </div>
                ) : null;
              })()}

              {/* Vertical Divider — only show when left column has content */}
              {!isAddingNewOnly && (electricalKey?.components?.length || 0) > 0 && (
                <div style={{ width: '1px', background: 'var(--border-color)', flexShrink: 0, alignSelf: 'stretch' }} />
              )}

                {/* RIGHT COLUMN — Create New */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
                {!isAddingNewOnly && (
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '6px',
                    color: 'var(--text-secondary)', 
                    fontSize: '13px', 
                    marginBottom: '12px', 
                    fontWeight: 500 
                  }}>
                    <Plus size={14} style={{ color: '#3b82f6' }} />
                    Create New
                  </label>
                )}

                {/* Reference Image Preview (for Add New Component flow) */}
                {isAddingNewOnly && referenceAreaImage && (
                  <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '8px', fontWeight: 500, alignSelf: 'flex-start' }}>
                      Scanned Symbol
                    </label>
                    <div style={{
                      width: '100%',
                      height: '140px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '12px',
                      background: 'var(--bg-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <img 
                        src={referenceAreaImage} 
                        alt="Scanned symbol" 
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          borderRadius: '4px',
                          background: 'var(--bg-primary)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                      />
                      <button
                        onClick={handleRetakeAreaSelection}
                        style={{
                          position: 'absolute',
                          bottom: '8px',
                          right: '8px',
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid var(--border-strong)',
                          borderRadius: '6px',
                          padding: '4px 8px',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: 'var(--text-secondary)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        <Camera size={12} />
                        Retake
                      </button>
                    </div>
                  </div>
                )}

                {/* Preset: Single-Pole Switch */}
                {!isAddingNewOnly && (() => {
                  const existingNames = new Set((electricalKey?.components || []).map((c: any) => c.name.toLowerCase()));
                  const hasSinglePoleSwitch = existingNames.has('single-pole switch') || existingNames.has('switch');
                  return !hasSinglePoleSwitch ? (
                    <button
                      onClick={async () => {
                        const presetSwitch = {
                          name: 'Single-Pole Switch',
                          color: 'rgba(34, 197, 94, 0.6)',
                          symbol: 'S',
                          componentType: 'Switches',
                          subscript: '',
                          description: 'Standard single-pole toggle switch',
                          referenceImage: '',
                          symbolType: 'Power',
                          pages: [],
                          totalCount: 0,
                        };
                        const currentComponents = electricalKey?.components || [];
                        const updatedKey = {
                          ...(electricalKey || {}),
                          components: [...currentComponents, presetSwitch],
                          extractedAt: (electricalKey as any)?.extractedAt || new Date().toISOString(),
                        };
                        if (onKeyExtracted) {
                          onKeyExtracted(updatedKey);
                        }
                        console.log('✅ Added preset: Single-Pole Switch (S)');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        width: '100%',
                        padding: '10px 12px',
                        marginBottom: '12px',
                        background: 'var(--green-tint-light)',
                        border: '1px dashed #86efac',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        textAlign: 'left',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#dcfce7'; e.currentTarget.style.borderColor = '#22c55e'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#86efac'; }}
                    >
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: '28px', height: '28px', borderRadius: '6px',
                        background: 'rgba(34, 197, 94, 0.15)', color: 'var(--green-text)',
                        fontWeight: 700, fontSize: '14px', flexShrink: 0,
                      }}>S</span>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>Single-Pole Switch</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-placeholder)' }}>Quick add preset</div>
                      </div>
                    </button>
                  ) : null;
                })()}

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '6px', fontWeight: 500 }}>
                    Component Name
                  </label>
                  <input
                    type="text"
                    value={newComponentName}
                    onChange={(e) => setNewComponentName(e.target.value)}
                    placeholder="e.g., Smoke Detector, Outlet"
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-strong)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newComponentName.trim()) {
                        if (countingModalMode === 'manual') {
                          handleStartQuickCount();
                        } else {
                          handleStartAreaSelection();
                        }
                      }
                    }}
                  />
                </div>

                {/* Action Button */}
                <div>
                  {countingModalMode === 'manual' ? (
                    <button
                      onClick={() => {
                        if (newComponentName.trim() && !isAddingComponent) {
                          if (referenceAreaImage) {
                            // If we have a snapshot, this is the "Add to Key" flow
                            handleConfirmAddToKey();
                          } else if (isAddingNewOnly) {
                            // If adding new component from sidebar, trigger scan flow
                            handleStartAreaSelection();
                          } else {
                            // Otherwise it's the normal "Start Counting" flow
                            handleStartQuickCount();
                          }
                        }
                      }}
                      disabled={!newComponentName.trim() || isAddingComponent}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: newComponentName.trim() && !isAddingComponent ? '#3b82f6' : 'var(--border-strong)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: newComponentName.trim() && !isAddingComponent ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (newComponentName.trim() && !isAddingComponent) {
                          e.currentTarget.style.background = '#2563eb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (newComponentName.trim() && !isAddingComponent) {
                          e.currentTarget.style.background = '#3b82f6';
                        }
                      }}
                    >
                      {isAddingComponent ? 'Adding...' : (referenceAreaImage ? 'Add to Key' : (isAddingNewOnly ? 'Scan Symbol' : 'Start Counting'))}
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        if (newComponentName.trim()) {
                          handleStartAreaSelection();
                        }
                      }}
                      disabled={!newComponentName.trim()}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: newComponentName.trim() ? '#3b82f6' : 'var(--border-strong)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: newComponentName.trim() ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (newComponentName.trim()) {
                          e.currentTarget.style.background = '#2563eb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (newComponentName.trim()) {
                          e.currentTarget.style.background = '#3b82f6';
                        }
                      }}
                    >
                      Select Area
                    </button>
                  )}
                  <div style={{ 
                    marginTop: '10px', 
                    fontSize: '12px', 
                    color: 'var(--text-muted)', 
                    textAlign: 'center',
                    lineHeight: '1.4'
                  }}>
                    {isAddingNewOnly ? (
                      referenceAreaImage 
                        ? 'Click "Add to Key" to save this component with its symbol' 
                        : 'Enter a name and click "Scan" to select the symbol from the PDF'
                    ) : (
                      countingModalMode === 'manual' 
                        ? (referenceAreaImage ? 'Click "Add to Key" to save this component with its symbol' : 'Click to place each component on the canvas')
                        : 'Select an area with one example to auto-detect all similar symbols'
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Autocount Area Selection Instruction Overlay */}
      {autocountMode === 'area-selection' && !showAutocountConfirmModal && pendingAutocountComponent && (
        <div style={{
          position: 'fixed',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(59, 130, 246, 0.95)',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '12px',
          fontSize: '15px',
          fontWeight: 500,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          zIndex: 100000,
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: 600 }}>
            {pendingAutocountComponent.name 
              ? `Select Reference Area for ${pendingAutocountComponent.name}`
              : 'Select Reference Area for Next Component'
            }
          </div>
          <div style={{ fontSize: '13px', opacity: 0.95 }}>
            Drag to select an area containing one example of the symbol
            {queuedComponents.length > 0 && (
              <span style={{ display: 'block', marginTop: '4px', fontWeight: 600 }}>
                {queuedComponents.length} component{queuedComponents.length > 1 ? 's' : ''} queued
              </span>
            )}
          </div>
          <button
            onClick={() => {
              setAutocountMode(null);
              setAutocountSelection(null);
              setPendingAutocountComponent(null);
              setQueuedComponents([]);
              setShowNewComponentModal(true);
            }}
            style={{
              marginTop: '12px',
              padding: '6px 16px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '13px',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Lighting Schedule Confirmation Modal */}
      {showLightingScheduleConfirmModal && lightingScheduleSnapshot && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100002
          }}
          onClick={() => {
            if (!isExtractingLightingSchedule) {
              setShowLightingScheduleConfirmModal(false);
              setLightingScheduleSnapshot(null);
            }
          }}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '28px',
              minWidth: '400px',
              maxWidth: '500px',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
              border: '1px solid var(--border-color)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Lightbulb size={22} style={{ color: 'var(--yellow-text)' }} />
                {isExtractingLightingSchedule ? 'Extracting Fixtures...' : 'Extract Lighting Schedule'}
              </h3>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>
                {isExtractingLightingSchedule
                  ? 'Analyzing the fixture schedule table with AI'
                  : 'I\'ll extract fixture codes and descriptions from this area.'}
              </p>
            </div>

            <div style={{
              marginBottom: '24px',
              border: '2px solid var(--border-color)',
              borderRadius: '10px',
              padding: '12px',
              background: 'var(--bg-secondary)',
              position: 'relative'
            }}>
              {isExtractingLightingSchedule && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1
                }}>
                  <Loader2 size={32} color="#f59e0b" style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              )}
              <img
                src={lightingScheduleSnapshot}
                alt="Selected lighting schedule area"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '300px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)'
                }}
              />
              {lightingScheduleSelection && (
                <p style={{
                  margin: '8px 0 0 0',
                  color: 'var(--text-muted)',
                  fontSize: '12px',
                  textAlign: 'center'
                }}>
                  Selected area: {Math.abs(lightingScheduleSelection.endX - lightingScheduleSelection.startX).toFixed(0)} x {Math.abs(lightingScheduleSelection.endY - lightingScheduleSelection.startY).toFixed(0)}px
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowLightingScheduleConfirmModal(false);
                  setLightingScheduleSnapshot(null);
                  setLightingScheduleSelection(null);
                  setLightingScheduleMode(false);
                  setIsExtractingLightingSchedule(false);
                }}
                disabled={false}
                style={{
                  padding: '12px 20px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--bg-divider)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--bg-tertiary)';
                }}
              >
                Cancel
              </button>
              {!isExtractingLightingSchedule && (
                <button
                  onClick={() => {
                    setShowLightingScheduleConfirmModal(false);
                    setLightingScheduleSnapshot(null);
                    setLightingScheduleSelection(null);
                    // Stay in selection mode so user can re-select
                  }}
                  style={{
                    padding: '12px 20px',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '8px',
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--bg-divider)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--bg-tertiary)';
                  }}
                >
                  Select Different Area
                </button>
              )}
              <button
                onClick={() => {
                  if (lightingScheduleSnapshot) {
                    setLightingScheduleMode(false);
                    handleLightingScheduleComplete(lightingScheduleSnapshot);
                  }
                }}
                disabled={isExtractingLightingSchedule}
                style={{
                  padding: '12px 24px',
                  background: isExtractingLightingSchedule ? 'var(--border-strong)' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: isExtractingLightingSchedule ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!isExtractingLightingSchedule) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isExtractingLightingSchedule) {
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                {isExtractingLightingSchedule ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    Confirm & Extract
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Selection Modal - before counting a component (above review modal) */}
      {showPageSelectionModal && pageSelectionContext && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100010,
            padding: '20px',
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => {}}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '24px',
              width: '95vw',
              maxWidth: '640px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
              border: '1px solid var(--border-color)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, marginBottom: '4px', color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600 }}>
              Page Selection
            </h3>
            <p style={{ margin: 0, marginBottom: '8px', color: 'var(--text-muted)', fontSize: '14px' }}>
              Select pages to count <strong>{pageSelectionContext.component.name}</strong> on. Click a page to toggle.
            </p>
            <p style={{ margin: 0, marginBottom: '16px', fontSize: '14px', color: '#2563eb', fontWeight: 700 }}>
              Current page: {pageNumber}
            </p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => numPages && setSelectedPagesForCount(new Set(Array.from({ length: numPages }, (_, i) => i + 1)))}
                style={{
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'var(--text-secondary)',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Select All
              </button>
              <button
                type="button"
                onClick={() => setSelectedPagesForCount(new Set())}
                style={{
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'var(--text-secondary)',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Deselect All
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '20px', minHeight: '200px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
                {numPages && Array.from({ length: numPages }, (_, i) => i + 1).map((pg) => {
                  const selected = selectedPagesForCount.has(pg);
                  const info = pageIndex?.find(p => p.page === pg);
                  const labelLower = info?.label?.toLowerCase() || '';
                  const badgeColors = labelLower.includes('power') ? { bg: '#dcfce7', color: 'var(--green-text)', border: '#bbf7d0' }
                    : labelLower.includes('lighting') ? { bg: '#fef9c3', color: 'var(--yellow-text)', border: '#fde68a' }
                    : labelLower.includes('demolition') || labelLower.includes('demo') ? { bg: 'var(--red-tint-medium)', color: '#dc2626', border: '#fecaca' }
                    : labelLower.includes('fire alarm') ? { bg: '#ffedd5', color: 'var(--yellow-text-dark)', border: '#fed7aa' }
                    : labelLower.includes('low voltage') || labelLower.includes('telecom') ? { bg: '#ede9fe', color: 'var(--purple-text)', border: '#ddd6fe' }
                    : labelLower.includes('panel') ? { bg: '#cffafe', color: 'var(--cyan-text-dark)', border: '#a5f3fc' }
                    : info?.label ? { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: 'var(--border-color)' }
                    : null;
                  return (
                    <button
                      key={pg}
                      type="button"
                      onClick={() => {
                        setSelectedPagesForCount(prev => {
                          const next = new Set(prev);
                          if (next.has(pg)) next.delete(pg);
                          else next.add(pg);
                          return next;
                        });
                      }}
                      style={{
                        padding: 0,
                        border: selected ? '3px solid #3b82f6' : '2px solid var(--border-color)',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        background: selected ? 'rgba(59, 130, 246, 0.08)' : 'var(--bg-secondary)',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px 0',
                        background: selected ? 'rgba(59, 130, 246, 0.05)' : 'var(--bg-secondary)',
                      }}>
                        <FileText size={32} style={{ color: selected ? '#3b82f6' : 'var(--text-placeholder)' }} />
                      </div>
                      <span style={{
                        padding: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: selected ? '#2563eb' : 'var(--text-secondary)',
                        textAlign: 'center',
                        background: selected ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-primary)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        <span>
                          Page {pg}
                          {selected && <Check size={14} style={{ marginLeft: '4px', verticalAlign: 'middle' }} />}
                        </span>
                        {badgeColors && info?.label && (
                          <span style={{
                            fontSize: '10px',
                            fontWeight: 600,
                            color: badgeColors.color,
                            background: badgeColors.bg,
                            border: `1px solid ${badgeColors.border}`,
                            borderRadius: '6px',
                            padding: '1px 6px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                          }}>
                            {info.sheetName ? `${info.sheetName} · ` : ''}{info.label}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
              <button
                type="button"
                onClick={() => {
                  setShowPageSelectionModal(false);
                  setPageSelectionContext(null);
                  setPageThumbnailsForSelection(null);
                  if (pageSelectionContext?.newComponents) {
                    setPendingKeyComponents([]);
                  }
                }}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--text-secondary)',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmPageSelection}
                disabled={selectedPagesForCount.size === 0}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff',
                  background: selectedPagesForCount.size === 0 ? 'var(--text-placeholder)' : '#3b82f6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: selectedPagesForCount.size === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                Start Counting ({selectedPagesForCount.size} {selectedPagesForCount.size === 1 ? 'page' : 'pages'})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Area Key Extraction Floating Panel */}
      {keyExtractionMode && !showKeyExtractionConfirmModal && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 360,
          maxHeight: 500,
          background: 'var(--bg-primary)',
          borderRadius: 16,
          boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
          zIndex: 100004,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '2px solid #3b82f6',
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 16px',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, var(--blue-tint-light) 0%, var(--bg-secondary) 100%)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Key size={16} style={{ color: '#3b82f6' }} />
              <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>
                Power Legend Extraction
              </span>
            </div>
            <button
              onClick={handleCancelKeyExtractionJobs}
              style={{ background: 'transparent', border: 'none', color: 'var(--text-placeholder)', cursor: 'pointer', padding: 4 }}
              title="Cancel"
            >
              <X size={16} />
            </button>
          </div>

          {/* Instruction or Job cards */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
            {keyExtractionJobs.length === 0 ? (
              <div style={{
                padding: '24px 12px',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: 13,
                lineHeight: 1.6,
              }}>
                <Key size={28} style={{ color: 'var(--border-strong)', marginBottom: 8 }} />
                <p style={{ margin: 0, fontWeight: 500, color: 'var(--text-secondary)' }}>
                  Select legend areas on the PDF
                </p>
                <p style={{ margin: '6px 0 0', fontSize: 12 }}>
                  Draw a rectangle around each legend section. You can add multiple areas if the legend is split.
                </p>
              </div>
            ) : (
              keyExtractionJobs.map((job, idx) => (
                <div
                  key={job.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 6px',
                    borderBottom: idx < keyExtractionJobs.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                  }}
                >
                  {/* Thumbnail */}
                  <img
                    src={job.snapshot}
                    alt={`Area ${idx + 1}`}
                    style={{
                      width: 64,
                      height: 44,
                      objectFit: 'cover',
                      borderRadius: 6,
                      border: '1px solid var(--border-color)',
                      flexShrink: 0,
                    }}
                  />
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>
                      Area {idx + 1}
                    </div>
                    <div style={{ fontSize: 11, color: job.status === 'error' ? '#ef4444' : 'var(--text-placeholder)', marginTop: 2 }}>
                      {job.status === 'queued' && 'Ready to extract'}
                      {job.status === 'processing' && 'Extracting...'}
                      {job.status === 'done' && `${job.results?.length || 0} symbols found`}
                      {job.status === 'error' && (job.error || 'Failed')}
                    </div>
                  </div>
                  {/* Status icon */}
                  <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    {job.status === 'queued' && <Check size={16} style={{ color: 'var(--text-placeholder)' }} />}
                    {job.status === 'processing' && <Loader2 size={16} className="spin" style={{ color: '#3b82f6' }} />}
                    {job.status === 'done' && <Check size={16} style={{ color: 'var(--green-text)' }} />}
                    {job.status === 'error' && <X size={16} style={{ color: '#ef4444' }} />}
                  </div>
                  {/* Delete button */}
                  <button
                    onClick={() => handleDeleteKeyExtractionJob(job.id)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--border-strong)',
                      cursor: 'pointer',
                      padding: 4,
                      flexShrink: 0,
                      borderRadius: 4,
                      transition: 'color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; }}
                    title="Remove this area"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Footer with Extract Key button */}
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            gap: 8,
            background: 'var(--bg-secondary)',
          }}>
            <div style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
              {keyExtractionJobs.length === 0
                ? 'Draw a rectangle on the PDF'
                : 'Select more areas or click Extract'}
            </div>
            <button
              onClick={handleFinishKeyExtractionSelection}
              disabled={keyExtractionJobs.length === 0}
              style={{
                padding: '8px 18px',
                background: keyExtractionJobs.length > 0
                  ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                  : 'var(--border-strong)',
                border: 'none',
                borderRadius: 8,
                color: '#ffffff',
                fontSize: 13,
                fontWeight: 600,
                cursor: keyExtractionJobs.length > 0 ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: keyExtractionJobs.length > 0
                  ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
              }}
            >
              <Sparkles size={14} />
              {keyExtractionJobs.length > 0 ? `Extract Key (${keyExtractionJobs.length})` : 'Extract Key'}
            </button>
          </div>
        </div>
      )}

      {/* Multi-Area Key Extraction: processing wait overlay */}
      {!keyExtractionMode && keyExtractionJobs.length > 0 && keyExtractionJobs.some(j => j.status === 'processing') && !showKeyExtractionPreview && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100003,
        }}>
          <div className="spin" style={{ marginBottom: 16 }}>
            <Loader2 size={48} color="#3b82f6" />
          </div>
          <p style={{ color: '#ffffff', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            Extracting Power Legend
          </p>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
            Analyzing legend areas for power symbols and names...
          </p>
          <button
            onClick={() => {
              keyExtractionJobs.forEach(j => {
                if (j.status === 'processing' && j.abortController) j.abortController.abort();
              });
              setKeyExtractionJobs([]);
            }}
            style={{
              marginTop: 24,
              padding: '10px 24px',
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 8,
              color: '#ffffff',
              fontSize: 14,
              cursor: 'pointer',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Key Extraction Confirmation Modal */}
      {showKeyExtractionConfirmModal && keyExtractionSnapshot && (
        <div 
          className="key-extraction-confirm-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100002,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => {
            setShowKeyExtractionConfirmModal(false);
            setKeyExtractionSnapshot(null);
            setKeyExtractionSelection(null);
            // Stay in key extraction mode for reselection
          }}
        >
          <div 
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '24px',
              width: '90%',
              maxWidth: '600px',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px'
            }}>
              <h3 style={{ 
                margin: 0, 
                color: 'var(--text-primary)', 
                fontSize: '18px', 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <Key size={20} style={{ color: '#3b82f6' }} />
                Confirm Key Selection
              </h3>
              <button
                onClick={() => {
                  setShowKeyExtractionConfirmModal(false);
                  setKeyExtractionSnapshot(null);
                  setKeyExtractionSelection(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Preview Image */}
            <div style={{ marginBottom: '20px' }}>
              <p style={{ 
                fontSize: '13px', 
                color: 'var(--text-muted)', 
                marginBottom: '10px',
                fontWeight: 500
              }}>
                Selected Area:
              </p>
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: '12px',
                padding: '12px',
                border: '2px solid #3b82f6',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <img 
                  src={keyExtractionSnapshot} 
                  alt="Selected key area" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '300px', 
                    borderRadius: '8px',
                    objectFit: 'contain'
                  }} 
                />
              </div>
              {keyExtractionSelection && (
                <p style={{ 
                  margin: '10px 0 0 0', 
                  color: 'var(--text-muted)', 
                  fontSize: '12px',
                  textAlign: 'center'
                }}>
                  Size: {Math.abs(keyExtractionSelection.endX - keyExtractionSelection.startX).toFixed(0)} × {Math.abs(keyExtractionSelection.endY - keyExtractionSelection.startY).toFixed(0)}px
                </p>
              )}
            </div>

            {/* Queued areas with thumbnails */}
            {keyExtractionJobs.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8, fontWeight: 500 }}>
                  Queued ({keyExtractionJobs.length})
                </label>
                <div style={{
                  maxHeight: 140,
                  overflowY: 'auto',
                  border: '1px solid var(--border-color)',
                  borderRadius: 8,
                  background: 'var(--bg-secondary)',
                }}>
                  {keyExtractionJobs.map((job, idx) => (
                    <div
                      key={job.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 12px',
                        borderBottom: idx < keyExtractionJobs.length - 1 ? '1px solid var(--border-color)' : 'none',
                      }}
                    >
                      <img
                        src={job.snapshot}
                        alt={`Area ${idx + 1}`}
                        style={{
                          width: 56,
                          height: 38,
                          objectFit: 'cover',
                          borderRadius: 4,
                          border: '1px solid var(--border-strong)',
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ flex: 1, fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
                        Area {idx + 1}
                      </span>
                      <button
                        onClick={() => handleDeleteKeyExtractionJob(job.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--border-strong)',
                          cursor: 'pointer',
                          padding: 4,
                          borderRadius: 4,
                          transition: 'color 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; }}
                        title="Remove this area"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Info Text */}
            <p style={{
              fontSize: '13px',
              color: 'var(--text-muted)',
              marginBottom: '20px',
              lineHeight: '1.5',
              textAlign: 'center'
            }}>
              This area will be analyzed to extract electrical symbols and their names.
            </p>

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}>
              <button
                onClick={() => {
                  setShowKeyExtractionConfirmModal(false);
                  setKeyExtractionSnapshot(null);
                  setKeyExtractionSelection(null);
                }}
                style={{
                  padding: '12px 20px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Reselect
              </button>
              <button
                onClick={handleExtractAndScanNext}
                style={{
                  padding: '12px 20px',
                  background: 'var(--bg-primary)',
                  border: '2px solid #22c55e',
                  borderRadius: '8px',
                  color: 'var(--green-text)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-primary)'; }}
              >
                <Plus size={16} />
                Add & Scan Next
              </button>
              <button
                onClick={handleExtractSingle}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                }}
              >
                <Sparkles size={16} />
                {keyExtractionJobs.length > 0 ? 'Extract & Done' : 'Extract Power Legend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Key Extraction Loading Overlay */}
      {isExtractingKeyFromLegend && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100003
        }}>
          <div className="spin" style={{ marginBottom: '16px' }}>
            <Loader2 size={48} color="#3b82f6" />
          </div>
          <p style={{ color: '#ffffff', fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
            Extracting Power Legend
          </p>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
            Analyzing legend table for power symbols and names...
          </p>
        </div>
      )}

      {/* Key Extraction Preview Modal */}
      {showKeyExtractionPreview && (
        <div 
          className="key-extraction-preview-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100002
          }}
          onClick={() => { setShowKeyExtractionPreview(false); setKeyExtractionJobs([]); }}
        >
          <div 
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '24px',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid var(--border-color)'
            }}>
              <div>
                <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Key size={22} style={{ color: '#3b82f6' }} />
                  Extracted Power Legend
                </h3>
                <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>
                  Found {extractedKeyPreview.length} power components from the legend
                  {selectedKeyPreviewIndices.size < extractedKeyPreview.length && (
                    <span style={{ marginLeft: '8px', color: '#3b82f6', fontWeight: 500 }}>
                      ({selectedKeyPreviewIndices.size} selected)
                    </span>
                  )}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => {
                    if (selectedKeyPreviewIndices.size === extractedKeyPreview.length) {
                      setSelectedKeyPreviewIndices(new Set());
                    } else {
                      setSelectedKeyPreviewIndices(new Set(extractedKeyPreview.map((_, i) => i)));
                    }
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '6px',
                    color: '#3b82f6',
                    cursor: 'pointer',
                    padding: '4px 10px',
                    fontSize: '12px',
                    fontWeight: 500,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {selectedKeyPreviewIndices.size === extractedKeyPreview.length ? 'Deselect All' : 'Select All'}
                </button>
              <button
                onClick={() => { setShowKeyExtractionPreview(false); setKeyExtractionJobs([]); }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                <X size={24} />
              </button>
              </div>
            </div>

            {/* Snapshot Preview */}
            {keyExtractionSnapshot && (
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: 500 }}>
                  Source Image:
                </p>
                <img 
                  src={keyExtractionSnapshot} 
                  alt="Key snapshot" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '120px', 
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    objectFit: 'contain'
                  }} 
                />
              </div>
            )}

            {/* Component Cards Grid */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '12px',
              padding: '4px'
            }}>
              {extractedKeyPreview.map((symbol, idx) => {
                const isSelected = selectedKeyPreviewIndices.has(idx);
                return (
                <div
                  key={idx}
                  onClick={() => {
                    setSelectedKeyPreviewIndices(prev => {
                      const next = new Set(prev);
                      next.has(idx) ? next.delete(idx) : next.add(idx);
                      return next;
                    });
                  }}
                  style={{
                    background: isSelected ? 'var(--bg-secondary)' : 'var(--bg-secondary)',
                    borderRadius: '10px',
                    padding: '14px',
                    border: isSelected ? '2px solid #3b82f6' : '1px solid var(--border-color)',
                    transition: 'all 0.2s',
                    opacity: isSelected ? 1 : 0.4,
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (isSelected) {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer',
                      accentColor: '#3b82f6'
                    }}
                  />
                  {symbol.croppedImage ? (
                    <div style={{
                      width: '100%',
                      height: '60px',
                      background: 'var(--bg-primary)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '10px',
                      border: '1px solid var(--border-color)',
                      overflow: 'hidden'
                    }}>
                      <img 
                        src={symbol.croppedImage} 
                        alt={symbol.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '56px',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '60px',
                      background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '10px',
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#6366f1'
                    }}>
                      {symbol.abbreviation || '◈'}
                    </div>
                  )}
                  
                  {/* Name */}
                  <h4 style={{ 
                    margin: '0 0 6px', 
                    fontSize: '13px', 
                    fontWeight: 600, 
                    color: 'var(--text-primary)',
                    lineHeight: '1.3'
                  }}>
                    {symbol.name}
                  </h4>
                  
                  {/* Abbreviation badge */}
                  {symbol.abbreviation && (
                    <span style={{
                      display: 'inline-block',
                      background: 'var(--indigo-tint-medium)',
                      color: 'var(--blue-text-dark)',
                      fontSize: '10px',
                      fontWeight: 600,
                      padding: '2px 6px',
                      borderRadius: '4px',
                      marginBottom: '6px'
                    }}>
                      {symbol.abbreviation}
                    </span>
                  )}
                  
                  {/* Status indicator */}
                  {!symbol.croppedImage && (
                    <p style={{ 
                      margin: 0, 
                      fontSize: '10px', 
                      color: 'var(--text-placeholder)',
                      fontStyle: 'italic'
                    }}>
                      {symbol.box_2d ? 'Image crop failed' : 'No bounding box'}
                    </p>
                  )}
                </div>
                );
              })}
              
              {extractedKeyPreview.length === 0 && (
                <div style={{
                  gridColumn: '1 / -1',
                  padding: '40px',
                  textAlign: 'center',
                  color: 'var(--text-placeholder)'
                }}>
                  <p style={{ fontSize: '16px', marginBottom: '8px' }}>No components extracted</p>
                  <p style={{ fontSize: '13px' }}>Try selecting a clearer area of the legend</p>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div style={{ 
              marginTop: '20px', 
              paddingTop: '16px', 
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowKeyExtractionPreview(false);
                  setExtractedKeyPreview([]);
                  setSelectedKeyPreviewIndices(new Set());
                  setKeyExtractionSnapshot(null);
                }}
                style={{
                  padding: '10px 20px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={handleAddToKeyOnly}
                disabled={extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length === 0}
                style={{
                  padding: '10px 20px',
                  background: extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length > 0
                    ? 'var(--bg-primary)'
                    : 'var(--bg-tertiary)',
                  border: extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length > 0
                    ? '2px solid #3b82f6'
                    : '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  color: extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length > 0
                    ? '#3b82f6'
                    : 'var(--text-placeholder)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length > 0 ? 'pointer' : 'not-allowed',
                  opacity: extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length > 0 ? 1 : 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Key size={16} />
                Add to Key Only ({extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length})
              </button>
              <button
                onClick={handleAddToKeyAndCount}
                disabled={extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length === 0}
                style={{
                  padding: '10px 20px',
                  background: extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length > 0 
                    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                    : 'var(--border-strong)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length > 0 ? 'pointer' : 'not-allowed',
                  opacity: extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length > 0 ? 1 : 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Zap size={16} />
                Add to Key & Count ({extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lighting Schedule Preview Modal */}
      {showLightingSchedulePreview && (
        <div 
          className="lighting-schedule-preview-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100002
          }}
          onClick={() => setShowLightingSchedulePreview(false)}
        >
          <div 
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '24px',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid var(--border-color)'
            }}>
              <div>
                <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Lightbulb size={22} style={{ color: 'var(--yellow-text)' }} />
                  Extracted Lighting Fixtures
                </h3>
                <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>
                  Found {extractedLightingFixtures.length} fixtures from the schedule
                  {selectedLightingFixtureIndices.size < extractedLightingFixtures.length && (
                    <span style={{ marginLeft: '8px', color: 'var(--yellow-text)', fontWeight: 500 }}>
                      ({selectedLightingFixtureIndices.size} selected)
                    </span>
                  )}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => {
                    if (selectedLightingFixtureIndices.size === extractedLightingFixtures.length) {
                      setSelectedLightingFixtureIndices(new Set());
                    } else {
                      setSelectedLightingFixtureIndices(new Set(extractedLightingFixtures.map((_, i) => i)));
                    }
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '6px',
                    color: 'var(--yellow-text)',
                    cursor: 'pointer',
                    padding: '4px 10px',
                    fontSize: '12px',
                    fontWeight: 500,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {selectedLightingFixtureIndices.size === extractedLightingFixtures.length ? 'Deselect All' : 'Select All'}
                </button>
                <button
                  onClick={() => setShowLightingSchedulePreview(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    padding: '8px'
                  }}
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Snapshot Preview */}
            {lightingScheduleSnapshot && (
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: 500 }}>
                  Source Image:
                </p>
                <img 
                  src={lightingScheduleSnapshot} 
                  alt="Lighting schedule snapshot" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '120px', 
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    objectFit: 'contain'
                  }} 
                />
              </div>
            )}

            {/* Fixture List */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              border: '1px solid var(--border-color)',
              borderRadius: '10px',
              background: 'var(--bg-secondary)'
            }}>
              {extractedLightingFixtures.length > 0 ? (
                extractedLightingFixtures.map((fixture, idx) => {
                  const isSelected = selectedLightingFixtureIndices.has(idx);
                  return (
                  <div
                    key={idx}
                    onClick={() => {
                      setSelectedLightingFixtureIndices(prev => {
                        const next = new Set(prev);
                        next.has(idx) ? next.delete(idx) : next.add(idx);
                        return next;
                      });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '14px',
                      padding: '14px 16px',
                      borderBottom: idx < extractedLightingFixtures.length - 1 ? '1px solid var(--border-color)' : 'none',
                      transition: 'all 0.2s',
                      opacity: isSelected ? 1 : 0.4,
                      cursor: 'pointer',
                      background: isSelected ? 'transparent' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--bg-primary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      style={{
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer',
                        accentColor: '#f59e0b',
                        flexShrink: 0,
                        marginTop: '16px'
                      }}
                    />
                    <div style={{
                      minWidth: '48px',
                      height: '48px',
                      background: 'linear-gradient(135deg, var(--yellow-tint-medium) 0%, var(--yellow-tint-strong) 100%)',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '16px',
                      color: 'var(--yellow-text-dark)',
                      border: '2px solid #f59e0b',
                      flexShrink: 0
                    }}>
                      {fixture.code}
                    </div>
                    
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h4 style={{ 
                        margin: '0 0 4px', 
                        fontSize: '14px', 
                        fontWeight: 600, 
                        color: 'var(--text-primary)',
                        lineHeight: '1.3'
                      }}>
                        {fixture.name}
                      </h4>
                      <p style={{ 
                        margin: 0, 
                        fontSize: '12px', 
                        color: 'var(--text-muted)',
                        lineHeight: '1.4',
                        wordBreak: 'break-word'
                      }}>
                        {fixture.description}
                      </p>
                    </div>
                  </div>
                  );
                })
              ) : (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: 'var(--text-placeholder)'
                }}>
                  <p style={{ fontSize: '16px', marginBottom: '8px' }}>No fixtures extracted</p>
                  <p style={{ fontSize: '13px' }}>Try selecting a clearer area of the lighting schedule</p>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div style={{ 
              marginTop: '20px', 
              paddingTop: '16px', 
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowLightingSchedulePreview(false);
                  setExtractedLightingFixtures([]);
                  setSelectedLightingFixtureIndices(new Set());
                  setLightingScheduleSnapshot(null);
                }}
                style={{
                  padding: '10px 20px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={handleAddLightingFixturesToKey}
                disabled={selectedLightingFixtureIndices.size === 0}
                style={{
                  padding: '10px 20px',
                  background: selectedLightingFixtureIndices.size > 0
                    ? 'var(--bg-primary)'
                    : 'var(--bg-tertiary)',
                  border: selectedLightingFixtureIndices.size > 0
                    ? '2px solid #f59e0b'
                    : '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  color: selectedLightingFixtureIndices.size > 0
                    ? '#d97706'
                    : 'var(--text-placeholder)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: selectedLightingFixtureIndices.size > 0 ? 'pointer' : 'not-allowed',
                  opacity: selectedLightingFixtureIndices.size > 0 ? 1 : 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Lightbulb size={16} />
                Add to Key Only ({selectedLightingFixtureIndices.size})
              </button>
              <button
                onClick={handleAddLightingFixturesToKeyAndCount}
                disabled={selectedLightingFixtureIndices.size === 0}
                style={{
                  padding: '10px 20px',
                  background: selectedLightingFixtureIndices.size > 0 
                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                    : 'var(--border-strong)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: selectedLightingFixtureIndices.size > 0 ? 'pointer' : 'not-allowed',
                  opacity: selectedLightingFixtureIndices.size > 0 ? 1 : 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Zap size={16} />
                Add & Count ({selectedLightingFixtureIndices.size})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Autocount Confirmation Modal — with batch queue */}
      {showAutocountConfirmModal && referenceAreaImage && pendingAutocountComponent && (
        <div 
          className="autocount-confirm-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100002
          }}
          onClick={() => {
            setShowAutocountConfirmModal(false);
            setReferenceAreaImage(null);
          }}
        >
          <div 
            className="autocount-confirm-modal"
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '28px',
              width: '90vw',
              maxWidth: '600px',
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
              border: '1px solid var(--border-color)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ marginBottom: '16px', flexShrink: 0 }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, marginBottom: '4px' }}>
                Add Component
              </h3>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '13px' }}>
                Name this component, then add more or start counting.
              </p>
            </div>

            {/* Current scan: reference image + name input */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexShrink: 0 }}>
              <div style={{
                width: '120px',
                height: '100px',
                border: '1px solid var(--border-color)',
                borderRadius: '10px',
                padding: '8px',
                background: 'var(--bg-primary)',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <img 
                  src={referenceAreaImage} 
                  alt="Selected reference area" 
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: '6px',
                    background: 'var(--bg-primary)'
                  }}
                />
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '6px', fontWeight: 500 }}>
                  Component Name
                </label>
                <input
                  type="text"
                  value={editingQueuedName}
                  onChange={(e) => setEditingQueuedName(e.target.value)}
                  placeholder="e.g., Duplex Receptacle"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '8px',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && editingQueuedName.trim()) {
                      // Add & scan next on Enter
                      setQueuedComponents(prev => [...prev, {
                        name: editingQueuedName.trim(),
                        color: pendingAutocountComponent.color,
                        referenceImage: referenceAreaImage
                      }]);
                      setShowAutocountConfirmModal(false);
                      setReferenceAreaImage(null);
                      setAutocountSelection(null);
                      setEditingQueuedName('');
                      setPendingAutocountComponent({ name: '', color: pendingAutocountComponent.color });
                    }
                  }}
                />
              </div>
            </div>

            {/* Queued components — list style matching component list */}
            {queuedComponents.length > 0 && (
              <div style={{ marginBottom: '16px', flexShrink: 0 }}>
                <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '8px', fontWeight: 500 }}>
                  Queued for counting ({queuedComponents.length})
                </label>
                <div style={{
                  maxHeight: '160px',
                  overflowY: 'auto',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-secondary)'
                }}>
                  {queuedComponents.map((comp, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        borderBottom: idx < queuedComponents.length - 1 ? '1px solid var(--border-color)' : 'none',
                        transition: 'background 0.15s',
                        background: 'transparent'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-primary)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <img 
                        src={comp.referenceImage} 
                        alt={comp.name}
                        style={{
                          width: '36px',
                          height: '36px',
                          objectFit: 'cover',
                          borderRadius: '6px',
                          border: `2px solid ${comp.color}`,
                          flexShrink: 0,
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <span style={{ flex: 1, fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {comp.name}
                      </span>
                      <button
                        onClick={() => {
                          setQueuedComponents(prev => prev.filter((_, i) => i !== idx));
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--text-placeholder)',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                          transition: 'color 0.15s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-placeholder)'; }}
                        title="Remove"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexShrink: 0 }}>
              <button
                onClick={() => {
                  setShowAutocountConfirmModal(false);
                  setReferenceAreaImage(null);
                  setAutocountSelection(null);
                  // Stay in area-selection mode so user can reselect
                }}
                style={{
                  padding: '10px 16px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  color: 'var(--text-secondary)',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-divider)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-tertiary)'; }}
              >
                Reselect
              </button>
              <button
                onClick={() => {
                  if (!editingQueuedName.trim()) return;
                  // Add current component to queue and go back to area selection
                  setQueuedComponents(prev => [...prev, {
                    name: editingQueuedName.trim(),
                    color: pendingAutocountComponent.color,
                    referenceImage: referenceAreaImage
                  }]);
                  setShowAutocountConfirmModal(false);
                  setReferenceAreaImage(null);
                  setAutocountSelection(null);
                  setEditingQueuedName('');
                  // Reset pending component for next scan
                  setPendingAutocountComponent({ name: '', color: pendingAutocountComponent.color });
                  // Stay in area-selection mode
                }}
                disabled={!editingQueuedName.trim()}
                style={{
                  padding: '10px 16px',
                  background: editingQueuedName.trim() ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
                  border: editingQueuedName.trim() ? '2px solid #22c55e' : '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  color: editingQueuedName.trim() ? '#16a34a' : 'var(--text-placeholder)',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: editingQueuedName.trim() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  if (editingQueuedName.trim()) {
                    e.currentTarget.style.background = '#f0fdf4';
                  }
                }}
                onMouseLeave={(e) => {
                  if (editingQueuedName.trim()) {
                    e.currentTarget.style.background = 'var(--bg-primary)';
                  }
                }}
              >
                <Plus size={14} />
                Add & Scan Next
              </button>
              <button
                onClick={() => {
                  if (!editingQueuedName.trim() && queuedComponents.length === 0) return;
                  
                  // Build the full list: queued + current
                  const allComponents = [...queuedComponents];
                  if (editingQueuedName.trim()) {
                    allComponents.push({
                      name: editingQueuedName.trim(),
                      color: pendingAutocountComponent.color,
                      referenceImage: referenceAreaImage
                    });
                  }
                  
                  if (allComponents.length === 0) return;
                  
                  // Add all components to the key
                  const existingComponents = electricalKey?.components || [];
                  const existingNames = existingComponents.map((c: any) => c.name);
                  
                  const newKeyComponents = allComponents.map(comp => {
                    const uniqueName = getUniqueComponentName(comp.name, existingNames);
                    existingNames.push(uniqueName);
                    return {
                      name: uniqueName,
                      color: comp.color,
                      symbol: comp.name,
                      referenceImage: comp.referenceImage,
                      pages: [] as any[],
                      totalCount: 0,
                      hasReferenceImage: true
                    };
                  });
                  
                  const updatedKey = {
                    ...(electricalKey || {}),
                    components: [...existingComponents, ...newKeyComponents],
                    extractedAt: (electricalKey as any)?.extractedAt || new Date().toISOString()
                  };
                  
                  // Save key
                  if (onKeyExtracted) {
                    onKeyExtracted(updatedKey);
                  }
                  
                  // Close modal and clean up
                  setShowAutocountConfirmModal(false);
                  setAutocountMode(null);
                  setReferenceAreaImage(null);
                  setAutocountSelection(null);
                  setEditingQueuedName('');
                  setQueuedComponents([]);
                  
                  // Start counting all components
                  if (pdfDocument && numPages) {
                    startCountingComponents(allComponents.map(c => ({
                      color: c.color,
                      name: c.name,
                      symbol: c.name,
                      referenceImage: c.referenceImage
                    })));
                  }
                }}
                disabled={!editingQueuedName.trim() && queuedComponents.length === 0}
                style={{
                  padding: '10px 20px',
                  background: (editingQueuedName.trim() || queuedComponents.length > 0)
                    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                    : 'var(--border-strong)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: (editingQueuedName.trim() || queuedComponents.length > 0) ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: (editingQueuedName.trim() || queuedComponents.length > 0)
                    ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (editingQueuedName.trim() || queuedComponents.length > 0) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Rocket size={14} />
                Count All ({queuedComponents.length + (editingQueuedName.trim() ? 1 : 0)})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Autocount Progress Overlay */}
      {isAutocounting && autocountProgress && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'var(--bg-primary)',
          color: 'var(--text-primary)',
          padding: '32px 40px',
          borderRadius: '16px',
          fontSize: '16px',
          fontWeight: 500,
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
          zIndex: 100003,
          minWidth: '480px',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflowY: 'auto',
          textAlign: 'center',
          border: '1px solid var(--border-color)'
        }}>
          <div style={{ marginBottom: '20px' }}>
            <Sparkles 
              size={48} 
              style={{ 
                color: '#3b82f6',
                animation: 'pulse 1.5s ease-in-out infinite',
                margin: '0 auto',
                display: 'block'
              }} 
            />
          </div>
          <div style={{ fontSize: '15px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            {autocountProgress}
          </div>
          
          {/* Page Progress Details */}
          {pageProgressMap.size > 0 && (
            <div style={{
              marginTop: '24px',
              maxHeight: '300px',
              overflowY: 'auto',
              background: 'var(--bg-secondary)',
              borderRadius: '8px',
              padding: '16px',
              textAlign: 'left'
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--text-muted)',
                marginBottom: '12px',
                textAlign: 'center'
              }}>
                Page Progress ({Array.from(pageProgressMap.values()).filter(p => p.status === 'complete').length}/{pageProgressMap.size} complete)
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '8px'
              }}>
                {Array.from(pageProgressMap.entries())
                  .sort(([a], [b]) => a - b)
                  .map(([page, progress]) => (
                    <div
                      key={page}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        background: progress.status === 'complete' ? 'var(--green-tint-strong)' : 
                                    progress.status === 'error' ? 'var(--red-tint-medium)' : 'var(--bg-primary)',
                        borderRadius: '6px',
                        fontSize: '12px',
                        border: '1px solid ' + (progress.status === 'complete' ? '#6ee7b7' : 
                                                 progress.status === 'error' ? '#fca5a5' : 'var(--border-color)')
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>Page {page}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                        {progress.status === 'complete' ? (
                          `✓ ${progress.detections} found`
                        ) : progress.status === 'error' ? (
                          '✗ Error'
                        ) : (
                          `${progress.current}/${progress.total}`
                        )}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Blocking Overlay During Processing */}
      {(isAutocounting || showAutocountReviewModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'transparent',
          zIndex: 100002,
          pointerEvents: isAutocounting ? 'all' : 'none',
          cursor: isAutocounting ? 'wait' : 'default'
        }} />
      )}

      {/* Count All Progress Overlay */}
      {(isCountingAll || showMultiComponentReviewModal || showLightingCountingModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'transparent',
          zIndex: 100002,
          pointerEvents: isCountingAll ? 'all' : 'none',
          cursor: isCountingAll ? 'wait' : 'default'
        }} />
      )}

      {/* Autocount Review Modal */}
      {showAutocountReviewModal && pendingAutocountComponent && (isAutocounting || autocountDetections.length > 0) && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100005,
            padding: '20px'
          }}
          onClick={() => {
            // Don't close on overlay click - force user to review
          }}
        >
          <div 
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '28px',
              width: '95vw',
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.4)',
              border: '1px solid var(--border-color)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ marginBottom: '20px', borderBottom: '2px solid var(--border-color)', paddingBottom: '16px' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '22px', fontWeight: 600, marginBottom: '8px' }}>
                Review Detections: {pendingAutocountComponent.name}
              </h3>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>
                {isAutocounting 
                  ? `Finding components... ${autocountDetections.length} found so far`
                  : `Found ${autocountDetections.length} components across ${(() => {
                      const pages = new Set(autocountDetections.map(d => d.prediction.page || pageNumber));
                      return pages.size;
                    })()} pages. Remove false positives by clicking on them, then confirm.`
                }
              </p>
              <div style={{ marginTop: '12px', display: 'flex', gap: '16px', fontSize: '14px' }}>
                <span style={{ color: 'var(--green-text)', fontWeight: 600 }}>
                  ✓ Selected: {selectedDetectionsToKeep.size}
                </span>
                <span style={{ color: '#dc2626', fontWeight: 600 }}>
                  ✗ Removed: {autocountDetections.length - selectedDetectionsToKeep.size}
                </span>
              </div>
            </div>

            {/* Progress Bar - Shows during counting */}
            {isAutocounting && autocountTotalBatches > 0 && (
              <div style={{
                padding: '12px 16px',
                borderRadius: '10px',
                marginBottom: '16px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontWeight: 500, color: 'var(--text-secondary)', fontSize: '14px' }}>
                    Counting...
                  </span>
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                    {Math.round((autocountCompletedBatches / autocountTotalBatches) * 100)}%
                  </span>
                </div>
                
                {/* Progress bar track */}
                <div style={{
                  width: '100%',
                  height: '6px',
                  background: 'var(--border-color)',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  {/* Progress bar fill */}
                  <div style={{
                    width: `${(autocountCompletedBatches / autocountTotalBatches) * 100}%`,
                    height: '100%',
                    background: '#3b82f6',
                    borderRadius: '3px',
                    transition: 'width 0.3s ease-out'
                  }} />
                </div>
                
                {/* Detection count only */}
                <div style={{ 
                  marginTop: '6px', 
                  fontSize: '12px', 
                  color: 'var(--text-muted)',
                  textAlign: 'right'
                }}>
                  {autocountDetections.length} detections found
                </div>
              </div>
            )}

            {/* Scrollable Grid */}
            <div 
              style={{
                flex: 1,
                overflowY: 'auto',
                marginBottom: '20px'
              }}
            >
              {/* Empty state - shown when no detections yet */}
              {autocountDetections.length === 0 && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'var(--text-muted)',
                  textAlign: 'center',
                  padding: '40px'
                }}>
                  {isAutocounting ? (
                    <>
                      <div style={{ marginBottom: '16px' }}>
                        <Sparkles size={48} color="#3b82f6" />
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                        Scanning for components...
                      </div>
                      <div style={{ fontSize: '14px', marginTop: '8px' }}>
                        Detections will appear here as they're found
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>😕</div>
                      <div style={{ fontSize: '18px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                        No components found
                      </div>
                      <div style={{ fontSize: '14px', marginTop: '8px' }}>
                        Try selecting a different reference area or adjusting the component type
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Flat grid of all detections (not grouped by page) */}
              {autocountDetections.length > 0 && (
              <div 
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '16px',
                  padding: '4px'
                }}
              >
                {autocountDetections.map((det, idx) => {
                  const isSelected = selectedDetectionsToKeep.has(det.id);
                  const detPage = det.prediction.page || pageNumber;
                  return (
                    <div
                      key={det.id}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleDetectionSelection(det.id);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = isSelected ? '0 0 12px rgba(59, 130, 246, 0.5)' : 'none';
                      }}
                      style={{
                        border: isSelected ? '3px solid #3b82f6' : '3px solid var(--border-color)',
                        borderRadius: '10px',
                        padding: '8px',
                        cursor: 'pointer',
                        background: isSelected ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
                        transition: 'all 0.2s',
                        opacity: isSelected ? 1 : 0.6,
                        position: 'relative',
                        boxShadow: isSelected ? '0 0 12px rgba(59, 130, 246, 0.5), 0 0 24px rgba(59, 130, 246, 0.3)' : 'none',
                        userSelect: 'none',
                        WebkitUserSelect: 'none'
                      }}
                    >
                      {/* Selection indicator */}
                      {!isSelected && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          background: '#dc2626',
                          color: 'white',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                        }}>
                          ✗
                        </div>
                      )}
                      
                      {/* Page badge */}
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        background: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 500
                      }}>
                        P{detPage}
                      </div>
                      
                      {/* Thumbnail - zoomed to center for easier verification */}
                      <div style={{
                        overflow: 'hidden',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        aspectRatio: '4/3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <img 
                          src={det.thumbnail} 
                          alt={`Detection ${idx + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center',
                            transform: pendingAutocountComponent.isLighting ? 'scale(1.8)' : 'scale(1)',
                            transformOrigin: 'center center',
                            background: 'var(--bg-primary)',
                            pointerEvents: 'none'
                          }}
                        />
                      </div>
                      
                      {/* Info */}
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                          #{idx + 1}
                        </div>
                        <div style={{ fontSize: '11px', marginTop: '2px' }}>
                          Confidence: {(det.prediction.confidence * 100).toFixed(0)}%
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'space-between',
              borderTop: '2px solid var(--border-color)',
              paddingTop: '16px'
            }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    // Select all
                    setSelectedDetectionsToKeep(new Set(autocountDetections.map(d => d.id)));
                  }}
                  style={{
                    padding: '10px 16px',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '8px',
                    color: 'var(--text-secondary)',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Select All
                </button>
                <button
                  onClick={() => {
                    // Deselect all
                    setSelectedDetectionsToKeep(new Set());
                  }}
                  style={{
                    padding: '10px 16px',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '8px',
                    color: 'var(--text-secondary)',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Clear All
                </button>
                <button
                  onClick={() => { void handleExportClassificationDataset('single'); }}
                  disabled={isAutocounting || autocountDetections.length === 0 || isExportingDataset}
                  style={{
                    padding: '10px 16px',
                    background: (isAutocounting || autocountDetections.length === 0 || isExportingDataset) ? 'var(--border-strong)' : 'var(--bg-tertiary)',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '8px',
                    color: 'var(--text-secondary)',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: (isAutocounting || autocountDetections.length === 0 || isExportingDataset) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  title="Download thumbnails sorted by class folders"
                >
                  <Download size={14} />
                  {isExportingDataset ? 'Exporting...' : 'Export Dataset'}
                </button>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setShowAutocountReviewModal(false);
                    setAutocountDetections([]);
                    setSelectedDetectionsToKeep(new Set());
                    setPendingAutocountComponent(null);
                    setReferenceAreaImage(null);
                    setAutocountSelection(null);
                    setIsAutocounting(false);
                    setAutocountTotalBatches(0);
                    setAutocountCompletedBatches(0);
                  }}
                  style={{
                    padding: '12px 20px',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '8px',
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleFinalAutocountConfirm}
                  disabled={selectedDetectionsToKeep.size === 0 || isAutocounting}
                style={{
                  padding: '12px 24px',
                  background: (selectedDetectionsToKeep.size > 0 && !isAutocounting) ? '#3b82f6' : 'var(--border-strong)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: (selectedDetectionsToKeep.size > 0 && !isAutocounting) ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (selectedDetectionsToKeep.size > 0 && !isAutocounting) {
                    e.currentTarget.style.background = '#2563eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedDetectionsToKeep.size > 0 && !isAutocounting) {
                    e.currentTarget.style.background = '#3b82f6';
                  }
                }}
              >
                {isAutocounting ? 'Counting...' : `Confirm ${selectedDetectionsToKeep.size > 0 ? `(${selectedDetectionsToKeep.size})` : ''}`}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lighting Counting Modal (progress-only, no review) */}
      {showLightingCountingModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100007,
            padding: '20px'
          }}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '40px',
              width: '480px',
              maxWidth: '90vw',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.4)',
              textAlign: 'center'
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              {lightingCountingFinished || lightingProgress.overallPct >= 100 ? (
                <Check size={48} color="#10b981" />
              ) : (
                <Lightbulb size={48} color="#f59e0b" style={{ animation: 'pulse 2s ease-in-out infinite' }} />
              )}
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: 600, color: 'var(--text-primary)' }}>
              {lightingCountingFinished ? 'Lighting Count Complete' : (lightingProgress.overallPct >= 100 ? 'Saving Lighting Fixtures...' : 'Counting Lighting Fixtures')}
            </h3>
            <p style={{ margin: '0 0 24px', color: 'var(--text-muted)', fontSize: '14px', minHeight: '20px' }}>
              {lightingCountingFinished ? `Found ${lightingProgress.detectionsFound} fixtures across ${lightingProgress.totalPages} pages.` : (lightingProgress.message || 'Starting...')}
            </p>

            {!lightingCountingFinished ? (
              <>
                {/* Progress bar */}
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'var(--border-color)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: `${lightingProgress.overallPct}%`,
                    height: '100%',
                    background: lightingProgress.overallPct >= 100 ? '#10b981' : '#f59e0b',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease-out, background 0.3s'
                  }} />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--text-muted)' }}>
                  <span>{lightingProgress.overallPct}%</span>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                      {lightingProgress.detectionsFound} verified
                    </div>
                    {lightingProgress.totalCandidates > 0 && (
                      <div style={{ fontSize: '11px' }}>
                        ({lightingProgress.totalCandidates} candidates found)
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '20px' }}>
                <button
                  onClick={handleFinishLightingCount}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#059669'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#10b981'}
                >
                  Finish and Add to Project
                </button>
                <button
                  onClick={handleCancelLightingCount}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'transparent',
                    color: '#ef4444',
                    border: '2px solid var(--red-tint-medium)',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#fef2f2';
                    e.currentTarget.style.borderColor = '#fecaca';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'var(--red-tint-medium)';
                  }}
                >
                  Cancel and Discard
                </button>
              </div>
            )}

            {/* Per-page detailed progress */}
            {lightingProgress.totalPages > 1 && lightingProgress.pageProgress.size > 0 && (
              <div style={{
                marginTop: '20px',
                maxHeight: '240px',
                overflowY: 'auto',
                padding: '16px',
                background: 'var(--bg-secondary)',
                borderRadius: '12px',
                textAlign: 'left',
                border: '1px solid var(--border-subtle)'
              }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Page Progress</span>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {Array.from(lightingProgress.pageProgress.values()).filter(p => p.phase === 'done').length}/{lightingProgress.totalPages} complete
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {Array.from(lightingProgress.pageProgress.entries())
                    .sort(([a], [b]) => a - b)
                    .map(([pg, pp]) => {
                      const displayPct = (() => {
                        if (pp.phase === 'done') return 100;
                        if (pp.phase === 'verifying') {
                          const verifyRatio = (pp.totalToVerify && pp.totalToVerify > 0) 
                            ? (pp.verifiedSoFar || 0) / pp.totalToVerify 
                            : 0;
                          return 88 + (verifyRatio * 12);
                        }
                        if (pp.totalTiles > 0 && pp.completedTiles > 0) {
                          const tileRatio = pp.completedTiles / pp.totalTiles;
                          return 15 + (tileRatio * 73);
                        }
                        return pp.pct || 0;
                      })();

                      return (
                        <div key={pg} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)' }}>Page {pg}</span>
                            <span style={{ fontSize: '10px', color: pp.phase === 'done' ? '#10b981' : pp.phase === 'verifying' ? '#f59e0b' : '#3b82f6', fontWeight: 500 }}>
                              {pp.phase === 'done' ? 'Complete' 
                                : pp.phase === 'verifying' ? (pp.totalToVerify ? `Verifying (${pp.verifiedSoFar}/${pp.totalToVerify})` : 'Verifying...') 
                                : pp.phase === 'rendering' ? 'Rendering' 
                                : `Scanning (${pp.completedTiles}/${pp.totalTiles})`}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1, height: '6px', background: 'var(--border-color)', borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ 
                                width: `${displayPct}%`, 
                                height: '100%', 
                                background: pp.phase === 'done' ? '#10b981' : pp.phase === 'verifying' ? '#f59e0b' : '#3b82f6',
                                transition: 'width 0.3s ease-out'
                              }} />
                            </div>
                            <span style={{ fontSize: '10px', color: 'var(--text-muted)', width: '25px', textAlign: 'right' }}>{Math.round(displayPct)}%</span>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Multi-Component Review Modal */}
      {showMultiComponentReviewModal && (isCountingAll || multiComponentDetections.length > 0) && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100006,
            padding: '20px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {(() => {
            const currentComponent = multiComponentDetections[currentReviewComponentIndex];
            const currentSelections = currentComponent ? (allComponentSelections.get(currentComponent.componentName) || new Set()) : new Set();
            const isLastComponent = currentReviewComponentIndex === multiComponentDetections.length - 1;
            
            return (
              <div 
                style={{
                  background: 'var(--bg-primary)',
                  borderRadius: '16px',
                  padding: '28px',
                  width: '95vw',
                  height: '90vh',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 25px 60px rgba(0, 0, 0, 0.4)',
                  border: '1px solid var(--border-color)',
                  position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                {(() => {
                  const isGroupedReceptacles = currentComponent?.componentName === '__RECEPTACLES_GROUP__';
                  const isGroupedLighting = currentComponent?.componentName === '__LIGHTING_GROUP__';
                  const headerDisplayName = isGroupedReceptacles ? 'RECEPTACLES' : isGroupedLighting ? 'LIGHTING' : currentComponent?.componentName;
                  return (
                <div style={{ marginBottom: '20px', borderBottom: '2px solid var(--border-color)', paddingBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '22px', fontWeight: 600 }}>
                      {isGroupedReceptacles
                        ? 'Review Receptacles'
                        : isGroupedLighting
                          ? 'Review Lighting Fixtures'
                          : `Review Components (${currentReviewComponentIndex + 1} of ${multiComponentDetections.length})`
                      }
                    </h3>
                    {currentComponent && (
                      <div
                        style={{ position: 'relative' }}
                        onMouseEnter={() => { if (isGroupedReceptacles && receptacleGroupComponents.length > 0) setShowReceptacleRefPopup(true); }}
                        onMouseLeave={() => setShowReceptacleRefPopup(false)}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '8px 16px 8px 8px',
                          background: currentComponent.componentColor,
                          borderRadius: '12px',
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: '16px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          cursor: isGroupedReceptacles && receptacleGroupComponents.length > 0 ? 'pointer' : 'default'
                        }}>
                          {!isGroupedReceptacles && !isGroupedLighting && currentComponent.referenceImage && (
                            <img 
                              src={currentComponent.referenceImage} 
                              alt={headerDisplayName}
                              style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                background: 'var(--bg-primary)'
                              }}
                            />
                          )}
                          {headerDisplayName}
                          {isGroupedReceptacles && receptacleGroupComponents.length > 0 && (
                            <span style={{ fontSize: '12px', opacity: 0.85 }}>▾</span>
                          )}
                        </div>
                        {showReceptacleRefPopup && isGroupedReceptacles && receptacleGroupComponents.length > 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: '8px',
                            background: 'var(--bg-primary)',
                            borderRadius: '12px',
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                            border: '1px solid var(--border-color)',
                            padding: '16px',
                            zIndex: 100010,
                            display: 'grid',
                            gridTemplateColumns: `repeat(${Math.min(receptacleGroupComponents.length, 4)}, 1fr)`,
                            gap: '16px',
                            minWidth: '280px',
                            maxWidth: '600px'
                          }}>
                            {receptacleGroupComponents.map((rc) => (
                              <div key={rc.name} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                {rc.referenceImage ? (
                                  <img
                                    src={rc.referenceImage}
                                    alt={rc.name}
                                    style={{
                                      width: '80px',
                                      height: '80px',
                                      objectFit: 'contain',
                                      borderRadius: '8px',
                                      border: `2px solid ${rc.color}`,
                                      background: 'var(--bg-secondary)'
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '8px',
                                    background: rc.color + '22',
                                    border: `2px solid ${rc.color}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: rc.color,
                                    fontSize: '24px'
                                  }}>
                                    ⚡
                                  </div>
                                )}
                                <span style={{
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  color: 'var(--text-secondary)',
                                  textAlign: 'center',
                                  lineHeight: '1.3',
                                  maxWidth: '100px',
                                  wordWrap: 'break-word'
                                }}>
                                  {rc.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px', marginBottom: '12px' }}>
                    {currentComponent 
                      ? (isCountingAll 
                          ? (countAllProgress || `Finding ${headerDisplayName}... ${currentComponent.detections.length} found so far`)
                          : isGroupedReceptacles
                            ? `Found ${currentComponent.detections.length} receptacles. Use the dropdown on each card to change its type. Click to remove false positives.`
                            : `Found ${currentComponent.detections.length} detections. Click to remove false positives.`)
                      : 'Loading...'
                    }
                  </p>
                  
                  {/* Progress Bar - Shows during counting (current component only) */}
                  {isCountingAll && countAllComponentProgress.length > 0 && (() => {
                    const current = countAllComponentProgress.find(p => p.status === 'processing');
                    const totalBatches = current ? (current.totalBatches || 0) : 0;
                    const completedBatches = current ? (current.completedBatches || 0) : 0;
                    const percentage = totalBatches > 0 ? Math.round((completedBatches / totalBatches) * 100) : 0;
                    const totalDetections = multiComponentDetections.reduce((sum, c) => sum + c.detections.length, 0);
                    
                    return (
                      <div style={{
                        padding: '12px 16px',
                        borderRadius: '10px',
                        marginBottom: '12px'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <span style={{ fontWeight: 500, color: 'var(--text-secondary)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {current ? (
                              percentage < 15 ? <><Layers size={14} style={{ color: 'var(--purple-text)' }} /> Rendering pages...</> :
                              percentage < 70 ? <><Crosshair size={14} style={{ color: 'var(--yellow-text)' }} className="animate-spin" /> Detecting {current.name}...</> :
                              percentage < 100 ? <><Sparkles size={14} style={{ color: '#3b82f6' }} /> Classifying {current.name}...</> :
                              <><Check size={14} style={{ color: '#10b981' }} /> {current.name}</>
                            ) : <><Loader2 size={14} className="animate-spin" /> Starting...</>}
                          </span>
                          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                            {percentage}%
                          </span>
                        </div>
                        
                        <div style={{
                          width: '100%',
                          height: '6px',
                          background: 'var(--border-color)',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${percentage}%`,
                            height: '100%',
                            background: '#3b82f6',
                            borderRadius: '3px',
                            transition: 'width 0.3s ease-out'
                          }} />
                        </div>
                        
                        <div style={{ 
                          marginTop: '6px', 
                          fontSize: '11px', 
                          color: 'var(--text-muted)',
                          textAlign: 'right'
                        }}>
                          {totalDetections} total detections
                        </div>
                      </div>
                    );
                  })()}
                  
                  {currentComponent && (
                    <div style={{ display: 'flex', gap: '16px', fontSize: '14px', alignItems: 'center' }}>
                      <span style={{ color: 'var(--green-text)', fontWeight: 600 }}>
                        ✓ Selected: {currentSelections.size}
                      </span>
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>
                        ✗ Removed: {currentComponent.detections.length - currentSelections.size}
                      </span>
                      {currentComponent.componentName === '__RECEPTACLES_GROUP__' && receptacleGroupComponents.length > 0 && (
                        <select
                          value={receptacleClassFilter}
                          onChange={(e) => setReceptacleClassFilter(e.target.value)}
                          style={{
                            marginLeft: 'auto',
                            padding: '5px 10px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-strong)',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: 'var(--text-secondary)',
                            background: 'var(--bg-secondary)',
                            cursor: 'pointer',
                            outline: 'none',
                            minWidth: '180px'
                          }}
                        >
                          <option value="all">All Types ({currentComponent.detections.length})</option>
                          {receptacleGroupComponents.map((rc) => {
                            const count = currentComponent.detections.filter((d: any) =>
                              (detectionClassifications.get(d.id) || d.prediction.classifiedName) === rc.name
                            ).length;
                            return (
                              <option key={rc.name} value={rc.name}>{rc.name} ({count})</option>
                            );
                          })}
                          {(() => {
                            const unclassifiedCount = currentComponent.detections.filter((d: any) =>
                              !detectionClassifications.get(d.id) && !d.prediction.classifiedName
                            ).length;
                            return unclassifiedCount > 0 ? (
                              <option value="__unclassified__">Unclassified ({unclassifiedCount})</option>
                            ) : null;
                          })()}
                        </select>
                      )}
                      {currentComponent.componentName === '__TELE_DATA_GROUP__' && teleDataGroupComponents.length > 0 && (
                        <select
                          value={teleDataClassFilter}
                          onChange={(e) => setTeleDataClassFilter(e.target.value)}
                          style={{
                            marginLeft: 'auto',
                            padding: '5px 10px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-strong)',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: 'var(--text-secondary)',
                            background: 'var(--bg-secondary)',
                            cursor: 'pointer',
                            outline: 'none',
                            minWidth: '180px'
                          }}
                        >
                          <option value="all">All Types ({currentComponent.detections.length})</option>
                          {teleDataGroupComponents.map((tc) => {
                            const count = currentComponent.detections.filter((d: any) =>
                              (detectionClassifications.get(d.id) || d.prediction.classifiedName) === tc.name
                            ).length;
                            return (
                              <option key={tc.name} value={tc.name}>{tc.name} ({count})</option>
                            );
                          })}
                        </select>
                      )}
                      {currentComponent.componentName === '__LIGHTING_GROUP__' && lightingGroupComponents.length > 0 && !isCountingAll && (
                        <select
                          value={lightingClassFilter}
                          onChange={(e) => setLightingClassFilter(e.target.value)}
                          style={{
                            marginLeft: 'auto',
                            padding: '5px 10px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-strong)',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: 'var(--text-secondary)',
                            background: 'var(--bg-secondary)',
                            cursor: 'pointer',
                            outline: 'none',
                            minWidth: '180px'
                          }}
                        >
                          <option value="all">All Fixtures ({currentComponent.detections.length})</option>
                          {lightingGroupComponents.map((lc) => {
                            const code = lc.symbol.toUpperCase();
                            const count = currentComponent.detections.filter((d: any) =>
                              (detectionClassifications.get(d.id) || d.prediction.classifiedName) === code
                            ).length;
                            return (
                              <option key={code} value={code}>{code} — {lc.name} ({count})</option>
                            );
                          })}
                        </select>
                      )}
                    </div>
                  )}
                </div>
                  );
                })()}

                {/* Bulk Reclassification Toolbar — sticky above grid */}
                {currentComponent?.componentName === '__RECEPTACLES_GROUP__' && receptacleGroupComponents.length > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 16px',
                    background: bulkSelectedDetections.size > 0 ? 'var(--green-tint-light)' : 'var(--bg-secondary)',
                    border: `1px solid ${bulkSelectedDetections.size > 0 ? '#bbf7d0' : 'var(--border-color)'}`,
                    borderRadius: '10px',
                    marginBottom: '12px',
                    flexShrink: 0,
                    transition: 'all 0.2s'
                  }}>
                    <div
                      onClick={() => {
                        if (!currentComponent) return;
                        const visibleIds = currentComponent.detections
                          .filter((d: any) => {
                            if (receptacleClassFilter === 'all') return true;
                            const cls = detectionClassifications.get(d.id) || d.prediction.classifiedName || '';
                            if (receptacleClassFilter === '__unclassified__') return !cls;
                            return cls === receptacleClassFilter;
                          })
                          .filter((d: any) => (allComponentSelections.get(currentComponent.componentName) || new Set()).has(d.id))
                          .map((d: any) => d.id);
                        const allChecked = visibleIds.length > 0 && visibleIds.every((id: string) => bulkSelectedDetections.has(id));
                        if (allChecked) {
                          setBulkSelectedDetections(new Set());
                        } else {
                          setBulkSelectedDetections(new Set(visibleIds));
                        }
                      }}
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                        border: `2px solid ${bulkSelectedDetections.size > 0 ? '#059669' : 'var(--text-placeholder)'}`,
                        background: bulkSelectedDetections.size > 0 ? '#059669' : 'var(--bg-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        flexShrink: 0
                      }}
                    >
                      {bulkSelectedDetections.size > 0 && (
                        <span style={{ color: '#ffffff', fontSize: '13px', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
                      )}
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: bulkSelectedDetections.size > 0 ? '#166534' : 'var(--text-muted)', minWidth: '90px' }}>
                      {bulkSelectedDetections.size > 0 ? `${bulkSelectedDetections.size} selected` : 'Select cards'}
                    </span>
                    <div style={{ width: '1px', height: '24px', background: 'var(--border-strong)', flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>Reclassify as:</span>
                    <select
                      value={bulkReclassTarget}
                      onChange={(e) => setBulkReclassTarget(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-strong)',
                        fontSize: '13px',
                        fontWeight: 500,
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                        minWidth: '200px',
                        outline: 'none'
                      }}
                    >
                      <option value="">Choose type...</option>
                      {receptacleGroupComponents.map((rc) => (
                        <option key={rc.name} value={rc.name}>{rc.name}</option>
                      ))}
                    </select>
                    <button
                      disabled={bulkSelectedDetections.size === 0 || !bulkReclassTarget}
                      onClick={() => {
                        setDetectionClassifications(prev => {
                          const newMap = new Map(prev);
                          bulkSelectedDetections.forEach(id => newMap.set(id, bulkReclassTarget));
                          return newMap;
                        });
                        setBulkSelectedDetections(new Set());
                        setBulkReclassTarget('');
                      }}
                      style={{
                        padding: '6px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: bulkSelectedDetections.size > 0 && bulkReclassTarget ? '#059669' : 'var(--border-strong)',
                        color: '#ffffff',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: bulkSelectedDetections.size > 0 && bulkReclassTarget ? 'pointer' : 'not-allowed',
                        transition: 'background 0.2s'
                      }}
                    >
                      Apply
                    </button>
                    {bulkSelectedDetections.size > 0 && (
                      <button
                        onClick={() => { setBulkSelectedDetections(new Set()); setBulkReclassTarget(''); }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          border: '1px solid var(--border-strong)',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-secondary)',
                          fontSize: '13px',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}

                {/* Scrollable Flat Grid for current component */}
                <div 
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    marginBottom: '20px'
                  }}
                >
                  {/* Empty/Loading state */}
                  {(!currentComponent || currentComponent.detections.length === 0) && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: 'var(--text-muted)',
                      textAlign: 'center',
                      padding: '40px'
                    }}>
                      {isCountingAll ? (
                        <>
                          <div style={{ marginBottom: '16px' }}>
                            <Sparkles size={48} color="#3b82f6" />
                          </div>
                          <div style={{ fontSize: '18px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                            Scanning for {currentComponent?.componentName === '__RECEPTACLES_GROUP__' ? 'receptacles' : currentComponent?.componentName === '__TELE_DATA_GROUP__' ? 'tele/data outlets' : currentComponent?.componentName === '__LIGHTING_GROUP__' ? 'lighting fixtures' : (currentComponent?.componentName || 'components')}...
                          </div>
                          <div style={{ fontSize: '14px', marginTop: '8px' }}>
                            Detections will appear here as they're found
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: '48px', marginBottom: '16px' }}>😕</div>
                          <div style={{ fontSize: '18px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                            No {currentComponent?.componentName === '__RECEPTACLES_GROUP__' ? 'receptacles' : currentComponent?.componentName === '__TELE_DATA_GROUP__' ? 'tele/data outlets' : currentComponent?.componentName === '__LIGHTING_GROUP__' ? 'lighting fixtures' : (currentComponent?.componentName || 'components')} found
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Flat grid of detections for current component */}
                  {currentComponent && currentComponent.detections.length > 0 && (
                    <div 
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                        gap: '16px',
                        padding: '4px'
                      }}
                    >
                      {currentComponent.detections.filter((det: any) => {
                        if (currentComponent.componentName === '__RECEPTACLES_GROUP__') {
                          if (receptacleClassFilter === 'all') return true;
                          const detClass = detectionClassifications.get(det.id) || det.prediction.classifiedName || '';
                          if (receptacleClassFilter === '__unclassified__') return !detClass;
                          return detClass === receptacleClassFilter;
                        }
                        if (currentComponent.componentName === '__TELE_DATA_GROUP__') {
                          if (teleDataClassFilter === 'all') return true;
                          const detClass = detectionClassifications.get(det.id) || det.prediction.classifiedName || '';
                          return detClass === teleDataClassFilter;
                        }
                        if (currentComponent.componentName === '__LIGHTING_GROUP__') {
                          if (lightingClassFilter === 'all') return true;
                          const detCode = detectionClassifications.get(det.id) || det.prediction.classifiedName || '';
                          return detCode === lightingClassFilter;
                        }
                        return true;
                      }).map((det: any, idx: number) => {
                        const isSelected = currentSelections.has(det.id);
                        const detPage = det.prediction.page || pageNumber;
                        
                        return (
                          <div
                            key={det.id}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleMultiDetectionSelection(currentComponent.componentName, det.id);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.02)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.boxShadow = isSelected ? `0 0 12px ${currentComponent.componentColor}40` : 'none';
                            }}
                            style={{
                              border: isSelected ? `3px solid ${currentComponent.componentColor}` : '3px solid var(--border-color)',
                              borderRadius: '10px',
                              padding: '8px',
                              cursor: isCountingAll ? 'wait' : 'pointer',
                              background: isSelected ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
                              transition: 'all 0.2s',
                              opacity: isCountingAll ? 0.7 : (isSelected ? 1 : 0.6),
                              position: 'relative',
                              boxShadow: isSelected ? `0 0 12px ${currentComponent.componentColor}40` : 'none',
                              userSelect: 'none',
                              WebkitUserSelect: 'none'
                            }}
                          >
                            {/* Deselected indicator */}
                            {!isSelected && (
                              <div style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                background: '#dc2626',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)'
                              }}>
                                ✗
                              </div>
                            )}
                            
                            {/* Bulk select checkbox (receptacles only) */}
                            {currentComponent.componentName === '__RECEPTACLES_GROUP__' && !isCountingAll && isSelected && (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBulkSelectedDetections(prev => {
                                    const newSet = new Set(prev);
                                    newSet.has(det.id) ? newSet.delete(det.id) : newSet.add(det.id);
                                    return newSet;
                                  });
                                }}
                                style={{
                                  position: 'absolute',
                                  top: '8px',
                                  left: '8px',
                                  zIndex: 2,
                                  width: '22px',
                                  height: '22px',
                                  borderRadius: '4px',
                                  border: `2px solid ${bulkSelectedDetections.has(det.id) ? '#059669' : 'rgba(255,255,255,0.8)'}`,
                                  background: bulkSelectedDetections.has(det.id) ? '#059669' : 'rgba(0,0,0,0.3)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.25)',
                                  transition: 'all 0.15s'
                                }}
                              >
                                {bulkSelectedDetections.has(det.id) && (
                                  <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
                                )}
                              </div>
                            )}

                            {/* Page badge */}
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              left: currentComponent.componentName === '__RECEPTACLES_GROUP__' && !isCountingAll && isSelected ? '36px' : '8px',
                              background: 'rgba(0, 0, 0, 0.6)',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: 500,
                              transition: 'left 0.15s'
                            }}>
                              P{detPage}
                            </div>
                            
                            {/* Thumbnail - zoomed to center for easier verification */}
                            <div style={{
                              overflow: 'hidden',
                              borderRadius: '6px',
                              marginBottom: '8px',
                              aspectRatio: '4/3',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <img 
                                src={det.thumbnail} 
                                alt={`Detection ${idx + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  objectPosition: 'center',
                                  transform: currentComponent?.isLighting ? 'scale(1.8)' : 'scale(1)',
                                  transformOrigin: 'center center',
                                  background: 'var(--bg-primary)',
                                  pointerEvents: 'none'
                                }}
                              />
                            </div>
                            
                            {/* Classification info */}
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>
                              {det.prediction.unfiltered && !det.prediction.classifiedName && (
                                <div style={{
                                  fontSize: '10px',
                                  marginTop: '4px',
                                  padding: '2px 6px',
                                  background: 'var(--yellow-tint-medium)',
                                  color: 'var(--yellow-text-dark)',
                                  borderRadius: '4px',
                                  fontWeight: 600,
                                  lineHeight: '1.3',
                                  animation: 'pulse 1.5s infinite'
                                }}>
                                  Classifying...
                                </div>
                              )}
                              {/* Grouped receptacles: show editable dropdown */}
                              {currentComponent.componentName === '__RECEPTACLES_GROUP__' && receptacleGroupComponents.length > 0 && !det.prediction.unfiltered ? (
                                <select
                                  value={detectionClassifications.get(det.id) || det.prediction.classifiedName || receptacleGroupComponents[0].name}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setDetectionClassifications(prev => {
                                      const newMap = new Map(prev);
                                      newMap.set(det.id, e.target.value);
                                      return newMap;
                                    });
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    fontSize: '10px',
                                    marginTop: '4px',
                                    padding: '3px 4px',
                                    background: 'var(--blue-tint-medium)',
                                    color: 'var(--blue-text-dark)',
                                    borderRadius: '4px',
                                    fontWeight: 600,
                                    border: '1px solid var(--blue-tint-strong)',
                                    width: '100%',
                                    cursor: 'pointer',
                                    outline: 'none'
                                  }}
                                >
                                  {receptacleGroupComponents.map((rc) => (
                                    <option key={rc.name} value={rc.name}>{rc.name}</option>
                                  ))}
                                </select>
                              ) : currentComponent.componentName === '__TELE_DATA_GROUP__' && teleDataGroupComponents.length > 0 && !det.prediction.unfiltered ? (
                                <select
                                  value={detectionClassifications.get(det.id) || det.prediction.classifiedName || teleDataGroupComponents[0].name}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setDetectionClassifications(prev => {
                                      const newMap = new Map(prev);
                                      newMap.set(det.id, e.target.value);
                                      return newMap;
                                    });
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    fontSize: '10px',
                                    marginTop: '4px',
                                    padding: '3px 4px',
                                    background: 'var(--indigo-tint-medium)',
                                    color: '#3730a3',
                                    borderRadius: '4px',
                                    fontWeight: 600,
                                    border: '1px solid #a5b4fc',
                                    width: '100%',
                                    cursor: 'pointer',
                                    outline: 'none'
                                  }}
                                >
                                  {teleDataGroupComponents.map((tc) => (
                                    <option key={tc.name} value={tc.name}>{tc.name}</option>
                                  ))}
                                </select>
                              ) : currentComponent.componentName === '__LIGHTING_GROUP__' && lightingGroupComponents.length > 0 ? (
                                (() => {
                                  const code = detectionClassifications.get(det.id) || det.prediction.classifiedName || '';
                                  const lcComp = lightingGroupComponents.find(lc => lc.symbol.toUpperCase() === code);
                                  return code ? (
                                    <div style={{
                                      fontSize: '10px',
                                      marginTop: '4px',
                                      padding: '2px 6px',
                                      background: 'var(--yellow-tint-medium)',
                                      color: '#854d0e',
                                      borderRadius: '4px',
                                      fontWeight: 600,
                                      lineHeight: '1.3'
                                    }}>
                                      {code}{lcComp ? ` — ${lcComp.name}` : ''}
                                    </div>
                                  ) : null;
                                })()
                              ) : (
                                det.prediction.classifiedName && !det.prediction.unfiltered && (
                                  <div style={{
                                    fontSize: '10px',
                                    marginTop: '4px',
                                    padding: '2px 6px',
                                    background: 'var(--blue-tint-medium)',
                                    color: 'var(--blue-text-dark)',
                                    borderRadius: '4px',
                                    fontWeight: 600,
                                    lineHeight: '1.3'
                                  }}>
                                    {det.prediction.classifiedName}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'space-between',
                  borderTop: '2px solid var(--border-color)',
                  paddingTop: '16px'
                }}>
                  {(() => {
                    // Get visible detection IDs based on active filter
                    const getVisibleIds = () => {
                      if (!currentComponent) return new Set<string>();
                      const isGroupedR = currentComponent.componentName === '__RECEPTACLES_GROUP__';
                      const isGroupedTD = currentComponent.componentName === '__TELE_DATA_GROUP__';
                      const isGroupedL = currentComponent.componentName === '__LIGHTING_GROUP__';
                      if (isGroupedR && receptacleClassFilter !== 'all') {
                        return new Set(currentComponent.detections.filter((d: any) => {
                          const cls = detectionClassifications.get(d.id) || d.prediction.classifiedName || '';
                          if (receptacleClassFilter === '__unclassified__') return !cls;
                          return cls === receptacleClassFilter;
                        }).map((d: any) => d.id));
                      }
                      if (isGroupedTD && teleDataClassFilter !== 'all') {
                        return new Set(currentComponent.detections.filter((d: any) => {
                          const cls = detectionClassifications.get(d.id) || d.prediction.classifiedName || '';
                          return cls === teleDataClassFilter;
                        }).map((d: any) => d.id));
                      }
                      if (isGroupedL && lightingClassFilter !== 'all') {
                        return new Set(currentComponent.detections.filter((d: any) => {
                          const code = detectionClassifications.get(d.id) || d.prediction.classifiedName || '';
                          return code === lightingClassFilter;
                        }).map((d: any) => d.id));
                      }
                      return new Set(currentComponent.detections.map((d: any) => d.id));
                    };
                    const isFiltered = (currentComponent?.componentName === '__RECEPTACLES_GROUP__' && receptacleClassFilter !== 'all') ||
                                       (currentComponent?.componentName === '__TELE_DATA_GROUP__' && teleDataClassFilter !== 'all') ||
                                       (currentComponent?.componentName === '__LIGHTING_GROUP__' && lightingClassFilter !== 'all');
                    return (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        if (currentComponent) {
                          const visibleIds = getVisibleIds();
                          setAllComponentSelections(prev => {
                            const newSelections = new Map(prev);
                            const existing = new Set(newSelections.get(currentComponent.componentName) || []);
                            visibleIds.forEach(id => existing.add(id));
                            newSelections.set(currentComponent.componentName, existing);
                            return newSelections;
                          });
                        }
                      }}
                      style={{
                        padding: '10px 16px',
                        background: 'var(--bg-tertiary)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '8px',
                        color: 'var(--text-secondary)',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      {isFiltered ? 'Select Filtered' : 'Select All'}
                    </button>
                    <button
                      onClick={() => {
                        if (currentComponent) {
                          const visibleIds = getVisibleIds();
                          setAllComponentSelections(prev => {
                            const newSelections = new Map(prev);
                            const existing = new Set(newSelections.get(currentComponent.componentName) || []);
                            visibleIds.forEach(id => existing.delete(id));
                            newSelections.set(currentComponent.componentName, existing);
                            return newSelections;
                          });
                        }
                      }}
                      style={{
                        padding: '10px 16px',
                        background: 'var(--bg-tertiary)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '8px',
                        color: 'var(--text-secondary)',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      {isFiltered ? 'Clear Filtered' : 'Clear All'}
                    </button>
                  </div>
                    );
                  })()}
                  
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={() => { void handleExportClassificationDataset('multi'); }}
                      disabled={isCountingAll || multiComponentDetections.length === 0 || isExportingDataset}
                      style={{
                        padding: '12px 20px',
                        background: (isCountingAll || multiComponentDetections.length === 0 || isExportingDataset) ? 'var(--border-strong)' : 'var(--bg-tertiary)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '8px',
                        color: 'var(--text-secondary)',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: (isCountingAll || multiComponentDetections.length === 0 || isExportingDataset) ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      title="Download thumbnails sorted by class folders"
                    >
                      <Download size={16} />
                      {isExportingDataset ? 'Exporting...' : 'Export Dataset'}
                    </button>
                    {/* Back button - only show if not first component */}
                    {currentReviewComponentIndex > 0 && (
                      <button
                        onClick={handleMultiComponentBack}
                        style={{
                          padding: '12px 20px',
                          background: 'var(--bg-tertiary)',
                          border: '1px solid var(--border-strong)',
                          borderRadius: '8px',
                          color: 'var(--text-secondary)',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <ArrowLeft size={16} />
                        Back
                      </button>
                    )}
                    
                    <button
                      onClick={() => {
                        setShowMultiComponentReviewModal(false);
                        setMultiComponentDetections([]);
                        setAllComponentSelections(new Map());
                        setCurrentReviewComponentIndex(0);
                        setIsCountingAll(false);
                        setIsParallelCountSession(false);
                        setCountAllProgress('');
                        setCountAllComponentProgress([]);
                        setPendingKeyComponents([]);
                        setReceptacleGroupComponents([]);
                        setLightingGroupComponents([]);
                        setDetectionClassifications(new Map());
                        setBulkSelectedDetections(new Set());
                        setBulkReclassTarget('');
                      }}
                      style={{
                        padding: '12px 20px',
                        background: 'var(--bg-tertiary)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '8px',
                        color: 'var(--text-secondary)',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    
                    {/* Next or Finish button */}
                    {isLastComponent || multiComponentDetections.length <= 1 ? (
                      <button
                        onClick={handleFinishMultiComponentReview}
                        disabled={isCountingAll}
                        style={{
                          padding: '12px 24px',
                          background: isCountingAll ? 'var(--border-strong)' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: isCountingAll ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          boxShadow: isCountingAll ? 'none' : '0 4px 12px rgba(59, 130, 246, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          if (!isCountingAll) {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isCountingAll) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                          }
                        }}
                      >
                        <Rocket size={16} />
                        {isCountingAll ? 'Counting...' : 'Finish & Estimate'}
                      </button>
                    ) : (
                      <button
                        onClick={handleMultiComponentNext}
                        disabled={(() => {
                          const currentProgress = countAllComponentProgress[currentReviewComponentIndex];
                          const isCurrentStillCounting = currentProgress?.status === 'processing';
                          return isCurrentStillCounting || (isCountingAll && !isParallelCountSession);
                        })()}
                        style={{
                          padding: '12px 24px',
                          background: (() => {
                            const currentProgress = countAllComponentProgress[currentReviewComponentIndex];
                            const isCurrentStillCounting = currentProgress?.status === 'processing';
                            return (isCurrentStillCounting || (isCountingAll && !isParallelCountSession)) ? 'var(--text-placeholder)' : '#3b82f6';
                          })(),
                          border: 'none',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: (() => {
                            const currentProgress = countAllComponentProgress[currentReviewComponentIndex];
                            const isCurrentStillCounting = currentProgress?.status === 'processing';
                            return (isCurrentStillCounting || (isCountingAll && !isParallelCountSession)) ? 'not-allowed' : 'pointer';
                          })(),
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          opacity: (() => {
                            const currentProgress = countAllComponentProgress[currentReviewComponentIndex];
                            const isCurrentStillCounting = currentProgress?.status === 'processing';
                            return (isCurrentStillCounting || (isCountingAll && !isParallelCountSession)) ? 0.7 : 1;
                          })()
                        }}
                        onMouseEnter={(e) => {
                          const currentProgress = countAllComponentProgress[currentReviewComponentIndex];
                          const isCurrentStillCounting = currentProgress?.status === 'processing';
                          if (!(isCurrentStillCounting || (isCountingAll && !isParallelCountSession))) e.currentTarget.style.background = '#2563eb';
                        }}
                        onMouseLeave={(e) => {
                          const currentProgress = countAllComponentProgress[currentReviewComponentIndex];
                          const isCurrentStillCounting = currentProgress?.status === 'processing';
                          if (!(isCurrentStillCounting || (isCountingAll && !isParallelCountSession))) e.currentTarget.style.background = '#3b82f6';
                        }}
                      >
                        {(() => {
                          const currentProgress = countAllComponentProgress[currentReviewComponentIndex];
                          const isCurrentStillCounting = currentProgress?.status === 'processing';
                          if (isCurrentStillCounting) {
                            return (
                              <>
                                <Loader2 size={16} className="spin" />
                                Counting current...
                              </>
                            );
                          }
                          if (isCountingAll && !isParallelCountSession) {
                            return (
                              <>
                                <Loader2 size={16} className="spin" />
                                Counting...
                              </>
                            );
                          }
                          if (pendingKeyComponents.length > 0) {
                            return (
                              <>
                                Next Symbol ({pendingKeyComponents.length} left)
                                <ArrowRight size={16} />
                              </>
                            );
                          }
                          return (
                            <>
                              {isParallelCountSession ? 'Next Component' : 'Next'}
                              <ArrowRight size={16} />
                            </>
                          );
                        })()}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Count All Progress Modal - Hidden when review modal is showing */}
      {isCountingAll && countAllComponentProgress.length > 0 && !showMultiComponentReviewModal && !showLightingCountingModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100007,
            padding: '20px',
            backdropFilter: 'blur(4px)'
          }}
        >
          <div 
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 1px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.8)'
            }}
          >
            {/* Header */}
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <h3 style={{ margin: 0, marginBottom: '8px', color: 'var(--text-primary)', fontSize: '18px', fontWeight: 700, letterSpacing: '-0.01em' }}>
                AI Auto-Counting
              </h3>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '13px' }}>
                {(() => {
                  const processingComponents = countAllComponentProgress.filter(p => p.status === 'processing');
                  if (processingComponents.length > 0 && processingComponents[0].totalBatches) {
                    // Lighting uses 20 batches/page (staggered grid), regular uses 5
                    const bpp = (processingComponents[0].totalBatches || 0) > 10 ? 20 : 5;
                    const totalPages = Math.ceil((processingComponents[0].totalBatches || 0) / bpp);
                    const uniquePages = new Set(processingComponents.map(p => p.currentPage).filter(Boolean));
                    return `Scanning ${totalPages} pages in parallel • ${uniquePages.size} active pages`;
                  }
                  return 'Analyzing components across all pages...';
                })()}
              </p>
            </div>
            
            {/* Current Component Progress Bar - resets for each component */}
            {(() => {
              const current = countAllComponentProgress.find(p => p.status === 'processing');
              const completedCount = countAllComponentProgress.filter(p => p.status === 'complete').length;
              const totalCount = countAllComponentProgress.length;
              const totalDetections = countAllComponentProgress.reduce((sum, p) => sum + (p.count || 0), 0);

              const totalBatches = current ? (current.totalBatches || 0) : 0;
              const completedBatches = current ? (current.completedBatches || 0) : 0;
              const percentage = totalBatches > 0 ? Math.round((completedBatches / totalBatches) * 100) : 0;

              return (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>
                      {current ? `Counting: ${current.name}` : (completedCount === totalCount ? 'Complete' : 'Waiting...')}
                    </span>
                    <span style={{ fontSize: '16px', color: '#3b82f6', fontWeight: 700 }}>
                      {percentage}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '10px',
                    background: 'var(--border-color)',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{
                      width: `${percentage}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                      transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      borderRadius: '10px',
                      boxShadow: percentage > 0 ? '0 0 8px rgba(59, 130, 246, 0.4)' : 'none'
                    }} />
                  </div>
                  <div style={{
                    marginTop: '6px',
                    textAlign: 'center',
                    fontSize: '11px',
                    color: 'var(--text-placeholder)',
                    fontWeight: 500
                  }}>
                    {totalDetections} detections • {completedCount}/{totalCount} components complete
                  </div>
                </div>
              );
            })()}
            
            {/* Component List - Scrollable */}
            <div 
              className="count-all-scrollbar"
              style={{
                maxHeight: '280px',
                overflowY: 'auto',
                overflowX: 'hidden',
                marginTop: '16px',
                paddingRight: '4px'
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {countAllComponentProgress.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      background: item.status === 'processing' ? 'rgba(59, 130, 246, 0.05)' : 'var(--bg-secondary)',
                      borderRadius: '10px',
                      border: item.status === 'processing' ? '1.5px solid #3b82f6' : '1px solid var(--border-color)',
                      transition: 'all 0.3s ease',
                      boxShadow: item.status === 'processing' ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none'
                    }}
                  >
                    {/* Reference Image Thumbnail */}
                    <div style={{
                      width: '40px',
                      height: '40px',
                      flexShrink: 0,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: `2px solid ${item.color}`,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      background: 'var(--bg-primary)'
                    }}>
                      <img 
                        src={item.referenceImage} 
                        alt={item.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                    
                    {/* Component Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: 600, 
                        color: 'var(--text-primary)', 
                        fontSize: '13px',
                        marginBottom: '2px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {item.name}
                      </div>
                      
                      {/* Status text with page and batch info */}
                      {item.status === 'processing' && (
                        <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: 600, marginTop: '2px' }}>
                          {item.currentPage && item.totalBatches ? (
                            <>
                              Pg {item.currentPage}/{Math.ceil(item.totalBatches / ((item.totalBatches || 0) > 10 ? 20 : 5))}
                              {item.currentBatch && (
                                <> • B{item.currentBatch}/{(item.totalBatches || 0) > 10 ? 20 : 5}</>
                              )}
                              {item.count !== undefined && item.count > 0 && (
                                <> • {item.count} found</>
                              )}
                            </>
                          ) : (
                            'Processing...'
                          )}
                        </div>
                      )}
                      
                      {item.status === 'complete' && item.count !== undefined && (
                        <div style={{ fontSize: '11px', color: 'var(--green-text)', fontWeight: 600, marginTop: '2px' }}>
                          ✓ Found {item.count}
                        </div>
                      )}
                      
                      {item.status === 'pending' && (
                        <div style={{ fontSize: '11px', color: 'var(--text-placeholder)', fontWeight: 500, marginTop: '2px' }}>
                          Waiting...
                        </div>
                      )}
                    </div>
                    
                    {/* Status Icon */}
                    <div style={{ 
                      flexShrink: 0,
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {item.status === 'pending' && (
                        <div style={{ 
                          width: '8px', 
                          height: '8px', 
                          borderRadius: '50%', 
                          background: 'var(--border-strong)' 
                        }} />
                      )}
                      {item.status === 'processing' && (
                        <Loader2 size={18} className="spin" style={{ color: '#3b82f6' }} />
                      )}
                      {item.status === 'complete' && (
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: '#059669',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ffffff',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Labor Estimation Progress Bar - Bottom */}
      {showLaborEstimationModal && (
        <div 
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            width: isAgentChatCollapsed ? '100%' : 'calc(100% - 540px)', // Leave space for chat when open
            background: 'linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)',
            borderTop: '2px solid var(--border-color)',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.12)',
            zIndex: 100008,
            pointerEvents: 'none',
            animation: 'slideUp 0.3s ease-out',
            transition: 'width 0.3s ease'
          }}
        >
          <div 
            style={{
              padding: '16px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              maxWidth: '1400px',
              margin: '0 auto'
            }}
          >
            {/* Icon & Title */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              flexShrink: 0
            }}>
              <Calculator size={20} style={{ color: '#10b981' }} />
              <span style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: 'var(--text-primary)',
                whiteSpace: 'nowrap'
              }}>
                Adding to Estimate
              </span>
            </div>
            
            {/* Component Statuses - Inline */}
            <div style={{ 
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              overflow: 'hidden',
              minWidth: 0
            }}>
              {laborEstimationProgress.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: item.status === 'complete' ? '#059669' : item.status === 'error' ? '#dc2626' : item.status === 'processing' ? '#10b981' : 'var(--text-muted)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.status === 'pending' && <span>⏱️</span>}
                  {item.status === 'processing' && <Loader2 size={14} className="spin" style={{ color: '#10b981' }} />}
                  {item.status === 'complete' && <span style={{ fontSize: '16px' }}>✓</span>}
                  {item.status === 'error' && <span>✗</span>}
                  <span>{item.name} ({item.count})</span>
                </div>
              ))}
            </div>
            
            {/* Progress Bar & Count */}
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexShrink: 0,
              minWidth: '220px'
            }}>
              {(() => {
                const completedCount = laborEstimationProgress.filter(i => i.status === 'complete').length;
                const totalCount = laborEstimationProgress.length;
                const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
                
                return (
                  <>
                    <div style={{
                      flex: 1,
                      height: '8px',
                      background: 'var(--border-color)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      minWidth: '140px'
                    }}>
                      <div style={{
                        width: `${percentage}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                        transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderRadius: '4px'
                      }} />
                    </div>
                    <span style={{ 
                      fontSize: '14px', 
                      fontWeight: 700, 
                      color: '#10b981',
                      minWidth: '50px',
                      textAlign: 'right'
                    }}>
                      {completedCount}/{totalCount}
                    </span>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Wire Type Selection Modal */}
      <WireTypeModal
        isOpen={showWireTypeModal}
        onClose={() => {
          setShowWireTypeModal(false);
          // If closing while already in wire run mode, exit the mode
          if (isWireRunMode) {
            setIsWireRunMode(false);
            setSelectedWireType(null);
            setCurrentTool('select');
          }
        }}
        onSave={handleWireTypeSelected}
        totalLength={0}
        hasCalibration={!!calibrationData[pageNumber]}
        unit={calibrationData[pageNumber]?.unit || 'ft'}
        isSelectMode={true}
        selectedWireType={selectedWireType}
      />
    </div>
  );
});

export default PDFAnnotator;
